import {businessWeekday} from '../utils/businessTime';
import {t, useLanguage, interfaceLocale} from '../i18n';
import {getMarket,formatMarketMoney} from '../utils/market';
import BusinessProfileLoading from "./BusinessProfileLoading";
// LOOP BUSINESSPAGE SERVICE CARDS POLISH V1
// LOOP SERVICE DESCRIPTION V1 — public service cards show concise descriptions.
// LOOP FROM PRICE V1 — public service price labels respect fixed/from/quote semantics.
import { businessRefreshStyles } from "./businessRefreshStyles";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Lucide from "lucide-react";
import { publicServicePaymentLabel } from "../utils/publicBookingPaymentClient";
import "./BusinessServicePaymentLayout.css";
import * as FaIcons from "react-icons/fa";
import * as GiIcons from "react-icons/gi";
import * as MdIcons from "react-icons/md";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  doc,
  collection,
  onSnapshot,
  query,
  orderBy,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db as defaultDb, auth } from '../firebase';
import { Mail, MapPin, Phone, Star, Heart, X, Globe, Instagram, Facebook, Navigation, MessageCircle, Clock, ChevronDown, CalendarDays, Share2, ExternalLink, Scissors, Wind, Sparkles, Droplets, Dumbbell, Palette, Wand2, Package } from 'lucide-react';
import BookingInterface from '../BookingInterface';
import PublicMembershipPlansSection from '../components/memberships/PublicMembershipPlansSection';
import { getBusinessLogo } from '../components/SmartLogoFrame';
import { SUPER_USERS } from '../config';
import fsClient from '../data/firestoreClient';
import { isIosCapacitor } from '../utils/nativeHttp';
import { getManualAuth, isManualAuthLikelyValid } from '../utils/manualAuth';
import { SERVICE_ICON_OPTIONS } from '../data/serviceIconOptions';
import { resolveBusinessIdentifier, getBusinessRouteParam, slugifyBusinessName } from '../utils/businessSlug';



const LOOP_ANALYTICS_VISITOR_KEY = "loop_analytics_visitor_id_v1";
const LOOP_ANALYTICS_SESSION_KEY = "loop_analytics_session_id_v1";

function loopSafeRandomId(prefix = "loop") {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
  } catch (_) {}
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getLoopAnalyticsStorageId(key, prefix, storageType = "local") {
  try {
    if (typeof window === "undefined") return loopSafeRandomId(prefix);
    const store = storageType === "session" ? window.sessionStorage : window.localStorage;
    if (!store) return loopSafeRandomId(prefix);
    let value = store.getItem(key);
    if (!value) {
      value = loopSafeRandomId(prefix);
      store.setItem(key, value);
    }
    return value;
  } catch (_) {
    return loopSafeRandomId(prefix);
  }
}

function getLoopAnalyticsSource() {
  try {
    if (typeof window === "undefined") return "Directo / desconocido";
    const params = new URLSearchParams(window.location.search || "");
    const explicit = params.get("utm_source") || params.get("source") || params.get("ref") || "";
    if (explicit) return explicit;

    const ref = String(document.referrer || "").trim();
    if (!ref) return "Directo / desconocido";
    try {
      const host = new URL(ref).hostname.replace(/^www\./, "");
      return host || ref;
    } catch (_) {
      return ref.slice(0, 120);
    }
  } catch (_) {
    return "Directo / desconocido";
  }
}

function getLoopAnalyticsUtm() {
  try {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search || "");
    return {
      utmSource: params.get("utm_source") || params.get("source") || null,
      utmMedium: params.get("utm_medium") || null,
      utmCampaign: params.get("utm_campaign") || null,
      utmContent: params.get("utm_content") || null,
      utmTerm: params.get("utm_term") || null,
    };
  } catch (_) {
    return {};
  }
}

function getLoopAnalyticsDevice() {
  try {
    if (typeof window === "undefined") return "unknown";
    const width = window.innerWidth || 0;
    if (width <= 640) return "mobile";
    if (width <= 1024) return "tablet";
    return "desktop";
  } catch (_) {
    return "unknown";
  }
}

async function trackBusinessAnalyticsEventSafe({ db, businessId, type, payload = {} }) {
  try {
    if (!db || !businessId || !type) return;

    const visitorId = getLoopAnalyticsStorageId(LOOP_ANALYTICS_VISITOR_KEY, "visitor", "local");
    const sessionId = getLoopAnalyticsStorageId(LOOP_ANALYTICS_SESSION_KEY, "session", "session");
    const url = typeof window !== "undefined" ? window.location.href : "";
    const path = typeof window !== "undefined" ? `${window.location.pathname || ""}${window.location.search || ""}` : "";

    const eventPayload = {
      type,
      eventType: type,
      name: type,
      action: type,
      businessId,
      visitorId,
      sessionId,
      source: getLoopAnalyticsSource(),
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      url,
      path,
      device: getLoopAnalyticsDevice(),
      ...getLoopAnalyticsUtm(),
      ...payload,
      createdAt: new Date().toISOString(),
      occurredAt: new Date().toISOString(),
    };

    if (isIosCapacitor()) {
      await fsClient.addDocToCollectionPath(`businesses/${businessId}/analyticsEvents`, eventPayload, { db });
      await fsClient.addDocToCollectionPath("platformEvents", { ...eventPayload, platformEventMirror: true }, { db });
      return;
    }

    await addDoc(collection(db, `businesses/${businessId}/analyticsEvents`), eventPayload);
    await addDoc(collection(db, "platformEvents"), { ...eventPayload, platformEventMirror: true });
  } catch (err) {
    // Analytics should never break the booking experience.
    try { console.warn("[BusinessPage] analytics event skipped", err); } catch (_) {}
  }
}


function isPublicBusiness(biz) {
  if (!biz || typeof biz !== "object") return false;
  return (
    biz.visible !== false &&
    biz.hidden !== true &&
    biz.isActive !== false &&
    biz.published !== false
  );
}


function normalizeBusinessCoverageArea(area, idx = 0) {
  if (!area) return null;
  if (typeof area === "string") {
    const label = String(area || "").trim();
    if (!label) return null;
    return { id: `legacy-${idx}`, label, level: "zone", extraFee: 0 };
  }

  const level = area.level || (area.zone ? "zone" : area.corregimiento ? "corregimiento" : area.district ? "district" : area.province ? "province" : "zone");
  const label = area.label || [area.zone, area.corregimiento, area.district, area.province].filter(Boolean).join(", ");
  if (!label) return null;

  return {
    ...area,
    id: area.id || `${level}-${label}-${idx}`,
    label,
    level,
    extraFee: Number(area.extraFee ?? area.fee ?? area.deliveryFee ?? 0) || 0,
  };
}

function getBusinessCoverageAreas(biz) {
  const advanced = Array.isArray(biz?.serviceAreasAdvanced)
    ? biz.serviceAreasAdvanced
    : Array.isArray(biz?.deliveryAreas)
      ? biz.deliveryAreas
      : [];

  if (advanced.length) return advanced.map(normalizeBusinessCoverageArea).filter(Boolean);

  const legacy = Array.isArray(biz?.serviceAreas)
    ? biz.serviceAreas
    : String(biz?.coverageArea || biz?.serviceArea || "").split(",");

  return legacy.map(normalizeBusinessCoverageArea).filter(Boolean);
}

function businessCoverageLevelLabel(area) {
  const level = String(area?.level || "zone").toLowerCase();
  if (level === "province") return "Provincia";
  if (level === "district") return "Distrito";
  if (level === "corregimiento") return "Corregimiento";
  return "Zona";
}

function getBusinessPageLogo(biz) {
  return getBusinessLogo(biz) || biz?.logoUrl || biz?.logo || biz?.menuImage || "";
}

function getStructuredBusinessAddress(biz) {
  if (!biz || typeof biz !== "object") return "";
  const locationObj = biz.location || biz.businessLocation || biz.structuredLocation || biz.localLocation || null;
  const label =
    biz.locationLabel ||
    biz.businessLocationLabel ||
    biz.structuredLocationLabel ||
    (locationObj && (locationObj.label || locationObj.formattedAddress || locationObj.addressLabel));
  if (label && String(label).trim()) return String(label).trim();
  if (locationObj && typeof locationObj === "object") {
    const parts = [locationObj.zone, locationObj.corregimiento, locationObj.district, locationObj.province].filter(Boolean);
    if (parts.length >= 2) return parts.join(", ");
  }
  return "";
}

function getBusinessMapsHref(biz, fallback) {
  const explicit =
    biz?.__viewGoogleMapsLink ||
    biz?.googleMapsLink ||
    biz?.mapsLink ||
    biz?.googleMapsUrl ||
    biz?.mapUrl ||
    biz?.locationUrl ||
    "";
  if (explicit) return explicit;
  const structured = getStructuredBusinessAddress(biz);
  if (structured) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(structured)}`;
  return fallback || "";
}

function hasBusinessMapsLink(biz, fallback = "") {
  return Boolean(
    biz?.__viewGoogleMapsLink ||
    biz?.googleMapsLink ||
    biz?.mapsLink ||
    biz?.googleMapsUrl ||
    biz?.mapUrl ||
    biz?.locationUrl ||
    fallback
  );
}

function getBusinessVisitMode(biz) {
  const raw = String(
    biz?.serviceMode ||
    biz?.visitMode ||
    biz?.attentionMode ||
    biz?.typeOfService ||
    biz?.businessServiceMode ||
    ""
  ).toLowerCase();

  if (raw.includes("hybrid") || raw.includes("mixto") || raw.includes("both")) return "hybrid";
  if (raw.includes("delivery") || raw.includes("domicilio") || raw.includes("home")) return "delivery";
  if (raw.includes("onsite") || raw.includes("local") || raw.includes("physical") || raw.includes("fisico") || raw.includes("físico")) return "onsite";

  const hasAreas = getBusinessCoverageAreas(biz).length > 0;
  const hasStructuredAddress = !!getStructuredBusinessAddress(biz);
  if (hasAreas && hasStructuredAddress) return "hybrid";
  if (hasAreas) return "delivery";
  return "onsite";
}

function shouldShowBusinessCoverage(biz) {
  const mode = getBusinessVisitMode(biz);
  return (mode === "delivery" || mode === "hybrid") && getBusinessCoverageAreas(biz).length > 0;
}

function shouldShowBusinessLocation(biz) {
  const mode = getBusinessVisitMode(biz);
  return (mode === "onsite" || mode === "hybrid") && !!getStructuredBusinessAddress(biz);
}


function getBusinessServiceOrderValue(service, fallback = 0) {
  const candidates = [
    service?.serviceOrder,
    service?.publicOrder,
    service?.displayOrder,
    service?.order,
    service?.sortOrder,
    service?.position,
    service?.index,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }

  return fallback;
}

function sortBusinessServicesForPublic(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((service, idx) => ({ service, idx }))
    .sort((a, b) => {
      const ao = getBusinessServiceOrderValue(a.service, a.idx);
      const bo = getBusinessServiceOrderValue(b.service, b.idx);
      if (ao !== bo) return ao - bo;

      const aCreated = Number(a.service?.createdAt?.seconds || a.service?.createdAt || 0);
      const bCreated = Number(b.service?.createdAt?.seconds || b.service?.createdAt || 0);
      if (aCreated !== bCreated) return aCreated - bCreated;

      return a.idx - b.idx;
    })
    .map((item) => item.service);
}

const pageStyles = `
  :root {
    --loop-green: #00796f;
    --loop-green-dark: #005f58;
    --loop-ink: #0f172a;
    --loop-muted: #667085;
    --loop-line: rgba(226, 232, 240, .92);
    --loop-soft: #f8fafc;
    --loop-card-shadow: 0 22px 70px rgba(15, 23, 42, .07);
  }

  .business-page-shell {
    min-height: 100vh;
    background:
      radial-gradient(circle at top center, rgba(0,121,111,.075), transparent 34%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 46%, #f8fafc 100%);
    padding: 18px 14px 54px;
  }

  .bp-container {
    width: min(100%, 1180px);
    margin: 0 auto;
  }

  .bp-page {
    background: #ffffff;
    border: 1px solid var(--loop-line);
    border-radius: 30px;
    box-shadow: var(--loop-card-shadow);
    overflow: hidden;
  }

  .bp-hero {
    padding: 28px;
    background:
      radial-gradient(circle at 18% 0%, rgba(0,121,111,.055), transparent 34%),
      #ffffff;
  }

  .bp-hero-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 20px;
  }

  .bp-title-wrap {
    min-width: 0;
  }

  .bp-title {
    margin: 0;
    color: var(--loop-green-dark);
    font-size: clamp(28px, 3.1vw, 40px);
    line-height: .98;
    font-weight: 950;
    letter-spacing: -0.062em;
  }

  .bp-tagline {
    margin-top: 8px;
    max-width: 680px;
    color: var(--loop-muted);
    font-size: 14px;
    line-height: 1.45;
    font-weight: 650;
  }

  .bp-rating {
    margin-top: 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(245,158,11,.30);
    background: rgba(255,251,235,.88);
    color: #92400e;
    border-radius: 999px;
    padding: 7px 11px;
    font-size: 12px;
    font-weight: 900;
  }

  .bp-rating span {
    color: #64748b;
    font-weight: 760;
  }

  .bp-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex: 0 0 auto;
  }

  .bp-icon-btn,
  .bp-primary-btn,
  .bp-secondary-btn {
    border: 0;
    cursor: pointer;
    font-family: inherit;
  }

  .bp-icon-btn {
    width: 48px;
    height: 48px;
    border-radius: 17px;
    border: 1px solid var(--loop-line);
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 24px rgba(15,23,42,.055);
  }

  .bp-primary-btn {
    height: 48px;
    min-height: 48px;
    border-radius: 17px;
    padding: 0 24px;
    background: #0f766e;
    color: #ffffff;
    font-size: 14px;
    font-weight: 950;
    box-shadow: 0 14px 30px rgba(15,118,110,.20);
  }

  .bp-secondary-btn {
    height: 48px;
    min-height: 48px;
    border-radius: 17px;
    padding: 0 18px;
    background: #ffffff;
    color: #344054;
    border: 1px solid var(--loop-line);
    font-size: 14px;
    font-weight: 900;
  }

  .bp-hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 316px;
    gap: 28px;
    align-items: start;
  }





  .bp-logo-empty {
    color: #94a3b8;
    font-size: 13px;
    font-weight: 850;
  }

  .bp-info-panel {
    border: 1px solid var(--loop-line);
    border-radius: 26px;
    background: rgba(255,255,255,.985);
    box-shadow: 0 18px 46px rgba(15,23,42,.06);
    padding: 16px;
  }

  .bp-info-row {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 10px;
    padding: 11px 0;
    border-bottom: 1px solid rgba(241,245,249,.96);
  }

  .bp-info-row:last-child {
    border-bottom: 0;
  }

  .bp-info-icon {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #0f766e;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .bp-info-label {
    font-size: 10px;
    line-height: 1;
    letter-spacing: .09em;
    color: #667085;
    font-weight: 950;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  .bp-info-value {
    color: #101828;
    font-size: 13px;
    line-height: 1.34;
    font-weight: 780;
    overflow-wrap: anywhere;
  }

  .bp-info-value.muted {
    color: #667085;
    font-weight: 720;
  }

  .bp-info-value a {
    color: #0f766e;
    text-decoration: none;
    font-weight: 950;
  }

  .bp-contact-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }

  .bp-contact-action {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 36px;
    border-radius: 999px;
    border: 1px solid rgba(219,227,238,.95);
    background: #ffffff;
    color: #0f766e;
    padding: 0 13px;
    font-size: 12px;
    font-weight: 950;
    text-decoration: none;
  }

  .bp-contact-action.whatsapp {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #047857;
  }

  .bp-zone-list {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 7px;
  }

  .bp-zone-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #99f6e4;
    background: #f0fdfa;
    color: #0f766e;
    border-radius: 999px;
    padding: 7px 9px;
    font-size: 11px;
    font-weight: 950;
  }

  .bp-zone-chip em {
    font-style: normal;
    color: #047857;
    background: #ffffff;
    border-radius: 999px;
    padding: 2px 5px;
    font-size: 10px;
    font-weight: 950;
  }

  .bp-map-card {
    position: relative;
    height: 118px;
    width: 100%;
    margin-top: 8px;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(226,232,240,.92);
    background: #eef2f7;
    box-shadow: 0 10px 24px rgba(15,23,42,.055);
  }

  .bp-map-card iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }

  .bp-map-open {
    position: absolute;
    left: 8px;
    bottom: 8px;
    z-index: 4;
    min-height: 28px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 999px;
    padding: 0 9px;
    background: rgba(255,255,255,.95);
    color: #0f766e;
    border: 1px solid rgba(204,251,241,.95);
    box-shadow: 0 8px 18px rgba(15,23,42,.12);
    font-size: 11px;
    font-weight: 950;
    text-decoration: none;
  }

  .bp-section {
    padding: 28px;
    border-top: 1px solid rgba(226,232,240,.66);
    background: #ffffff;
  }

  .bp-section-title {
    margin: 0 0 14px;
    color: var(--loop-ink);
    font-size: 20px;
    line-height: 1.1;
    letter-spacing: -.045em;
    font-weight: 950;
  }

  .bp-about {
    max-width: 820px;
    color: #344054;
    font-size: 14px;
    line-height: 1.72;
    text-align: left;
  }

  .bp-team-grid {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    align-items: stretch;
  }

  .bp-reviews-head {
    max-width: 860px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
  }

  .bp-review-average {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #f0fdfa;
    color: #0f766e;
    border: 1px solid #ccfbf1;
    border-radius: 999px;
    padding: 7px 11px;
    font-size: 12px;
    font-weight: 950;
  }

  .bp-reviews-list {
    max-width: 860px;
  }

  .bp-rating-card {
    min-height: 150px;
    border-radius: 20px;
    background: rgba(255,255,255,.92);
    border: 1px solid rgba(226,232,240,.92);
    box-shadow: 0 14px 34px rgba(54,41,25,.07);
    padding: 16px;
  }

  .bp-rating-card-head {
    display: grid;
    grid-template-columns: 46px minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
  }

  .bp-rating-avatar {
    width: 46px;
    height: 46px;
    border-radius: 999px;
    overflow: hidden;
    background: #ecfdf5;
    color: #00675f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
  }

  .bp-rating-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .bp-rating-user {
    min-width: 0;
    display: grid;
    gap: 3px;
  }

  .bp-rating-user strong {
    color: #0f172a;
    font-size: 14px;
    line-height: 1.15;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .bp-rating-user span {
    color: #64748b;
    font-size: 11.5px;
    font-weight: 750;
  }

  .bp-rating-user em {
    color: #00675f;
    font-style: normal;
    font-size: 11px;
    font-weight: 900;
  }

  .bp-rating-score {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #f59e0b;
    font-size: 13px;
    font-weight: 950;
    white-space: nowrap;
  }

  .bp-rating-score > div {
    display: inline-flex;
    align-items: center;
    gap: 1px;
  }

  .bp-rating-comment {
    margin: 14px 0 0;
    color: #334155;
    font-size: 13px;
    line-height: 1.48;
    font-weight: 700;
  }

  .bp-rating-comment.is-empty {
    color: #94a3b8;
    font-style: italic;
    font-weight: 750;
  }

  .business-show-more-reviews {
    margin-top: 14px;
    min-height: 42px;
    border-radius: 999px;
    border: 1px solid rgba(226,232,240,.92);
    background: #ffffff;
    color: #0f766e;
    padding: 0 15px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 950;
    box-shadow: 0 10px 24px rgba(15,23,42,.045);
    cursor: pointer;
  }

  .business-show-more-reviews svg {
    transition: transform .18s ease;
  }

  .business-show-more-reviews svg.is-open {
    transform: rotate(180deg);
  }

  .bp-packages {
    padding: 30px 28px 26px;
    border-top: 1px solid rgba(226,232,240,.66);
    background: linear-gradient(180deg, #ffffff 0%, #fbfdfd 100%);
  }

  .bp-packages-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 18px;
  }

  .bp-kicker {
    color: #008878;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .16em;
    text-transform: uppercase;
    margin-bottom: 7px;
  }

  .bp-packages-title {
    margin: 0;
    color: #101828;
    font-size: clamp(30px, 3.2vw, 44px);
    line-height: .92;
    letter-spacing: -.07em;
    font-weight: 950;
  }

  .bp-packages-copy {
    margin: 7px 0 0;
    color: #667085;
    font-size: 13px;
    font-weight: 720;
  }

  .bp-package-actions {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    flex: 0 0 auto;
  }

  .bp-carousel-arrow {
    width: 44px;
    height: 44px;
    border-radius: 999px;
    border: 1px solid rgba(226,232,240,.95);
    background: #ffffff;
    color: #101828;
    font-size: 20px;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(15,23,42,.06);
  }

  .bp-packages-rail,
  .event-packages-grid {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(250px, 282px);
    gap: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 2px 2px 10px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .bp-packages-rail::-webkit-scrollbar,
  .event-packages-grid::-webkit-scrollbar {
    display: none;
  }

  .event-package-card {
    scroll-snap-align: start;
    background: #ffffff;
    border: 1px solid rgba(226,232,240,.9);
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 12px 30px rgba(15,23,42,.065);
    cursor: pointer;
  }

  .event-package-photo {
    height: 154px;
    background: #f1f5f9;
    overflow: hidden;
    position: relative;
  }

  .event-package-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .event-package-photo span {
    color: #94a3b8;
    font-size: 13px;
    font-weight: 850;
  }

  .event-package-badges {
    position: absolute;
    left: 10px;
    bottom: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .event-package-badges span {
    border-radius: 999px;
    background: rgba(255,255,255,.92);
    color: #101828;
    padding: 5px 8px;
    font-size: 10px;
    font-weight: 950;
  }

  .event-package-body {
    padding: 14px;
  }

  .event-package-body h4 {
    margin: 0;
    color: #101828;
    font-size: 16px;
    line-height: 1.05;
    letter-spacing: -.04em;
    font-weight: 950;
  }

  .event-package-body p {
    margin: 8px 0 0;
    color: #667085;
    font-size: 12px;
    line-height: 1.4;
    min-height: 34px;
    font-weight: 680;
  }

  .event-package-meta {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
    color: #00675f;
    font-size: 12px;
    font-weight: 950;
  }

  .event-package-reserve {
    margin-top: 12px;
    width: 100%;
    height: 42px;
    border: 0;
    border-radius: 15px;
    background: #0f766e;
    color: #ffffff;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
  }

  .event-packages-empty {
    padding: 20px;
    border-radius: 18px;
    background: #f8fafc;
    border: 1px solid var(--loop-line);
    color: #667085;
    font-size: 14px;
    font-weight: 750;
  }

  .business-booking-overlay {
    position: fixed;
    inset: 0;
    z-index: 30000;
    background: rgba(15, 23, 42, 0.58);
    padding: 18px;
    overflow: hidden;
    overscroll-behavior: contain;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .business-booking-modal {
    width: min(1100px, calc(100vw - 36px));
    height: min(860px, calc(100dvh - 36px));
    max-height: calc(100dvh - 36px);
    margin: 0;
    background: #ffffff;
    border-radius: 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.22);
  }

  .business-booking-modal > div:first-child {
    position: relative;
    z-index: 5;
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    flex: 0 0 auto;
  }

  .business-booking-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    background: #ffffff;
  }

  .business-loading-card {
    background: #fff;
    border: 1px solid #e9eef4;
    border-radius: 26px;
    box-shadow: 0 18px 50px rgba(15,23,42,.07);
    overflow: hidden;
    padding: 22px;
  }

  .business-loading-top {
    display: grid;
    grid-template-columns: minmax(0,1fr) 310px;
    gap: 24px;
    align-items: start;
  }

  .business-skeleton {
    position: relative;
    overflow: hidden;
    background: #eef2f7;
    border-radius: 18px;
  }

  .business-skeleton::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.72), transparent);
    animation: businessSkeleton 1.15s infinite;
  }

  @keyframes businessSkeleton {
    100% { transform: translateX(100%); }
  }

  .business-skeleton-title { height: 34px; width: 320px; max-width: 70%; margin-bottom: 12px; }
  .business-skeleton-pill { height: 28px; width: 150px; border-radius: 999px; margin-bottom: 18px; }
  .business-skeleton-logo { height: 250px; width: 100%; }
  .business-skeleton-side { height: 118px; width: 100%; margin-bottom: 14px; }

  @media (min-width: 1180px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(255px, 275px);
    }
  }

  @media (max-width: 920px) {
    .business-page-shell {
      padding: 8px 8px calc(28px + env(safe-area-inset-bottom, 0px));
      background: #f8fafc;
    }

    .bp-page {
      border-radius: 24px;
    }

    .bp-hero {
      padding: 14px;
    }

    .bp-hero-top {
      flex-direction: column;
      gap: 14px;
      margin-bottom: 14px;
    }

    .bp-actions {
      width: 100%;
      position: sticky;
      bottom: calc(8px + env(safe-area-inset-bottom, 0px));
      z-index: 10;
      display: grid;
      grid-template-columns: 56px minmax(0,1fr);
      gap: 10px;
      padding: 8px;
      border-radius: 22px;
      border: 1px solid rgba(226,232,240,.92);
      background: rgba(255,255,255,.92);
      box-shadow: 0 18px 48px rgba(15,23,42,.16);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
    }

    .bp-icon-btn {
      width: 56px;
      height: 54px;
      border-radius: 18px;
      box-shadow: none;
    }

    .bp-primary-btn {
      width: 100%;
      height: 54px;
      min-height: 54px;
      border-radius: 18px;
      font-size: 15px;
    }

    .bp-secondary-btn {
      display: none;
    }

    .bp-hero-grid {
      grid-template-columns: 1fr;
      gap: 14px;
    }



    .bp-info-panel {
      border-radius: 22px;
      padding: 14px;
    }

    .bp-map-card {
      height: 146px;
    }

    .bp-section {
      padding: 22px 16px 0;
    }

    .bp-packages {
      padding: 24px 16px 18px;
    }

    .bp-packages-head {
      align-items: flex-start;
      flex-direction: column;
    }

    .bp-package-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .bp-packages-title {
      font-size: 31px;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(230px, 260px);
    }

    .bp-team-grid {
      flex-wrap: nowrap;
      overflow-x: auto;
      gap: 12px;
      padding-bottom: 6px;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }

    .bp-team-grid::-webkit-scrollbar {
      display: none;
    }

    .bp-reviews-head {
      align-items: flex-start;
      flex-direction: column;
      gap: 9px;
    }

    .business-booking-overlay {
      padding: 0;
      background: #ffffff;
      align-items: stretch;
      justify-content: stretch;
    }

    .business-booking-modal {
      width: 100vw;
      height: 100dvh;
      max-height: 100dvh;
      border-radius: 0;
    }

    .business-booking-modal > div:first-child {
      padding-top: calc(8px + env(safe-area-inset-top, 0px));
    }
  }

  @media (max-width: 420px) {
    .bp-title {
      font-size: 28px;
    }



    .bp-info-row {
      grid-template-columns: 32px minmax(0,1fr);
    }
  }





  .bp-hours-row {
    align-items: flex-start;
  }

  .bp-hours-today,
  .bp-hours-day {
    display: grid;
    grid-template-columns: minmax(92px, 1fr) auto;
    gap: 12px;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px solid rgba(241,245,249,.96);
    color: #344054;
    font-size: 13px;
    line-height: 1.2;
  }

  .bp-hours-today {
    padding-top: 2px;
  }

  .bp-hours-week {
    margin-top: 4px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid rgba(226,232,240,.86);
    padding: 7px 10px;
  }

  .bp-hours-week .bp-hours-day:last-child {
    border-bottom: 0;
  }

  .bp-hours-today span,
  .bp-hours-day span {
    color: #475467;
    font-weight: 780;
  }

  .bp-hours-today strong,
  .bp-hours-day strong {
    color: #101828;
    font-weight: 950;
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    text-align: right;
  }

  .bp-hours-today em,
  .bp-hours-day em {
    font-style: normal;
  }

  .bp-hours-toggle {
    margin-top: 9px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 0;
    background: transparent;
    color: #00796f;
    font-family: inherit;
    font-size: 12px;
    font-weight: 950;
    padding: 0;
    cursor: pointer;
  }

  .bp-hours-toggle svg {
    transition: transform .18s ease;
  }

  .bp-hours-toggle svg.is-open {
    transform: rotate(180deg);
  }

  .bp-maps-link {
    margin-top: 10px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 36px;
    border-radius: 999px;
    border: 1px solid #ccfbf1;
    background: #f0fdfa;
    color: #0f766e !important;
    padding: 0 13px;
    font-size: 12px;
    font-weight: 950;
    text-decoration: none;
    box-shadow: 0 8px 18px rgba(15,23,42,.04);
  }

  .bp-map-card {
    display: none !important;
  }

  @media (max-width: 920px) {



  }

  @media (max-width: 420px) {

  }


  /* =========================================================
     LOOP BUSINESS PAGE — PHASE 8 WEB REVAMP
     Airbnb + Apple + Fresha: logo-first, clean, no place photo.
     ========================================================= */
  :root {
    --bp-cream: #fff8ef;
    --bp-cream-2: #fbf3e8;
    --bp-green: #00675f;
    --bp-green-2: #0f766e;
    --bp-ink: #111827;
    --bp-muted: #5f6f83;
    --bp-line-soft: rgba(108,88,61,.13);
    --bp-shadow-soft: 0 24px 70px rgba(54,41,25,.10);
  }

  .business-page-shell {
    background:
      radial-gradient(circle at 12% 4%, rgba(0,103,95,.09), transparent 30%),
      radial-gradient(circle at 92% 12%, rgba(245,158,11,.08), transparent 26%),
      linear-gradient(180deg, #fffaf4 0%, #fff8ef 45%, #fffaf4 100%) !important;
    padding: 0 0 52px !important;
  }

  .bp-container {
    width: min(100%, 1320px) !important;
    max-width: 1320px !important;
    padding: 28px clamp(18px, 4.2vw, 58px) 0 !important;
  }

  .bp-page {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  .bp-hero {
    padding: 0 !important;
    background: transparent !important;
  }

  .bp-hero-top {
    display: grid !important;
    grid-template-columns: minmax(360px, .82fr) minmax(0, 1fr) !important;
    gap: 34px !important;
    align-items: center !important;
    margin: 0 0 22px !important;
  }

  .bp-title-wrap {
    grid-column: 2 !important;
    grid-row: 1 !important;
    align-self: center !important;
  }

  .bp-title {
    color: var(--bp-green) !important;
    font-size: clamp(42px, 4.4vw, 66px) !important;
    line-height: .93 !important;
    letter-spacing: -.08em !important;
    font-weight: 950 !important;
    max-width: 700px !important;
  }

  .bp-tagline {
    color: #39465a !important;
    font-size: 16px !important;
    line-height: 1.45 !important;
    font-weight: 650 !important;
    margin-top: 10px !important;
  }

  .bp-rating {
    margin-top: 14px !important;
    background: #fff6e7 !important;
    border: 1px solid rgba(245,158,11,.26) !important;
    color: #b45309 !important;
    box-shadow: none !important;
  }

  .bp-actions {
    grid-column: 2 !important;
    grid-row: 1 !important;
    justify-self: start !important;
    align-self: end !important;
    transform: translateY(76px) !important;
    display: flex !important;
    gap: 14px !important;
    padding: 0 !important;
    position: static !important;
  }

  .bp-icon-btn,
  .bp-secondary-btn {
    background: rgba(255,255,255,.92) !important;
    border: 1px solid var(--bp-line-soft) !important;
    border-radius: 18px !important;
    box-shadow: 0 14px 34px rgba(54,41,25,.08) !important;
  }

  .bp-primary-btn {
    min-width: 172px !important;
    height: 58px !important;
    border-radius: 18px !important;
    background: linear-gradient(135deg, #00675f, #008577) !important;
    box-shadow: 0 18px 38px rgba(0,103,95,.20) !important;
    font-size: 16px !important;
  }

  .bp-hero-grid {
    display: contents !important;
  }





  .bp-info-panel {
    margin-top: 26px !important;
    display: grid !important;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 0 !important;
    padding: 24px 28px !important;
    border-radius: 28px !important;
    border: 1px solid var(--bp-line-soft) !important;
    background: rgba(255,255,255,.86) !important;
    box-shadow: var(--bp-shadow-soft) !important;
    backdrop-filter: blur(16px) !important;
    -webkit-backdrop-filter: blur(16px) !important;
  }

  .bp-info-row {
    grid-template-columns: 48px minmax(0, 1fr) !important;
    gap: 14px !important;
    padding: 8px 20px !important;
    border-bottom: 0 !important;
    border-right: 1px solid rgba(108,88,61,.12) !important;
  }

  .bp-info-row:last-child,
  .bp-info-row:nth-child(4n) {
    border-right: 0 !important;
  }

  .bp-info-icon {
    width: 46px !important;
    height: 46px !important;
    background: #eaf8f5 !important;
    color: var(--bp-green) !important;
  }

  .bp-info-label {
    color: #758195 !important;
    font-size: 11px !important;
    letter-spacing: .10em !important;
    margin-bottom: 7px !important;
  }

  .bp-info-value {
    color: #111827 !important;
    font-size: 14px !important;
    line-height: 1.35 !important;
    font-weight: 760 !important;
  }

  .bp-contact-actions,
  .bp-zone-list {
    gap: 8px !important;
  }

  .bp-contact-action,
  .bp-maps-link,
  .bp-zone-chip {
    border-radius: 999px !important;
    background: #f0fdfa !important;
    border-color: #ccfbf1 !important;
    color: var(--bp-green) !important;
    box-shadow: none !important;
  }

  .bp-section,
  .bp-packages {
    padding: 32px 0 0 !important;
    border-top: 1px solid rgba(108,88,61,.12) !important;
    background: transparent !important;
    margin-top: 32px !important;
  }

  .bp-about-section {
    max-width: 720px !important;
    border-top: 0 !important;
    margin-top: 38px !important;
  }

  .bp-section-title,
  .bp-packages-title {
    color: var(--bp-green) !important;
    font-size: clamp(24px, 2.2vw, 34px) !important;
    line-height: 1 !important;
    letter-spacing: -.06em !important;
    font-weight: 950 !important;
  }

  .bp-about {
    max-width: 720px !important;
    color: #243144 !important;
    font-size: 15px !important;
    line-height: 1.72 !important;
    font-weight: 520 !important;
  }

  .bp-packages-head {
    margin-bottom: 18px !important;
  }

  .bp-kicker {
    color: var(--bp-green) !important;
    font-size: 12px !important;
    letter-spacing: .18em !important;
  }

  .bp-packages-copy {
    color: var(--bp-muted) !important;
    font-size: 14px !important;
    font-weight: 650 !important;
  }

  .bp-packages-rail,
  .event-packages-grid {
    display: grid !important;
    grid-auto-flow: column !important;
    grid-auto-columns: minmax(230px, 265px) !important;
    gap: 18px !important;
    padding: 2px 4px 16px !important;
  }

  .event-package-card {
    border-radius: 20px !important;
    border: 1px solid var(--bp-line-soft) !important;
    background: rgba(255,255,255,.88) !important;
    box-shadow: 0 16px 38px rgba(54,41,25,.08) !important;
  }

  .event-package-photo {
    height: 148px !important;
    background:
      radial-gradient(circle at 20% 0%, rgba(0,103,95,.065), transparent 38%),
      linear-gradient(135deg, #fff8f1, #f7efe6) !important;
    display: grid !important;
    place-items: center !important;
  }

  .event-package-photo img {
    object-fit: cover !important;
  }

  .event-package-photo span {
    color: var(--bp-green) !important;
    font-size: 13px !important;
    font-weight: 950 !important;
    letter-spacing: -.02em !important;
  }

  .event-package-badges {
    display: none !important;
  }

  .event-package-body h4 {
    color: #151a24 !important;
    font-size: 17px !important;
  }

  .event-package-meta {
    color: var(--bp-green) !important;
  }

  .event-package-reserve {
    display: none !important;
  }

  .bp-team-grid {
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    gap: 28px !important;
    padding: 6px 4px 18px !important;
    scrollbar-width: none !important;
  }

  .bp-team-grid::-webkit-scrollbar { display: none !important; }

  .bp-team-grid > * {
    flex: 0 0 112px !important;
    background: transparent !important;
    box-shadow: none !important;
    border-color: transparent !important;
  }

  .bp-reviews-head {
    max-width: none !important;
  }

  .bp-reviews-list {
    max-width: none !important;
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 18px !important;
  }

  .bp-reviews-list > * {
    margin: 0 !important;
    border-radius: 20px !important;
    background: rgba(255,255,255,.86) !important;
    border: 1px solid var(--bp-line-soft) !important;
    box-shadow: 0 14px 34px rgba(54,41,25,.07) !important;
  }

  .bp-review-average {
    background: #fffdf7 !important;
    border-color: rgba(245,158,11,.22) !important;
    color: var(--bp-green) !important;
  }

  .bp-final-cta {
    margin: 42px 0 0 !important;
    border-radius: 24px !important;
    background: linear-gradient(135deg, #eaf8f5 0%, #f4fbfa 100%) !important;
    border: 1px solid #d6f3ef !important;
    padding: 24px 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 20px !important;
  }

  .bp-final-cta strong {
    color: var(--bp-green) !important;
    font-size: 26px !important;
    letter-spacing: -.06em !important;
    line-height: 1 !important;
  }

  @media (max-width: 920px) {
    .bp-container { padding: 16px 14px 0 !important; }
    .bp-hero-top {
      grid-template-columns: 1fr !important;
      gap: 18px !important;
    }

    .bp-title { font-size: 36px !important; }
    .bp-actions {
      width: 100% !important;
      display: grid !important;
      grid-template-columns: 56px 1fr !important;
      position: static !important;
      padding: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      border: 0 !important;
      backdrop-filter: none !important;
    }
    .bp-info-panel {
      margin-top: 18px !important;
      grid-template-columns: 1fr !important;
      padding: 16px !important;
    }
    .bp-info-row {
      border-right: 0 !important;
      border-bottom: 1px solid rgba(108,88,61,.10) !important;
      padding: 14px 0 !important;
    }
    .bp-info-row:last-child { border-bottom: 0 !important; }
    .bp-reviews-list { grid-template-columns: 1fr !important; }
    .bp-packages-rail,
    .event-packages-grid { grid-auto-columns: minmax(210px, 230px) !important; }
  }



  /* =========================================================
     PHASE 8B — WEB BUSINESS PAGE REALIGN
     Keeps the first mockup direction: logo left, info right,
     compact hero, full-width info strip below.
     ========================================================= */
  @media (min-width: 921px) {
    .business-page-shell {
      background:
        radial-gradient(circle at 8% 5%, rgba(0,103,95,.08), transparent 26%),
        radial-gradient(circle at 92% 12%, rgba(245,158,11,.07), transparent 24%),
        linear-gradient(180deg, #fffaf4 0%, #fff8ef 42%, #fffaf4 100%) !important;
    }

    .bp-container {
      width: min(100%, 1320px) !important;
      max-width: 1320px !important;
      padding: 26px clamp(24px, 4vw, 58px) 0 !important;
    }

    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(360px, 430px) minmax(0, 1fr) !important;
      column-gap: clamp(34px, 5vw, 66px) !important;
      row-gap: 22px !important;
      align-items: center !important;
      padding: 0 !important;
      background: transparent !important;
    }

    .bp-hero-top {
      grid-column: 2 !important;
      grid-row: 1 !important;
      display: block !important;
      margin: 0 !important;
      min-width: 0 !important;
    }

    .bp-title-wrap {
      margin: 0 !important;
      max-width: 720px !important;
    }

    .bp-title {
      font-size: clamp(42px, 4.2vw, 62px) !important;
      line-height: .94 !important;
      letter-spacing: -.078em !important;
      color: #00675f !important;
      text-align: left !important;
    }

    .bp-tagline {
      margin-top: 10px !important;
      color: #39465a !important;
      font-size: 16px !important;
      font-weight: 650 !important;
    }

    .bp-actions {
      position: static !important;
      display: flex !important;
      justify-content: flex-start !important;
      align-items: center !important;
      gap: 14px !important;
      margin-top: 22px !important;
      padding: 0 !important;
      transform: none !important;
      width: auto !important;
    }

    .bp-hero-grid {
      grid-column: 1 / -1 !important;
      grid-row: 1 / span 2 !important;
      display: contents !important;
    }





    .bp-info-panel {
      grid-column: 1 / -1 !important;
      grid-row: 3 !important;
      margin-top: 0 !important;
      display: grid !important;
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 0 !important;
      padding: 22px 28px !important;
      border-radius: 28px !important;
      border: 1px solid rgba(108,88,61,.13) !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 24px 70px rgba(54,41,25,.10) !important;
      backdrop-filter: blur(16px) !important;
      -webkit-backdrop-filter: blur(16px) !important;
    }

    .bp-info-row {
      padding: 0 20px !important;
      border-bottom: 0 !important;
      border-right: 1px solid rgba(108,88,61,.12) !important;
      min-height: 78px !important;
      display: grid !important;
      grid-template-columns: 42px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 14px !important;
    }

    .bp-info-row:last-child { border-right: 0 !important; }

    .bp-packages,
    .bp-section,
    .bp-final-cta {
      max-width: 1320px !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }
  }

  @media (max-width: 920px) {
    .bp-hero { display: block !important; }
    .bp-hero-grid { display: block !important; }
    .bp-actions { transform: none !important; }
  }

  /* =========================================================
     PHASE 8F — BUSINESS PAGE CLEANUP
     - Fixes service CSS text leak
     - Non-event services are text-only cards
     - Cleaner logo canvas and info panel
     ========================================================= */
  @media (min-width: 921px) {
    .bp-hero-top {
      grid-template-columns: minmax(360px, .82fr) minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 42px !important;
    }





    .bp-actions {
      display: flex !important;
      gap: 12px !important;
      align-items: center !important;
      margin-top: 18px !important;
    }

    .bp-icon-btn {
      width: 48px !important;
      height: 48px !important;
      border-radius: 18px !important;
      background: rgba(255,255,255,.94) !important;
      border: 1px solid rgba(226,232,240,.9) !important;
      box-shadow: 0 14px 34px rgba(54,41,25,.075) !important;
    }

    .bp-primary-btn {
      height: 48px !important;
      min-height: 48px !important;
      border-radius: 18px !important;
      padding: 0 34px !important;
      background: linear-gradient(135deg, #00675f 0%, #008878 100%) !important;
      box-shadow: 0 18px 38px rgba(0,103,95,.18) !important;
    }
  }

  .bp-info-panel {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 0 !important;
    padding: 22px 26px !important;
    border-radius: 28px !important;
    background: rgba(255,255,255,.86) !important;
    border: 1px solid rgba(108,88,61,.12) !important;
    box-shadow: 0 24px 70px rgba(54,41,25,.08) !important;
    backdrop-filter: blur(18px) !important;
    -webkit-backdrop-filter: blur(18px) !important;
  }

  .bp-info-row {
    border-bottom: 0 !important;
    border-right: 1px solid rgba(108,88,61,.12) !important;
    padding: 8px 22px !important;
    min-height: 92px !important;
    align-items: center !important;
  }

  .bp-info-row:first-child { padding-left: 0 !important; }
  .bp-info-row:last-child { border-right: 0 !important; padding-right: 0 !important; }

  .bp-info-icon {
    width: 42px !important;
    height: 42px !important;
    background: #eaf8f4 !important;
    color: #00796f !important;
  }

  .bp-info-label {
    color: #718096 !important;
    letter-spacing: .15em !important;
    font-size: 10px !important;
    margin-bottom: 7px !important;
  }

  .bp-info-value {
    color: #111827 !important;
    font-size: 13px !important;
    line-height: 1.35 !important;
    font-weight: 850 !important;
  }

  .bp-packages {
    padding-top: 34px !important;
  }

  .bp-packages-title {
    letter-spacing: -.075em !important;
  }

  .event-package-card {
    border-radius: 24px !important;
    background: rgba(255,255,255,.82) !important;
    border: 1px solid rgba(108,88,61,.12) !important;
    box-shadow: 0 18px 48px rgba(54,41,25,.075) !important;
  }

  .event-package-body.no-photo-body {
    min-height: 168px !important;
    padding: 22px 18px 16px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    background:
      radial-gradient(circle at 18% 0%, rgba(0,103,95,.075), transparent 38%),
      linear-gradient(180deg, rgba(255,255,255,.88), rgba(255,248,239,.78)) !important;
  }

  .event-package-body.no-photo-body h4 {
    font-size: 18px !important;
    line-height: 1.02 !important;
    letter-spacing: -.055em !important;
    color: #111827 !important;
  }

  .event-package-body.no-photo-body p,
  .event-package-body.no-photo-body .event-package-badges,
  .event-package-photo.no-photo {
    display: none !important;
  }

  .event-package-meta {
    margin-top: 18px !important;
    align-items: flex-end !important;
  }

  .event-package-meta span:first-child {
    color: #00675f !important;
  }

  .event-package-meta span:last-child {
    font-weight: 950 !important;
    color: #00675f !important;
  }

  .event-package-reserve {
    margin-top: 14px !important;
    height: 40px !important;
    border-radius: 15px !important;
    background: #00675f !important;
  }

  @media (max-width: 920px) {
    .bp-info-panel {
      display: block !important;
      padding: 14px !important;
      border-radius: 22px !important;
      background: #ffffff !important;
    }

    .bp-info-row {
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226,232,240,.78) !important;
      min-height: 0 !important;
      padding: 12px 0 !important;
    }

    .bp-info-row:last-child { border-bottom: 0 !important; }

  }

  /* =========================================================
     BP FINAL LOGO SYSTEM — SINGLE SOURCE / ADAPTIVE MAIN MENU MATCH
     ========================================================= */

  .bp-logo-card {
    width: 100% !important;
    max-width: 430px !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .bp-logo-stage {
    width: 100% !important;
    aspect-ratio: 16 / 9 !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;

    border-radius: 28px !important;
    overflow: hidden !important;
    position: relative !important;

    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    border: 1px solid rgba(108, 88, 61, .12) !important;
    background: transparent !important;
    box-shadow: 0 20px 58px rgba(54, 41, 25, .08) !important;
  }

  .bp-logo-stage::before,
  .bp-logo-stage::after {
    display: none !important;
    content: none !important;
  }

  .bp-logo-stage .smart-logo-frame {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: 100% !important;
    max-height: 100% !important;

    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    overflow: hidden !important;
    isolation: isolate !important;

    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    border: 0 !important;
    box-shadow: none !important;
    border-radius: 28px !important;
  }

  .bp-logo-stage .smart-logo-frame::before,
  .bp-logo-stage .smart-logo-frame::after,
  .bp-logo-stage .smart-logo-inner::before,
  .bp-logo-stage .smart-logo-inner::after,
  .bp-logo-stage .smart-logo-wash,
  .bp-logo-stage .smart-logo-blur {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
    content: none !important;
  }

  .bp-logo-stage .smart-logo-inner {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;

    padding: 18px !important;
    box-sizing: border-box !important;

    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    overflow: hidden !important;
    border-radius: 28px !important;

    background: var(--smart-logo-bg, #f7efe6) !important;
  }

  .bp-logo-stage .smart-logo-img,
  .bp-logo-stage img {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: 100% !important;
    max-height: 100% !important;

    object-position: center center !important;

    display: block !important;
    margin: 0 auto !important;

    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    transform: none !important;
    filter: none !important;
  }

  /* 
     Auto logic:
     - cover = similar to MainMenu card (best for wide/event visuals)
     - contain = complete logo/emblem (best for square logo marks like Faces Salon)
  */
  .bp-logo-stage .smart-logo-fit-cover .smart-logo-inner {
    padding: 0 !important;
  }

  .bp-logo-stage .smart-logo-fit-cover .smart-logo-img,
  .bp-logo-stage .smart-logo-fit-cover img {
    object-fit: cover !important;
  }

  .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner {
    padding: 18px !important;
  }

  .bp-logo-stage .smart-logo-fit-contain .smart-logo-img,
  .bp-logo-stage .smart-logo-fit-contain img {
    object-fit: contain !important;
  }

  @media (max-width: 920px) {
    .bp-logo-card {
      max-width: 100% !important;
    }

    .bp-logo-stage {
      border-radius: 22px !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 22px !important;
    }

    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner {
      padding: 14px !important;
    }
  }


  /* =========================================================
     PHASE 8I — PREMIUM BUSINESS PAGE POLISH
     Services compact, cleaner info bar, softer actions, refined about.
     ========================================================= */

  /* Actions / CTA */
  .bp-primary-btn {
    height: 52px !important;
    min-height: 52px !important;
    border-radius: 18px !important;
    padding: 0 34px !important;
    background: linear-gradient(135deg, #00675f 0%, #008878 100%) !important;
    box-shadow: 0 14px 30px rgba(0,103,95,.16) !important;
    font-size: 15px !important;
    letter-spacing: -.02em !important;
    font-weight: 900 !important;
  }

  .bp-icon-btn {
    width: 52px !important;
    height: 52px !important;
    border-radius: 18px !important;
    background: rgba(255,255,255,.94) !important;
    border: 1px solid rgba(108,88,61,.08) !important;
    box-shadow: 0 12px 28px rgba(54,41,25,.06) !important;
    transition: transform .18s ease, box-shadow .18s ease !important;
  }

  .bp-icon-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 16px 34px rgba(54,41,25,.09) !important;
  }

  /* Info strip: same-height, softer, cleaner */
  .bp-info-panel {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 0 !important;
    padding: 18px 24px !important;
    border-radius: 24px !important;
    background: rgba(255,255,255,.72) !important;
    border: 1px solid rgba(108,88,61,.08) !important;
    box-shadow: 0 10px 30px rgba(54,41,25,.05) !important;
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
  }

  .bp-info-row {
    min-height: 84px !important;
    padding: 8px 20px !important;
    border-bottom: 0 !important;
    border-right: 1px solid rgba(108,88,61,.08) !important;
    display: grid !important;
    grid-template-columns: 42px minmax(0, 1fr) !important;
    align-items: center !important;
    gap: 13px !important;
  }

  .bp-info-row:first-child {
    padding-left: 0 !important;
  }

  .bp-info-row:last-child,
  .bp-info-row:nth-child(3n) {
    border-right: 0 !important;
    padding-right: 0 !important;
  }

  .bp-info-icon {
    width: 40px !important;
    height: 40px !important;
    background: #eaf8f4 !important;
    color: #00796f !important;
  }

  .bp-info-label {
    color: #718096 !important;
    letter-spacing: .14em !important;
    font-size: 10px !important;
    margin-bottom: 6px !important;
  }

  .bp-info-value {
    color: #111827 !important;
    font-size: 13px !important;
    line-height: 1.35 !important;
    font-weight: 800 !important;
  }

  .bp-contact-actions {
    margin-top: 8px !important;
    gap: 7px !important;
  }

  .bp-contact-action,
  .bp-maps-link,
  .bp-maps-link-premium,
  .bp-zone-chip {
    min-height: 32px !important;
    padding: 0 12px !important;
    border-radius: 999px !important;
    background: #f0fdfa !important;
    border-color: #ccfbf1 !important;
    color: #00675f !important;
    box-shadow: none !important;
    font-size: 11.5px !important;
    font-weight: 900 !important;
  }

  /* About: cleaner editorial width */
  .bp-about {
    max-width: 680px !important;
    color: #243144 !important;
    font-size: 14px !important;
    line-height: 1.74 !important;
    font-weight: 520 !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 5 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
  }

  .bp-about-section {
    max-width: 760px !important;
  }

  /* Services rail: compact premium cards when there is no photo */
  .bp-packages-rail,
  .event-packages-grid {
    grid-auto-columns: minmax(210px, 235px) !important;
    gap: 14px !important;
    padding: 2px 4px 14px !important;
  }

  .event-package-card {
    border-radius: 22px !important;
    background: rgba(255,255,255,.82) !important;
    border: 1px solid rgba(108,88,61,.10) !important;
    box-shadow: 0 10px 26px rgba(54,41,25,.05) !important;
    overflow: hidden !important;
    transition: transform .18s ease, box-shadow .18s ease !important;
  }

  .event-package-card:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 16px 34px rgba(54,41,25,.09) !important;
  }

  .event-package-body.no-photo-body {
    min-height: 118px !important;
    padding: 18px 18px 14px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    background: linear-gradient(180deg, rgba(255,255,255,.94), rgba(255,248,239,.86)) !important;
    border-top: 1px solid rgba(108,88,61,.06) !important;
  }

  .event-package-body.no-photo-body h4 {
    font-size: 17px !important;
    line-height: 1.04 !important;
    letter-spacing: -.05em !important;
    color: #111827 !important;
    font-weight: 900 !important;
    margin-bottom: 8px !important;
  }

  .event-package-body.no-photo-body p,
  .event-package-body.no-photo-body .event-package-badges,
  .event-package-photo.no-photo {
    display: none !important;
  }

  .event-package-meta {
    margin-top: 10px !important;
    align-items: center !important;
    color: #00675f !important;
    font-size: 12px !important;
    font-weight: 900 !important;
  }

  .event-package-meta span:first-child,
  .event-package-meta span:last-child {
    color: #00675f !important;
    font-weight: 900 !important;
  }

  .event-package-reserve {
    display: none !important;
  }

  .bp-packages {
    padding-top: 30px !important;
  }

  .bp-packages-head {
    margin-bottom: 15px !important;
  }

  .bp-packages-title {
    letter-spacing: -.075em !important;
  }

  @media (max-width: 920px) {
    .bp-info-panel {
      display: block !important;
      padding: 14px !important;
      border-radius: 22px !important;
      background: #ffffff !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 0 !important;
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226,232,240,.78) !important;
      padding: 12px 0 !important;
    }

    .bp-info-row:first-child {
      padding-left: 0 !important;
    }

    .bp-info-row:last-child {
      border-bottom: 0 !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(205px, 232px) !important;
      gap: 12px !important;
    }

    .event-package-body.no-photo-body {
      min-height: 112px !important;
      padding: 16px 16px 13px !important;
    }
  }


  /* =========================================================
     PHASE 8J — PREMIUM PROFILE FINAL DIRECTION
     Matches the desired Airbnb + Apple + Fresha reference.
     ========================================================= */
  @media (min-width: 921px) {
    .bp-container {
      max-width: 1360px !important;
      padding: 30px clamp(42px, 5vw, 76px) 0 !important;
    }

    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(410px, 520px) minmax(0, 1fr) !important;
      column-gap: clamp(42px, 5vw, 70px) !important;
      row-gap: 28px !important;
      align-items: center !important;
    }

    .bp-hero-top {
      grid-column: 2 !important;
      grid-row: 1 !important;
      display: block !important;
      margin: 0 !important;
    }

    .bp-title {
      color: #10323d !important;
      font-size: clamp(42px, 4.2vw, 60px) !important;
      letter-spacing: -.07em !important;
    }

    .bp-tagline {
      display: block !important;
      margin-top: 8px !important;
      color: #475569 !important;
      font-size: 17px !important;
      font-weight: 650 !important;
    }

    .bp-rating {
      margin-top: 16px !important;
      font-size: 15px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      color: #f59e0b !important;
      box-shadow: none !important;
      gap: 9px !important;
    }

    .bp-rating span {
      color: #334155 !important;
      font-size: 15px !important;
      font-weight: 760 !important;
    }

    .bp-actions {
      margin-top: 28px !important;
      transform: none !important;
      display: flex !important;
      gap: 18px !important;
      align-items: center !important;
    }

    .bp-primary-btn,
    .bp-secondary-btn,
    .bp-icon-btn {
      height: 62px !important;
      min-height: 62px !important;
      border-radius: 18px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 12px !important;
      font-size: 17px !important;
      font-weight: 900 !important;
      letter-spacing: -.02em !important;
    }

    .bp-primary-btn {
      min-width: 178px !important;
      padding: 0 28px !important;
      background: linear-gradient(135deg, #00675f 0%, #008878 100%) !important;
      color: #ffffff !important;
      box-shadow: 0 18px 40px rgba(0,103,95,.24) !important;
    }

    .bp-icon-btn {
      width: 62px !important;
      color: #ef4444 !important;
      background: rgba(255,255,255,.94) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 16px 38px rgba(54,41,25,.08) !important;
    }

    .bp-secondary-btn {
      min-width: 150px !important;
      padding: 0 24px !important;
      color: #111827 !important;
      background: rgba(255,255,255,.96) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 16px 38px rgba(54,41,25,.08) !important;
    }

    .bp-logo-card {
      grid-column: 1 !important;
      grid-row: 1 !important;
      max-width: 520px !important;
    }

    .bp-logo-stage {
      border-radius: 30px !important;
      box-shadow: 0 28px 80px rgba(54,41,25,.12) !important;
    }

    .bp-info-panel {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      margin-top: 4px !important;
      display: grid !important;
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      padding: 30px 34px !important;
      border-radius: 28px !important;
      background: rgba(255,255,255,.90) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 26px 76px rgba(54,41,25,.09) !important;
    }

    .bp-info-row {
      display: grid !important;
      grid-template-columns: 58px minmax(0, 1fr) !important;
      min-height: 92px !important;
      align-items: flex-start !important;
      padding: 0 28px !important;
      gap: 18px !important;
      border-right: 1px solid rgba(108,88,61,.10) !important;
      border-bottom: 0 !important;
    }

    .bp-info-row:first-child { padding-left: 0 !important; }
    .bp-info-row:last-child { padding-right: 0 !important; border-right: 0 !important; }

    .bp-info-icon {
      width: 54px !important;
      height: 54px !important;
      background: #e6f7f3 !important;
      color: #00675f !important;
    }

    .bp-info-label {
      font-size: 12px !important;
      color: #78869a !important;
      letter-spacing: .12em !important;
      margin-bottom: 10px !important;
    }

    .bp-info-value {
      font-size: 15px !important;
      line-height: 1.45 !important;
      color: #142033 !important;
      font-weight: 650 !important;
    }
  }

  .bp-inline-link,
  .bp-text-link {
    margin-top: 14px;
    border: 0;
    background: transparent;
    color: #00675f;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0;
    text-decoration: none;
    font-family: inherit;
    font-size: 14px;
    font-weight: 900;
    cursor: pointer;
  }

  .bp-social-actions {
    display: flex;
    gap: 14px;
    margin-top: 14px;
  }

  .bp-social-actions a {
    color: #00675f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
  }

  .bp-contact-action {
    min-height: auto !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    color: #00675f !important;
    font-size: 14px !important;
    font-weight: 900 !important;
  }

  .bp-packages {
    margin-top: 42px !important;
    padding-top: 36px !important;
  }

  .bp-packages-head {
    align-items: center !important;
  }

  .bp-packages-title,
  .bp-section-title {
    color: #10323d !important;
    font-size: clamp(26px, 2.3vw, 34px) !important;
    letter-spacing: -.055em !important;
  }

  .bp-section-title::before,
  .bp-packages-title::before {
    content: "";
    display: inline-block;
    width: 5px;
    height: .92em;
    border-radius: 999px;
    background: #00796f;
    margin-right: 14px;
    transform: translateY(3px);
  }

  .bp-kicker {
    display: none !important;
  }

  .bp-package-actions {
    display: none !important;
  }

  .bp-packages-rail,
  .event-packages-grid {
    grid-auto-columns: minmax(188px, 206px) !important;
    gap: 22px !important;
    padding: 6px 2px 20px !important;
  }

  .event-package-card {
    border-radius: 18px !important;
    background: rgba(255,255,255,.92) !important;
    border: 1px solid rgba(108,88,61,.10) !important;
    box-shadow: 0 16px 38px rgba(54,41,25,.075) !important;
  }

  .event-package-body.no-photo-body {
    min-height: 190px !important;
    padding: 26px 22px 20px !important;
    background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.92)) !important;
  }

  .event-package-icon-bubble {
    width: 62px;
    height: 62px;
    border-radius: 999px;
    background: #e6f7f3;
    color: #00675f;
    display: grid;
    place-items: center;
    margin: 0 0 22px;
  }

  .event-package-body.no-photo-body h4 {
    font-size: 17px !important;
    color: #111827 !important;
    letter-spacing: -.035em !important;
    line-height: 1.08 !important;
  }

  .event-package-meta {
    margin-top: 18px !important;
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 8px !important;
    color: #111827 !important;
    font-size: 13px !important;
  }

  .event-package-meta span:not(.event-package-chevron) {
    color: #111827 !important;
    font-weight: 760 !important;
  }

  .event-package-chevron {
    margin-left: auto;
    color: #111827 !important;
    font-size: 24px;
    line-height: 1;
  }

  .bp-split-section {
    display: grid !important;
    grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr) !important;
    gap: 52px !important;
    align-items: start !important;
  }

  .bp-split-column + .bp-split-column {
    border-left: 1px solid rgba(108,88,61,.11);
    padding-left: 52px;
  }

  .bp-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
  }

  .bp-head-link {
    margin-top: 0 !important;
    white-space: nowrap;
  }

  .bp-about {
    max-width: 560px !important;
    color: #334155 !important;
    font-size: 16px !important;
    line-height: 1.82 !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 6 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
  }

  .bp-team-grid {
    justify-content: space-between !important;
    gap: 24px !important;
    padding: 4px 0 10px !important;
  }

  .bp-team-grid > * {
    width: 112px !important;
    flex: 0 0 112px !important;
  }

  .bp-reviews-head {
    max-width: none !important;
    align-items: center !important;
    justify-content: space-between !important;
    margin-bottom: 22px !important;
  }

  .bp-review-average {
    background: #fff8ea !important;
    border: 1px solid rgba(245,158,11,.24) !important;
    color: #00675f !important;
    margin-left: 8px;
  }

  .bp-reviews-list {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 24px !important;
    max-width: none !important;
  }

  .bp-reviews-list > * {
    border-radius: 18px !important;
    border: 1px solid rgba(108,88,61,.10) !important;
    background: rgba(255,255,255,.92) !important;
    box-shadow: 0 16px 38px rgba(54,41,25,.06) !important;
    min-height: 170px;
  }

  .bp-final-cta {
    display: grid !important;
    grid-template-columns: 76px minmax(0, 1fr) auto !important;
    align-items: center !important;
    gap: 22px !important;
    padding: 26px 36px !important;
    border-radius: 24px !important;
    background: linear-gradient(135deg, #eefcf9 0%, #f7fffd 100%) !important;
    border: 1px solid #caeee8 !important;
    box-shadow: 0 18px 46px rgba(0,103,95,.08) !important;
  }

  .bp-final-icon {
    width: 64px;
    height: 64px;
    border-radius: 999px;
    background: #d9f6ef;
    color: #00675f;
    display: grid;
    place-items: center;
  }

  .bp-final-btn .bp-btn-arrow {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    display: inline-grid;
    place-items: center;
    font-size: 26px;
    line-height: 1;
  }

  @media (max-width: 920px) {
    .bp-split-section {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
    }

    .bp-split-column + .bp-split-column {
      border-left: 0;
      padding-left: 0;
    }

    .bp-reviews-list {
      grid-template-columns: 1fr !important;
    }

    .bp-final-cta {
      grid-template-columns: 56px 1fr !important;
    }

    .bp-final-cta .bp-final-btn {
      grid-column: 1 / -1;
      width: 100%;
    }
  }



  /* =========================================================
     PHASE 8K — POLISH: LIGHTER LOGO, COMPACT SERVICES, SCROLL RAILS
     ========================================================= */
  @media (min-width: 921px) {
    /* 1) Logo: remove the heavy floating shadow and make it calmer */
    .bp-logo-card {
      max-width: 500px !important;
    }

    .bp-logo-stage {
      border-radius: 26px !important;
      box-shadow: 0 10px 28px rgba(54, 41, 25, .045) !important;
      border: 1px solid rgba(108, 88, 61, .085) !important;
    }

    .bp-logo-stage .smart-logo-inner {
      border-radius: 26px !important;
    }

    /* 2) Services: smaller, cleaner, closer to the reference */
    .bp-packages {
      margin-top: 34px !important;
      padding-top: 28px !important;
    }

    .bp-packages-head {
      margin-bottom: 16px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      display: grid !important;
      grid-auto-flow: column !important;
      grid-auto-columns: 172px !important;
      gap: 16px !important;
      overflow-x: auto !important;
      overflow-y: visible !important;
      scroll-snap-type: x mandatory !important;
      scroll-padding-left: 2px !important;
      padding: 4px 4px 18px !important;
      -webkit-overflow-scrolling: touch !important;
      scrollbar-width: none !important;
    }

    .bp-packages-rail::-webkit-scrollbar,
    .event-packages-grid::-webkit-scrollbar {
      display: none !important;
    }

    .event-package-card {
      min-height: 176px !important;
      border-radius: 18px !important;
      box-shadow: 0 8px 22px rgba(54, 41, 25, .045) !important;
      scroll-snap-align: start !important;
    }

    .event-package-card:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 14px 30px rgba(54, 41, 25, .075) !important;
    }

    .event-package-body.no-photo-body {
      min-height: 176px !important;
      padding: 18px 16px 14px !important;
    }

    .event-package-icon-bubble {
      width: 48px !important;
      height: 48px !important;
      margin: 0 0 18px !important;
      box-shadow: none !important;
    }

    .event-package-body.no-photo-body h4 {
      font-size: 15px !important;
      line-height: 1.1 !important;
      letter-spacing: -.035em !important;
    }

    .event-package-meta {
      margin-top: 12px !important;
      font-size: 11.5px !important;
      gap: 6px !important;
    }

    .event-package-chevron {
      font-size: 18px !important;
    }

    /* 3) Make arrows usable again */
    .bp-package-actions {
      display: inline-flex !important;
      align-items: center !important;
      gap: 10px !important;
      flex: 0 0 auto !important;
    }

    .bp-carousel-arrow {
      width: 40px !important;
      height: 40px !important;
      border-radius: 999px !important;
      box-shadow: 0 8px 20px rgba(54, 41, 25, .06) !important;
      background: rgba(255,255,255,.96) !important;
    }

    /* 4) Team: show all workers in horizontal premium rail */
    .bp-team-grid {
      display: flex !important;
      flex-wrap: nowrap !important;
      justify-content: flex-start !important;
      gap: 28px !important;
      overflow-x: auto !important;
      overflow-y: visible !important;
      padding: 6px 4px 16px !important;
      scroll-snap-type: x mandatory !important;
      -webkit-overflow-scrolling: touch !important;
      scrollbar-width: none !important;
    }

    .bp-team-grid::-webkit-scrollbar {
      display: none !important;
    }

    .bp-team-grid > * {
      flex: 0 0 104px !important;
      width: 104px !important;
      scroll-snap-align: start !important;
    }
  }

  @media (max-width: 920px) {
    .bp-logo-stage {
      box-shadow: 0 8px 22px rgba(54, 41, 25, .045) !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: 150px !important;
      gap: 12px !important;
      overflow-x: auto !important;
      overflow-y: visible !important;
      scroll-snap-type: x mandatory !important;
      padding: 4px 2px 16px !important;
      -webkit-overflow-scrolling: touch !important;
      scrollbar-width: none !important;
    }

    .event-package-body.no-photo-body {
      min-height: 158px !important;
      padding: 16px 14px 13px !important;
    }

    .event-package-icon-bubble {
      width: 44px !important;
      height: 44px !important;
      margin-bottom: 14px !important;
    }

    .event-package-body.no-photo-body h4 {
      font-size: 14px !important;
    }

    .bp-team-grid {
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      gap: 18px !important;
      padding-bottom: 14px !important;
      -webkit-overflow-scrolling: touch !important;
      scrollbar-width: none !important;
    }
  }


  /* =========================================================
     PHASE 8L — CLEANER INFO PANEL / REMOVE GRAY GLOW
     - Removes the gray blur/glow above and below the info card.
     - Keeps the premium white card look.
     - Softens logo/card shadow without changing layout.
     ========================================================= */

  .bp-info-panel {
    background: #ffffff !important;
    border: 1px solid rgba(108,88,61,.08) !important;
    box-shadow: 0 4px 14px rgba(0,0,0,.03) !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .bp-info-row {
    border-right: 1px solid rgba(226,232,240,.9) !important;
  }

  .bp-info-row:last-child,
  .bp-info-row:nth-child(3n),
  .bp-info-row:nth-child(4n) {
    border-right: 0 !important;
  }

  .bp-logo-stage {
    box-shadow: 0 6px 18px rgba(54,41,25,.035) !important;
  }

  .bp-logo-card {
    filter: none !important;
  }

  .bp-logo-stage .smart-logo-frame,
  .bp-logo-stage .smart-logo-inner,
  .bp-logo-stage .smart-logo-img,
  .bp-logo-stage img {
    box-shadow: none !important;
    filter: none !important;
  }

  @media (max-width: 920px) {
    .bp-info-panel {
      background: #ffffff !important;
      box-shadow: 0 3px 12px rgba(0,0,0,.025) !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226,232,240,.78) !important;
    }

    .bp-info-row:last-child {
      border-bottom: 0 !important;
    }
  }

  /* =========================================================
     PHASE 8M — PREMIUM INFO STRIP CLEANUP
     Dynamic 3/4 columns, no heavy glow, cleaner contact/location/hours.
     ========================================================= */
  .bp-info-panel {
    overflow: visible !important;
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(245px, 1fr)) !important;
    gap: 0 !important;
    padding: 18px 22px !important;
    border-radius: 28px !important;
    background: rgba(255, 255, 255, .96) !important;
    border: 1px solid rgba(226, 232, 240, .92) !important;
    box-shadow: 0 6px 18px rgba(15, 23, 42, .035) !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .bp-info-row {
    position: relative !important;
    min-height: 92px !important;
    padding: 12px 24px !important;
    display: grid !important;
    grid-template-columns: 46px minmax(0, 1fr) !important;
    align-items: center !important;
    gap: 15px !important;
    border-right: 1px solid rgba(226, 232, 240, .92) !important;
    border-bottom: 0 !important;
  }

  .bp-info-row:first-child {
    padding-left: 4px !important;
  }

  .bp-info-row:last-child {
    border-right: 0 !important;
    padding-right: 4px !important;
  }

  .bp-info-icon {
    width: 42px !important;
    height: 42px !important;
    border-radius: 999px !important;
    background: #e8f8f4 !important;
    color: #00796f !important;
    box-shadow: none !important;
  }

  .bp-info-content {
    min-width: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
  }

  .bp-info-label {
    margin: 0 0 8px !important;
    color: #7a8799 !important;
    font-size: 10px !important;
    line-height: 1 !important;
    letter-spacing: .16em !important;
    font-weight: 950 !important;
    text-transform: uppercase !important;
  }

  .bp-info-value,
  .bp-contact-number {
    color: #111827 !important;
    font-size: 14px !important;
    line-height: 1.32 !important;
    font-weight: 850 !important;
    overflow-wrap: anywhere !important;
  }

  .bp-info-muted {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 750 !important;
  }

  .bp-inline-link,
  .bp-info-main-action,
  .bp-maps-link {
    margin-top: 8px !important;
    min-height: 34px !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 7px !important;
    padding: 0 13px !important;
    border-radius: 999px !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    text-decoration: none !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    box-shadow: none !important;
  }

  .bp-contact-actions {
    margin-top: 9px !important;
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
  }

  .bp-contact-action {
    min-height: 32px !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 7px !important;
    padding: 0 12px !important;
    border-radius: 999px !important;
    background: #ffffff !important;
    border: 1px solid rgba(204, 251, 241, .95) !important;
    color: #00675f !important;
    text-decoration: none !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    box-shadow: none !important;
  }

  .bp-contact-action.whatsapp {
    background: #f0fdfa !important;
    border-color: #99f6e4 !important;
    color: #047857 !important;
  }

  .bp-hours-row {
    position: relative !important;
    z-index: 10 !important;
  }

  .bp-hours-summary {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 12px !important;
    border-radius: 14px !important;
    background: #f8fafc !important;
    border: 1px solid rgba(226, 232, 240, .92) !important;
    padding: 9px 11px !important;
  }

  .bp-hours-summary-label {
    color: #64748b !important;
    font-size: 12px !important;
    font-weight: 850 !important;
  }

  .bp-hours-summary strong {
    color: #111827 !important;
    font-size: 12px !important;
    line-height: 1.15 !important;
    font-weight: 950 !important;
    text-align: right !important;
  }

  .bp-hours-toggle {
    margin-top: 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
    border: 0 !important;
    background: transparent !important;
    color: #00796f !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    padding: 0 !important;
  }

  .bp-hours-week-premium {
    position: absolute !important;
    left: 62px !important;
    top: calc(100% + 8px) !important;
    z-index: 50 !important;
    width: 285px !important;
    margin: 0 !important;
    padding: 10px 12px !important;
    border-radius: 18px !important;
    background: #ffffff !important;
    border: 1px solid rgba(226, 232, 240, .95) !important;
    box-shadow: 0 18px 44px rgba(15, 23, 42, .12) !important;
  }

  .bp-hours-week-premium .bp-hours-day {
    display: grid !important;
    grid-template-columns: 86px minmax(0, 1fr) !important;
    align-items: center !important;
    gap: 10px !important;
    padding: 7px 0 !important;
    border-bottom: 1px solid rgba(241, 245, 249, .98) !important;
    font-size: 12px !important;
  }

  .bp-hours-week-premium .bp-hours-day:last-child {
    border-bottom: 0 !important;
  }

  .bp-hours-week-premium .bp-hours-day span {
    color: #64748b !important;
    font-weight: 850 !important;
  }

  .bp-hours-week-premium .bp-hours-day strong {
    color: #111827 !important;
    font-size: 12px !important;
    line-height: 1.2 !important;
    font-weight: 950 !important;
    text-align: right !important;
  }

  .bp-social-actions {
    margin-top: 8px !important;
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
  }

  .bp-social-actions a {
    width: 30px !important;
    height: 30px !important;
    border-radius: 999px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #f0fdfa !important;
    color: #00675f !important;
    text-decoration: none !important;
  }

  @media (max-width: 920px) {
    .bp-info-panel {
      display: block !important;
      padding: 16px !important;
      border-radius: 24px !important;
      background: #ffffff !important;
      box-shadow: 0 6px 18px rgba(15, 23, 42, .035) !important;
    }

    .bp-info-row {
      min-height: 0 !important;
      grid-template-columns: 42px minmax(0, 1fr) !important;
      padding: 14px 0 !important;
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226, 232, 240, .92) !important;
    }

    .bp-info-row:first-child {
      padding-left: 0 !important;
    }

    .bp-info-row:last-child {
      border-bottom: 0 !important;
      padding-right: 0 !important;
    }

    .bp-hours-week-premium {
      position: static !important;
      width: 100% !important;
      margin-top: 10px !important;
      box-shadow: none !important;
    }
  }



  /* =========================================================
     PHASE 8N — LUXURY MINIMALISM PASS
     - Info panel is thinner and adaptive.
     - Empty info blocks disappear.
     - Service cards remove descriptions and become compact.
     ========================================================= */

  .business-page-shell {
    background:
      radial-gradient(circle at 10% 5%, rgba(0,103,95,.055), transparent 28%),
      radial-gradient(circle at 94% 10%, rgba(245,158,11,.045), transparent 24%),
      linear-gradient(180deg, #fffdf9 0%, #fff9f1 46%, #fffdf9 100%) !important;
  }

  .bp-info-panel {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)) !important;
    align-items: stretch !important;
    gap: 0 !important;
    padding: 14px 16px !important;
    border-radius: 22px !important;
    background: rgba(255,255,255,.92) !important;
    border: 1px solid rgba(226,232,240,.88) !important;
    box-shadow: 0 4px 12px rgba(15,23,42,.028) !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .bp-info-row {
    min-height: 66px !important;
    padding: 6px 18px !important;
    grid-template-columns: 34px minmax(0, 1fr) !important;
    gap: 12px !important;
    align-items: center !important;
    border-right: 1px solid rgba(226,232,240,.82) !important;
    border-bottom: 0 !important;
  }

  .bp-info-row:first-child { padding-left: 4px !important; }
  .bp-info-row:last-child { border-right: 0 !important; padding-right: 4px !important; }

  .bp-info-icon {
    width: 34px !important;
    height: 34px !important;
    background: #eaf8f4 !important;
    color: #00675f !important;
  }

  .bp-info-icon svg {
    width: 17px !important;
    height: 17px !important;
  }

  .bp-info-label {
    display: none !important;
  }

  .bp-info-value,
  .bp-contact-number,
  .bp-info-value-primary {
    color: #152033 !important;
    font-size: 13px !important;
    line-height: 1.28 !important;
    font-weight: 820 !important;
  }

  .bp-inline-link,
  .bp-info-main-action,
  .bp-contact-action,
  .bp-maps-link {
    margin-top: 7px !important;
    min-height: auto !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    color: #00675f !important;
    font-size: 12px !important;
    line-height: 1.2 !important;
    font-weight: 900 !important;
  }

  .bp-contact-actions,
  .bp-social-actions {
    margin-top: 6px !important;
    gap: 10px !important;
  }

  .bp-contact-action.whatsapp {
    background: transparent !important;
    border: 0 !important;
    color: #00675f !important;
  }

  .bp-social-actions a {
    width: 24px !important;
    height: 24px !important;
    background: transparent !important;
    color: #00675f !important;
  }

  .bp-social-actions svg {
    width: 17px !important;
    height: 17px !important;
  }

  .bp-hours-summary {
    width: auto !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 10px !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
  }

  .bp-hours-summary-label {
    color: #64748b !important;
    font-size: 12px !important;
    font-weight: 820 !important;
  }

  .bp-hours-summary strong {
    color: #152033 !important;
    font-size: 12.5px !important;
    font-weight: 900 !important;
  }

  .bp-hours-toggle {
    margin-top: 7px !important;
    font-size: 12px !important;
    color: #00675f !important;
  }

  .bp-hours-week-premium {
    left: 48px !important;
    top: calc(100% + 6px) !important;
    width: 250px !important;
    border-radius: 16px !important;
    box-shadow: 0 14px 34px rgba(15,23,42,.10) !important;
  }

  .bp-packages {
    margin-top: 38px !important;
    padding-top: 30px !important;
  }

  .bp-packages-head {
    margin-bottom: 14px !important;
  }

  .bp-packages-title,
  .bp-section-title {
    font-size: clamp(24px, 2.05vw, 31px) !important;
    letter-spacing: -.055em !important;
  }

  .bp-packages-copy {
    font-size: 13px !important;
    color: #64748b !important;
  }

  .bp-packages-rail,
  .event-packages-grid {
    grid-auto-columns: 164px !important;
    gap: 14px !important;
    padding: 3px 2px 16px !important;
  }

  .event-package-card {
    min-height: 0 !important;
    border-radius: 16px !important;
    background: rgba(255,255,255,.94) !important;
    border: 1px solid rgba(226,232,240,.86) !important;
    box-shadow: 0 5px 14px rgba(15,23,42,.035) !important;
  }

  .event-package-card:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 10px 22px rgba(15,23,42,.055) !important;
  }

  .event-package-photo {
    height: 104px !important;
  }

  .event-package-body,
  .event-package-body.no-photo-body {
    min-height: 92px !important;
    padding: 12px 12px 11px !important;
    background: #ffffff !important;
    border-top: 0 !important;
  }

  .event-package-body p,
  .event-package-body.no-photo-body p,
  .event-package-badges,
  .event-package-reserve {
    display: none !important;
  }

  .event-package-icon-bubble {
    width: 38px !important;
    height: 38px !important;
    margin: 0 0 12px !important;
    background: #eaf8f4 !important;
  }

  .event-package-icon-bubble svg {
    width: 20px !important;
    height: 20px !important;
  }

  .event-package-body h4,
  .event-package-body.no-photo-body h4 {
    font-size: 14px !important;
    line-height: 1.08 !important;
    letter-spacing: -.035em !important;
    margin: 0 !important;
    color: #111827 !important;
  }

  .event-package-meta {
    margin-top: 10px !important;
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 6px !important;
    font-size: 11.5px !important;
    color: #111827 !important;
  }

  .event-package-meta span:not(.event-package-chevron) {
    color: #111827 !important;
    font-weight: 780 !important;
  }

  .event-package-chevron {
    margin-left: auto !important;
    color: #00675f !important;
    font-size: 18px !important;
    line-height: 1 !important;
  }

  .bp-carousel-arrow {
    width: 34px !important;
    height: 34px !important;
    box-shadow: 0 4px 12px rgba(15,23,42,.04) !important;
  }

  @media (max-width: 920px) {
    .bp-info-panel {
      display: block !important;
      padding: 12px 14px !important;
      border-radius: 20px !important;
    }

    .bp-info-row {
      min-height: 0 !important;
      grid-template-columns: 34px minmax(0,1fr) !important;
      padding: 11px 0 !important;
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226,232,240,.82) !important;
    }

    .bp-info-row:last-child {
      border-bottom: 0 !important;
    }

    .bp-hours-week-premium {
      position: static !important;
      width: 100% !important;
      margin-top: 8px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: 148px !important;
      gap: 12px !important;
    }

    .event-package-photo {
      height: 96px !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 88px !important;
      padding: 11px 11px 10px !important;
    }
  }

  /* =========================================================
     PHASE 8O — ABOUT / TEAM / REVIEWS INTERACTION POLISH
     ========================================================= */

  .bp-about.is-expanded {
    display: block !important;
    -webkit-line-clamp: unset !important;
    overflow: visible !important;
  }

  .bp-text-link {
    cursor: pointer !important;
  }

  .bp-text-link svg {
    transition: transform .18s ease !important;
  }

  .bp-text-link svg.is-open {
    transform: rotate(180deg) !important;
  }

  .bp-team-column {
    min-width: 0 !important;
  }

  .bp-team-grid {
    display: flex !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scroll-snap-type: x proximity !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    padding: 6px 6px 18px !important;
    gap: 30px !important;
  }

  .bp-team-grid::-webkit-scrollbar {
    display: none !important;
  }

  .bp-team-grid > * {
    flex: 0 0 104px !important;
    scroll-snap-align: start !important;
  }

  .bp-reviews-section,
  .bp-section.bp-about-section {
    max-width: 1320px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    text-align: left !important;
  }

  .bp-section.bp-about-section {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .bp-reviews-head {
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 22px !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    max-width: 100% !important;
    text-align: left !important;
  }

  .bp-reviews-head .bp-section-head {
    justify-content: flex-start !important;
    text-align: left !important;
  }

  .bp-reviews-head .bp-head-link {
    margin-left: 8px !important;
  }

  .bp-reviews-list,
  .business-show-more-reviews {
    margin-left: 0 !important;
    margin-right: auto !important;
  }

  @media (max-width: 920px) {
    .bp-team-grid {
      gap: 22px !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .bp-team-grid > * {
      flex-basis: 96px !important;
    }

    .bp-reviews-head {
      align-items: flex-start !important;
      flex-direction: column !important;
      gap: 10px !important;
    }

    .bp-section.bp-about-section {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }


  /* =========================================================
     PHASE 8P — MOBILE PROFILE SPACE FIX / WEB-SAFE
     Web stays untouched except team arrows navigation.
     ========================================================= */

  .bp-team-actions {
    display: inline-flex !important;
    align-items: center !important;
    gap: 9px !important;
    margin-left: auto !important;
  }

  .bp-team-arrow {
    width: 42px !important;
    height: 42px !important;
    font-size: 18px !important;
    box-shadow: 0 8px 18px rgba(15,23,42,.045) !important;
  }

  @media (max-width: 920px) {
    .business-page-shell {
      background: linear-gradient(180deg, #fffaf4 0%, #fff8ef 48%, #fffaf4 100%) !important;
      padding: 0 0 calc(96px + env(safe-area-inset-bottom, 0px)) !important;
      overflow-x: hidden !important;
    }

    .bp-container {
      width: 100% !important;
      max-width: 100% !important;
      padding: 0 16px !important;
    }

    .bp-page {
      overflow: visible !important;
    }

    .bp-hero {
      display: flex !important;
      flex-direction: column !important;
      gap: 16px !important;
      padding: 18px 0 0 !important;
      background: transparent !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-logo-card {
      order: 1 !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 auto !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 7.7 !important;
      max-height: 228px !important;
      border-radius: 24px !important;
      box-shadow: 0 8px 22px rgba(54,41,25,.045) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner {
      padding: 12px !important;
    }

    .bp-hero-top {
      order: 2 !important;
      display: block !important;
      margin: 0 !important;
      padding: 0 2px !important;
      text-align: left !important;
    }

    .bp-title-wrap {
      display: block !important;
      width: 100% !important;
    }

    .bp-title {
      font-size: clamp(32px, 10vw, 42px) !important;
      line-height: .94 !important;
      letter-spacing: -.07em !important;
      text-align: left !important;
      max-width: 100% !important;
      margin: 0 !important;
      color: #10323d !important;
    }

    .bp-tagline {
      display: block !important;
      margin-top: 6px !important;
      font-size: 14px !important;
      line-height: 1.35 !important;
      color: #64748b !important;
      font-weight: 650 !important;
    }

    .bp-rating {
      margin-top: 10px !important;
      font-size: 13px !important;
      gap: 7px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    .bp-rating span {
      font-size: 13px !important;
    }

    .bp-actions {
      position: static !important;
      transform: none !important;
      width: 100% !important;
      margin-top: 14px !important;
      padding: 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) 58px !important;
      gap: 10px !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-actions .bp-secondary-btn,
    .bp-actions .bp-share-btn {
      display: none !important;
    }

    .bp-primary-btn {
      width: 100% !important;
      height: 54px !important;
      min-height: 54px !important;
      border-radius: 18px !important;
      font-size: 16px !important;
      box-shadow: 0 10px 22px rgba(0,103,95,.16) !important;
    }

    .bp-icon-btn {
      width: 58px !important;
      height: 54px !important;
      min-height: 54px !important;
      border-radius: 18px !important;
      box-shadow: 0 8px 18px rgba(54,41,25,.045) !important;
    }

    .bp-info-panel {
      order: 3 !important;
      display: block !important;
      margin-top: 4px !important;
      padding: 6px 18px !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.94) !important;
      border: 1px solid rgba(226,232,240,.82) !important;
      box-shadow: 0 8px 22px rgba(15,23,42,.035) !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 68px !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 12px !important;
      padding: 12px 0 !important;
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226,232,240,.82) !important;
      align-items: center !important;
    }

    .bp-info-row:first-child,
    .bp-info-row:last-child {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .bp-info-row:last-child {
      border-bottom: 0 !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
    }

    .bp-info-label {
      display: none !important;
    }

    .bp-info-value {
      font-size: 14px !important;
      line-height: 1.25 !important;
      font-weight: 850 !important;
    }

    .bp-info-main-action,
    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      min-height: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      font-size: 14px !important;
      box-shadow: none !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 7px !important;
      gap: 14px !important;
    }

    .bp-packages {
      margin-top: 28px !important;
      padding: 28px 0 8px !important;
    }

    .bp-packages-title,
    .bp-section-title {
      font-size: 30px !important;
      line-height: .98 !important;
      letter-spacing: -.065em !important;
    }

    .bp-packages-copy {
      font-size: 15px !important;
      line-height: 1.35 !important;
      margin-top: 8px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(206px, 228px) !important;
      gap: 14px !important;
      padding: 2px 2px 12px !important;
      scroll-snap-type: x mandatory !important;
    }

    .event-package-card {
      border-radius: 20px !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.045) !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 122px !important;
      padding: 16px !important;
    }

    .bp-split-section {
      display: block !important;
      margin-top: 28px !important;
      padding: 28px 0 0 !important;
      border-top: 1px solid rgba(108,88,61,.11) !important;
    }

    .bp-split-column {
      width: 100% !important;
      min-width: 0 !important;
    }

    .bp-about-column {
      margin-bottom: 34px !important;
    }

    .bp-section-head {
      align-items: center !important;
      gap: 12px !important;
    }

    .bp-about {
      font-size: 16px !important;
      line-height: 1.62 !important;
      font-weight: 620 !important;
      color: #334155 !important;
      -webkit-line-clamp: 4 !important;
      max-width: 100% !important;
      margin-top: 18px !important;
    }

    .bp-about.is-expanded {
      display: block !important;
      -webkit-line-clamp: unset !important;
      overflow: visible !important;
    }

    .bp-text-link {
      font-size: 15px !important;
      margin-top: 12px !important;
    }

    .bp-team-grid {
      gap: 22px !important;
      padding: 14px 2px 22px !important;
      scroll-snap-type: x mandatory !important;
      overflow-x: auto !important;
    }

    .bp-team-grid > * {
      flex: 0 0 96px !important;
      scroll-snap-align: start !important;
    }

    .bp-team-actions {
      display: none !important;
    }

    .bp-section.bp-about-section {
      margin-top: 28px !important;
      padding: 28px 0 0 !important;
      border-top: 1px solid rgba(108,88,61,.11) !important;
      text-align: left !important;
    }

    .bp-reviews-head {
      display: block !important;
      text-align: left !important;
      margin-bottom: 22px !important;
    }

    .bp-reviews-head .bp-section-head {
      display: block !important;
      text-align: left !important;
    }

    .bp-review-average {
      margin-top: 12px !important;
      width: fit-content !important;
      padding: 8px 12px !important;
      font-size: 13px !important;
    }

    .bp-reviews-head .bp-head-link {
      display: inline-flex !important;
      margin: 14px 0 0 !important;
      font-size: 15px !important;
    }

    .bp-reviews-list,
    .business-show-more-reviews {
      margin-left: 0 !important;
      margin-right: auto !important;
    }

    .bp-final-cta {
      margin-top: 34px !important;
      padding: 18px !important;
      border-radius: 24px !important;
      display: grid !important;
      grid-template-columns: 54px minmax(0, 1fr) !important;
      gap: 14px !important;
      align-items: center !important;
    }

    .bp-final-icon {
      width: 54px !important;
      height: 54px !important;
      grid-row: 1 / span 2 !important;
    }

    .bp-final-cta strong {
      font-size: 24px !important;
      line-height: 1.03 !important;
    }

    .bp-final-cta p {
      font-size: 14px !important;
      line-height: 1.35 !important;
    }

    .bp-final-cta .bp-final-btn {
      grid-column: 1 / -1 !important;
      margin-top: 6px !important;
      height: 52px !important;
    }
  }



  /* =========================================================
     PHASE 8Q — MOBILE PHASE 2 POLISH / WEB-SAFE
     - Web unchanged except existing team arrows.
     - Centers reserve button content.
     - Premium mobile team carousel.
     - Smooth about expand/collapse.
     - Cleaner mobile reviews + compact final CTA.
     ========================================================= */

  .bp-primary-btn,
  .bp-secondary-btn,
  .bp-icon-btn {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px !important;
    text-align: center !important;
    line-height: 1 !important;
  }

  .bp-primary-btn svg,
  .bp-secondary-btn svg,
  .bp-icon-btn svg {
    flex: 0 0 auto !important;
    margin: 0 !important;
  }

  @media (max-width: 920px) {
    .bp-actions .bp-primary-btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 10px !important;
      padding: 0 18px !important;
      text-align: center !important;
      white-space: nowrap !important;
    }

    .bp-actions .bp-primary-btn svg {
      width: 20px !important;
      height: 20px !important;
      flex: 0 0 20px !important;
    }

    .bp-actions .bp-icon-btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    /* Smooth about section: collapsed editorial preview + clean expand */
    .bp-about {
      display: block !important;
      position: relative !important;
      max-height: 8.25em !important;
      overflow: hidden !important;
      -webkit-line-clamp: unset !important;
      transition: max-height .32s ease !important;
    }

    .bp-about:not(.is-expanded)::after {
      content: "" !important;
      position: absolute !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      height: 42px !important;
      pointer-events: none !important;
      background: linear-gradient(180deg, rgba(255,248,239,0), #fff8ef 78%) !important;
    }

    .bp-about.is-expanded {
      max-height: 1200px !important;
      overflow: visible !important;
    }

    .bp-text-link {
      display: inline-flex !important;
      align-items: center !important;
      gap: 7px !important;
      width: fit-content !important;
      min-height: 34px !important;
      color: #00796f !important;
      font-weight: 950 !important;
    }

    /* Premium team carousel: intentional horizontal rail, not accidental overflow */
    .bp-team-column {
      position: relative !important;
      overflow: hidden !important;
    }

    .bp-team-column .bp-section-head {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 14px !important;
      margin-bottom: 12px !important;
    }

    .bp-team-grid {
      display: grid !important;
      grid-auto-flow: column !important;
      grid-auto-columns: 108px !important;
      gap: 18px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      scroll-snap-type: x mandatory !important;
      scroll-padding-left: 16px !important;
      -webkit-overflow-scrolling: touch !important;
      scrollbar-width: none !important;
      margin: 0 -16px !important;
      padding: 12px 16px 26px !important;
    }

    .bp-team-grid::-webkit-scrollbar {
      display: none !important;
    }

    .bp-team-grid > * {
      width: 108px !important;
      flex: 0 0 108px !important;
      scroll-snap-align: start !important;
    }

    .bp-team-grid > button > div:first-child {
      width: 78px !important;
      height: 78px !important;
      box-shadow: 0 8px 20px rgba(15,23,42,.075) !important;
      border-width: 3px !important;
    }

    .bp-team-grid .text-center {
      margin-top: 10px !important;
    }

    .bp-team-grid .text-sm {
      font-size: 13px !important;
      line-height: 1.1 !important;
      font-weight: 900 !important;
      color: #111827 !important;
    }

    .bp-team-grid .text-xs {
      font-size: 12px !important;
      line-height: 1.15 !important;
      color: #64748b !important;
    }

    .bp-team-column::after {
      content: "" !important;
      position: absolute !important;
      top: 66px !important;
      right: 0 !important;
      width: 34px !important;
      height: 118px !important;
      pointer-events: none !important;
      background: linear-gradient(90deg, rgba(255,248,239,0), #fff8ef 76%) !important;
    }

    /* Reviews: lighter hierarchy and cleaner empty state */
    .bp-section.bp-about-section {
      margin-top: 22px !important;
      padding-top: 24px !important;
    }

    .bp-reviews-head {
      display: block !important;
      margin-bottom: 16px !important;
      text-align: left !important;
    }

    .bp-reviews-head .bp-section-head {
      display: block !important;
      text-align: left !important;
    }

    .bp-review-average {
      display: inline-flex !important;
      margin-top: 12px !important;
      padding: 8px 12px !important;
      border-radius: 999px !important;
      font-size: 12px !important;
      line-height: 1.15 !important;
      background: rgba(255,251,235,.74) !important;
      border-color: rgba(245,158,11,.24) !important;
      box-shadow: none !important;
    }

    .bp-reviews-head .bp-head-link {
      display: inline-flex !important;
      margin: 12px 0 0 !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
    }

    .bp-reviews-list + .business-show-more-reviews,
    .business-show-more-reviews {
      margin-top: 12px !important;
    }

    .bp-section.bp-about-section .text-slate-500 {
      font-size: 17px !important;
      line-height: 1.45 !important;
      color: #64748b !important;
      max-width: 320px !important;
    }

    /* Final CTA: compact app-style card */
    .bp-final-cta {
      margin-top: 28px !important;
      padding: 16px !important;
      border-radius: 22px !important;
      display: grid !important;
      grid-template-columns: 46px minmax(0, 1fr) !important;
      gap: 12px !important;
      align-items: center !important;
      box-shadow: 0 8px 22px rgba(0,103,95,.055) !important;
    }

    .bp-final-icon {
      width: 46px !important;
      height: 46px !important;
      grid-row: 1 !important;
    }

    .bp-final-cta strong {
      font-size: 21px !important;
      line-height: 1.05 !important;
      letter-spacing: -.055em !important;
    }

    .bp-final-cta p {
      margin-top: 5px !important;
      font-size: 13px !important;
      line-height: 1.32 !important;
      font-weight: 650 !important;
    }

    .bp-final-cta .bp-final-btn {
      grid-column: 1 / -1 !important;
      width: 100% !important;
      height: 50px !important;
      min-height: 50px !important;
      margin-top: 6px !important;
      border-radius: 17px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 10px !important;
      font-size: 15px !important;
    }
  }

  /* =========================================================
     PHASE 8R — TABLET / IPAD RESPONSIVE POLISH
     Desktop >1180 stays intact. Phone <768 stays intact.
     This only refines the middle space: iPad/tablet portrait + landscape.
     ========================================================= */
  @media (min-width: 768px) and (max-width: 1180px) {
    .business-page-shell {
      background:
        radial-gradient(circle at 10% 4%, rgba(0,103,95,.075), transparent 28%),
        radial-gradient(circle at 96% 10%, rgba(245,158,11,.055), transparent 24%),
        linear-gradient(180deg, #fffaf4 0%, #fff8ef 48%, #fffaf4 100%) !important;
      padding: 0 0 calc(78px + env(safe-area-inset-bottom, 0px)) !important;
      overflow-x: hidden !important;
    }

    .bp-container {
      width: 100% !important;
      max-width: 100% !important;
      padding: 22px clamp(24px, 4.8vw, 46px) 0 !important;
    }

    .bp-page {
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      overflow: visible !important;
    }

    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(280px, 42%) minmax(0, 1fr) !important;
      gap: 24px !important;
      align-items: center !important;
      padding: 0 !important;
      background: transparent !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-logo-card {
      grid-column: 1 !important;
      grid-row: 1 !important;
      order: unset !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 9 !important;
      max-height: 255px !important;
      border-radius: 26px !important;
      box-shadow: 0 10px 28px rgba(54,41,25,.055) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 26px !important;
    }

    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner {
      padding: 14px !important;
    }

    .bp-hero-top {
      grid-column: 2 !important;
      grid-row: 1 !important;
      order: unset !important;
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      min-width: 0 !important;
      text-align: left !important;
    }

    .bp-title {
      font-size: clamp(34px, 5vw, 48px) !important;
      line-height: .94 !important;
      letter-spacing: -.07em !important;
      color: #10323d !important;
      max-width: 620px !important;
      margin: 0 !important;
    }

    .bp-tagline {
      display: block !important;
      margin-top: 7px !important;
      font-size: 14px !important;
      line-height: 1.38 !important;
      color: #64748b !important;
      font-weight: 650 !important;
      max-width: 520px !important;
    }

    .bp-rating {
      margin-top: 11px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      gap: 7px !important;
      font-size: 13px !important;
    }

    .bp-rating span {
      font-size: 13px !important;
    }

    .bp-actions {
      position: static !important;
      transform: none !important;
      width: auto !important;
      margin-top: 16px !important;
      padding: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 12px !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-actions .bp-secondary-btn,
    .bp-actions .bp-share-btn {
      display: inline-flex !important;
    }

    .bp-primary-btn,
    .bp-secondary-btn,
    .bp-icon-btn {
      height: 50px !important;
      min-height: 50px !important;
      border-radius: 17px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 10px !important;
      line-height: 1 !important;
    }

    .bp-primary-btn {
      min-width: 156px !important;
      padding: 0 24px !important;
      font-size: 15px !important;
      box-shadow: 0 12px 26px rgba(0,103,95,.16) !important;
    }

    .bp-icon-btn {
      width: 50px !important;
      box-shadow: 0 8px 18px rgba(54,41,25,.045) !important;
    }

    .bp-secondary-btn {
      padding: 0 18px !important;
      font-size: 14px !important;
      box-shadow: 0 8px 18px rgba(54,41,25,.045) !important;
    }

    .bp-info-panel {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      order: unset !important;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 0 !important;
      margin-top: 8px !important;
      padding: 18px 22px !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.94) !important;
      border: 1px solid rgba(226,232,240,.86) !important;
      box-shadow: 0 8px 22px rgba(15,23,42,.035) !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 70px !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 12px !important;
      padding: 12px 18px !important;
      border-right: 1px solid rgba(226,232,240,.88) !important;
      border-bottom: 1px solid rgba(226,232,240,.78) !important;
      align-items: center !important;
    }

    .bp-info-row:nth-child(2n),
    .bp-info-row:last-child {
      border-right: 0 !important;
    }

    .bp-info-row:nth-last-child(-n + 2) {
      border-bottom: 0 !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
    }

    .bp-info-label {
      font-size: 9.5px !important;
      letter-spacing: .12em !important;
      margin-bottom: 5px !important;
    }

    .bp-info-value {
      font-size: 13px !important;
      line-height: 1.28 !important;
      font-weight: 830 !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 7px !important;
      gap: 11px !important;
    }

    .bp-info-main-action,
    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      min-height: 30px !important;
      padding: 0 10px !important;
      font-size: 12px !important;
      box-shadow: none !important;
    }

    .bp-packages {
      margin-top: 34px !important;
      padding: 28px 0 10px !important;
      border-top: 1px solid rgba(108,88,61,.10) !important;
    }

    .bp-packages-title,
    .bp-section-title {
      font-size: clamp(28px, 4vw, 38px) !important;
      line-height: .98 !important;
      letter-spacing: -.065em !important;
    }

    .bp-packages-copy {
      max-width: 560px !important;
      font-size: 14px !important;
      line-height: 1.36 !important;
      margin-top: 7px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(205px, 230px) !important;
      gap: 14px !important;
      padding: 2px 2px 14px !important;
      scroll-snap-type: x mandatory !important;
    }

    .event-package-card {
      border-radius: 20px !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.045) !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 118px !important;
      padding: 15px !important;
    }

    .bp-split-section {
      display: grid !important;
      grid-template-columns: minmax(0, .95fr) minmax(0, 1.05fr) !important;
      gap: 34px !important;
      margin-top: 34px !important;
      padding: 30px 0 0 !important;
      border-top: 1px solid rgba(108,88,61,.10) !important;
      align-items: start !important;
    }

    .bp-split-column {
      min-width: 0 !important;
    }

    .bp-about-column {
      margin-bottom: 0 !important;
    }

    .bp-about {
      font-size: 15px !important;
      line-height: 1.62 !important;
      font-weight: 600 !important;
      max-height: 8em !important;
      max-width: 100% !important;
      margin-top: 16px !important;
      color: #334155 !important;
      transition: max-height .32s ease !important;
    }

    .bp-about.is-expanded {
      max-height: 1200px !important;
      overflow: visible !important;
    }

    .bp-team-column {
      overflow: hidden !important;
      position: relative !important;
    }

    .bp-team-column .bp-section-head {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 14px !important;
    }

    .bp-team-grid {
      display: grid !important;
      grid-auto-flow: column !important;
      grid-auto-columns: 104px !important;
      gap: 18px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      scroll-snap-type: x mandatory !important;
      -webkit-overflow-scrolling: touch !important;
      scrollbar-width: none !important;
      margin: 0 -6px !important;
      padding: 14px 6px 20px !important;
    }

    .bp-team-grid::-webkit-scrollbar {
      display: none !important;
    }

    .bp-team-grid > * {
      width: 104px !important;
      flex: 0 0 104px !important;
      scroll-snap-align: start !important;
    }

    .bp-team-actions {
      display: inline-flex !important;
      margin-left: 0 !important;
    }

    .bp-team-arrow {
      width: 38px !important;
      height: 38px !important;
      font-size: 16px !important;
    }

    .bp-section.bp-about-section,
    .bp-reviews-section {
      margin-top: 32px !important;
      padding: 30px 0 0 !important;
      border-top: 1px solid rgba(108,88,61,.10) !important;
      text-align: left !important;
    }

    .bp-reviews-head {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 16px !important;
      margin-bottom: 18px !important;
      text-align: left !important;
    }

    .bp-reviews-head .bp-section-head {
      display: flex !important;
      align-items: center !important;
      text-align: left !important;
    }

    .bp-review-average {
      margin-top: 0 !important;
      width: fit-content !important;
      padding: 8px 12px !important;
      font-size: 12px !important;
      box-shadow: none !important;
    }

    .bp-reviews-head .bp-head-link {
      margin: 0 0 0 auto !important;
      font-size: 13px !important;
    }

    .bp-final-cta {
      margin-top: 34px !important;
      padding: 20px 22px !important;
      border-radius: 24px !important;
      display: grid !important;
      grid-template-columns: 52px minmax(0, 1fr) auto !important;
      gap: 16px !important;
      align-items: center !important;
      box-shadow: 0 8px 22px rgba(0,103,95,.055) !important;
    }

    .bp-final-icon {
      width: 52px !important;
      height: 52px !important;
      grid-row: 1 / span 2 !important;
    }

    .bp-final-cta strong {
      font-size: clamp(22px, 3vw, 28px) !important;
      line-height: 1.03 !important;
      letter-spacing: -.055em !important;
    }

    .bp-final-cta p {
      font-size: 14px !important;
      line-height: 1.34 !important;
      margin-top: 5px !important;
    }

    .bp-final-cta .bp-final-btn {
      grid-column: 3 !important;
      grid-row: 1 / span 2 !important;
      width: auto !important;
      min-width: 170px !important;
      height: 52px !important;
      min-height: 52px !important;
      margin-top: 0 !important;
    }
  }

  @media (min-width: 768px) and (max-width: 920px) and (orientation: portrait) {
    .bp-hero {
      grid-template-columns: 1fr !important;
      gap: 16px !important;
    }

    .bp-logo-card,
    .bp-hero-top,
    .bp-info-panel {
      grid-column: 1 !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 7.6 !important;
      max-height: 260px !important;
    }

    .bp-info-panel {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }

    .bp-split-section {
      grid-template-columns: 1fr !important;
      gap: 26px !important;
    }

    .bp-final-cta {
      grid-template-columns: 52px minmax(0, 1fr) !important;
    }

    .bp-final-cta .bp-final-btn {
      grid-column: 1 / -1 !important;
      grid-row: auto !important;
      width: 100% !important;
      margin-top: 6px !important;
    }
  }


  /* =========================================================
     PHASE 8S — TABLET SAFE LAYOUT PASS
     Fixes mid-size iPad/tablet issues where hero/actions/info/services
     begin to overlap or feel cramped. Desktop and phone rules stay intact.
     ========================================================= */
  @media (min-width: 768px) and (max-width: 1180px) {
    html,
    body {
      overflow-x: hidden !important;
    }

    .business-page-shell,
    .bp-container,
    .bp-page,
    .bp-hero,
    .bp-section,
    .bp-packages,
    .bp-split-section,
    .bp-final-cta {
      max-width: 100vw !important;
      box-sizing: border-box !important;
    }

    .business-page-shell {
      padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .bp-container {
      width: 100% !important;
      padding-left: clamp(22px, 4vw, 44px) !important;
      padding-right: clamp(22px, 4vw, 44px) !important;
    }

    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(292px, 42vw) minmax(0, 1fr) !important;
      gap: clamp(22px, 3vw, 34px) !important;
      align-items: center !important;
      overflow: visible !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-logo-card {
      grid-column: 1 !important;
      grid-row: 1 !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
    }

    .bp-logo-stage {
      width: 100% !important;
      aspect-ratio: 16 / 9 !important;
      min-height: 0 !important;
      max-height: 260px !important;
      border-radius: 26px !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 26px !important;
    }

    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner {
      padding: clamp(12px, 1.8vw, 18px) !important;
    }

    .bp-hero-top {
      grid-column: 2 !important;
      grid-row: 1 !important;
      display: block !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      transform: none !important;
    }

    .bp-title-wrap {
      min-width: 0 !important;
      max-width: 100% !important;
    }

    .bp-title {
      font-size: clamp(34px, 4.65vw, 50px) !important;
      line-height: .96 !important;
      letter-spacing: -.066em !important;
      max-width: 100% !important;
      overflow-wrap: anywhere !important;
    }

    .bp-tagline {
      max-width: 100% !important;
      font-size: clamp(13px, 1.55vw, 15px) !important;
      line-height: 1.4 !important;
    }

    .bp-rating {
      max-width: 100% !important;
      flex-wrap: wrap !important;
    }

    .bp-actions {
      position: static !important;
      transform: none !important;
      margin-top: 16px !important;
      padding: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 10px !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-actions .bp-primary-btn,
    .bp-actions .bp-secondary-btn {
      flex: 0 1 auto !important;
      min-width: 140px !important;
      max-width: 100% !important;
      white-space: nowrap !important;
    }

    .bp-actions .bp-icon-btn {
      flex: 0 0 50px !important;
    }

    .bp-primary-btn,
    .bp-secondary-btn,
    .bp-icon-btn {
      height: 50px !important;
      min-height: 50px !important;
      border-radius: 17px !important;
      font-size: 14px !important;
      line-height: 1 !important;
    }

    .bp-icon-btn {
      width: 50px !important;
      min-width: 50px !important;
    }

    .bp-info-panel {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      width: 100% !important;
      min-width: 0 !important;
      margin-top: 4px !important;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 0 !important;
      padding: 16px 18px !important;
      border-radius: 24px !important;
      overflow: hidden !important;
    }

    .bp-info-row {
      min-width: 0 !important;
      min-height: 72px !important;
      display: grid !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 12px !important;
      padding: 12px 16px !important;
      border-right: 1px solid rgba(226,232,240,.82) !important;
      border-bottom: 1px solid rgba(226,232,240,.72) !important;
    }

    .bp-info-row:nth-child(2n) {
      border-right: 0 !important;
    }

    .bp-info-row:nth-last-child(-n + 2) {
      border-bottom: 0 !important;
    }

    .bp-info-value,
    .bp-info-value a {
      min-width: 0 !important;
      max-width: 100% !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
    }

    .bp-contact-actions,
    .bp-social-actions,
    .bp-zone-list {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
    }

    .bp-info-main-action,
    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium,
    .bp-zone-chip {
      max-width: 100% !important;
      white-space: nowrap !important;
    }

    .bp-packages,
    .bp-section,
    .bp-reviews-section,
    .bp-split-section {
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .bp-packages-head {
      display: flex !important;
      align-items: flex-end !important;
      justify-content: space-between !important;
      gap: 16px !important;
    }

    .bp-packages-title,
    .bp-section-title {
      font-size: clamp(27px, 3.5vw, 38px) !important;
    }

    .bp-packages-copy {
      max-width: 620px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      width: 100% !important;
      max-width: 100% !important;
      grid-auto-columns: minmax(205px, 235px) !important;
      gap: 14px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      scroll-snap-type: x mandatory !important;
      -webkit-overflow-scrolling: touch !important;
    }

    .event-package-card {
      scroll-snap-align: start !important;
    }

    .bp-split-section {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
      gap: clamp(24px, 3.2vw, 34px) !important;
      align-items: start !important;
      overflow: hidden !important;
    }

    .bp-split-column,
    .bp-about-column,
    .bp-team-column {
      min-width: 0 !important;
      max-width: 100% !important;
    }

    .bp-team-grid {
      width: 100% !important;
      max-width: 100% !important;
      display: grid !important;
      grid-auto-flow: column !important;
      grid-auto-columns: 104px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      scrollbar-width: none !important;
      -webkit-overflow-scrolling: touch !important;
    }

    .bp-team-grid::-webkit-scrollbar {
      display: none !important;
    }

    .bp-reviews-list {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 14px !important;
      max-width: 100% !important;
    }

    .bp-final-cta {
      width: 100% !important;
      display: grid !important;
      grid-template-columns: 52px minmax(0, 1fr) auto !important;
      gap: 14px !important;
      align-items: center !important;
      overflow: hidden !important;
    }

    .bp-final-cta .bp-final-btn {
      min-width: 158px !important;
      white-space: nowrap !important;
    }

    .business-booking-overlay {
      padding: 18px !important;
      align-items: center !important;
      justify-content: center !important;
      background: rgba(15,23,42,.58) !important;
    }

    .business-booking-modal {
      width: min(940px, calc(100vw - 36px)) !important;
      height: min(820px, calc(100dvh - 36px)) !important;
      max-height: calc(100dvh - 36px) !important;
      border-radius: 24px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 920px) and (orientation: portrait) {
    .bp-hero {
      grid-template-columns: 1fr !important;
      gap: 18px !important;
    }

    .bp-logo-card,
    .bp-hero-top,
    .bp-info-panel {
      grid-column: 1 !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 8 !important;
      max-height: 270px !important;
    }

    .bp-actions {
      display: grid !important;
      grid-template-columns: 54px minmax(0, 1fr) !important;
      gap: 10px !important;
    }

    .bp-actions .bp-primary-btn {
      width: 100% !important;
      min-width: 0 !important;
    }

    .bp-actions .bp-secondary-btn,
    .bp-actions .bp-share-btn {
      display: none !important;
    }

    .bp-info-panel {
      grid-template-columns: 1fr !important;
      padding: 14px 16px !important;
    }

    .bp-info-row {
      border-right: 0 !important;
      border-bottom: 1px solid rgba(226,232,240,.72) !important;
      padding: 13px 0 !important;
      min-height: 0 !important;
    }

    .bp-info-row:last-child {
      border-bottom: 0 !important;
    }

    .bp-split-section,
    .bp-reviews-list {
      grid-template-columns: 1fr !important;
    }

    .bp-final-cta {
      grid-template-columns: 52px minmax(0, 1fr) !important;
    }

    .bp-final-cta .bp-final-btn {
      grid-column: 1 / -1 !important;
      width: 100% !important;
      margin-top: 4px !important;
    }
  }

  @media (min-width: 921px) and (max-width: 1180px) and (orientation: portrait) {
    .bp-hero {
      grid-template-columns: minmax(300px, 38vw) minmax(0, 1fr) !important;
    }

    .bp-info-panel {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }

  @media (min-width: 921px) and (max-width: 1180px) and (orientation: landscape) {
    .bp-logo-stage {
      max-height: 232px !important;
    }

    .bp-title {
      font-size: clamp(36px, 4.1vw, 52px) !important;
    }

    .bp-info-panel {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }

    .bp-info-row:nth-child(2n) {
      border-right: 1px solid rgba(226,232,240,.82) !important;
    }

    .bp-info-row:nth-child(3n),
    .bp-info-row:last-child {
      border-right: 0 !important;
    }

    .bp-info-row:nth-last-child(-n + 2) {
      border-bottom: 1px solid rgba(226,232,240,.72) !important;
    }

    .bp-info-row:nth-last-child(-n + 3) {
      border-bottom: 0 !important;
    }
  }


  /* =========================================================
     BUSINESS PAGE 2A — TABLET POLISH / LOGO + ACTIONS + ALIGNMENT
     Fixes tablet profile issues:
     - Logo never crops: force contain and balanced canvas.
     - Favorite, reserve and share visible on tablet.
     - Services header aligned left and less visually heavy.
     - Tablet info/services spacing cleaned up.
     ========================================================= */

  .bp-logo-stage .smart-logo-fit-cover .smart-logo-inner,
  .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner,
  .bp-logo-stage .smart-logo-inner {
    padding: clamp(16px, 2.4vw, 26px) !important;
    background: linear-gradient(135deg, #fff8ef 0%, #f8efe4 100%) !important;
  }

  .bp-logo-stage .smart-logo-fit-cover .smart-logo-img,
  .bp-logo-stage .smart-logo-fit-cover img,
  .bp-logo-stage .smart-logo-fit-contain .smart-logo-img,
  .bp-logo-stage .smart-logo-fit-contain img,
  .bp-logo-stage .smart-logo-img,
  .bp-logo-stage img {
    object-fit: contain !important;
    object-position: center center !important;
    transform: none !important;
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-container {
      padding-left: clamp(28px, 4.8vw, 54px) !important;
      padding-right: clamp(28px, 4.8vw, 54px) !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 8.3 !important;
      max-height: 286px !important;
      background: linear-gradient(135deg, #fff8ef 0%, #f8efe4 100%) !important;
      box-shadow: 0 12px 28px rgba(54,41,25,.055) !important;
    }

    .bp-logo-stage .smart-logo-inner {
      padding: clamp(18px, 2.8vw, 32px) !important;
    }

    .bp-actions {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      flex-wrap: nowrap !important;
      gap: 10px !important;
      margin-top: 18px !important;
      width: 100% !important;
      overflow: visible !important;
    }

    .bp-actions .bp-primary-btn {
      order: 2 !important;
      flex: 1 1 170px !important;
      min-width: 158px !important;
      max-width: 230px !important;
    }

    .bp-actions .bp-icon-btn {
      order: 1 !important;
      flex: 0 0 50px !important;
      width: 50px !important;
      min-width: 50px !important;
    }

    .bp-actions .bp-share-btn {
      order: 3 !important;
      display: inline-flex !important;
      flex: 0 0 50px !important;
      width: 50px !important;
      min-width: 50px !important;
      padding: 0 !important;
      border-radius: 17px !important;
    }

    .bp-actions .bp-share-btn span {
      display: none !important;
    }

    .bp-actions .bp-secondary-btn:not(.bp-share-btn) {
      order: 4 !important;
    }

    .bp-info-panel {
      margin-top: 10px !important;
      border-radius: 26px !important;
    }

    .bp-packages {
      margin-top: 30px !important;
      padding-top: 28px !important;
    }

    .bp-packages-head {
      display: flex !important;
      align-items: flex-end !important;
      justify-content: space-between !important;
      text-align: left !important;
      margin-bottom: 16px !important;
      width: 100% !important;
    }

    .bp-packages-head > div:first-child {
      min-width: 0 !important;
      width: auto !important;
      max-width: 680px !important;
      margin: 0 !important;
      text-align: left !important;
    }

    .bp-kicker,
    .bp-packages-title,
    .bp-packages-copy {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .bp-packages-title::before {
      margin-right: 12px !important;
    }

    .bp-package-actions {
      flex: 0 0 auto !important;
      margin-left: auto !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      margin-left: 0 !important;
      padding-left: 0 !important;
      scroll-padding-left: 0 !important;
    }
  }

  @media (min-width: 768px) and (max-width: 920px) and (orientation: portrait) {
    .bp-hero {
      gap: 16px !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 7.2 !important;
      max-height: 300px !important;
      border-radius: 28px !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 28px !important;
    }

    .bp-logo-stage .smart-logo-inner {
      padding: clamp(18px, 3.2vw, 34px) !important;
    }

    .bp-hero-top {
      display: block !important;
      padding: 0 2px !important;
    }

    .bp-actions {
      display: grid !important;
      grid-template-columns: 56px minmax(0, 1fr) 56px !important;
      gap: 10px !important;
      margin-top: 14px !important;
    }

    .bp-actions .bp-icon-btn {
      order: 1 !important;
      width: 56px !important;
      min-width: 56px !important;
      height: 54px !important;
      min-height: 54px !important;
    }

    .bp-actions .bp-primary-btn {
      order: 2 !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
      height: 54px !important;
      min-height: 54px !important;
      font-size: 16px !important;
    }

    .bp-actions .bp-share-btn {
      order: 3 !important;
      display: inline-flex !important;
      width: 56px !important;
      min-width: 56px !important;
      height: 54px !important;
      min-height: 54px !important;
      padding: 0 !important;
    }

    .bp-actions .bp-share-btn span {
      display: none !important;
    }

    .bp-info-panel {
      margin-top: 2px !important;
      display: grid !important;
      grid-template-columns: 1fr !important;
      padding: 8px 18px !important;
    }

    .bp-packages {
      margin-top: 30px !important;
      padding-top: 30px !important;
    }

    .bp-packages-head {
      display: block !important;
      text-align: left !important;
      margin-bottom: 18px !important;
    }

    .bp-package-actions {
      display: none !important;
    }

    .bp-packages-title {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      text-align: left !important;
      width: 100% !important;
      font-size: clamp(30px, 5.1vw, 42px) !important;
    }

    .bp-packages-copy {
      max-width: 620px !important;
      text-align: left !important;
    }
  }

  @media (max-width: 767px) {
    .bp-logo-stage .smart-logo-inner {
      padding: 14px !important;
    }

    .bp-logo-stage .smart-logo-img,
    .bp-logo-stage img {
      object-fit: contain !important;
    }
  }


  /* =========================================================
     BUSINESS PAGE 2B — TABLET SHOWROOM REVAMP
     Goal: tablet-first selling view for salons.
     - Business name and CTA always above the logo on tablet/mobile.
     - Favorite / reserve / share always visible.
     - Info panel becomes premium cards instead of a dense table.
     - Services section aligned and calmer on tablet.
     ========================================================= */

  @media (max-width: 1180px) {
    .business-page-shell {
      background:
        radial-gradient(circle at 18% 0%, rgba(0,103,95,.10), transparent 32%),
        linear-gradient(180deg, #fffaf4 0%, #fff8ef 48%, #fffaf4 100%) !important;
      padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .bp-container {
      width: 100% !important;
      max-width: 980px !important;
      padding: clamp(14px, 2.6vw, 28px) clamp(18px, 4vw, 44px) 0 !important;
    }

    .bp-hero {
      display: flex !important;
      flex-direction: column !important;
      gap: 16px !important;
      padding: 0 !important;
    }

    .bp-hero-top {
      order: 1 !important;
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 14px !important;
      margin: 0 !important;
      padding: 0 2px !important;
      align-items: start !important;
    }

    .bp-title-wrap {
      display: block !important;
      grid-column: auto !important;
      grid-row: auto !important;
      order: 1 !important;
      max-width: 760px !important;
      min-width: 0 !important;
      align-self: auto !important;
    }

    .bp-title {
      display: block !important;
      color: #10323d !important;
      font-size: clamp(34px, 5.2vw, 54px) !important;
      line-height: .96 !important;
      letter-spacing: -.068em !important;
      margin: 0 !important;
      text-align: left !important;
      max-width: 780px !important;
    }

    .bp-tagline {
      display: block !important;
      margin-top: 8px !important;
      color: #475569 !important;
      font-size: clamp(14px, 1.8vw, 16px) !important;
      line-height: 1.42 !important;
      max-width: 680px !important;
    }

    .bp-rating {
      margin-top: 10px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      color: #f59e0b !important;
      font-size: 13px !important;
      gap: 7px !important;
    }

    .bp-rating span {
      color: #64748b !important;
      font-size: 13px !important;
      font-weight: 750 !important;
    }

    .bp-actions {
      order: 2 !important;
      position: static !important;
      transform: none !important;
      width: 100% !important;
      max-width: 520px !important;
      display: grid !important;
      grid-template-columns: 56px minmax(0, 1fr) 56px !important;
      gap: 10px !important;
      margin: 2px 0 0 !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      align-items: stretch !important;
      justify-self: start !important;
      overflow: visible !important;
    }

    .bp-actions .bp-icon-btn {
      order: 1 !important;
      width: 56px !important;
      min-width: 56px !important;
      height: 54px !important;
      min-height: 54px !important;
      border-radius: 18px !important;
      background: rgba(255,255,255,.94) !important;
      border: 1px solid rgba(108,88,61,.12) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.06) !important;
    }

    .bp-actions .bp-primary-btn {
      order: 2 !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
      height: 54px !important;
      min-height: 54px !important;
      border-radius: 18px !important;
      font-size: 15px !important;
      padding: 0 18px !important;
      display: inline-flex !important;
    }

    .bp-actions .bp-share-btn {
      order: 3 !important;
      display: inline-flex !important;
      width: 56px !important;
      min-width: 56px !important;
      height: 54px !important;
      min-height: 54px !important;
      border-radius: 18px !important;
      padding: 0 !important;
      background: rgba(255,255,255,.94) !important;
      border: 1px solid rgba(108,88,61,.12) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.06) !important;
      color: #0f766e !important;
    }

    .bp-actions .bp-share-btn span {
      display: none !important;
    }

    .bp-actions .bp-secondary-btn:not(.bp-share-btn) {
      display: none !important;
    }

    .bp-hero-grid {
      order: 2 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 16px !important;
      width: 100% !important;
    }

    .bp-logo-card {
      order: 1 !important;
      width: 100% !important;
      max-width: none !important;
    }

    .bp-logo-stage {
      width: 100% !important;
      aspect-ratio: 16 / 6.6 !important;
      min-height: 220px !important;
      max-height: 330px !important;
      border-radius: 30px !important;
      background: linear-gradient(135deg, #fff8ef 0%, #f8efe4 100%) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 18px 46px rgba(54,41,25,.075) !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 30px !important;
    }

    .bp-logo-stage .smart-logo-inner {
      padding: clamp(22px, 3.2vw, 38px) !important;
      background: linear-gradient(135deg, #fff8ef 0%, #f8efe4 100%) !important;
    }

    .bp-logo-stage .smart-logo-img,
    .bp-logo-stage img {
      object-fit: contain !important;
      object-position: center center !important;
    }

    .bp-info-panel {
      order: 2 !important;
      margin-top: 0 !important;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 112px !important;
      display: grid !important;
      grid-template-columns: 46px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 14px !important;
      padding: 16px !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 12px 30px rgba(54,41,25,.055) !important;
    }

    .bp-info-icon {
      width: 46px !important;
      height: 46px !important;
      border-radius: 18px !important;
      background: #eaf8f4 !important;
      color: #00675f !important;
    }

    .bp-info-label {
      display: none !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      color: #102a33 !important;
      font-size: 14px !important;
      line-height: 1.35 !important;
      font-weight: 900 !important;
    }

    .bp-inline-link,
    .bp-info-main-action,
    .bp-contact-action,
    .bp-maps-link,
    .bp-zone-chip {
      min-height: 32px !important;
      padding: 0 11px !important;
      border-radius: 999px !important;
      background: #f0fdfa !important;
      border: 1px solid #ccfbf1 !important;
      color: #00675f !important;
      font-size: 11.5px !important;
      font-weight: 950 !important;
      box-shadow: none !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 9px !important;
      gap: 7px !important;
    }

    .bp-hours-today,
    .bp-hours-day {
      grid-template-columns: 1fr auto !important;
      font-size: 13px !important;
    }

    .bp-packages {
      margin-top: 28px !important;
      padding-top: 28px !important;
      border-top: 1px solid rgba(108,88,61,.10) !important;
    }

    .bp-packages-head {
      width: 100% !important;
      display: flex !important;
      align-items: flex-end !important;
      justify-content: space-between !important;
      gap: 14px !important;
      margin-bottom: 16px !important;
      text-align: left !important;
    }

    .bp-packages-head > div:first-child {
      width: auto !important;
      max-width: 720px !important;
      min-width: 0 !important;
      margin: 0 !important;
      text-align: left !important;
    }

    .bp-kicker,
    .bp-packages-title,
    .bp-packages-copy {
      margin-left: 0 !important;
      margin-right: 0 !important;
      text-align: left !important;
    }

    .bp-packages-title {
      font-size: clamp(30px, 4.8vw, 42px) !important;
      justify-content: flex-start !important;
    }

    .bp-package-actions {
      display: none !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(220px, 250px) !important;
      gap: 14px !important;
      padding-left: 0 !important;
      margin-left: 0 !important;
    }

    .bp-section {
      margin-top: 28px !important;
      padding-top: 28px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1180px) and (orientation: landscape) {
    .bp-hero-grid {
      display: grid !important;
      grid-template-columns: minmax(0, 1.05fr) minmax(330px, .75fr) !important;
      gap: 18px !important;
      align-items: stretch !important;
    }

    .bp-logo-card {
      min-width: 0 !important;
    }

    .bp-logo-stage {
      height: 100% !important;
      min-height: 300px !important;
      aspect-ratio: auto !important;
    }

    .bp-info-panel {
      grid-template-columns: 1fr !important;
      align-content: start !important;
    }

    .bp-info-row {
      min-height: 92px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-container {
      padding: 12px 12px 0 !important;
    }

    .bp-title {
      font-size: 34px !important;
    }

    .bp-actions {
      max-width: none !important;
      grid-template-columns: 54px minmax(0, 1fr) 54px !important;
      gap: 8px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn,
    .bp-actions .bp-primary-btn {
      height: 52px !important;
      min-height: 52px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 54px !important;
      min-width: 54px !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 8.4 !important;
      min-height: 176px !important;
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-inner {
      padding: 16px !important;
    }

    .bp-info-panel {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }

    .bp-info-row {
      min-height: auto !important;
      grid-template-columns: 42px minmax(0, 1fr) !important;
      padding: 14px !important;
      border-radius: 22px !important;
    }

    .bp-info-icon {
      width: 42px !important;
      height: 42px !important;
    }

    .bp-packages-head {
      display: block !important;
    }

    .bp-packages-copy {
      max-width: 360px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(205px, 232px) !important;
    }
  }


  /* =========================================================
     BUSINESS PAGE 2C — HERO RESPONSIVE CLEANUP
     Goal: make the first screen showroom-ready on tablet + mobile.
     - Mobile: no action buttons over the title/rating.
     - Mobile/tablet: business name always above logo.
     - Tablet: logo canvas is calmer, shorter, and less cream-heavy.
     - Actions: favorite + reserve + share always visible and aligned.
     ========================================================= */

  .bp-hero,
  .bp-hero-top,
  .bp-title-wrap,
  .bp-actions,
  .bp-logo-card,
  .bp-logo-stage {
    box-sizing: border-box !important;
  }

  .bp-actions .bp-share-btn {
    display: inline-flex !important;
  }

  .bp-actions .bp-icon-btn,
  .bp-actions .bp-primary-btn,
  .bp-actions .bp-share-btn {
    flex-shrink: 0 !important;
  }

  /* Tablet showroom: title/action bar first, logo below, cleaner canvas. */
  @media (min-width: 768px) and (max-width: 1180px) {
    .business-page-shell {
      background:
        radial-gradient(circle at 12% 0%, rgba(0,103,95,.08), transparent 28%),
        linear-gradient(180deg, #fffaf4 0%, #fff8ef 52%, #fffaf4 100%) !important;
    }

    .bp-container {
      padding-left: clamp(28px, 4vw, 42px) !important;
      padding-right: clamp(28px, 4vw, 42px) !important;
    }

    .bp-hero {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 16px !important;
      align-items: stretch !important;
      padding-top: 20px !important;
      overflow: visible !important;
    }

    .bp-hero-top {
      grid-column: 1 !important;
      grid-row: 1 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      gap: 18px !important;
      align-items: center !important;
      width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .bp-title-wrap {
      min-width: 0 !important;
      width: 100% !important;
      max-width: none !important;
      text-align: left !important;
    }

    .bp-title {
      color: #10323d !important;
      font-size: clamp(36px, 4.7vw, 52px) !important;
      line-height: .96 !important;
      letter-spacing: -.068em !important;
      max-width: 100% !important;
      margin: 0 !important;
      overflow-wrap: anywhere !important;
      text-align: left !important;
    }

    .bp-tagline {
      max-width: 680px !important;
      margin-top: 8px !important;
      font-size: 14px !important;
      line-height: 1.38 !important;
      color: #64748b !important;
    }

    .bp-rating {
      margin-top: 10px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      color: #d97706 !important;
      font-size: 13px !important;
      line-height: 1.2 !important;
      max-width: 100% !important;
      flex-wrap: wrap !important;
    }

    .bp-rating span {
      font-size: 13px !important;
      color: #64748b !important;
    }

    .bp-actions {
      grid-column: 2 !important;
      grid-row: 1 !important;
      position: static !important;
      transform: none !important;
      justify-self: end !important;
      align-self: center !important;
      display: grid !important;
      grid-template-columns: 58px minmax(150px, 174px) 58px !important;
      gap: 10px !important;
      width: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 58px !important;
      min-width: 58px !important;
      height: 54px !important;
      min-height: 54px !important;
      padding: 0 !important;
      border-radius: 18px !important;
      background: rgba(255,255,255,.96) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.055) !important;
    }

    .bp-actions .bp-icon-btn { order: 1 !important; }
    .bp-actions .bp-primary-btn { order: 2 !important; }
    .bp-actions .bp-share-btn { order: 3 !important; }

    .bp-actions .bp-primary-btn {
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      height: 54px !important;
      min-height: 54px !important;
      padding: 0 18px !important;
      border-radius: 18px !important;
      font-size: 15px !important;
      box-shadow: 0 12px 28px rgba(0,103,95,.17) !important;
      white-space: nowrap !important;
    }

    .bp-actions .bp-share-btn span,
    .bp-actions .bp-secondary-btn:not(.bp-share-btn) {
      display: none !important;
    }

    .bp-hero-grid {
      grid-column: 1 !important;
      grid-row: 2 / span 2 !important;
      display: contents !important;
    }

    .bp-logo-card {
      grid-column: 1 !important;
      grid-row: 2 !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
    }

    .bp-logo-stage {
      width: 100% !important;
      aspect-ratio: 16 / 5.35 !important;
      max-height: 242px !important;
      min-height: 0 !important;
      border-radius: 28px !important;
      background: rgba(255,255,255,.48) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 10px 26px rgba(54,41,25,.052) !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 28px !important;
    }

    .bp-logo-stage .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-cover .smart-logo-inner {
      padding: clamp(7px, 1.15vw, 12px) !important;
      background: rgba(255,248,239,.36) !important;
    }

    .bp-logo-stage .smart-logo-img,
    .bp-logo-stage img {
      object-fit: contain !important;
      object-position: center center !important;
    }
  }

  /* Narrow tablet / large phone: keep actions under text, never over it. */
  @media (min-width: 768px) and (max-width: 960px) {
    .bp-hero-top {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
      align-items: start !important;
    }

    .bp-actions {
      grid-column: 1 !important;
      grid-row: 2 !important;
      justify-self: start !important;
      width: min(100%, 420px) !important;
      grid-template-columns: 58px minmax(0, 1fr) 58px !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 5.85 !important;
      max-height: 236px !important;
    }
  }

  /* Phone: clear stack. Title/rating -> actions -> logo. No overlap. */
  @media (max-width: 767px) {
    .business-page-shell {
      padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
      overflow-x: hidden !important;
    }

    .bp-container {
      width: 100% !important;
      max-width: 100% !important;
      padding: 0 16px !important;
    }

    .bp-hero {
      display: flex !important;
      flex-direction: column !important;
      gap: 14px !important;
      padding-top: 22px !important;
      overflow: visible !important;
    }

    .bp-hero-top {
      order: 1 !important;
      display: block !important;
      width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      position: relative !important;
      z-index: 2 !important;
    }

    .bp-title-wrap {
      width: 100% !important;
      min-width: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .bp-title {
      font-size: clamp(34px, 11.5vw, 46px) !important;
      line-height: .94 !important;
      letter-spacing: -.072em !important;
      color: #10323d !important;
      margin: 0 !important;
      max-width: 100% !important;
      text-align: left !important;
      overflow-wrap: normal !important;
      word-break: normal !important;
    }

    .bp-tagline {
      display: block !important;
      margin-top: 7px !important;
      font-size: 14px !important;
      line-height: 1.35 !important;
      color: #64748b !important;
      max-width: 100% !important;
    }

    .bp-rating {
      margin-top: 10px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      font-size: 13px !important;
      line-height: 1.22 !important;
      color: #d97706 !important;
      max-width: 100% !important;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 6px !important;
    }

    .bp-rating span {
      font-size: 13px !important;
      color: #64748b !important;
    }

    .bp-actions {
      position: static !important;
      transform: none !important;
      width: 100% !important;
      margin: 14px 0 0 !important;
      padding: 0 !important;
      display: grid !important;
      grid-template-columns: 62px minmax(0, 1fr) 62px !important;
      gap: 10px !important;
      align-items: center !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      z-index: 3 !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 62px !important;
      min-width: 62px !important;
      height: 58px !important;
      min-height: 58px !important;
      padding: 0 !important;
      border-radius: 20px !important;
      background: rgba(255,255,255,.96) !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.055) !important;
      display: inline-flex !important;
    }

    .bp-actions .bp-icon-btn { order: 1 !important; }
    .bp-actions .bp-primary-btn { order: 2 !important; }
    .bp-actions .bp-share-btn { order: 3 !important; }

    .bp-actions .bp-primary-btn {
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      height: 58px !important;
      min-height: 58px !important;
      border-radius: 20px !important;
      padding: 0 18px !important;
      font-size: 18px !important;
      line-height: 1 !important;
      box-shadow: 0 12px 26px rgba(0,103,95,.18) !important;
      white-space: nowrap !important;
    }

    .bp-actions .bp-secondary-btn:not(.bp-share-btn),
    .bp-actions .bp-share-btn span {
      display: none !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-logo-card {
      order: 2 !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      position: relative !important;
      z-index: 1 !important;
    }

    .bp-logo-stage {
      width: 100% !important;
      aspect-ratio: 16 / 8.45 !important;
      max-height: 242px !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.40) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.045) !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-cover .smart-logo-inner {
      padding: 8px !important;
      background: rgba(255,248,239,.30) !important;
    }

    .bp-logo-stage .smart-logo-img,
    .bp-logo-stage img {
      object-fit: contain !important;
      object-position: center center !important;
    }

    .bp-info-panel {
      order: 3 !important;
    }
  }

  @media (max-width: 380px) {
    .bp-actions {
      grid-template-columns: 56px minmax(0, 1fr) 56px !important;
      gap: 8px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 56px !important;
      min-width: 56px !important;
    }

    .bp-actions .bp-primary-btn {
      font-size: 16px !important;
      padding: 0 12px !important;
    }
  }


  /* =========================================================
     BUSINESS PAGE 2D — INFO CARDS PREMIUM
     Goal: turn business details into clean, showroom-ready cards.
     - Tablet: compact 2x2 information cards with clear visual rhythm.
     - Mobile: lighter cards, less vertical waste, no heavy table feeling.
     - Web: softer information strip without fighting the hero.
     ========================================================= */

  .bp-info-panel {
    isolation: isolate !important;
  }

  .bp-info-row {
    position: relative !important;
    overflow: hidden !important;
    text-align: left !important;
  }

  .bp-info-row::before {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    pointer-events: none !important;
    background: radial-gradient(circle at 0% 0%, rgba(0,103,95,.055), transparent 42%) !important;
    opacity: .9 !important;
  }

  .bp-info-icon,
  .bp-info-content {
    position: relative !important;
    z-index: 1 !important;
  }

  .bp-info-content {
    min-width: 0 !important;
    display: grid !important;
    align-content: center !important;
  }

  .bp-info-value-primary,
  .bp-contact-number,
  .bp-hours-summary strong,
  .bp-social-row .bp-info-value a {
    color: #102a33 !important;
    letter-spacing: -.018em !important;
  }

  .bp-inline-link,
  .bp-info-main-action,
  .bp-contact-action,
  .bp-hours-toggle,
  .bp-social-actions a {
    transition: transform .16s ease, background .16s ease, border-color .16s ease, box-shadow .16s ease !important;
  }

  .bp-inline-link:hover,
  .bp-info-main-action:hover,
  .bp-contact-action:hover,
  .bp-hours-toggle:hover,
  .bp-social-actions a:hover {
    transform: translateY(-1px) !important;
  }

  .bp-hours-summary {
    display: inline-grid !important;
    grid-template-columns: auto 1fr !important;
    gap: 8px !important;
    align-items: baseline !important;
    width: fit-content !important;
    max-width: 100% !important;
    color: #102a33 !important;
  }

  .bp-hours-summary-label {
    color: #64748b !important;
    font-size: 12px !important;
    font-weight: 900 !important;
  }

  .bp-hours-summary strong {
    font-size: 14px !important;
    font-weight: 950 !important;
    text-align: left !important;
    align-items: flex-start !important;
  }

  .bp-hours-week-premium {
    margin-top: 10px !important;
    border-radius: 16px !important;
    background: rgba(248,250,252,.86) !important;
    border: 1px solid rgba(226,232,240,.82) !important;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.75) !important;
  }

  .bp-hours-week-premium .bp-hours-day {
    padding: 8px 10px !important;
  }

  .bp-social-actions a {
    width: 34px !important;
    height: 34px !important;
    min-width: 34px !important;
    border-radius: 999px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    text-decoration: none !important;
  }

  @media (min-width: 1181px) {
    .bp-info-panel {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 12px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 106px !important;
      padding: 17px 18px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.78) !important;
      box-shadow: 0 12px 30px rgba(54,41,25,.045) !important;
      grid-template-columns: 42px minmax(0,1fr) !important;
      gap: 13px !important;
    }

    .bp-info-row:first-child,
    .bp-info-row:last-child {
      padding-left: 18px !important;
      padding-right: 18px !important;
    }

    .bp-info-icon {
      width: 42px !important;
      height: 42px !important;
      border-radius: 16px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-info-panel {
      gap: 14px !important;
      margin-top: 2px !important;
    }

    .bp-info-row {
      min-height: 100px !important;
      padding: 15px 16px !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.78) !important;
      border: 1px solid rgba(108,88,61,.085) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.045) !important;
      grid-template-columns: 44px minmax(0, 1fr) !important;
      gap: 13px !important;
    }

    .bp-info-icon {
      width: 44px !important;
      height: 44px !important;
      border-radius: 17px !important;
      background: linear-gradient(135deg, #eaf8f4 0%, #f3fbf9 100%) !important;
      box-shadow: inset 0 0 0 1px rgba(0,103,95,.04) !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13.5px !important;
      line-height: 1.34 !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 8px !important;
    }

    .bp-contact-action,
    .bp-info-main-action,
    .bp-inline-link,
    .bp-hours-toggle {
      min-height: 31px !important;
      padding: 0 11px !important;
      font-size: 11.5px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 960px) {
    .bp-info-panel {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 10px !important;
      margin-top: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    .bp-info-row {
      min-height: 92px !important;
      padding: 14px 14px !important;
      border-radius: 24px !important;
      border: 1px solid rgba(108,88,61,.085) !important;
      background: rgba(255,255,255,.82) !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.042) !important;
      grid-template-columns: 44px minmax(0, 1fr) !important;
      gap: 12px !important;
    }

    .bp-info-icon {
      width: 44px !important;
      height: 44px !important;
      border-radius: 18px !important;
      background: #eaf8f4 !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 14px !important;
      line-height: 1.32 !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 8px !important;
      gap: 7px !important;
    }

    .bp-contact-action,
    .bp-info-main-action,
    .bp-inline-link,
    .bp-hours-toggle {
      min-height: 32px !important;
      padding: 0 11px !important;
      font-size: 12px !important;
    }

    .bp-hours-summary {
      grid-template-columns: auto minmax(0, 1fr) !important;
      width: 100% !important;
    }

    .bp-hours-summary strong {
      justify-self: start !important;
    }
  }



  /* =========================================================
     BUSINESSPAGE 2E — SECTIONS ALIGNMENT + VISUAL RHYTHM
     Keeps the showroom hero/info work, but makes the lower page
     feel editorial: same left edge, no accidental centering,
     cleaner spacing between services/about/team/reviews.
     ========================================================= */

  .bp-content-max,
  .bp-packages,
  .bp-split-section,
  .bp-section.bp-about-section,
  .bp-final-cta {
    width: 100% !important;
    max-width: 1320px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    text-align: left !important;
  }

  .bp-packages,
  .bp-split-section,
  .bp-section.bp-about-section {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .bp-packages {
    margin-top: 34px !important;
    padding-top: 34px !important;
  }

  .bp-split-section {
    margin-top: 34px !important;
    padding-top: 34px !important;
    border-top: 1px solid rgba(108,88,61,.11) !important;
    display: grid !important;
    grid-template-columns: minmax(0, .95fr) minmax(280px, .55fr) !important;
    gap: clamp(34px, 5vw, 78px) !important;
    align-items: start !important;
    background: transparent !important;
  }

  .bp-section.bp-about-section {
    margin-top: 34px !important;
    padding-top: 34px !important;
    border-top: 1px solid rgba(108,88,61,.11) !important;
    background: transparent !important;
  }

  .bp-section-head,
  .bp-packages-head,
  .bp-reviews-head,
  .bp-reviews-head .bp-section-head {
    width: 100% !important;
    max-width: 100% !important;
    display: flex !important;
    align-items: flex-end !important;
    justify-content: flex-start !important;
    gap: 14px !important;
    text-align: left !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .bp-packages-head {
    margin-bottom: 18px !important;
  }

  .bp-packages-head > div,
  .bp-section-head > div,
  .bp-reviews-head > div {
    text-align: left !important;
  }

  .bp-section-title,
  .bp-packages-title {
    text-align: left !important;
    margin-left: 0 !important;
  }

  .bp-section-title::before,
  .bp-packages-title::before {
    margin-left: 0 !important;
  }

  .bp-packages-copy,
  .bp-kicker {
    text-align: left !important;
  }

  .bp-package-actions {
    margin-left: auto !important;
    justify-content: flex-end !important;
  }

  .bp-packages-rail,
  .event-packages-grid,
  .bp-team-grid,
  .bp-reviews-list {
    margin-left: 0 !important;
    margin-right: 0 !important;
    text-align: left !important;
  }

  .bp-about-column,
  .bp-team-column {
    min-width: 0 !important;
    text-align: left !important;
  }

  .bp-about-section,
  .bp-about,
  .bp-about p,
  .bp-about div,
  .bp-text-link,
  .business-show-more-reviews {
    text-align: left !important;
  }

  .bp-about {
    max-width: 760px !important;
    margin-left: 0 !important;
    margin-right: auto !important;
  }

  .bp-reviews-head {
    align-items: center !important;
    margin-bottom: 18px !important;
  }

  .bp-reviews-head .bp-section-head {
    flex: 0 1 auto !important;
    width: auto !important;
  }

  .bp-review-average {
    flex: 0 0 auto !important;
    margin-left: 6px !important;
  }

  .bp-reviews-head .bp-head-link {
    margin-left: auto !important;
  }

  .bp-reviews-list {
    width: 100% !important;
    max-width: 100% !important;
  }

  .bp-reviews-list > * {
    text-align: left !important;
  }

  .business-show-more-reviews {
    margin-left: 0 !important;
    margin-right: auto !important;
  }

  .bp-final-cta {
    margin-top: 38px !important;
  }

  @media (min-width: 921px) and (max-width: 1180px) {
    .bp-packages,
    .bp-split-section,
    .bp-section.bp-about-section,
    .bp-final-cta {
      max-width: 100% !important;
    }

    .bp-split-section {
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 30px !important;
    }

    .bp-team-column {
      max-width: 100% !important;
    }
  }

  @media (max-width: 920px) {
    .bp-packages,
    .bp-split-section,
    .bp-section.bp-about-section,
    .bp-final-cta {
      max-width: 100% !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .bp-packages {
      margin-top: 28px !important;
      padding-top: 28px !important;
    }

    .bp-split-section,
    .bp-section.bp-about-section {
      margin-top: 28px !important;
      padding-top: 28px !important;
      display: block !important;
    }

    .bp-packages-head,
    .bp-section-head,
    .bp-reviews-head,
    .bp-reviews-head .bp-section-head {
      display: flex !important;
      align-items: flex-start !important;
      justify-content: flex-start !important;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .bp-package-actions,
    .bp-team-actions {
      display: none !important;
    }

    .bp-reviews-head .bp-head-link,
    .bp-review-average {
      margin-left: 0 !important;
      margin-top: 2px !important;
    }

    .bp-about {
      max-width: 100% !important;
    }
  }

  /* =========================================================
     BUSINESSPAGE 2F — MOBILE STICKY CTA
     - Mobile converts better: favorite/reserve/share live in a bottom bar.
     - Hero stays clean: no buttons fighting with title, rating or logo.
     - Tablet remains showroom layout from 2C/2D/2E.
     ========================================================= */

  @media (max-width: 767px) {
    .business-page-shell {
      padding-bottom: calc(122px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .bp-container {
      padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .bp-hero-top {
      padding-bottom: 0 !important;
    }

    .bp-title-wrap {
      padding-right: 0 !important;
    }

    .bp-title {
      padding-right: 0 !important;
    }

    .bp-actions {
      position: fixed !important;
      left: max(14px, env(safe-area-inset-left, 0px)) !important;
      right: max(14px, env(safe-area-inset-right, 0px)) !important;
      bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
      z-index: 25000 !important;
      width: auto !important;
      max-width: none !important;
      height: auto !important;
      margin: 0 !important;
      padding: 8px !important;
      display: grid !important;
      grid-template-columns: 52px minmax(0, 1fr) 52px !important;
      gap: 8px !important;
      align-items: center !important;
      border-radius: 24px !important;
      border: 1px solid rgba(108,88,61,.12) !important;
      background: rgba(255,255,255,.92) !important;
      box-shadow: 0 18px 50px rgba(15,23,42,.18), 0 1px 0 rgba(255,255,255,.86) inset !important;
      backdrop-filter: blur(18px) saturate(1.08) !important;
      -webkit-backdrop-filter: blur(18px) saturate(1.08) !important;
      overflow: visible !important;
      transform: translateZ(0) !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 52px !important;
      min-width: 52px !important;
      height: 52px !important;
      min-height: 52px !important;
      padding: 0 !important;
      border-radius: 18px !important;
      background: rgba(255,255,255,.96) !important;
      border: 1px solid rgba(226,232,240,.86) !important;
      box-shadow: none !important;
      color: #0f766e !important;
    }

    .bp-actions .bp-icon-btn {
      order: 1 !important;
      color: #ef4444 !important;
    }

    .bp-actions .bp-primary-btn {
      order: 2 !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      height: 52px !important;
      min-height: 52px !important;
      padding: 0 16px !important;
      border-radius: 18px !important;
      font-size: 15px !important;
      font-weight: 950 !important;
      letter-spacing: -.02em !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      background: linear-gradient(135deg, #00675f 0%, #008878 100%) !important;
      color: #ffffff !important;
      box-shadow: 0 12px 28px rgba(0,103,95,.22) !important;
      white-space: nowrap !important;
    }

    .bp-actions .bp-share-btn {
      order: 3 !important;
      display: inline-flex !important;
    }

    .bp-actions .bp-share-btn span,
    .bp-actions .bp-secondary-btn:not(.bp-share-btn) {
      display: none !important;
    }

    .bp-final-cta {
      margin-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important;
    }
  }

  @media (max-width: 380px) {
    .bp-actions {
      left: max(10px, env(safe-area-inset-left, 0px)) !important;
      right: max(10px, env(safe-area-inset-right, 0px)) !important;
      grid-template-columns: 48px minmax(0, 1fr) 48px !important;
      padding: 7px !important;
      gap: 7px !important;
      border-radius: 22px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn,
    .bp-actions .bp-primary-btn {
      height: 50px !important;
      min-height: 50px !important;
      border-radius: 17px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 48px !important;
      min-width: 48px !important;
    }

    .bp-actions .bp-primary-btn {
      font-size: 14px !important;
      padding: 0 12px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2G — SERVICES CARDS POLISH
     Goal: make highlighted services feel premium and easier to sell
     on tablet/mobile demos without adding visual noise.
     ========================================================= */

  .bp-packages {
    position: relative !important;
    margin-top: clamp(28px, 4vw, 42px) !important;
    padding-top: clamp(26px, 3.4vw, 38px) !important;
  }

  .bp-packages-head {
    align-items: flex-end !important;
    gap: 18px !important;
    margin-bottom: 18px !important;
  }

  .bp-packages-head > div:first-child {
    max-width: 720px !important;
    text-align: left !important;
  }

  .bp-packages-title {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 10px !important;
    color: #102a33 !important;
    font-size: clamp(28px, 3vw, 40px) !important;
    letter-spacing: -.07em !important;
  }

  .bp-packages-title::before {
    width: 10px !important;
    height: 10px !important;
    min-width: 10px !important;
    border-radius: 999px !important;
    background: #00675f !important;
    box-shadow: 0 0 0 6px rgba(0,103,95,.08) !important;
  }

  .bp-packages-copy {
    max-width: 560px !important;
    color: #64748b !important;
    font-size: 13.5px !important;
    line-height: 1.45 !important;
    font-weight: 700 !important;
  }

  .bp-package-actions {
    align-self: center !important;
  }

  .bp-carousel-arrow {
    width: 42px !important;
    height: 42px !important;
    border-radius: 999px !important;
    color: #0f172a !important;
    border: 1px solid rgba(108,88,61,.11) !important;
    background: rgba(255,255,255,.92) !important;
    box-shadow: 0 10px 24px rgba(54,41,25,.055) !important;
  }

  .bp-packages-rail,
  .event-packages-grid {
    gap: 16px !important;
    padding: 4px 4px 18px !important;
    scroll-padding-left: 4px !important;
    grid-auto-columns: clamp(228px, 23vw, 282px) !important;
  }

  .event-package-card {
    position: relative !important;
    min-height: 192px !important;
    border-radius: 24px !important;
    border: 1px solid rgba(108,88,61,.10) !important;
    background: rgba(255,255,255,.90) !important;
    box-shadow: 0 14px 34px rgba(54,41,25,.06) !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    transform: translateZ(0) !important;
  }

  .event-package-card::after {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    pointer-events: none !important;
    border-radius: inherit !important;
    background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(0,103,95,.025)) !important;
    opacity: .8 !important;
  }

  .event-package-card:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 20px 44px rgba(54,41,25,.10) !important;
    border-color: rgba(0,103,95,.20) !important;
  }

  .event-package-photo {
    height: 132px !important;
    border-bottom: 1px solid rgba(108,88,61,.08) !important;
    background: linear-gradient(135deg, #fff8ef, #eef9f6) !important;
  }

  .event-package-photo.has-photo img {
    object-fit: cover !important;
    transform: scale(1.002) !important;
  }

  .event-package-badges {
    left: 12px !important;
    bottom: 12px !important;
  }

  .event-package-badges span {
    padding: 6px 9px !important;
    background: rgba(255,255,255,.94) !important;
    color: #102a33 !important;
    border: 1px solid rgba(255,255,255,.72) !important;
    box-shadow: 0 8px 18px rgba(15,23,42,.10) !important;
  }

  .event-package-body,
  .event-package-body.no-photo-body {
    position: relative !important;
    z-index: 1 !important;
    flex: 1 1 auto !important;
    min-height: 150px !important;
    padding: 16px 16px 14px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    gap: 12px !important;
    background: transparent !important;
    border-top: 0 !important;
  }

  .event-package-body.no-photo-body {
    min-height: 178px !important;
    background:
      radial-gradient(circle at 16% 0%, rgba(0,103,95,.08), transparent 36%),
      linear-gradient(180deg, rgba(255,255,255,.96), rgba(255,248,239,.76)) !important;
  }

  .event-package-icon-bubble {
    width: 46px !important;
    height: 46px !important;
    min-width: 46px !important;
    border-radius: 16px !important;
    background: #eaf8f4 !important;
    color: #00675f !important;
    border: 1px solid rgba(0,103,95,.10) !important;
    box-shadow: none !important;
  }

  .event-package-icon-bubble svg {
    width: 23px !important;
    height: 23px !important;
  }

  .event-package-body h4,
  .event-package-body.no-photo-body h4 {
    margin: 0 !important;
    color: #101828 !important;
    font-size: 17px !important;
    line-height: 1.08 !important;
    letter-spacing: -.052em !important;
    font-weight: 950 !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
  }

  .event-package-body p {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    min-height: 0 !important;
    margin-top: 7px !important;
    color: #667085 !important;
    font-size: 12.5px !important;
    line-height: 1.42 !important;
    font-weight: 680 !important;
  }

  .event-package-meta {
    margin-top: auto !important;
    display: grid !important;
    grid-template-columns: auto 1fr auto !important;
    align-items: center !important;
    gap: 8px !important;
    color: #00675f !important;
    font-size: 12px !important;
    font-weight: 950 !important;
  }

  .event-package-meta span:not(.event-package-chevron) {
    min-height: 28px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    padding: 0 9px !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    white-space: nowrap !important;
  }

  .event-package-meta span:nth-child(2):not(.event-package-chevron) {
    justify-self: start !important;
    background: #fff8ef !important;
    border-color: rgba(108,88,61,.12) !important;
    color: #102a33 !important;
  }

  .event-package-chevron {
    width: 28px !important;
    height: 28px !important;
    border-radius: 999px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #ffffff !important;
    border: 1px solid rgba(108,88,61,.12) !important;
    color: #00675f !important;
    font-size: 20px !important;
    line-height: 1 !important;
  }

  .event-package-reserve {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 40px !important;
    margin-top: 2px !important;
    border-radius: 15px !important;
    background: #00675f !important;
    color: #ffffff !important;
    font-size: 13px !important;
    font-weight: 950 !important;
    letter-spacing: -.01em !important;
    box-shadow: 0 10px 22px rgba(0,103,95,.14) !important;
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-packages {
      margin-top: 30px !important;
      padding-top: 30px !important;
    }

    .bp-packages-head {
      display: flex !important;
      align-items: flex-end !important;
      justify-content: space-between !important;
      margin-bottom: 16px !important;
    }

    .bp-package-actions {
      display: inline-flex !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(225px, calc((100vw - 118px) / 3)) !important;
      gap: 14px !important;
      padding-bottom: 16px !important;
    }

    .event-package-card {
      min-height: 184px !important;
      border-radius: 22px !important;
    }

    .event-package-photo {
      height: 118px !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 146px !important;
      padding: 15px !important;
    }

    .event-package-body.no-photo-body {
      min-height: 168px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-packages {
      margin-top: 24px !important;
      padding-top: 24px !important;
    }

    .bp-packages-head {
      margin-bottom: 14px !important;
    }

    .bp-packages-title {
      font-size: 28px !important;
      letter-spacing: -.065em !important;
    }

    .bp-packages-copy {
      max-width: 310px !important;
      font-size: 13px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(222px, 78vw) !important;
      gap: 12px !important;
      padding: 2px 2px 16px !important;
      margin-left: 0 !important;
    }

    .event-package-card {
      min-height: 176px !important;
      border-radius: 22px !important;
      box-shadow: 0 12px 28px rgba(54,41,25,.055) !important;
    }

    .event-package-photo {
      height: 116px !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 140px !important;
      padding: 15px !important;
      gap: 10px !important;
    }

    .event-package-body.no-photo-body {
      min-height: 158px !important;
    }

    .event-package-icon-bubble {
      width: 42px !important;
      height: 42px !important;
      min-width: 42px !important;
      border-radius: 15px !important;
    }

    .event-package-body h4,
    .event-package-body.no-photo-body h4 {
      font-size: 16px !important;
    }

    .event-package-meta {
      grid-template-columns: 1fr auto !important;
      gap: 7px !important;
    }

    .event-package-meta span:nth-child(2):not(.event-package-chevron) {
      justify-self: end !important;
    }

    .event-package-chevron {
      display: none !important;
    }

    .event-package-reserve {
      height: 38px !important;
    }
  }


  /* =========================================================
     BUSINESS PAGE 2H — LOGO FULL-BLEED / MAIN MENU STYLE
     Removes the extra cream cube around logos and makes the
     hero image behave like the MainMenu business cards.
     ========================================================= */

  .bp-logo-card {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  .bp-logo-stage {
    background: #f4eadc !important;
    border: 1px solid rgba(108, 88, 61, .08) !important;
    box-shadow: 0 18px 44px rgba(54, 41, 25, .07) !important;
    overflow: hidden !important;
  }

  .bp-logo-stage .smart-logo-frame,
  .bp-logo-stage .smart-logo-inner {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    border: 0 !important;
    box-shadow: none !important;
    overflow: hidden !important;
  }

  .bp-logo-stage .smart-logo-frame::before,
  .bp-logo-stage .smart-logo-frame::after,
  .bp-logo-stage .smart-logo-inner::before,
  .bp-logo-stage .smart-logo-inner::after,
  .bp-logo-stage .smart-logo-wash,
  .bp-logo-stage .smart-logo-blur {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
    content: none !important;
  }

  .bp-logo-stage .smart-logo-img,
  .bp-logo-stage img {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: cover !important;
    object-position: center center !important;
    display: block !important;
    margin: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    transform: scale(1.015) !important;
  }

  .bp-logo-stage::after {
    content: "" !important;
    display: block !important;
    position: absolute !important;
    inset: 0 !important;
    pointer-events: none !important;
    background:
      linear-gradient(180deg, rgba(255,255,255,.02) 0%, rgba(255,255,255,0) 54%, rgba(20,17,13,.05) 100%) !important;
    opacity: .72 !important;
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-logo-stage {
      aspect-ratio: 16 / 6.9 !important;
      min-height: 190px !important;
      max-height: 300px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-logo-stage {
      aspect-ratio: 16 / 8.2 !important;
      min-height: 164px !important;
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 24px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2I — RAW HERO LOGO FIX
     Diagnostic fix: bypasses SmartLogoFrame in the hero logo so
     no generated cream/wash/background layer can appear behind it.
     The image itself becomes the visual surface, matching MainMenu's
     direct image treatment much more closely.
     ========================================================= */

  .bp-logo-card-raw {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .bp-logo-stage-raw {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    border: 0 !important;
    box-shadow: none !important;
    padding: 0 !important;
    overflow: visible !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .bp-logo-stage-raw::before,
  .bp-logo-stage-raw::after {
    display: none !important;
    content: none !important;
  }

  .bp-logo-raw-img {
    display: block !important;
    width: 100% !important;
    height: auto !important;
    max-width: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    margin: 0 !important;
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-logo-stage-raw {
      aspect-ratio: auto !important;
      min-height: 0 !important;
      max-height: none !important;
    }

    .bp-logo-raw-img {
      max-height: 260px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-logo-stage-raw {
      aspect-ratio: auto !important;
      min-height: 0 !important;
      max-height: none !important;
    }

    .bp-logo-raw-img {
      max-height: 210px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2J — LOGO SHAPE + RESPONSIVE STACK FIX
     Final overrides after raw logo fix:
     - Gives the hero logo a premium rounded image surface.
     - Prevents tablet/mobile logo and info cards from overlapping.
     - Forces section headers back to left alignment.
     ========================================================= */

  .bp-logo-card-raw,
  .bp-logo-stage-raw {
    position: relative !important;
    z-index: 1 !important;
  }

  .bp-logo-card-raw {
    max-width: 100% !important;
    overflow: visible !important;
  }

  .bp-logo-stage-raw {
    width: 100% !important;
    border-radius: 34px !important;
    overflow: hidden !important;
    background: transparent !important;
    box-shadow: 0 18px 44px rgba(54,41,25,.075) !important;
  }

  .bp-logo-raw-img {
    width: 100% !important;
    height: auto !important;
    display: block !important;
    border-radius: 34px !important;
    overflow: hidden !important;
    object-fit: contain !important;
    object-position: center center !important;
    box-shadow: none !important;
  }

  .bp-info-panel {
    position: relative !important;
    z-index: 2 !important;
  }

  @media (min-width: 1181px) {
    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(420px, 560px) minmax(0, 1fr) !important;
      grid-template-areas:
        "logo title"
        "info info" !important;
      column-gap: clamp(44px, 5vw, 76px) !important;
      row-gap: 28px !important;
      align-items: center !important;
    }

    .bp-hero-top {
      grid-area: title !important;
      grid-column: auto !important;
      grid-row: auto !important;
      align-self: center !important;
      position: relative !important;
      z-index: 3 !important;
      margin: 0 !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-logo-card-raw {
      grid-area: logo !important;
      grid-column: auto !important;
      grid-row: auto !important;
    }

    .bp-info-panel {
      grid-area: info !important;
      grid-column: auto !important;
      grid-row: auto !important;
      margin-top: 0 !important;
    }
  }

  @media (max-width: 1180px) {
    .bp-hero {
      display: block !important;
      position: relative !important;
      overflow: visible !important;
    }

    .bp-hero-top {
      display: block !important;
      grid-column: auto !important;
      grid-row: auto !important;
      margin: 0 0 18px !important;
      position: relative !important;
      z-index: 3 !important;
    }

    .bp-hero-grid {
      display: flex !important;
      flex-direction: column !important;
      gap: 18px !important;
      position: relative !important;
      z-index: 1 !important;
    }

    .bp-logo-card-raw,
    .bp-info-panel {
      width: 100% !important;
      max-width: none !important;
      grid-column: auto !important;
      grid-row: auto !important;
      margin: 0 !important;
    }

    .bp-logo-stage-raw {
      border-radius: 30px !important;
      max-height: none !important;
      min-height: 0 !important;
    }

    .bp-logo-raw-img {
      border-radius: 30px !important;
      max-height: 300px !important;
      object-fit: contain !important;
    }

    .bp-info-panel {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 118px !important;
      border: 1px solid rgba(226,232,240,.82) !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 12px 28px rgba(54,41,25,.055) !important;
      padding: 18px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-container {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .bp-hero-top {
      margin-bottom: 16px !important;
    }

    .bp-logo-stage-raw,
    .bp-logo-raw-img {
      border-radius: 26px !important;
    }

    .bp-logo-raw-img {
      max-height: 230px !important;
    }

    .bp-info-panel {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }

    .bp-info-row {
      min-height: 108px !important;
      border-radius: 22px !important;
      padding: 16px !important;
    }
  }

  .bp-section,
  .bp-packages,
  .bp-about-section,
  .bp-split-section,
  .bp-split-column,
  .bp-team-column,
  .bp-about-column,
  .bp-reviews-head,
  .bp-section-head,
  .bp-packages-head,
  .bp-packages-head > div:first-child {
    text-align: left !important;
  }

  .bp-section-head,
  .bp-packages-head {
    width: 100% !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
  }

  .bp-section-title,
  .bp-packages-title,
  .bp-packages-copy,
  .bp-kicker {
    text-align: left !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .bp-section-title::before,
  .bp-packages-title::before {
    margin-left: 0 !important;
  }

  .bp-team-column .bp-section-head,
  .bp-reviews-head .bp-section-head {
    justify-content: space-between !important;
  }

  @media (max-width: 1180px) {
    .bp-packages-head,
    .bp-section-head,
    .bp-reviews-head {
      display: flex !important;
      flex-direction: row !important;
      align-items: flex-start !important;
      justify-content: space-between !important;
      gap: 14px !important;
    }

    .bp-packages-head > div:first-child,
    .bp-section-head > div:first-child {
      margin: 0 !important;
      max-width: none !important;
    }
  }

  @media (max-width: 767px) {
    .bp-packages-head,
    .bp-reviews-head,
    .bp-section-head {
      align-items: flex-start !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2L — MINIMAL INFO STRIP
     Scope: only business info area. Keeps hero/logo/actions untouched.
     Direction: one elegant, quiet strip on web; compact cards on tablet/mobile.
     ========================================================= */

  .bp-info-panel {
    width: 100% !important;
    position: relative !important;
    z-index: 2 !important;
    display: grid !important;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 0 !important;
    padding: 0 !important;
    margin-top: 0 !important;
    border-radius: 24px !important;
    overflow: hidden !important;
    background: rgba(255,255,255,.66) !important;
    border: 1px solid rgba(108,88,61,.10) !important;
    box-shadow: 0 12px 34px rgba(54,41,25,.055) !important;
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
  }

  .bp-info-row {
    position: relative !important;
    min-width: 0 !important;
    min-height: 86px !important;
    display: grid !important;
    grid-template-columns: 34px minmax(0, 1fr) !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 17px 18px !important;
    border: 0 !important;
    border-right: 1px solid rgba(108,88,61,.085) !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .bp-info-row:last-child,
  .bp-info-row:nth-child(4n) {
    border-right: 0 !important;
  }

  .bp-info-row::before {
    display: none !important;
    content: none !important;
  }

  .bp-info-icon {
    width: 34px !important;
    height: 34px !important;
    border-radius: 999px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: #00675f !important;
    background: rgba(234,248,244,.95) !important;
    border: 1px solid rgba(0,103,95,.06) !important;
    box-shadow: none !important;
    flex: 0 0 auto !important;
  }

  .bp-info-icon svg {
    width: 16px !important;
    height: 16px !important;
    stroke-width: 2.2 !important;
  }

  .bp-info-content {
    min-width: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    justify-content: center !important;
  }

  .bp-info-label {
    display: none !important;
  }

  .bp-info-value,
  .bp-info-value-primary,
  .bp-contact-number,
  .bp-hours-summary strong,
  .bp-social-row .bp-info-value a {
    max-width: 100% !important;
    color: #102a34 !important;
    font-size: 13.5px !important;
    line-height: 1.18 !important;
    font-weight: 900 !important;
    letter-spacing: -.018em !important;
    text-align: left !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
  }

  .bp-contact-actions,
  .bp-social-actions {
    margin-top: 7px !important;
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 7px !important;
  }

  .bp-info-main-action,
  .bp-contact-action,
  .bp-maps-link,
  .bp-maps-link-premium,
  .bp-hours-toggle {
    min-height: 27px !important;
    height: 27px !important;
    border-radius: 999px !important;
    padding: 0 10px !important;
    font-size: 11px !important;
    line-height: 1 !important;
    font-weight: 900 !important;
    letter-spacing: -.01em !important;
    margin-top: 7px !important;
  }

  .bp-info-main-action,
  .bp-contact-action,
  .bp-maps-link,
  .bp-maps-link-premium {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 6px !important;
    background: rgba(240,253,250,.82) !important;
    border: 1px solid rgba(204,251,241,.88) !important;
    color: #00675f !important;
    text-decoration: none !important;
    box-shadow: none !important;
  }

  .bp-info-main-action svg,
  .bp-contact-action svg,
  .bp-maps-link svg,
  .bp-maps-link-premium svg,
  .bp-hours-toggle svg {
    width: 13px !important;
    height: 13px !important;
  }

  .bp-hours-summary {
    width: 100% !important;
    display: flex !important;
    align-items: baseline !important;
    justify-content: flex-start !important;
    gap: 7px !important;
  }

  .bp-hours-summary-label {
    color: #718096 !important;
    font-size: 11.5px !important;
    font-weight: 850 !important;
  }

  .bp-hours-toggle {
    display: inline-flex !important;
    align-items: center !important;
    gap: 5px !important;
    padding: 0 !important;
    height: auto !important;
    min-height: 0 !important;
    color: #00675f !important;
    background: transparent !important;
    border: 0 !important;
  }

  @media (min-width: 1181px) {
    .bp-hero {
      grid-template-columns: minmax(0, 1fr) minmax(420px, 560px) !important;
      grid-template-areas:
        "title logo"
        "info info" !important;
      column-gap: clamp(44px, 5vw, 76px) !important;
      row-gap: 28px !important;
      align-items: center !important;
    }

    .bp-hero-top { grid-area: title !important; }
    .bp-logo-card-raw { grid-area: logo !important; }
    .bp-info-panel { grid-area: info !important; }
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-info-panel {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 10px !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      overflow: visible !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 78px !important;
      padding: 15px 16px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      border-radius: 20px !important;
      background: rgba(255,255,255,.68) !important;
      box-shadow: 0 10px 26px rgba(54,41,25,.045) !important;
    }
  }

  @media (max-width: 767px) {
    .bp-info-panel {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      overflow: visible !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 72px !important;
      grid-template-columns: 34px minmax(0, 1fr) !important;
      gap: 11px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      border-radius: 18px !important;
      padding: 13px 14px !important;
      background: rgba(255,255,255,.74) !important;
      box-shadow: 0 8px 22px rgba(54,41,25,.04) !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number,
    .bp-hours-summary strong,
    .bp-social-row .bp-info-value a {
      font-size: 13px !important;
      line-height: 1.22 !important;
    }

    .bp-info-main-action,
    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      min-height: 26px !important;
      height: 26px !important;
      padding: 0 9px !important;
      font-size: 10.5px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2M — SERVICE IMAGES FULL ESSENCE FIX
     Keeps full service photos visible without ugly white side gaps.
     ========================================================= */

  .bp-service-photo-frame.event-package-photo.has-photo {
    position: relative !important;
    height: clamp(150px, 12.5vw, 178px) !important;
    overflow: hidden !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-bottom: 1px solid rgba(108,88,61,.09) !important;
    background:
      radial-gradient(circle at 18% 10%, rgba(0,103,95,.11), transparent 34%),
      linear-gradient(135deg, #fff8ef 0%, #f3fbf9 52%, #fff4e8 100%) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo::before {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    z-index: 1 !important;
    pointer-events: none !important;
    background:
      linear-gradient(180deg, rgba(255,255,255,.08) 0%, rgba(255,255,255,0) 48%, rgba(15,23,42,.045) 100%),
      radial-gradient(circle at 50% 0%, rgba(255,255,255,.26), transparent 52%) !important;
  }

  .bp-service-photo-frame .bp-service-photo-backdrop {
    position: absolute !important;
    inset: -14px !important;
    width: calc(100% + 28px) !important;
    height: calc(100% + 28px) !important;
    object-fit: cover !important;
    object-position: center center !important;
    transform: scale(1.08) !important;
    filter: blur(18px) saturate(1.12) brightness(1.04) !important;
    opacity: .34 !important;
    z-index: 0 !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    position: relative !important;
    z-index: 2 !important;
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    transform: none !important;
    filter: none !important;
    display: block !important;
    margin: 0 auto !important;
    background: transparent !important;
  }

  .bp-service-photo-frame .event-package-badges {
    z-index: 4 !important;
  }

  .bp-service-photo-frame + .event-package-body {
    min-height: 142px !important;
  }

  @media (min-width: 1180px) {
    .bp-service-photo-frame.event-package-photo.has-photo {
      height: clamp(162px, 12vw, 188px) !important;
    }
  }

  @media (max-width: 920px) {
    .bp-service-photo-frame.event-package-photo.has-photo {
      height: 148px !important;
      border-radius: 0 !important;
    }

    .bp-service-photo-frame .bp-service-photo-backdrop {
      filter: blur(16px) saturate(1.10) brightness(1.04) !important;
      opacity: .30 !important;
    }

    .bp-service-photo-frame + .event-package-body {
      min-height: 132px !important;
    }
  }

  @media (max-width: 520px) {
    .bp-service-photo-frame.event-package-photo.has-photo {
      height: 142px !important;
    }
  }



  /* =========================================================
     BUSINESSPAGE 2N — SERVICE CARD IMAGE RATIO PREMIUM
     Gives real service photos more presence while keeping cards balanced.
     Builds on 2M full-essence image treatment.
     ========================================================= */

  .bp-packages-rail,
  .event-packages-grid {
    grid-auto-columns: minmax(272px, 308px) !important;
    gap: 18px !important;
    align-items: stretch !important;
  }

  .event-package-card {
    display: flex !important;
    flex-direction: column !important;
    min-height: 388px !important;
    border-radius: 24px !important;
  }

  .event-package-card:has(.bp-service-photo-frame) {
    background: rgba(255,255,255,.92) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo {
    height: clamp(188px, 15.4vw, 222px) !important;
    flex: 0 0 auto !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    padding: 0 !important;
  }

  .bp-service-photo-frame + .event-package-body {
    flex: 1 1 auto !important;
    min-height: 158px !important;
    padding: 18px 18px 16px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
  }

  .bp-service-photo-frame + .event-package-body h4 {
    font-size: 18px !important;
    line-height: 1.05 !important;
    letter-spacing: -.052em !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
  }

  .bp-service-photo-frame + .event-package-body p {
    margin-top: 8px !important;
    min-height: 0 !important;
    color: #5f6f83 !important;
    font-size: 12.5px !important;
    line-height: 1.34 !important;
    font-weight: 690 !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-meta {
    margin-top: 12px !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-reserve {
    display: block !important;
    margin-top: 12px !important;
    height: 42px !important;
    border-radius: 15px !important;
    background: #00675f !important;
    color: #ffffff !important;
    font-weight: 950 !important;
  }

  @media (min-width: 1280px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(286px, 322px) !important;
    }

    .bp-service-photo-frame.event-package-photo.has-photo {
      height: clamp(198px, 14.6vw, 232px) !important;
    }
  }

  @media (max-width: 920px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(248px, 278px) !important;
      gap: 14px !important;
    }

    .event-package-card {
      min-height: 348px !important;
    }

    .bp-service-photo-frame.event-package-photo.has-photo {
      height: 176px !important;
    }

    .bp-service-photo-frame + .event-package-body {
      min-height: 150px !important;
      padding: 16px 16px 14px !important;
    }
  }

  @media (max-width: 520px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(238px, 82vw) !important;
    }

    .event-package-card {
      min-height: 330px !important;
    }

    .bp-service-photo-frame.event-package-photo.has-photo {
      height: 162px !important;
    }

    .bp-service-photo-frame + .event-package-body h4 {
      font-size: 17px !important;
    }
  }



  /* =========================================================
     BUSINESSPAGE 2O — SMART SERVICE IMAGE FOCAL POINT
     Adds per-service image focus and fit classes without changing data shape.
     Supported optional fields: imageFocus/photoFocus/focalPoint/imagePosition,
     plus imageFit/photoFit when a business wants intentional cover cropping.
     ========================================================= */

  .bp-service-photo-frame.bp-service-focus-left .bp-service-photo-main,
  .bp-service-photo-frame.bp-service-focus-left .bp-service-photo-backdrop {
    object-position: left center !important;
  }

  .bp-service-photo-frame.bp-service-focus-right .bp-service-photo-main,
  .bp-service-photo-frame.bp-service-focus-right .bp-service-photo-backdrop {
    object-position: right center !important;
  }

  .bp-service-photo-frame.bp-service-focus-top .bp-service-photo-main,
  .bp-service-photo-frame.bp-service-focus-top .bp-service-photo-backdrop {
    object-position: center top !important;
  }

  .bp-service-photo-frame.bp-service-focus-bottom .bp-service-photo-main,
  .bp-service-photo-frame.bp-service-focus-bottom .bp-service-photo-backdrop {
    object-position: center bottom !important;
  }

  .bp-service-photo-frame.bp-service-focus-center .bp-service-photo-main,
  .bp-service-photo-frame.bp-service-focus-center .bp-service-photo-backdrop {
    object-position: center center !important;
  }

  .bp-service-photo-frame.bp-service-fit-contain .bp-service-photo-main {
    object-fit: contain !important;
  }

  .bp-service-photo-frame.bp-service-fit-cover .bp-service-photo-main {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
  }

  .bp-service-photo-frame.bp-service-fit-cover .bp-service-photo-backdrop {
    opacity: .22 !important;
  }


  /* =========================================================
     BUSINESSPAGE 2P — SERVICE IMAGE OVERLAY GRADIENT CLEANUP
     Makes service photos feel intentional without hiding the actual image.
     Keeps 2M/2N/2O full-essence behavior intact.
     ========================================================= */

  .bp-service-photo-frame.event-package-photo.has-photo {
    isolation: isolate !important;
    border-bottom: 0 !important;
    background:
      radial-gradient(circle at 18% 10%, rgba(0,103,95,.08), transparent 34%),
      linear-gradient(135deg, #fff8ef 0%, #f4fbf9 50%, #fff4e8 100%) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo::before {
    z-index: 2 !important;
    background:
      linear-gradient(180deg, rgba(255,255,255,.10) 0%, rgba(255,255,255,0) 38%, rgba(16,42,51,.035) 100%),
      radial-gradient(circle at 50% 0%, rgba(255,255,255,.22), transparent 54%) !important;
    opacity: .72 !important;
    mix-blend-mode: normal !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo::after {
    content: "" !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    height: 42px !important;
    z-index: 3 !important;
    pointer-events: none !important;
    background: linear-gradient(180deg, rgba(255,255,255,0), rgba(255,255,255,.56)) !important;
    opacity: .42 !important;
  }

  .bp-service-photo-frame .bp-service-photo-backdrop {
    opacity: .24 !important;
    filter: blur(20px) saturate(1.08) brightness(1.06) !important;
    transform: scale(1.12) !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    z-index: 4 !important;
    padding: 8px !important;
    object-fit: contain !important;
  }

  .bp-service-photo-frame.bp-service-fit-cover .bp-service-photo-main {
    padding: 0 !important;
  }

  .bp-service-photo-frame.bp-service-fit-cover::after {
    opacity: .32 !important;
  }

  .bp-service-photo-frame .event-package-badges {
    z-index: 6 !important;
    left: 12px !important;
    bottom: 12px !important;
  }

  .bp-service-photo-frame .event-package-badges span {
    background: rgba(255,255,255,.88) !important;
    border: 1px solid rgba(255,255,255,.68) !important;
    color: #102a33 !important;
    box-shadow: 0 10px 22px rgba(15,23,42,.10) !important;
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
  }

  .event-package-card:has(.bp-service-photo-frame) {
    overflow: hidden !important;
  }

  .event-package-card:has(.bp-service-photo-frame)::after {
    opacity: .52 !important;
    background: linear-gradient(180deg, rgba(255,255,255,.04), rgba(0,103,95,.018)) !important;
  }

  .bp-service-photo-frame + .event-package-body {
    border-top: 1px solid rgba(108,88,61,.06) !important;
    background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.91)) !important;
  }

  .bp-service-photo-frame + .event-package-body p {
    color: #64748b !important;
  }

  @media (max-width: 920px) {
    .bp-service-photo-frame.event-package-photo.has-photo::after {
      height: 34px !important;
      opacity: .34 !important;
    }

    .bp-service-photo-frame .bp-service-photo-main {
      padding: 6px !important;
    }

    .bp-service-photo-frame .bp-service-photo-backdrop {
      opacity: .20 !important;
      filter: blur(17px) saturate(1.06) brightness(1.06) !important;
    }
  }

  /* =========================================================
     BUSINESSPAGE 2Q — SERVICE CARDS FINAL POLISH
     Final pass for highlighted service cards: cleaner hierarchy,
     stable CTA alignment, better spacing, and responsive polish.
     ========================================================= */

  .event-package-card:has(.bp-service-photo-frame) {
    position: relative !important;
    min-height: 392px !important;
    border-radius: 26px !important;
    background: rgba(255,255,255,.94) !important;
    border: 1px solid rgba(108,88,61,.105) !important;
    box-shadow: 0 14px 38px rgba(54,41,25,.065) !important;
    overflow: hidden !important;
    isolation: isolate !important;
  }

  .event-package-card:has(.bp-service-photo-frame)::before {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    pointer-events: none !important;
    z-index: 0 !important;
    background:
      linear-gradient(180deg, rgba(255,255,255,.10) 0%, rgba(255,255,255,0) 38%),
      radial-gradient(circle at 18% 0%, rgba(0,103,95,.035), transparent 34%) !important;
  }

  .event-package-card:has(.bp-service-photo-frame) > * {
    position: relative !important;
    z-index: 1 !important;
  }

  .event-package-card:has(.bp-service-photo-frame):hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 20px 46px rgba(54,41,25,.10) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo {
    border-bottom: 1px solid rgba(108,88,61,.075) !important;
  }

  .bp-service-photo-frame + .event-package-body {
    gap: 0 !important;
    padding: 17px 17px 15px !important;
    background: linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(255,252,247,.95) 100%) !important;
  }

  .bp-service-photo-frame + .event-package-body h4 {
    margin: 0 !important;
    color: #111827 !important;
    font-size: 17.5px !important;
    line-height: 1.04 !important;
    letter-spacing: -.052em !important;
    font-weight: 950 !important;
    max-width: 100% !important;
    min-height: 37px !important;
  }

  .bp-service-photo-frame + .event-package-body p {
    margin: 7px 0 0 !important;
    color: #64748b !important;
    font-size: 12.25px !important;
    line-height: 1.34 !important;
    font-weight: 680 !important;
    min-height: 32px !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-meta {
    margin-top: 12px !important;
    padding-top: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
    color: #00675f !important;
    font-size: 12px !important;
    font-weight: 950 !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-meta span {
    min-width: 0 !important;
    color: #00675f !important;
    font-weight: 950 !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-meta span:first-child {
    display: inline-flex !important;
    align-items: center !important;
    max-width: 52% !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    color: #64748b !important;
    font-size: 11.5px !important;
    font-weight: 850 !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-meta span:last-child {
    flex: 0 0 auto !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    min-height: 28px !important;
    padding: 0 10px !important;
    border-radius: 999px !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    box-shadow: none !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-reserve {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 42px !important;
    margin-top: 13px !important;
    border-radius: 15px !important;
    background: linear-gradient(135deg, #00675f 0%, #008878 100%) !important;
    color: #ffffff !important;
    font-size: 13px !important;
    letter-spacing: -.015em !important;
    font-weight: 950 !important;
    box-shadow: 0 12px 24px rgba(0,103,95,.16) !important;
  }

  .bp-service-photo-frame + .event-package-body .event-package-reserve:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 16px 28px rgba(0,103,95,.20) !important;
  }

  .event-package-card:not(:has(.bp-service-photo-frame)) {
    min-height: 176px !important;
  }

  .event-package-card:not(:has(.bp-service-photo-frame)) .event-package-body.no-photo-body {
    min-height: 176px !important;
    padding: 20px 18px 16px !important;
  }

  .event-package-card:not(:has(.bp-service-photo-frame)) .event-package-meta {
    margin-top: 14px !important;
  }

  @media (min-width: 1280px) {
    .event-package-card:has(.bp-service-photo-frame) {
      min-height: 404px !important;
    }

    .bp-service-photo-frame + .event-package-body h4 {
      font-size: 18px !important;
    }
  }

  @media (max-width: 920px) {
    .event-package-card:has(.bp-service-photo-frame) {
      min-height: 356px !important;
      border-radius: 24px !important;
    }

    .bp-service-photo-frame + .event-package-body {
      padding: 15px 15px 14px !important;
    }

    .bp-service-photo-frame + .event-package-body h4 {
      min-height: 35px !important;
      font-size: 17px !important;
    }

    .bp-service-photo-frame + .event-package-body p {
      min-height: 30px !important;
      font-size: 12px !important;
    }

    .bp-service-photo-frame + .event-package-body .event-package-reserve {
      height: 40px !important;
      margin-top: 12px !important;
    }
  }

  @media (max-width: 520px) {
    .event-package-card:has(.bp-service-photo-frame) {
      min-height: 342px !important;
    }

    .bp-service-photo-frame + .event-package-body {
      padding: 14px 14px 13px !important;
    }

    .bp-service-photo-frame + .event-package-body .event-package-meta span:first-child {
      max-width: 48% !important;
    }
  }

  /* =========================================================
     BUSINESSPAGE 2R — EVENT IMAGE FRAME CLEANUP
     Removes the gray/matte frame around event service photos.
     Keeps full characters visible with an immersive photo-derived backdrop.
     ========================================================= */

  .event-package-card:has(.bp-service-photo-frame) {
    background: rgba(255,255,255,.94) !important;
    border: 1px solid rgba(108,88,61,.09) !important;
    box-shadow: 0 14px 34px rgba(54,41,25,.065) !important;
    overflow: hidden !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo {
    background: #fff8ef !important;
    border-bottom: 0 !important;
    box-shadow: none !important;
    isolation: isolate !important;
    overflow: hidden !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo::before {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    z-index: 1 !important;
    pointer-events: none !important;
    background:
      linear-gradient(180deg, rgba(255,255,255,.04) 0%, rgba(255,255,255,0) 48%, rgba(255,248,239,.12) 100%),
      radial-gradient(circle at 50% 0%, rgba(255,255,255,.18), transparent 48%) !important;
    opacity: .72 !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo::after {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    z-index: 3 !important;
    pointer-events: none !important;
    border-radius: inherit !important;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,.18),
      inset 0 -1px 0 rgba(108,88,61,.035) !important;
    background: transparent !important;
  }

  .bp-service-photo-frame .bp-service-photo-backdrop {
    inset: -26px !important;
    width: calc(100% + 52px) !important;
    height: calc(100% + 52px) !important;
    object-fit: cover !important;
    object-position: inherit !important;
    transform: scale(1.14) !important;
    filter: blur(22px) saturate(1.25) brightness(1.03) contrast(1.04) !important;
    opacity: .72 !important;
    z-index: 0 !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    z-index: 2 !important;
    object-fit: contain !important;
    object-position: inherit !important;
    width: 100% !important;
    height: 100% !important;
    padding: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    border: 0 !important;
  }

  .bp-service-photo-frame .event-package-badges {
    z-index: 5 !important;
    left: 12px !important;
    bottom: 12px !important;
  }

  .bp-service-photo-frame .event-package-badges span {
    background: rgba(255,255,255,.82) !important;
    border: 1px solid rgba(255,255,255,.52) !important;
    color: #10323d !important;
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
    box-shadow: 0 8px 20px rgba(15,23,42,.10) !important;
  }

  .bp-service-photo-frame + .event-package-body {
    border-top: 1px solid rgba(108,88,61,.055) !important;
    background: rgba(255,255,255,.96) !important;
  }

  @media (max-width: 920px) {
    .bp-service-photo-frame .bp-service-photo-backdrop {
      inset: -22px !important;
      width: calc(100% + 44px) !important;
      height: calc(100% + 44px) !important;
      filter: blur(20px) saturate(1.22) brightness(1.04) contrast(1.03) !important;
      opacity: .68 !important;
    }

    .bp-service-photo-frame.event-package-photo.has-photo::before {
      opacity: .62 !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2S — SERVICE PHOTO EDGE LINE REMOVAL
     Removes the remaining horizontal color/gray bands above and below
     event service images while keeping the full-image contain system.
     ========================================================= */

  .event-package-card:has(.bp-service-photo-frame) {
    background: rgba(255,255,255,.96) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo {
    aspect-ratio: 16 / 9 !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    background: transparent !important;
    border: 0 !important;
    border-bottom: 0 !important;
    box-shadow: none !important;
    line-height: 0 !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo::before,
  .bp-service-photo-frame.event-package-photo.has-photo::after {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
    content: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  .bp-service-photo-frame .bp-service-photo-backdrop {
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transform: scale(1.08) !important;
    filter: blur(18px) saturate(1.18) brightness(1.03) contrast(1.02) !important;
    opacity: .34 !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
    object-position: inherit !important;
    display: block !important;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  .bp-service-photo-frame + .event-package-body {
    border-top: 0 !important;
    margin-top: 0 !important;
  }

  @media (max-width: 920px) {
    .bp-service-photo-frame.event-package-photo.has-photo {
      aspect-ratio: 16 / 9 !important;
      height: auto !important;
    }

    .bp-service-photo-frame .bp-service-photo-backdrop {
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      transform: scale(1.06) !important;
      filter: blur(16px) saturate(1.15) brightness(1.03) contrast(1.02) !important;
      opacity: .30 !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2T — SERVICE CARDS ADAPTIVE CLEANUP
     One-pass fix for: side bands, generic descriptions, mobile density,
     and mixed image/icon service cards.
     ========================================================= */

  .event-package-card.has-service-photo,
  .event-package-card.has-service-icon {
    display: flex !important;
    flex-direction: column !important;
    min-height: 0 !important;
    background: rgba(255,255,255,.96) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo,
  .bp-service-icon-frame.event-package-photo.no-photo {
    height: auto !important;
    aspect-ratio: 16 / 8.85 !important;
    min-height: 0 !important;
    max-height: none !important;
    border: 0 !important;
    border-bottom: 0 !important;
    box-shadow: none !important;
    overflow: hidden !important;
  }

  .bp-service-photo-frame .bp-service-photo-backdrop {
    inset: -18px !important;
    width: calc(100% + 36px) !important;
    height: calc(100% + 36px) !important;
    object-fit: cover !important;
    transform: scale(1.18) !important;
    filter: blur(18px) saturate(1.24) brightness(1.04) contrast(1.04) !important;
    opacity: .58 !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    width: 108% !important;
    height: 108% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: contain !important;
    object-position: inherit !important;
    transform: scale(1.045) !important;
    transform-origin: center center !important;
  }

  .bp-service-fit-cover .bp-service-photo-main {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    transform: none !important;
  }

  .bp-service-icon-frame.event-package-photo.no-photo {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    padding: 18px !important;
    background:
      radial-gradient(circle at 12% 0%, rgba(0,103,95,.10), transparent 38%),
      linear-gradient(180deg, rgba(255,255,255,.96), rgba(255,248,239,.78)) !important;
  }

  .bp-service-icon-frame .event-package-icon-bubble {
    width: 48px !important;
    height: 48px !important;
    margin: 0 !important;
    flex: 0 0 auto !important;
  }

  .event-package-body,
  .event-package-body.no-photo-body {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    min-height: 190px !important;
    padding: 16px 16px 15px !important;
    background: rgba(255,255,255,.98) !important;
    border-top: 0 !important;
  }

  .event-package-body.no-photo-body {
    background: rgba(255,255,255,.98) !important;
  }

  .event-package-body h4 {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    min-height: 38px !important;
  }

  .event-package-body p {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    min-height: 0 !important;
    margin-top: 8px !important;
  }

  .event-package-body p:empty {
    display: none !important;
  }

  .event-package-meta {
    margin-top: 13px !important;
    align-items: center !important;
  }

  .event-package-reserve {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-top: 14px !important;
  }

  @media (min-width: 921px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(244px, 274px) !important;
      gap: 16px !important;
    }
  }

  @media (max-width: 920px) {
    .bp-packages {
      padding-left: 12px !important;
      padding-right: 0 !important;
    }

    .bp-packages-head {
      padding-right: 12px !important;
      margin-bottom: 12px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(198px, 218px) !important;
      gap: 10px !important;
      padding: 2px 12px 12px 0 !important;
    }

    .bp-service-photo-frame.event-package-photo.has-photo,
    .bp-service-icon-frame.event-package-photo.no-photo {
      aspect-ratio: 16 / 8.6 !important;
    }

    .bp-service-photo-frame .bp-service-photo-backdrop {
      inset: -14px !important;
      width: calc(100% + 28px) !important;
      height: calc(100% + 28px) !important;
      transform: scale(1.16) !important;
      filter: blur(15px) saturate(1.20) brightness(1.04) contrast(1.03) !important;
      opacity: .54 !important;
    }

    .bp-service-photo-frame .bp-service-photo-main {
      width: 112% !important;
      height: 112% !important;
      transform: scale(1.055) !important;
    }

    .bp-service-icon-frame.event-package-photo.no-photo {
      padding: 14px !important;
    }

    .bp-service-icon-frame .event-package-icon-bubble {
      width: 42px !important;
      height: 42px !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 168px !important;
      padding: 13px 13px 12px !important;
    }

    .event-package-body h4 {
      font-size: 15.5px !important;
      line-height: 1.05 !important;
      min-height: 33px !important;
      letter-spacing: -.045em !important;
    }

    .event-package-body p {
      font-size: 11.5px !important;
      line-height: 1.32 !important;
      margin-top: 7px !important;
      -webkit-line-clamp: 2 !important;
    }

    .event-package-meta {
      margin-top: 10px !important;
      gap: 6px !important;
    }

    .event-package-meta span {
      font-size: 11px !important;
      padding-left: 9px !important;
      padding-right: 9px !important;
    }

    .event-package-reserve {
      height: 39px !important;
      margin-top: 11px !important;
      border-radius: 14px !important;
      font-size: 12.5px !important;
    }
  }

  @media (max-width: 430px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(190px, 206px) !important;
      gap: 10px !important;
    }

    .event-package-body,
    .event-package-body.no-photo-body {
      min-height: 158px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2U — SERVICE CARDS RESPONSIVE QA
     Final responsive pass for clean photo edges, compact mobile,
     and consistent mixed image/icon service cards.
     ========================================================= */

  .event-package-card.has-service-photo,
  .event-package-card.has-service-icon {
    height: auto !important;
    min-height: 0 !important;
    border-radius: 22px !important;
    overflow: hidden !important;
    background: rgba(255,255,255,.98) !important;
  }

  .bp-service-photo-frame.event-package-photo.has-photo,
  .bp-service-icon-frame.event-package-photo.no-photo {
    aspect-ratio: 16 / 8.65 !important;
    height: auto !important;
    min-height: 0 !important;
    background: #f8fafc !important;
  }

  /* Photo cards: remove the remaining side color bands by making the main image fill the frame softly. */
  .bp-service-photo-frame .bp-service-photo-backdrop {
    opacity: 0 !important;
    display: none !important;
  }

  .bp-service-photo-frame .bp-service-photo-main {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: cover !important;
    object-position: inherit !important;
    transform: scale(1.012) !important;
    transform-origin: center center !important;
  }

  .bp-service-photo-frame.bp-service-fit-contain .bp-service-photo-main {
    object-fit: cover !important;
  }

  .bp-service-photo-frame::before,
  .bp-service-photo-frame::after {
    display: none !important;
    opacity: 0 !important;
    content: none !important;
  }

  .bp-service-photo-frame .event-package-badges {
    display: none !important;
  }

  /* Icon cards: same visual footprint as photo cards, but intentionally minimal. */
  .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    padding: 16px !important;
    background:
      radial-gradient(circle at 12% 0%, rgba(0,103,95,.09), transparent 40%),
      linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,248,239,.86)) !important;
    border-bottom: 0 !important;
  }

  .event-package-card.has-service-icon .event-package-icon-bubble {
    width: 44px !important;
    height: 44px !important;
    border-radius: 15px !important;
    margin: 0 !important;
  }

  .event-package-card.has-service-photo .event-package-body,
  .event-package-card.has-service-icon .event-package-body.no-photo-body {
    min-height: 170px !important;
    padding: 15px 15px 14px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    background: rgba(255,255,255,.99) !important;
  }

  .event-package-card.has-service-photo .event-package-body > div:first-child,
  .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    min-height: 58px !important;
  }

  .event-package-card.has-service-photo .event-package-body h4,
  .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
    font-size: 16px !important;
    line-height: 1.08 !important;
    min-height: 35px !important;
    margin: 0 !important;
  }

  .event-package-card.has-service-photo .event-package-body p,
  .event-package-card.has-service-icon .event-package-body.no-photo-body p {
    margin-top: 7px !important;
    font-size: 11.5px !important;
    line-height: 1.32 !important;
    color: #64748b !important;
    -webkit-line-clamp: 2 !important;
  }

  .event-package-card.has-service-photo .event-package-meta,
  .event-package-card.has-service-icon .event-package-meta {
    margin-top: 10px !important;
    gap: 7px !important;
  }

  .event-package-card.has-service-photo .event-package-reserve,
  .event-package-card.has-service-icon .event-package-reserve {
    height: 39px !important;
    margin-top: 11px !important;
    border-radius: 14px !important;
  }

  @media (min-width: 921px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(236px, 264px) !important;
      gap: 15px !important;
    }
  }

  @media (max-width: 920px) {
    .bp-packages {
      padding-left: 10px !important;
      padding-right: 0 !important;
      padding-top: 22px !important;
    }

    .bp-packages-head {
      padding-right: 10px !important;
      margin-bottom: 10px !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(176px, 188px) !important;
      gap: 9px !important;
      padding: 2px 10px 12px 0 !important;
    }

    .event-package-card.has-service-photo,
    .event-package-card.has-service-icon {
      border-radius: 18px !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.055) !important;
    }

    .bp-service-photo-frame.event-package-photo.has-photo,
    .bp-service-icon-frame.event-package-photo.no-photo {
      aspect-ratio: 16 / 8.8 !important;
    }

    .bp-service-photo-frame .bp-service-photo-main {
      transform: scale(1.018) !important;
    }

    .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
      padding: 12px !important;
    }

    .event-package-card.has-service-icon .event-package-icon-bubble {
      width: 38px !important;
      height: 38px !important;
      border-radius: 14px !important;
    }

    .event-package-card.has-service-photo .event-package-body,
    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 138px !important;
      padding: 11px 11px 10px !important;
    }

    .event-package-card.has-service-photo .event-package-body > div:first-child,
    .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
      min-height: 46px !important;
    }

    .event-package-card.has-service-photo .event-package-body h4,
    .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
      font-size: 14px !important;
      line-height: 1.06 !important;
      min-height: 30px !important;
      -webkit-line-clamp: 2 !important;
    }

    .event-package-card.has-service-photo .event-package-body p,
    .event-package-card.has-service-icon .event-package-body.no-photo-body p {
      font-size: 10.8px !important;
      line-height: 1.26 !important;
      margin-top: 5px !important;
      -webkit-line-clamp: 1 !important;
    }

    .event-package-card.has-service-photo .event-package-meta,
    .event-package-card.has-service-icon .event-package-meta {
      margin-top: 8px !important;
      gap: 5px !important;
    }

    .event-package-card.has-service-photo .event-package-meta span,
    .event-package-card.has-service-icon .event-package-meta span {
      font-size: 10.5px !important;
      min-height: 25px !important;
      padding: 0 8px !important;
    }

    .event-package-card.has-service-photo .event-package-reserve,
    .event-package-card.has-service-icon .event-package-reserve {
      height: 35px !important;
      margin-top: 8px !important;
      border-radius: 12px !important;
      font-size: 11.5px !important;
    }
  }

  @media (max-width: 430px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(168px, 178px) !important;
      gap: 8px !important;
    }

    .event-package-card.has-service-photo .event-package-body,
    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 132px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE 2V — ICON SERVICES + MIXED RAIL BALANCE
     Restores no-photo services to compact premium cards and
     gives mixed photo/icon rails a cleaner, intentional layout.
     ========================================================= */

  .event-packages-grid {
    align-items: stretch !important;
  }

  .event-package-card.has-service-icon {
    position: relative !important;
    isolation: isolate !important;
    overflow: hidden !important;
    background:
      radial-gradient(circle at 12% 0%, rgba(0,103,95,.075), transparent 34%),
      linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(255,250,244,.92) 100%) !important;
  }

  /* No-photo services should not pretend they have an image banner. */
  .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    position: absolute !important;
    top: 18px !important;
    left: 18px !important;
    z-index: 3 !important;
    width: 46px !important;
    height: 46px !important;
    min-height: 0 !important;
    aspect-ratio: auto !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .event-package-card.has-service-icon .event-package-icon-bubble {
    width: 46px !important;
    height: 46px !important;
    border-radius: 15px !important;
    margin: 0 !important;
    box-shadow: 0 10px 24px rgba(0,103,95,.075) !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body {
    min-height: 246px !important;
    padding: 88px 16px 15px !important;
    background: transparent !important;
    border-top: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    min-height: 0 !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
    min-height: 0 !important;
    font-size: 16px !important;
    line-height: 1.08 !important;
    letter-spacing: -.047em !important;
    -webkit-line-clamp: 2 !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body p {
    display: none !important;
  }

  .event-package-card.has-service-icon .event-package-meta {
    margin-top: 16px !important;
  }

  /* When every service is icon-only, make the rail lighter and more compact. */
  .event-packages-grid:not(:has(.has-service-photo)),
  .bp-packages-rail:not(:has(.has-service-photo)) {
    grid-auto-columns: minmax(214px, 238px) !important;
    gap: 13px !important;
  }

  .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon {
    min-height: 248px !important;
    border-radius: 20px !important;
    box-shadow: 0 10px 26px rgba(54,41,25,.045) !important;
  }

  .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon .event-package-body.no-photo-body {
    min-height: 248px !important;
    padding-top: 84px !important;
  }

  /* Mixed rails: photo + icon services should feel like the same family. */
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-photo,
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon {
    min-height: 318px !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
    min-height: 318px !important;
    padding-top: 92px !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon::after {
    content: "" !important;
    position: absolute !important;
    inset: auto 14px 54px 14px !important;
    height: 1px !important;
    background: linear-gradient(90deg, transparent, rgba(108,88,61,.10), transparent) !important;
    opacity: .65 !important;
    pointer-events: none !important;
  }

  @media (max-width: 920px) {
    .event-packages-grid:not(:has(.has-service-photo)),
    .bp-packages-rail:not(:has(.has-service-photo)) {
      grid-auto-columns: minmax(158px, 172px) !important;
      gap: 9px !important;
    }

    .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
      top: 13px !important;
      left: 13px !important;
      width: 38px !important;
      height: 38px !important;
    }

    .event-package-card.has-service-icon .event-package-icon-bubble {
      width: 38px !important;
      height: 38px !important;
      border-radius: 13px !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 188px !important;
      padding: 66px 11px 10px !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
      font-size: 13.5px !important;
      line-height: 1.06 !important;
      min-height: 0 !important;
    }

    .event-package-card.has-service-icon .event-package-meta {
      margin-top: 9px !important;
    }

    .event-package-card.has-service-icon .event-package-reserve {
      height: 34px !important;
      margin-top: 8px !important;
      border-radius: 12px !important;
      font-size: 11.5px !important;
    }

    .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon {
      min-height: 188px !important;
    }

    .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 188px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-photo,
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon {
      min-height: 254px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 254px !important;
      padding-top: 72px !important;
    }
  }

  @media (max-width: 430px) {
    .event-packages-grid:not(:has(.has-service-photo)),
    .bp-packages-rail:not(:has(.has-service-photo)) {
      grid-auto-columns: minmax(154px, 166px) !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 180px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-photo,
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon {
      min-height: 246px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 246px !important;
    }
  }


  /* =========================================================
     BUSINESSPAGE SERVICE ICON CONNECT — Loop pro SVG library
     Uses the same premium service icons selected in ServicesSection.
     ========================================================= */
  .event-package-icon-bubble-pro {
    background: linear-gradient(180deg, #ffffff 0%, #f2fbf8 100%) !important;
    border: 1px solid rgba(0, 103, 95, .13) !important;
    color: #00675f !important;
  }

  .event-package-icon-bubble-pro .event-package-icon-img {
    width: 72% !important;
    height: 72% !important;
    display: block !important;
    object-fit: contain !important;
    object-position: center !important;
  }

  .event-package-card.has-service-icon .event-package-icon-bubble-pro .event-package-icon-img {
    width: 68% !important;
    height: 68% !important;
  }

  @media (max-width: 920px) {
    .event-package-card.has-service-icon .event-package-icon-bubble-pro .event-package-icon-img {
      width: 70% !important;
      height: 70% !important;
    }
  }



  /* =========================================================
     BUSINESSPAGE 2W — TABLET-ONLY RESPONSIVE PASS
     - Fixes the services header/copy squeezing into vertical text.
     - Makes icon-only service cards more compact on tablet.
     - Reduces logo visual weight on tablet without changing desktop/mobile.
     ========================================================= */
  @media (min-width: 721px) and (max-width: 1024px) {
    .bp-container {
      padding-left: clamp(18px, 3vw, 30px) !important;
      padding-right: clamp(18px, 3vw, 30px) !important;
    }

    /* Tablet logo: keep the current web/mobile language, but reduce the hero footprint. */
    .bp-logo-card {
      width: min(100%, 392px) !important;
      max-width: 392px !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 7.45 !important;
      max-height: 178px !important;
      border-radius: 22px !important;
      box-shadow: 0 6px 16px rgba(54,41,25,.035) !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 22px !important;
    }

    .bp-logo-stage .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-cover .smart-logo-inner {
      padding: 8px !important;
    }

    /* Services header: prevent the subtitle from collapsing into one-word-per-line. */
    .bp-packages-head {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      align-items: end !important;
      gap: 14px !important;
      padding-right: 0 !important;
      margin-bottom: 14px !important;
    }

    .bp-packages-head > div:first-child {
      min-width: 0 !important;
      width: 100% !important;
      max-width: 560px !important;
    }

    .bp-packages-title {
      display: block !important;
      max-width: 420px !important;
      margin: 0 !important;
      line-height: .98 !important;
    }

    .bp-packages-copy {
      display: block !important;
      width: auto !important;
      min-width: 0 !important;
      max-width: 380px !important;
      margin-top: 8px !important;
      line-height: 1.34 !important;
      white-space: normal !important;
      word-break: normal !important;
      overflow-wrap: normal !important;
      hyphens: none !important;
      text-wrap: pretty !important;
    }

    .bp-package-actions {
      display: inline-flex !important;
      justify-self: end !important;
      align-self: end !important;
      gap: 10px !important;
      flex: 0 0 auto !important;
    }

    .bp-carousel-arrow {
      width: 38px !important;
      height: 38px !important;
    }

    /* Tablet service rail: denser cards, especially when service has an icon instead of photo. */
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(156px, 172px) !important;
      gap: 10px !important;
      padding: 3px 2px 14px !important;
    }

    .event-package-card.has-service-icon {
      min-height: 172px !important;
      border-radius: 17px !important;
      box-shadow: 0 6px 16px rgba(54,41,25,.04) !important;
    }

    .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
      top: 12px !important;
      left: 12px !important;
      width: 34px !important;
      height: 34px !important;
    }

    .event-package-card.has-service-icon .event-package-icon-bubble {
      width: 34px !important;
      height: 34px !important;
      border-radius: 12px !important;
      box-shadow: 0 7px 16px rgba(0,103,95,.055) !important;
    }

    .event-package-card.has-service-icon .event-package-icon-bubble-pro .event-package-icon-img {
      width: 68% !important;
      height: 68% !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 172px !important;
      padding: 56px 10px 10px !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
      font-size: 13.4px !important;
      line-height: 1.06 !important;
      letter-spacing: -.038em !important;
      -webkit-line-clamp: 2 !important;
    }

    .event-package-card.has-service-icon .event-package-meta {
      margin-top: 8px !important;
      gap: 5px !important;
    }

    .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron) {
      min-height: 24px !important;
      padding: 0 7px !important;
      font-size: 10px !important;
      border-radius: 999px !important;
    }

    .event-package-card.has-service-icon .event-package-chevron {
      font-size: 16px !important;
    }

    .event-package-card.has-service-icon .event-package-reserve {
      height: 33px !important;
      margin-top: 8px !important;
      border-radius: 12px !important;
      font-size: 11.4px !important;
    }

    .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon,
    .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 172px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-photo,
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon {
      min-height: 226px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 226px !important;
      padding-top: 62px !important;
    }
  }

  /* =========================================================
     BUSINESSPAGE 2X — TABLET HERO + SERVICE CARD FINAL FIX
     - Keeps the new larger tablet logo.
     - Prevents Reservar CTA from collapsing/distorting.
     - Forces icon-service cards into the requested compact layout:
       icon + title on top, chips in middle, button at bottom.
     ========================================================= */
  @media (min-width: 721px) and (max-width: 1024px) {
    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(280px, 42%) minmax(0, 1fr) !important;
      column-gap: clamp(18px, 3.2vw, 34px) !important;
      row-gap: 16px !important;
      align-items: center !important;
    }

    .bp-logo-card {
      grid-column: 1 !important;
      grid-row: 1 !important;
      width: 100% !important;
      max-width: 420px !important;
      margin: 0 !important;
      align-self: center !important;
    }

    .bp-logo-stage {
      aspect-ratio: 16 / 7.7 !important;
      max-height: 190px !important;
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-frame,
    .bp-logo-stage .smart-logo-inner {
      border-radius: 24px !important;
    }

    .bp-logo-stage .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-contain .smart-logo-inner,
    .bp-logo-stage .smart-logo-fit-cover .smart-logo-inner {
      padding: 10px !important;
    }

    .bp-hero-top {
      grid-column: 2 !important;
      grid-row: 1 !important;
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 16px !important;
      align-content: center !important;
      align-self: center !important;
      min-width: 0 !important;
      margin: 0 !important;
    }

    .bp-title-wrap {
      grid-column: auto !important;
      grid-row: auto !important;
      min-width: 0 !important;
      max-width: 100% !important;
    }

    .bp-title {
      font-size: clamp(32px, 4.1vw, 46px) !important;
      line-height: .95 !important;
      max-width: 100% !important;
    }

    .bp-tagline {
      max-width: 520px !important;
      font-size: 14px !important;
      line-height: 1.36 !important;
    }

    .bp-actions {
      grid-column: auto !important;
      grid-row: auto !important;
      position: static !important;
      transform: none !important;
      width: auto !important;
      max-width: 100% !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 10px !important;
      padding: 0 !important;
      margin: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      flex: 0 0 52px !important;
      width: 52px !important;
      height: 52px !important;
      min-width: 52px !important;
      border-radius: 18px !important;
      padding: 0 !important;
    }

    .bp-actions .bp-primary-btn {
      flex: 0 0 auto !important;
      width: auto !important;
      min-width: 138px !important;
      max-width: none !important;
      height: 52px !important;
      min-height: 52px !important;
      border-radius: 18px !important;
      padding: 0 22px !important;
      white-space: nowrap !important;
      overflow: visible !important;
      text-overflow: clip !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-info-panel {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
    }

    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(156px, 168px) !important;
      gap: 10px !important;
    }

    .event-package-card.has-service-icon {
      position: relative !important;
      min-height: 154px !important;
      height: 154px !important;
      border-radius: 18px !important;
      overflow: hidden !important;
    }

    .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
      position: absolute !important;
      inset: 12px auto auto 12px !important;
      width: 36px !important;
      height: 36px !important;
      margin: 0 !important;
      z-index: 2 !important;
    }

    .event-package-card.has-service-icon .event-package-icon-bubble,
    .event-package-card.has-service-icon .event-package-icon-bubble-pro {
      width: 36px !important;
      height: 36px !important;
      border-radius: 13px !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 154px !important;
      height: 154px !important;
      padding: 13px 12px 12px !important;
      display: grid !important;
      grid-template-rows: 42px auto 34px !important;
      gap: 8px !important;
      align-content: stretch !important;
      justify-content: stretch !important;
      background: linear-gradient(180deg, rgba(255,255,255,.97), rgba(255,248,239,.86)) !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
      min-height: 42px !important;
      margin: 0 0 0 48px !important;
      padding: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      text-align: left !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
      margin: 0 !important;
      color: #111827 !important;
      font-size: 13.2px !important;
      line-height: 1.08 !important;
      letter-spacing: -.035em !important;
      font-weight: 950 !important;
      text-align: left !important;
      display: -webkit-box !important;
      -webkit-line-clamp: 2 !important;
      -webkit-box-orient: vertical !important;
      overflow: hidden !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body p {
      display: none !important;
    }

    .event-package-card.has-service-icon .event-package-meta {
      margin: 0 !important;
      width: 100% !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 6px !important;
      min-width: 0 !important;
    }

    .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron) {
      flex: 0 0 auto !important;
      min-height: 25px !important;
      height: 25px !important;
      padding: 0 8px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      border-radius: 999px !important;
      font-size: 10.2px !important;
      line-height: 1 !important;
      white-space: nowrap !important;
    }

    .event-package-card.has-service-icon .event-package-chevron {
      margin-left: auto !important;
      width: 25px !important;
      height: 25px !important;
      min-width: 25px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      border-radius: 999px !important;
      font-size: 16px !important;
      line-height: 1 !important;
    }

    .event-package-card.has-service-icon .event-package-reserve {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 34px !important;
      margin: 0 !important;
      border-radius: 13px !important;
      font-size: 11.5px !important;
      font-weight: 950 !important;
      line-height: 1 !important;
    }

    .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon,
    .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon .event-package-body.no-photo-body,
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon,
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      min-height: 154px !important;
      height: 154px !important;
      padding-top: 13px !important;
    }
  }

  @media (min-width: 721px) and (max-width: 820px) {
    .bp-hero {
      grid-template-columns: minmax(260px, 45%) minmax(0, 1fr) !important;
      column-gap: 18px !important;
    }

    .bp-actions {
      gap: 8px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      flex-basis: 48px !important;
      width: 48px !important;
      height: 48px !important;
      min-width: 48px !important;
    }

    .bp-actions .bp-primary-btn {
      min-width: 124px !important;
      height: 48px !important;
      min-height: 48px !important;
      padding: 0 18px !important;
      font-size: 14px !important;
    }

    .event-packages-grid,
    .bp-packages-rail {
      grid-auto-columns: minmax(148px, 158px) !important;
    }

    .event-package-card.has-service-icon {
      height: 150px !important;
      min-height: 150px !important;
    }

    .event-package-card.has-service-icon .event-package-body.no-photo-body {
      height: 150px !important;
      min-height: 150px !important;
      padding-left: 11px !important;
      padding-right: 11px !important;
    }

    .event-package-card.has-service-icon .event-package-meta {
      gap: 5px !important;
    }

    .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron) {
      padding: 0 7px !important;
      font-size: 9.8px !important;
    }
  }



  /* =========================================================
     BUSINESSPAGE 2Y — SERVICE CARDS SAME TABLET SIZE EVERYWHERE
     Tablet card dimensions are now the single source of truth.
     Applies to web, tablet and mobile so Reservar never leaves the card.
     ========================================================= */
  .bp-packages-rail,
  .event-packages-grid {
    grid-auto-columns: minmax(156px, 168px) !important;
    gap: 10px !important;
    padding: 2px 4px 14px !important;
    align-items: stretch !important;
  }

  .event-package-card.has-service-icon {
    position: relative !important;
    width: auto !important;
    min-width: 0 !important;
    min-height: 154px !important;
    height: 154px !important;
    border-radius: 18px !important;
    overflow: hidden !important;
    display: block !important;
  }

  .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    position: absolute !important;
    inset: 12px auto auto 12px !important;
    width: 36px !important;
    height: 36px !important;
    min-height: 0 !important;
    aspect-ratio: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    z-index: 2 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .event-package-card.has-service-icon .event-package-icon-bubble,
  .event-package-card.has-service-icon .event-package-icon-bubble-pro {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    border-radius: 13px !important;
    margin: 0 !important;
    box-shadow: 0 8px 18px rgba(0,103,95,.06) !important;
  }

  .event-package-card.has-service-icon .event-package-icon-bubble-pro .event-package-icon-img,
  .event-package-card.has-service-icon .event-package-icon-img {
    width: 68% !important;
    height: 68% !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body {
    min-height: 154px !important;
    height: 154px !important;
    padding: 13px 12px 12px !important;
    box-sizing: border-box !important;
    display: grid !important;
    grid-template-rows: 42px minmax(25px, 1fr) 34px !important;
    gap: 8px !important;
    align-content: stretch !important;
    justify-content: stretch !important;
    background: linear-gradient(180deg, rgba(255,255,255,.97), rgba(255,248,239,.86)) !important;
    border-top: 0 !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    min-height: 42px !important;
    height: 42px !important;
    margin: 0 0 0 48px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    text-align: left !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
    margin: 0 !important;
    color: #111827 !important;
    font-size: 13.2px !important;
    line-height: 1.08 !important;
    letter-spacing: -.035em !important;
    font-weight: 950 !important;
    text-align: left !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body p {
    display: none !important;
  }

  .event-package-card.has-service-icon .event-package-meta {
    margin: 0 !important;
    width: 100% !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron) {
    flex: 0 0 auto !important;
    min-height: 25px !important;
    height: 25px !important;
    padding: 0 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 10.2px !important;
    line-height: 1 !important;
    white-space: nowrap !important;
  }

  .event-package-card.has-service-icon .event-package-chevron {
    margin-left: auto !important;
    width: 25px !important;
    height: 25px !important;
    min-width: 25px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 16px !important;
    line-height: 1 !important;
  }

  .event-package-card.has-service-icon .event-package-reserve {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 34px !important;
    min-height: 34px !important;
    margin: 0 !important;
    border-radius: 13px !important;
    font-size: 11.5px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    box-sizing: border-box !important;
  }

  .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon,
  .event-packages-grid:not(:has(.has-service-photo)) .event-package-card.has-service-icon .event-package-body.no-photo-body,
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon,
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body,
  .bp-packages-rail:not(:has(.has-service-photo)) .event-package-card.has-service-icon,
  .bp-packages-rail:not(:has(.has-service-photo)) .event-package-card.has-service-icon .event-package-body.no-photo-body {
    min-height: 154px !important;
    height: 154px !important;
    padding-top: 13px !important;
  }

  /* Photo service cards follow the same compact rail width; they stay taller only when an image exists. */
  .event-package-card.has-service-photo {
    min-height: 218px !important;
    border-radius: 18px !important;
  }

  .event-package-card.has-service-photo .event-package-photo.has-photo,
  .event-package-card.has-service-photo .bp-service-photo-frame {
    height: 92px !important;
  }

  .event-package-card.has-service-photo .event-package-body {
    padding: 11px 12px 12px !important;
  }

  .event-package-card.has-service-photo .event-package-body h4 {
    font-size: 13.6px !important;
    line-height: 1.08 !important;
    -webkit-line-clamp: 2 !important;
  }

  .event-package-card.has-service-photo .event-package-body p {
    display: none !important;
  }

  .event-package-card.has-service-photo .event-package-meta {
    margin-top: 8px !important;
    font-size: 10.5px !important;
  }

  @media (max-width: 430px) {
    .bp-packages-rail,
    .event-packages-grid {
      grid-auto-columns: minmax(156px, 168px) !important;
      gap: 10px !important;
    }
  }


  /* =========================================================
     BP SERVICE CARD BUTTON CLIP FIX — keep visual size, fix overflow
     The compact service card size is correct. This only removes inherited
     card padding that was pushing the internal reserve button below the card.
     ========================================================= */
  .event-package-card.has-service-icon {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body {
    box-sizing: border-box !important;
    max-height: 154px !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-reserve {
    align-self: end !important;
    flex: 0 0 auto !important;
    max-width: 100% !important;
  }


  /* =========================================================
     BP SERVICE CTA HARD LOCK — keep the same card look, force CTA inside.
     The previous compact size is preserved; only positioning is corrected.
     Applies to icon/no-photo service cards across web, tablet and mobile.
     ========================================================= */
  .event-package-card.has-service-icon {
    position: relative !important;
    height: 154px !important;
    min-height: 154px !important;
    max-height: 154px !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    position: absolute !important;
    top: 12px !important;
    left: 12px !important;
    right: auto !important;
    bottom: auto !important;
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    max-width: 36px !important;
    max-height: 36px !important;
    margin: 0 !important;
    padding: 0 !important;
    z-index: 3 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
    pointer-events: none !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body {
    position: static !important;
    height: 100% !important;
    min-height: 0 !important;
    max-height: none !important;
    padding: 13px 12px 54px !important;
    box-sizing: border-box !important;
    display: block !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    height: 42px !important;
    min-height: 42px !important;
    max-height: 42px !important;
    margin: 0 0 0 48px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-meta {
    position: absolute !important;
    left: 12px !important;
    right: 12px !important;
    bottom: 50px !important;
    width: auto !important;
    height: 26px !important;
    min-height: 26px !important;
    margin: 0 !important;
    padding: 0 !important;
    z-index: 4 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-icon .event-package-reserve {
    position: absolute !important;
    left: 12px !important;
    right: 12px !important;
    bottom: 12px !important;
    z-index: 5 !important;
    width: auto !important;
    max-width: none !important;
    height: 31px !important;
    min-height: 31px !important;
    margin: 0 !important;
    padding: 0 10px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 12px !important;
    box-sizing: border-box !important;
    line-height: 1 !important;
  }

  .event-package-card.has-service-icon .event-package-reserve:focus-visible {
    outline: 2px solid rgba(0, 103, 95, .28) !important;
    outline-offset: 2px !important;
  }

  /* =========================================================
     BP EVENT / PHOTO SERVICE CARDS — HARMONY FIX
     - Does NOT touch icon-only cards.
     - Makes event/photo services wider, shorter and cleaner.
     - Fixes long service names so they do not look randomly cut.
     ========================================================= */

  .event-packages-grid:has(.has-service-photo),
  .bp-packages-rail:has(.has-service-photo) {
    grid-auto-columns: clamp(190px, 16vw, 226px) !important;
    gap: 14px !important;
    align-items: stretch !important;
    padding-bottom: 18px !important;
  }

  .event-package-card.has-service-photo {
    height: 262px !important;
    min-height: 262px !important;
    max-height: 262px !important;
    border-radius: 18px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    background: rgba(255,255,255,.94) !important;
    box-shadow: 0 10px 26px rgba(54,41,25,.055) !important;
  }

  .event-package-card.has-service-photo .event-package-photo.has-photo,
  .event-package-card.has-service-photo .bp-service-photo-frame {
    height: 108px !important;
    min-height: 108px !important;
    max-height: 108px !important;
    flex: 0 0 108px !important;
    border-radius: 0 !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-photo .event-package-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    padding: 12px 12px 12px !important;
    display: grid !important;
    grid-template-rows: minmax(46px, auto) 28px 38px !important;
    gap: 8px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.94)) !important;
  }

  .event-package-card.has-service-photo .event-package-body > div:first-child {
    min-height: 46px !important;
    height: auto !important;
    display: flex !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-photo .event-package-body h4 {
    margin: 0 !important;
    color: #111827 !important;
    font-size: 13.5px !important;
    line-height: 1.08 !important;
    letter-spacing: -.032em !important;
    font-weight: 950 !important;
    text-align: left !important;

    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: normal !important;
    overflow-wrap: anywhere !important;
  }

  .event-package-card.has-service-photo .event-package-body p {
    display: none !important;
  }

  .event-package-card.has-service-photo .event-package-meta {
    margin: 0 !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: hidden !important;
  }

  .event-package-card.has-service-photo .event-package-meta span:not(.event-package-chevron) {
    flex: 0 0 auto !important;
    min-height: 26px !important;
    height: 26px !important;
    padding: 0 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 10.5px !important;
    line-height: 1 !important;
    white-space: nowrap !important;
  }

  .event-package-card.has-service-photo .event-package-chevron {
    margin-left: auto !important;
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 16px !important;
    line-height: 1 !important;
  }

  .event-package-card.has-service-photo .event-package-reserve {
    position: static !important;
    inset: auto !important;
    align-self: end !important;
    width: 100% !important;
    height: 38px !important;
    min-height: 38px !important;
    margin: 0 !important;
    border-radius: 13px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    box-sizing: border-box !important;
  }

  /* Mixed rails: make photo cards and icon cards feel intentionally related,
     but keep the icon-only design untouched when there are no photo cards. */
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon),
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) {
    grid-auto-columns: clamp(190px, 16vw, 226px) !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon {
    height: 262px !important;
    min-height: 262px !important;
    max-height: 262px !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
    height: 262px !important;
    min-height: 262px !important;
    max-height: 262px !important;
    grid-template-rows: 50px minmax(1fr, auto) 28px 38px !important;
    padding: 16px 14px 12px !important;
  }

  @media (max-width: 920px) {
    .event-packages-grid:has(.has-service-photo),
    .bp-packages-rail:has(.has-service-photo) {
      grid-auto-columns: clamp(182px, 62vw, 214px) !important;
      gap: 12px !important;
    }

    .event-package-card.has-service-photo {
      height: 252px !important;
      min-height: 252px !important;
      max-height: 252px !important;
    }

    .event-package-card.has-service-photo .event-package-photo.has-photo,
    .event-package-card.has-service-photo .bp-service-photo-frame {
      height: 104px !important;
      min-height: 104px !important;
      max-height: 104px !important;
      flex-basis: 104px !important;
    }

    .event-package-card.has-service-photo .event-package-body {
      grid-template-rows: minmax(44px, auto) 28px 38px !important;
      padding: 11px 12px 12px !important;
    }

    .event-package-card.has-service-photo .event-package-body h4 {
      font-size: 13.2px !important;
      -webkit-line-clamp: 3 !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon,
    .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon,
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body,
    .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      height: 252px !important;
      min-height: 252px !important;
      max-height: 252px !important;
    }
  }


  /* =========================================================
     BP HYBRID SERVICE RAIL HARMONY FIX
     Only applies when services include BOTH photo cards and icon cards.
     Icon-only businesses keep the compact design unchanged.
     ========================================================= */

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon),
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) {
    grid-auto-columns: clamp(205px, 17vw, 236px) !important;
    gap: 16px !important;
    align-items: stretch !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card {
    height: 258px !important;
    min-height: 258px !important;
    max-height: 258px !important;
    border-radius: 18px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    background: rgba(255,255,255,.94) !important;
    box-shadow: 0 10px 26px rgba(54,41,25,.055) !important;
  }

  /* In hybrid rails, icon cards get a real top visual area so they match photo cards. */
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    position: relative !important;
    inset: auto !important;
    width: 100% !important;
    height: 108px !important;
    min-height: 108px !important;
    max-height: 108px !important;
    flex: 0 0 108px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: grid !important;
    place-items: center !important;
    overflow: hidden !important;
    border: 0 !important;
    border-radius: 0 !important;
    background:
      radial-gradient(circle at 22% 0%, rgba(0,103,95,.12), transparent 42%),
      linear-gradient(135deg, #f2fbf8 0%, #fffaf4 100%) !important;
    box-shadow: none !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-icon-bubble,
  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-icon-bubble-pro,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-icon-bubble,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-icon-bubble-pro {
    width: 48px !important;
    height: 48px !important;
    min-width: 48px !important;
    min-height: 48px !important;
    border-radius: 16px !important;
    margin: 0 !important;
    background: rgba(255,255,255,.84) !important;
    border: 1px solid rgba(0,103,95,.12) !important;
    box-shadow: 0 10px 22px rgba(0,103,95,.08) !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
    flex: 1 1 auto !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    padding: 12px 12px 12px !important;
    display: grid !important;
    grid-template-rows: minmax(44px, auto) 28px 38px !important;
    gap: 8px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.94)) !important;
    border-top: 0 !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    height: auto !important;
    min-height: 44px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: flex !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow: hidden !important;
    text-align: left !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body h4,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
    margin: 0 !important;
    font-size: 13.5px !important;
    line-height: 1.08 !important;
    letter-spacing: -.032em !important;
    font-weight: 950 !important;
    color: #111827 !important;
    text-align: left !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    overflow-wrap: anywhere !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-meta,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-meta {
    margin: 0 !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: hidden !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron),
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron) {
    flex: 0 0 auto !important;
    min-height: 26px !important;
    height: 26px !important;
    padding: 0 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 10.5px !important;
    line-height: 1 !important;
    white-space: nowrap !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-chevron,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-chevron {
    margin-left: auto !important;
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 16px !important;
    line-height: 1 !important;
  }

  .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-reserve,
  .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-reserve {
    position: static !important;
    inset: auto !important;
    align-self: end !important;
    width: 100% !important;
    height: 38px !important;
    min-height: 38px !important;
    margin: 0 !important;
    border-radius: 13px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    box-sizing: border-box !important;
  }

  @media (max-width: 920px) {
    .event-packages-grid:has(.has-service-photo):has(.has-service-icon),
    .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) {
      grid-auto-columns: clamp(190px, 62vw, 222px) !important;
      gap: 12px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card,
    .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card {
      height: 252px !important;
      min-height: 252px !important;
      max-height: 252px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo,
    .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
      height: 104px !important;
      min-height: 104px !important;
      max-height: 104px !important;
      flex-basis: 104px !important;
    }

    .event-packages-grid:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body,
    .bp-packages-rail:has(.has-service-photo):has(.has-service-icon) .event-package-card.has-service-icon .event-package-body.no-photo-body {
      grid-template-rows: minmax(42px, auto) 28px 38px !important;
      padding: 11px 12px 12px !important;
    }
  }


  /* =========================================================
     BP MIXED SERVICE RAIL — FINAL ALIGNMENT FIX
     Only applies to rails with BOTH image services and icon services.
     Goal: same card height, same title baseline, same meta baseline, same CTA baseline.
     Icon-only and photo-only rails remain unchanged.
     ========================================================= */

  .event-packages-grid.mixed-service-rail,
  .bp-packages-rail.mixed-service-rail {
    grid-auto-columns: clamp(205px, 17vw, 236px) !important;
    gap: 16px !important;
    align-items: stretch !important;
    overflow-x: auto !important;
    overflow-y: visible !important;
    padding: 6px 4px 18px !important;
  }

  .mixed-service-rail .event-package-card {
    position: relative !important;
    width: auto !important;
    height: 262px !important;
    min-height: 262px !important;
    max-height: 262px !important;
    display: grid !important;
    grid-template-rows: 108px 154px !important;
    border-radius: 18px !important;
    overflow: hidden !important;
    background: rgba(255,255,255,.95) !important;
    box-shadow: 0 10px 26px rgba(54,41,25,.055) !important;
  }

  .mixed-service-rail .event-package-card.has-service-photo .event-package-photo.has-photo,
  .mixed-service-rail .event-package-card.has-service-photo .bp-service-photo-frame,
  .mixed-service-rail .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    position: relative !important;
    inset: auto !important;
    grid-row: 1 !important;
    width: 100% !important;
    height: 108px !important;
    min-height: 108px !important;
    max-height: 108px !important;
    flex: none !important;
    display: grid !important;
    place-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    overflow: hidden !important;
    box-shadow: none !important;
  }

  .mixed-service-rail .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
    background:
      radial-gradient(circle at 22% 0%, rgba(0,103,95,.11), transparent 44%),
      linear-gradient(135deg, #f2fbf8 0%, #fffaf4 100%) !important;
  }

  .mixed-service-rail .event-package-card.has-service-icon .event-package-icon-bubble,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-icon-bubble-pro {
    width: 48px !important;
    height: 48px !important;
    min-width: 48px !important;
    min-height: 48px !important;
    border-radius: 16px !important;
    margin: 0 !important;
    display: grid !important;
    place-items: center !important;
    background: rgba(255,255,255,.88) !important;
    border: 1px solid rgba(0,103,95,.12) !important;
    box-shadow: 0 10px 22px rgba(0,103,95,.08) !important;
  }

  .mixed-service-rail .event-package-card .event-package-body,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-body.no-photo-body {
    grid-row: 2 !important;
    height: 154px !important;
    min-height: 154px !important;
    max-height: 154px !important;
    padding: 12px 12px 12px !important;
    display: grid !important;
    grid-template-rows: 48px 28px 38px !important;
    gap: 8px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    background: linear-gradient(180deg, rgba(255,255,255,.99), rgba(255,255,255,.95)) !important;
    border-top: 0 !important;
  }

  .mixed-service-rail .event-package-card .event-package-body > div:first-child,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: flex !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow: hidden !important;
    text-align: left !important;
  }

  .mixed-service-rail .event-package-card .event-package-body h4,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
    margin: 0 !important;
    color: #111827 !important;
    font-size: 13.5px !important;
    line-height: 1.08 !important;
    letter-spacing: -.032em !important;
    font-weight: 950 !important;
    text-align: left !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: normal !important;
    overflow-wrap: anywhere !important;
  }

  .mixed-service-rail .event-package-card .event-package-body p,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-body.no-photo-body p {
    display: none !important;
  }

  .mixed-service-rail .event-package-card .event-package-meta,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-meta {
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
    margin: 0 !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: hidden !important;
  }

  .mixed-service-rail .event-package-card .event-package-meta span:not(.event-package-chevron),
  .mixed-service-rail .event-package-card.has-service-icon .event-package-meta span:not(.event-package-chevron) {
    flex: 0 0 auto !important;
    height: 26px !important;
    min-height: 26px !important;
    padding: 0 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 10.5px !important;
    line-height: 1 !important;
    white-space: nowrap !important;
  }

  .mixed-service-rail .event-package-card .event-package-chevron,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-chevron {
    margin-left: auto !important;
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 16px !important;
    line-height: 1 !important;
  }

  .mixed-service-rail .event-package-card .event-package-reserve,
  .mixed-service-rail .event-package-card.has-service-icon .event-package-reserve {
    position: static !important;
    inset: auto !important;
    align-self: end !important;
    width: 100% !important;
    height: 38px !important;
    min-height: 38px !important;
    margin: 0 !important;
    border-radius: 13px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    box-sizing: border-box !important;
  }

  @media (max-width: 920px) {
    .event-packages-grid.mixed-service-rail,
    .bp-packages-rail.mixed-service-rail {
      grid-auto-columns: clamp(190px, 62vw, 222px) !important;
      gap: 12px !important;
    }

    .mixed-service-rail .event-package-card {
      height: 254px !important;
      min-height: 254px !important;
      max-height: 254px !important;
      grid-template-rows: 104px 150px !important;
    }

    .mixed-service-rail .event-package-card.has-service-photo .event-package-photo.has-photo,
    .mixed-service-rail .event-package-card.has-service-photo .bp-service-photo-frame,
    .mixed-service-rail .event-package-card.has-service-icon .bp-service-icon-frame.event-package-photo.no-photo {
      height: 104px !important;
      min-height: 104px !important;
      max-height: 104px !important;
    }

    .mixed-service-rail .event-package-card .event-package-body,
    .mixed-service-rail .event-package-card.has-service-icon .event-package-body.no-photo-body {
      height: 150px !important;
      min-height: 150px !important;
      max-height: 150px !important;
      grid-template-rows: 44px 28px 38px !important;
      padding: 11px 12px 12px !important;
    }
  }


  /* =========================================================
     BP HYBRID SERVICES — HARD EQUALIZE V2
     This is intentionally scoped only to .mixed-service-rail.
     It forces photo and icon service cards to use the exact same geometry.
     ========================================================= */

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail {
    grid-auto-columns: 236px !important;
    gap: 16px !important;
    align-items: stretch !important;
    padding: 6px 4px 22px !important;
    overflow-x: auto !important;
    overflow-y: visible !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card {
    width: 236px !important;
    min-width: 236px !important;
    max-width: 236px !important;
    height: 262px !important;
    min-height: 262px !important;
    max-height: 262px !important;
    display: grid !important;
    grid-template-rows: 108px 154px !important;
    grid-template-columns: 1fr !important;
    border-radius: 18px !important;
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    background: rgba(255,255,255,.95) !important;
    box-shadow: 0 10px 26px rgba(54,41,25,.055) !important;
    transform: none !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card:hover {
    transform: translateY(-2px) !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-photo.has-photo,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .bp-service-photo-frame.event-package-photo.has-photo,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .bp-service-icon-frame.event-package-photo.no-photo {
    grid-row: 1 !important;
    grid-column: 1 !important;
    position: relative !important;
    inset: auto !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    height: 108px !important;
    min-height: 108px !important;
    max-height: 108px !important;
    flex: none !important;
    display: grid !important;
    place-items: center !important;
    padding: 0 !important;
    margin: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    overflow: hidden !important;
    background: #f7fbf9 !important;
    box-shadow: none !important;
    filter: none !important;
    opacity: 1 !important;
    z-index: 1 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .bp-service-icon-frame.event-package-photo.no-photo::before,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .bp-service-icon-frame.event-package-photo.no-photo::after {
    display: none !important;
    content: none !important;
    opacity: 0 !important;
    box-shadow: none !important;
    filter: none !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .bp-service-icon-frame .event-package-icon-bubble,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .bp-service-icon-frame .event-package-icon-bubble-pro {
    width: 48px !important;
    min-width: 48px !important;
    max-width: 48px !important;
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 16px !important;
    display: grid !important;
    place-items: center !important;
    background: rgba(255,255,255,.92) !important;
    border: 1px solid rgba(0,103,95,.12) !important;
    box-shadow: 0 8px 18px rgba(0,103,95,.06) !important;
    filter: none !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
    grid-row: 2 !important;
    grid-column: 1 !important;
    position: relative !important;
    width: 100% !important;
    height: 154px !important;
    min-height: 154px !important;
    max-height: 154px !important;
    padding: 12px 12px 12px !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    display: grid !important;
    grid-template-rows: 48px 28px 38px !important;
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 8px !important;
    align-content: stretch !important;
    background: linear-gradient(180deg, rgba(255,255,255,.99), rgba(255,255,255,.95)) !important;
    border: 0 !important;
    overflow: hidden !important;
    z-index: 2 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body > div:first-child,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body > div:first-child {
    grid-row: 1 !important;
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: flex !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow: hidden !important;
    text-align: left !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card h4 {
    margin: 0 !important;
    padding: 0 !important;
    color: #111827 !important;
    font-size: 13.5px !important;
    line-height: 1.08 !important;
    letter-spacing: -.032em !important;
    font-weight: 950 !important;
    text-align: left !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: normal !important;
    overflow-wrap: anywhere !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card p {
    display: none !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta {
    grid-row: 2 !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
    margin: 0 !important;
    padding: 0 !important;
    min-width: 0 !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: hidden !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron) {
    flex: 0 0 auto !important;
    height: 26px !important;
    min-height: 26px !important;
    padding: 0 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 10.5px !important;
    line-height: 1 !important;
    font-weight: 900 !important;
    white-space: nowrap !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron {
    margin-left: auto !important;
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    font-size: 16px !important;
    line-height: 1 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-reserve {
    grid-row: 3 !important;
    position: static !important;
    inset: auto !important;
    width: 100% !important;
    height: 38px !important;
    min-height: 38px !important;
    max-height: 38px !important;
    margin: 0 !important;
    padding: 0 12px !important;
    border-radius: 13px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    box-sizing: border-box !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  @media (max-width: 920px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail {
      grid-auto-columns: 214px !important;
      gap: 12px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card {
      width: 214px !important;
      min-width: 214px !important;
      max-width: 214px !important;
      height: 254px !important;
      min-height: 254px !important;
      max-height: 254px !important;
      grid-template-rows: 104px 150px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-photo.has-photo,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .bp-service-photo-frame.event-package-photo.has-photo,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .bp-service-icon-frame.event-package-photo.no-photo {
      height: 104px !important;
      min-height: 104px !important;
      max-height: 104px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
      height: 150px !important;
      min-height: 150px !important;
      max-height: 150px !important;
      grid-template-rows: 44px 28px 38px !important;
      padding: 11px 12px 12px !important;
    }
  }


  /* =========================================================
     BP HYBRID SERVICE CHEVRON FIX
     Only fixes the arrow pill in hybrid icon service cards.
     Keeps the approved hybrid geometry untouched.
     ========================================================= */

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta {
    gap: 6px !important;
    padding-right: 2px !important;
    box-sizing: border-box !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron) {
    max-width: calc(100% - 34px) !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
    flex: 0 0 26px !important;
    width: 26px !important;
    min-width: 26px !important;
    max-width: 26px !important;
    height: 26px !important;
    min-height: 26px !important;
    max-height: 26px !important;
    margin-left: auto !important;
    margin-right: 2px !important;
    transform: none !important;
    position: static !important;
    right: auto !important;
    top: auto !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 999px !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    font-size: 15px !important;
    line-height: 1 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
    display: grid !important;
    grid-template-columns: max-content max-content 28px !important;
    column-gap: 6px !important;
    align-items: center !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
    grid-column: 3 !important;
    justify-self: end !important;
  }

  @media (max-width: 920px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
      grid-template-columns: max-content minmax(0, max-content) 28px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron {
      margin-right: 1px !important;
    }
  }


  /* =========================================================
     BP HYBRID META ROW — NO CLIP FINAL
     Fixes the duration / price / arrow row in mixed-service-rail.
     Guarantees the arrow stays inside the card.
     ========================================================= */

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-body,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
    padding-left: 12px !important;
    padding-right: 12px !important;
    overflow: hidden !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
    grid-row: 2 !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
    margin: 0 !important;
    padding: 0 2px 0 0 !important;
    box-sizing: border-box !important;

    display: grid !important;
    grid-template-columns: 50px minmax(0, 1fr) 24px !important;
    column-gap: 7px !important;
    align-items: center !important;
    justify-content: stretch !important;
    overflow: hidden !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron) {
    height: 26px !important;
    min-height: 26px !important;
    max-height: 26px !important;
    min-width: 0 !important;
    max-width: 100% !important;
    box-sizing: border-box !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 999px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):first-child {
    grid-column: 1 !important;
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    padding: 0 5px !important;
    justify-self: start !important;
    font-size: 10px !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):nth-child(2) {
    grid-column: 2 !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    padding: 0 7px !important;
    justify-self: stretch !important;
    font-size: 9.8px !important;
    letter-spacing: -.025em !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
    grid-column: 3 !important;
    justify-self: end !important;
    align-self: center !important;

    width: 24px !important;
    min-width: 24px !important;
    max-width: 24px !important;
    height: 24px !important;
    min-height: 24px !important;
    max-height: 24px !important;
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;

    position: static !important;
    inset: auto !important;
    right: auto !important;
    left: auto !important;
    top: auto !important;
    bottom: auto !important;
    transform: none !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 999px !important;
    overflow: hidden !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    font-size: 14px !important;
    line-height: 1 !important;
  }

  @media (max-width: 920px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
      grid-template-columns: 48px minmax(0, 1fr) 24px !important;
      column-gap: 5px !important;
      padding-right: 2px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):first-child {
      width: 48px !important;
      min-width: 48px !important;
      max-width: 48px !important;
      font-size: 9.6px !important;
      padding-left: 4px !important;
      padding-right: 4px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):nth-child(2) {
      font-size: 9.2px !important;
      padding-left: 5px !important;
      padding-right: 5px !important;
      letter-spacing: -.035em !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
      width: 24px !important;
      min-width: 24px !important;
      max-width: 24px !important;
      height: 24px !important;
      min-height: 24px !important;
      max-height: 24px !important;
      font-size: 13px !important;
    }
  }


  /* =========================================================
     BP HYBRID META ROW — ARROW INSIDE HARD FIX
     Scoped only to mixed-service-rail.
     The arrow is positioned inside the card and the price pill
     is given a safe max-width so it cannot push the arrow out.
     ========================================================= */

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-body,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
    overflow: hidden !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
    position: relative !important;
    grid-row: 2 !important;

    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;

    margin: 0 !important;
    padding: 0 32px 0 0 !important;
    box-sizing: border-box !important;

    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;
    overflow: visible !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron) {
    height: 26px !important;
    min-height: 26px !important;
    max-height: 26px !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 999px !important;
    box-sizing: border-box !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;

    font-weight: 950 !important;
    line-height: 1 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):first-child {
    flex: 0 0 50px !important;
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    padding: 0 5px !important;
    font-size: 10px !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):nth-child(2) {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    max-width: calc(100% - 56px) !important;
    padding: 0 8px !important;
    font-size: 9.8px !important;
    letter-spacing: -.025em !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
    position: absolute !important;
    right: 0 !important;
    top: 50% !important;
    left: auto !important;
    bottom: auto !important;
    transform: translateY(-50%) !important;

    width: 26px !important;
    min-width: 26px !important;
    max-width: 26px !important;
    height: 26px !important;
    min-height: 26px !important;
    max-height: 26px !important;

    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 999px !important;
    overflow: hidden !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    font-size: 14px !important;
    line-height: 1 !important;
    z-index: 3 !important;
  }

  @media (max-width: 920px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
      padding-right: 30px !important;
      gap: 5px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):first-child {
      flex-basis: 48px !important;
      width: 48px !important;
      min-width: 48px !important;
      max-width: 48px !important;
      font-size: 9.6px !important;
      padding-left: 4px !important;
      padding-right: 4px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):nth-child(2) {
      max-width: calc(100% - 53px) !important;
      font-size: 9.2px !important;
      padding-left: 5px !important;
      padding-right: 5px !important;
      letter-spacing: -.035em !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
      width: 24px !important;
      min-width: 24px !important;
      max-width: 24px !important;
      height: 24px !important;
      min-height: 24px !important;
      max-height: 24px !important;
      font-size: 13px !important;
    }
  }


  /* =========================================================
     BP HYBRID CARD BODY ROW LOCK — FINAL
     Fixes the last issue where the meta row/arrow could overlap the service name.
     Scoped only to mixed-service-rail.
     ========================================================= */

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
    grid-row: 2 !important;
    grid-column: 1 !important;

    width: 100% !important;
    height: 154px !important;
    min-height: 154px !important;
    max-height: 154px !important;

    padding: 12px 12px 12px !important;
    margin: 0 !important;
    box-sizing: border-box !important;

    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    grid-template-rows: 52px 28px 38px !important;
    row-gap: 6px !important;
    column-gap: 0 !important;
    align-content: stretch !important;
    justify-content: stretch !important;

    overflow: hidden !important;
    position: relative !important;
    background: linear-gradient(180deg, rgba(255,255,255,.99), rgba(255,255,255,.95)) !important;
    border: 0 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body > div:first-child,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body > div:first-child {
    grid-row: 1 !important;
    grid-column: 1 !important;

    width: 100% !important;
    height: 52px !important;
    min-height: 52px !important;
    max-height: 52px !important;

    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;

    display: flex !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;

    overflow: hidden !important;
    position: relative !important;
    z-index: 1 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card h4 {
    margin: 0 !important;
    padding: 0 !important;
    color: #111827 !important;

    font-size: 13.5px !important;
    line-height: 1.08 !important;
    letter-spacing: -.032em !important;
    font-weight: 950 !important;
    text-align: left !important;

    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: normal !important;
    overflow-wrap: anywhere !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
    grid-row: 2 !important;
    grid-column: 1 !important;

    align-self: center !important;
    justify-self: stretch !important;

    position: relative !important;
    z-index: 2 !important;

    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;

    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;

    margin: 0 !important;
    padding: 0 31px 0 0 !important;
    box-sizing: border-box !important;

    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;

    overflow: hidden !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-reserve,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-reserve {
    grid-row: 3 !important;
    grid-column: 1 !important;

    align-self: end !important;
    justify-self: stretch !important;

    position: static !important;
    inset: auto !important;
    transform: none !important;

    width: 100% !important;
    height: 38px !important;
    min-height: 38px !important;
    max-height: 38px !important;

    margin: 0 !important;
    padding: 0 12px !important;
    box-sizing: border-box !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 13px !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    z-index: 1 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
    position: absolute !important;
    right: 0 !important;
    top: 50% !important;
    left: auto !important;
    bottom: auto !important;
    transform: translateY(-50%) !important;
    z-index: 3 !important;
  }

  @media (max-width: 920px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
      height: 150px !important;
      min-height: 150px !important;
      max-height: 150px !important;
      grid-template-rows: 48px 28px 38px !important;
      row-gap: 6px !important;
      padding: 11px 12px 12px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body > div:first-child,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body > div:first-child {
      height: 48px !important;
      min-height: 48px !important;
      max-height: 48px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
      padding-right: 30px !important;
      gap: 5px !important;
    }
  }


  /* =========================================================
     BP HYBRID META BASELINE FIX — TRUE FINAL
     Problem: in mixed-service-rail, icon cards were placing duration/price/arrow
     too high versus photo cards. This pins the meta row to the same bottom
     baseline for BOTH photo and icon cards, without moving the reserve button.
     ========================================================= */

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card {
    position: relative !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body {
    position: relative !important;
    overflow: hidden !important;
    padding: 12px !important;
    box-sizing: border-box !important;
  }

  /* Title stays in the upper content area and cannot collide with meta. */
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card > .event-package-body > div:first-child,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon > .event-package-body.no-photo-body > div:first-child {
    position: relative !important;
    z-index: 1 !important;
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    overflow: hidden !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Pin duration / price / arrow to a shared baseline above the button. */
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
    position: absolute !important;
    left: 12px !important;
    right: 12px !important;
    bottom: 50px !important;

    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;

    margin: 0 !important;
    padding: 0 32px 0 0 !important;
    box-sizing: border-box !important;

    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 6px !important;

    overflow: hidden !important;
    z-index: 3 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron) {
    height: 26px !important;
    min-height: 26px !important;
    max-height: 26px !important;
    box-sizing: border-box !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 999px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;

    line-height: 1 !important;
    font-weight: 950 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):first-child {
    flex: 0 0 52px !important;
    width: 52px !important;
    min-width: 52px !important;
    max-width: 52px !important;
    padding: 0 5px !important;
    font-size: 10px !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):nth-child(2) {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    max-width: 100% !important;
    padding: 0 8px !important;
    font-size: 9.8px !important;
    letter-spacing: -.025em !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
    position: absolute !important;
    right: 0 !important;
    top: 50% !important;
    left: auto !important;
    bottom: auto !important;
    transform: translateY(-50%) !important;

    width: 26px !important;
    min-width: 26px !important;
    max-width: 26px !important;
    height: 26px !important;
    min-height: 26px !important;
    max-height: 26px !important;

    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 999px !important;
    overflow: hidden !important;
    background: #f0fdfa !important;
    border: 1px solid #ccfbf1 !important;
    color: #00675f !important;
    font-size: 14px !important;
    line-height: 1 !important;
    z-index: 4 !important;
  }

  /* Keep reserve button fixed at the same bottom baseline. */
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-reserve,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-reserve {
    position: absolute !important;
    left: 12px !important;
    right: 12px !important;
    bottom: 12px !important;

    width: auto !important;
    height: 38px !important;
    min-height: 38px !important;
    max-height: 38px !important;

    margin: 0 !important;
    padding: 0 12px !important;
    box-sizing: border-box !important;

    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    border-radius: 13px !important;
    font-size: 12px !important;
    font-weight: 950 !important;
    line-height: 1 !important;
    z-index: 2 !important;
  }

  @media (max-width: 920px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
      bottom: 50px !important;
      left: 12px !important;
      right: 12px !important;
      padding-right: 30px !important;
      gap: 5px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):first-child {
      flex-basis: 50px !important;
      width: 50px !important;
      min-width: 50px !important;
      max-width: 50px !important;
      font-size: 9.6px !important;
      padding-left: 4px !important;
      padding-right: 4px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-meta span:not(.event-package-chevron):nth-child(2) {
      font-size: 9.2px !important;
      padding-left: 5px !important;
      padding-right: 5px !important;
      letter-spacing: -.035em !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card .event-package-chevron,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-chevron {
      width: 24px !important;
      min-width: 24px !important;
      max-width: 24px !important;
      height: 24px !important;
      min-height: 24px !important;
      max-height: 24px !important;
      font-size: 13px !important;
    }
  }


  /* =========================================================
     LOOP MOBILE SAFE CTA — ANDROID NAV SAFE / PHASE 1
     Scope: BusinessPage bottom actions only.
     Goal: keep Reservar / Favorito / Compartir above Android navigation buttons.
     Desktop and tablet remain visually untouched.
     ========================================================= */

  :root {
    --loop-mobile-system-nav-safe: max(env(safe-area-inset-bottom, 0px), 34px);
    --loop-mobile-cta-gap: 10px;
    --loop-mobile-cta-height: 58px;
  }

  @media (max-width: 767px) {
    .business-page-shell {
      padding-bottom: calc(
        var(--loop-mobile-cta-height) +
        var(--loop-mobile-system-nav-safe) +
        34px
      ) !important;
    }

    .bp-container {
      padding-bottom: calc(
        var(--loop-mobile-cta-height) +
        var(--loop-mobile-system-nav-safe) +
        18px
      ) !important;
    }

    .bp-actions {
      position: fixed !important;
      left: 12px !important;
      right: 12px !important;
      bottom: calc(var(--loop-mobile-system-nav-safe) + var(--loop-mobile-cta-gap)) !important;

      width: auto !important;
      max-width: 560px !important;
      margin: 0 auto !important;
      padding: 7px !important;

      display: grid !important;
      grid-template-columns: 44px minmax(0, 1fr) 44px !important;
      gap: 8px !important;
      align-items: center !important;

      border-radius: 22px !important;
      border: 1px solid rgba(226, 232, 240, .92) !important;
      background: rgba(255, 255, 255, .94) !important;
      box-shadow: 0 18px 44px rgba(15, 23, 42, .16) !important;
      backdrop-filter: blur(18px) !important;
      -webkit-backdrop-filter: blur(18px) !important;

      z-index: 28000 !important;
      transform: none !important;
      overflow: visible !important;
      box-sizing: border-box !important;
    }

    .bp-actions .bp-icon-btn {
      order: 1 !important;
      width: 44px !important;
      min-width: 44px !important;
      max-width: 44px !important;
      height: 44px !important;
      min-height: 44px !important;
      max-height: 44px !important;

      border-radius: 16px !important;
      background: #ffffff !important;
      border: 1px solid rgba(226, 232, 240, .96) !important;
      box-shadow: none !important;
      color: #0f766e !important;
      padding: 0 !important;
    }

    .bp-actions .bp-primary-btn {
      order: 2 !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      height: 44px !important;
      min-height: 44px !important;
      max-height: 44px !important;

      border-radius: 16px !important;
      padding: 0 16px !important;
      font-size: 14px !important;
      font-weight: 950 !important;
      letter-spacing: -.02em !important;
      box-shadow: 0 10px 22px rgba(0, 103, 95, .16) !important;
    }

    .bp-actions .bp-share-btn {
      order: 3 !important;
      display: inline-flex !important;
      width: 44px !important;
      min-width: 44px !important;
      max-width: 44px !important;
      height: 44px !important;
      min-height: 44px !important;
      max-height: 44px !important;

      border-radius: 16px !important;
      background: #ffffff !important;
      border: 1px solid rgba(226, 232, 240, .96) !important;
      box-shadow: none !important;
      color: #0f766e !important;
      padding: 0 !important;
    }

    .bp-actions .bp-share-btn span,
    .bp-actions .bp-secondary-btn:not(.bp-share-btn) {
      display: none !important;
    }

    .bp-actions .bp-icon-btn svg,
    .bp-actions .bp-primary-btn svg,
    .bp-actions .bp-share-btn svg {
      width: 19px !important;
      height: 19px !important;
      flex: 0 0 19px !important;
      margin: 0 !important;
    }

    .bp-actions .bp-primary-btn span {
      line-height: 1 !important;
    }

    .bp-final-cta {
      margin-bottom: calc(var(--loop-mobile-cta-height) + var(--loop-mobile-system-nav-safe) + 18px) !important;
    }
  }

  @media (max-width: 380px) {
    .bp-actions {
      left: 10px !important;
      right: 10px !important;
      grid-template-columns: 42px minmax(0, 1fr) 42px !important;
      gap: 7px !important;
      padding: 6px !important;
    }

    .bp-actions .bp-icon-btn,
    .bp-actions .bp-share-btn {
      width: 42px !important;
      min-width: 42px !important;
      max-width: 42px !important;
      height: 42px !important;
      min-height: 42px !important;
      max-height: 42px !important;
    }

    .bp-actions .bp-primary-btn {
      height: 42px !important;
      min-height: 42px !important;
      max-height: 42px !important;
      font-size: 13.5px !important;
      padding: 0 12px !important;
    }
  }


  /* =========================================================
     HOTFIX 37 — BUSINESS PAGE LOGO RATIO + MOBILE TOP GAP
     Base: BusinessPage(137).jsx. No frame/border added.
     - The logo keeps its natural ratio, so wide logos do not get huge.
     - Mobile removes the extra cream gap under the global header.
     ========================================================= */

  .bp-logo-card-raw,
  .bp-logo-stage-raw,
  .bp-logo-raw-img {
    border: 0 !important;
    outline: 0 !important;
  }

  .bp-logo-card-raw {
    width: 100% !important;
    min-width: 0 !important;
    overflow: visible !important;
  }

  .bp-logo-stage-raw {
    width: 100% !important;
    aspect-ratio: var(--bp-logo-ratio, 1.777) !important;
    max-height: clamp(230px, 25vw, 345px) !important;
    min-height: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    box-shadow: 0 18px 44px rgba(54, 41, 25, .065) !important;
    border-radius: 34px !important;
    overflow: hidden !important;
  }

  .bp-logo-stage-raw::before,
  .bp-logo-stage-raw::after {
    display: none !important;
    content: none !important;
  }

  .bp-logo-raw-img {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    display: block !important;
    margin: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    transform: none !important;
  }

  .bp-logo-shape-square .bp-logo-stage-raw,
  .bp-logo-shape-tall .bp-logo-stage-raw {
    width: min(100%, 430px) !important;
    margin-left: auto !important;
    margin-right: auto !important;
    max-height: 430px !important;
  }

  .bp-logo-shape-ultrawide .bp-logo-stage-raw {
    max-height: clamp(170px, 19vw, 270px) !important;
  }

  @media (min-width: 1181px) {
    .bp-logo-card-raw {
      max-width: 560px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-logo-stage-raw {
      max-height: 252px !important;
      border-radius: 30px !important;
    }

    .bp-logo-raw-img {
      border-radius: 30px !important;
    }
  }

  @media (max-width: 767px) {
    .business-page-shell {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }

    .bp-container {
      padding-top: 6px !important;
    }

    .bp-hero {
      padding-top: 0 !important;
      gap: 12px !important;
    }

    .bp-hero-top {
      margin-top: 0 !important;
      margin-bottom: 12px !important;
    }

    .bp-logo-card-raw {
      margin-top: 0 !important;
    }

    .bp-logo-stage-raw {
      max-height: min(52vw, 220px) !important;
      border-radius: 24px !important;
      box-shadow: 0 10px 24px rgba(54, 41, 25, .055) !important;
    }

    .bp-logo-raw-img {
      border-radius: 24px !important;
    }

    .bp-logo-shape-ultrawide .bp-logo-stage-raw {
      max-height: min(38vw, 168px) !important;
    }

    .bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-shape-tall .bp-logo-stage-raw {
      max-height: min(62vw, 260px) !important;
    }
  }

  @media (max-width: 420px) {
    .bp-container {
      padding-top: 2px !important;
    }

    .bp-logo-stage-raw {
      max-height: min(50vw, 200px) !important;
    }
  }


  /* =========================================================
     HOTFIX 38 — BUSINESS PAGE SMOOTH HERO BREAKPOINT
     Fixes the awkward Note/tablet state where the logo becomes
     a full-width banner below the title. Keeps the desktop and
     mobile directions, but adds a clean two-column middle state.
     ========================================================= */

  @media (min-width: 768px) and (max-width: 1180px) {
    .business-page-shell {
      padding-top: 0 !important;
    }

    .bp-container {
      padding-top: clamp(10px, 1.4vw, 18px) !important;
    }

    .bp-hero {
      display: grid !important;
      grid-template-columns: minmax(300px, .92fr) minmax(300px, min(48vw, 540px)) !important;
      grid-template-areas:
        "title logo"
        "info info" !important;
      column-gap: clamp(24px, 4vw, 52px) !important;
      row-gap: 24px !important;
      align-items: center !important;
      overflow: visible !important;
    }

    .bp-hero-top {
      grid-area: title !important;
      grid-column: auto !important;
      grid-row: auto !important;
      display: block !important;
      margin: 0 !important;
      align-self: center !important;
      position: relative !important;
      z-index: 3 !important;
    }

    .bp-title-wrap {
      max-width: 560px !important;
    }

    .bp-title {
      font-size: clamp(38px, 5vw, 56px) !important;
      line-height: .94 !important;
      letter-spacing: -.078em !important;
    }

    .bp-rating {
      margin-top: 12px !important;
    }

    .bp-actions {
      position: static !important;
      transform: none !important;
      width: auto !important;
      display: flex !important;
      grid-template-columns: none !important;
      justify-content: flex-start !important;
      align-items: center !important;
      gap: 12px !important;
      margin-top: 22px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-logo-card-raw {
      grid-area: logo !important;
      grid-column: auto !important;
      grid-row: auto !important;
      width: 100% !important;
      max-width: min(48vw, 540px) !important;
      justify-self: end !important;
      align-self: center !important;
      margin: 0 !important;
    }

    .bp-logo-stage-raw {
      width: 100% !important;
      aspect-ratio: 16 / 9 !important;
      max-height: clamp(210px, 29vw, 315px) !important;
      min-height: 0 !important;
      border: 0 !important;
      outline: 0 !important;
      border-radius: 30px !important;
      overflow: hidden !important;
      background: transparent !important;
      box-shadow: 0 16px 38px rgba(54, 41, 25, .065) !important;
    }

    .bp-logo-shape-ultrawide .bp-logo-stage-raw,
    .bp-logo-shape-wide .bp-logo-stage-raw,
    .bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-shape-tall .bp-logo-stage-raw {
      width: 100% !important;
      max-width: 100% !important;
      aspect-ratio: 16 / 9 !important;
      max-height: clamp(210px, 29vw, 315px) !important;
      margin: 0 !important;
    }

    .bp-logo-raw-img {
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      object-fit: contain !important;
      object-position: center center !important;
      border: 0 !important;
      outline: 0 !important;
      border-radius: 30px !important;
      transform: none !important;
      background: transparent !important;
    }

    .bp-info-panel {
      grid-area: info !important;
      grid-column: auto !important;
      grid-row: auto !important;
      margin-top: 0 !important;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      overflow: visible !important;
    }

    .bp-info-row {
      min-height: 104px !important;
      border: 1px solid rgba(226,232,240,.82) !important;
      border-radius: 24px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 12px 28px rgba(54,41,25,.055) !important;
      padding: 16px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 900px) {
    .bp-hero {
      grid-template-columns: minmax(280px, .88fr) minmax(270px, min(46vw, 430px)) !important;
      column-gap: 22px !important;
    }

    .bp-logo-card-raw {
      max-width: min(46vw, 430px) !important;
    }

    .bp-logo-stage-raw,
    .bp-logo-shape-ultrawide .bp-logo-stage-raw,
    .bp-logo-shape-wide .bp-logo-stage-raw,
    .bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-shape-tall .bp-logo-stage-raw {
      max-height: 235px !important;
    }
  }

  @media (max-width: 767px) {
    .business-page-shell {
      padding-top: 0 !important;
      margin-top: 0 !important;
    }

    .bp-container {
      padding-top: 0 !important;
    }

    .bp-hero {
      display: block !important;
      padding-top: 0 !important;
      margin-top: 0 !important;
    }

    .bp-hero-top {
      margin-top: 0 !important;
      margin-bottom: 14px !important;
    }

    .bp-logo-stage-raw,
    .bp-logo-shape-ultrawide .bp-logo-stage-raw,
    .bp-logo-shape-wide .bp-logo-stage-raw,
    .bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-shape-tall .bp-logo-stage-raw {
      width: 100% !important;
      aspect-ratio: 16 / 9 !important;
      max-height: min(44vw, 196px) !important;
      border: 0 !important;
      outline: 0 !important;
      border-radius: 24px !important;
      overflow: hidden !important;
      background: transparent !important;
    }

    .bp-logo-raw-img {
      width: 100% !important;
      height: 100% !important;
      object-fit: contain !important;
      object-position: center center !important;
      border: 0 !important;
      outline: 0 !important;
      border-radius: 24px !important;
      transform: none !important;
    }
  }


  /* =========================================================
     HOTFIX 39 — BUSINESS PAGE TRUE CONTAIN LOGO / NO CROP
     Fixes phone + narrow tablet sizes where older .bp-logo-stage img
     rules were still forcing cover/min-size and cutting wide logos.
     ========================================================= */

  .bp-logo-card-raw,
  .bp-logo-card-raw.bp-logo-shape-unknown,
  .bp-logo-card-raw.bp-logo-shape-ultrawide,
  .bp-logo-card-raw.bp-logo-shape-wide,
  .bp-logo-card-raw.bp-logo-shape-square,
  .bp-logo-card-raw.bp-logo-shape-tall {
    overflow: visible !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  .bp-logo-stage.bp-logo-stage-raw,
  .bp-logo-card-raw .bp-logo-stage.bp-logo-stage-raw,
  .bp-logo-card-raw.bp-logo-shape-unknown .bp-logo-stage-raw,
  .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw,
  .bp-logo-card-raw.bp-logo-shape-wide .bp-logo-stage-raw,
  .bp-logo-card-raw.bp-logo-shape-square .bp-logo-stage-raw,
  .bp-logo-card-raw.bp-logo-shape-tall .bp-logo-stage-raw {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    border: 0 !important;
    outline: 0 !important;
  }

  .bp-logo-stage.bp-logo-stage-raw > img.bp-logo-raw-img,
  .bp-logo-card-raw .bp-logo-stage.bp-logo-stage-raw > img.bp-logo-raw-img,
  .bp-logo-card-raw .bp-logo-stage-raw img.bp-logo-raw-img,
  .bp-logo-stage.bp-logo-stage-raw img.bp-logo-raw-img {
    width: 100% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    transform: none !important;
    scale: 1 !important;
    margin: 0 auto !important;
    display: block !important;
    border: 0 !important;
    outline: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-logo-stage.bp-logo-stage-raw,
    .bp-logo-card-raw .bp-logo-stage.bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-unknown .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-wide .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-tall .bp-logo-stage-raw {
      width: 100% !important;
      aspect-ratio: var(--bp-logo-ratio, 1.777) !important;
      height: auto !important;
      min-height: 0 !important;
      max-height: clamp(190px, 28vw, 285px) !important;
      border-radius: 28px !important;
    }

    .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw {
      aspect-ratio: var(--bp-logo-ratio, 2.65) !important;
      max-height: clamp(150px, 22vw, 235px) !important;
    }
  }

  @media (max-width: 767px) {
    .business-page-shell {
      padding-top: 0 !important;
      margin-top: 0 !important;
    }

    .bp-container {
      padding-top: 0 !important;
    }

    .bp-hero {
      padding-top: 0 !important;
      margin-top: 0 !important;
      gap: 12px !important;
    }

    .bp-logo-card-raw {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
    }

    .bp-logo-stage.bp-logo-stage-raw,
    .bp-logo-card-raw .bp-logo-stage.bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-unknown .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-wide .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-tall .bp-logo-stage-raw {
      width: 100% !important;
      aspect-ratio: var(--bp-logo-ratio, 1.777) !important;
      height: auto !important;
      min-height: 0 !important;
      max-height: min(54vw, 224px) !important;
      border-radius: 24px !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.045) !important;
      background: transparent !important;
      border: 0 !important;
      outline: 0 !important;
    }

    .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw {
      aspect-ratio: var(--bp-logo-ratio, 2.65) !important;
      max-height: min(42vw, 188px) !important;
    }

    .bp-logo-stage.bp-logo-stage-raw > img.bp-logo-raw-img,
    .bp-logo-card-raw .bp-logo-stage.bp-logo-stage-raw > img.bp-logo-raw-img,
    .bp-logo-card-raw .bp-logo-stage-raw img.bp-logo-raw-img,
    .bp-logo-stage.bp-logo-stage-raw img.bp-logo-raw-img {
      border-radius: 24px !important;
    }
  }

  @media (max-width: 420px) {
    .bp-logo-stage.bp-logo-stage-raw,
    .bp-logo-card-raw .bp-logo-stage.bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-unknown .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-wide .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-square .bp-logo-stage-raw,
    .bp-logo-card-raw.bp-logo-shape-tall .bp-logo-stage-raw {
      max-height: min(56vw, 220px) !important;
    }

    .bp-logo-card-raw.bp-logo-shape-ultrawide .bp-logo-stage-raw {
      max-height: min(44vw, 184px) !important;
    }
  }


  /* =========================================================
     HOTFIX 40 — BUSINESS PAGE CONTACT PANEL POLISH
     - Contact cards become compact/stable across tablet/mobile.
     - Panama phones display as +507 ####-#### or ####-####.
     - Prevents oversized bubbles and ugly wrapping.
     ========================================================= */

  .bp-info-panel {
    width: 100% !important;
    min-width: 0 !important;
  }

  .bp-info-row {
    min-width: 0 !important;
    overflow: hidden !important;
  }

  .bp-info-content {
    min-width: 0 !important;
    overflow: hidden !important;
  }

  .bp-info-value,
  .bp-info-value-primary,
  .bp-contact-number {
    max-width: 100% !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .bp-contact-number {
    white-space: nowrap !important;
    font-variant-numeric: tabular-nums !important;
    letter-spacing: -.015em !important;
  }

  .bp-contact-actions,
  .bp-social-actions {
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 7px !important;
    min-width: 0 !important;
  }

  .bp-contact-action,
  .bp-maps-link,
  .bp-maps-link-premium {
    white-space: nowrap !important;
    flex: 0 0 auto !important;
  }

  @media (min-width: 921px) {
    .bp-info-panel {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 10px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 78px !important;
      padding: 14px 16px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      border-radius: 22px !important;
      background: rgba(255,255,255,.84) !important;
      box-shadow: 0 10px 26px rgba(54,41,25,.045) !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 12px !important;
    }

    .bp-info-row:first-child,
    .bp-info-row:last-child {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      flex: 0 0 38px !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13px !important;
      line-height: 1.22 !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      min-height: 29px !important;
      padding: 0 10px !important;
      font-size: 11px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 920px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 10px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    .bp-info-row {
      min-height: 84px !important;
      padding: 13px 14px !important;
      border: 1px solid rgba(226,232,240,.82) !important;
      border-radius: 22px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.045) !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 11px !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13px !important;
      line-height: 1.24 !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      min-height: 29px !important;
      padding: 0 10px !important;
      font-size: 11px !important;
    }
  }

  @media (max-width: 767px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 10px !important;
      padding: 0 !important;
      margin-top: 14px !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    .bp-info-row {
      min-height: 74px !important;
      padding: 13px 14px !important;
      border: 1px solid rgba(226,232,240,.82) !important;
      border-radius: 20px !important;
      background: rgba(255,255,255,.9) !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.04) !important;
      grid-template-columns: 36px minmax(0, 1fr) !important;
      gap: 11px !important;
    }

    .bp-info-icon {
      width: 36px !important;
      height: 36px !important;
    }

    .bp-info-label {
      display: none !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13px !important;
      line-height: 1.22 !important;
    }

    .bp-contact-actions {
      margin-top: 7px !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      min-height: 28px !important;
      padding: 0 10px !important;
      font-size: 11px !important;
    }
  }

  @media (max-width: 390px) {
    .bp-info-row {
      min-height: 70px !important;
      padding: 12px !important;
      border-radius: 18px !important;
    }

    .bp-info-icon {
      width: 34px !important;
      height: 34px !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium {
      padding: 0 9px !important;
      font-size: 10.5px !important;
    }
  }


  /* =========================================================
     HOTFIX 41 — BUSINESS PAGE INFO STRIP RESTORE
     Restore the premium unified info bar on web and prevent
     tablet/mobile contact cards from becoming tall broken columns.
     ========================================================= */

  .bp-info-panel,
  .bp-info-panel * {
    box-sizing: border-box !important;
  }

  .bp-info-row::before {
    display: none !important;
    content: none !important;
  }

  .bp-info-content {
    min-width: 0 !important;
    overflow: hidden !important;
  }

  .bp-contact-number {
    font-variant-numeric: tabular-nums !important;
    letter-spacing: -.018em !important;
  }

  .bp-contact-actions,
  .bp-social-actions {
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 7px !important;
    min-width: 0 !important;
  }

  .bp-contact-action,
  .bp-maps-link,
  .bp-maps-link-premium,
  .bp-inline-link,
  .bp-info-main-action,
  .bp-hours-toggle {
    white-space: nowrap !important;
    width: fit-content !important;
    max-width: 100% !important;
  }

  @media (min-width: 1024px) {
    .bp-info-panel {
      grid-column: 1 / -1 !important;
      display: grid !important;
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 0 !important;
      margin-top: 24px !important;
      padding: 0 22px !important;
      border-radius: 26px !important;
      overflow: hidden !important;
      background: rgba(255,255,255,.84) !important;
      border: 1px solid rgba(108,88,61,.11) !important;
      box-shadow: 0 16px 42px rgba(54,41,25,.065) !important;
      backdrop-filter: blur(14px) !important;
      -webkit-backdrop-filter: blur(14px) !important;
    }

    .bp-info-row {
      min-height: 86px !important;
      padding: 15px 18px !important;
      border: 0 !important;
      border-right: 1px solid rgba(108,88,61,.10) !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      display: grid !important;
      grid-template-columns: 40px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 13px !important;
      overflow: hidden !important;
    }

    .bp-info-row:first-child {
      padding-left: 0 !important;
    }

    .bp-info-row:last-child,
    .bp-info-row:nth-child(4n) {
      border-right: 0 !important;
      padding-right: 0 !important;
    }

    .bp-info-icon {
      width: 40px !important;
      height: 40px !important;
      min-width: 40px !important;
      border-radius: 999px !important;
      background: #eaf8f4 !important;
      color: #00796f !important;
      box-shadow: none !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13.5px !important;
      line-height: 1.28 !important;
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .bp-contact-number {
      white-space: nowrap !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium,
    .bp-inline-link,
    .bp-info-main-action,
    .bp-hours-toggle {
      min-height: 30px !important;
      padding: 0 11px !important;
      border-radius: 999px !important;
      font-size: 11.5px !important;
      line-height: 1 !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
      margin-top: 16px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-row {
      min-height: 92px !important;
      padding: 14px 15px !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      border-radius: 22px !important;
      background: rgba(255,255,255,.86) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.045) !important;
      display: grid !important;
      grid-template-columns: 39px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 11px !important;
      overflow: hidden !important;
    }

    .bp-info-icon {
      width: 39px !important;
      height: 39px !important;
      min-width: 39px !important;
      border-radius: 999px !important;
      background: #eaf8f4 !important;
      color: #00796f !important;
      box-shadow: none !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13px !important;
      line-height: 1.28 !important;
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .bp-contact-number {
      white-space: nowrap !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium,
    .bp-inline-link,
    .bp-info-main-action,
    .bp-hours-toggle {
      min-height: 29px !important;
      padding: 0 10px !important;
      font-size: 11px !important;
      line-height: 1 !important;
    }
  }

  @media (min-width: 560px) and (max-width: 767px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 10px !important;
      margin-top: 14px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    .bp-info-row {
      min-height: 86px !important;
      padding: 13px 13px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      border-radius: 20px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.04) !important;
      display: grid !important;
      grid-template-columns: 36px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 10px !important;
      overflow: hidden !important;
    }

    .bp-info-icon {
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      border-radius: 999px !important;
    }

    .bp-info-label {
      display: none !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 12.5px !important;
      line-height: 1.25 !important;
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .bp-contact-number {
      white-space: nowrap !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium,
    .bp-inline-link,
    .bp-info-main-action,
    .bp-hours-toggle {
      min-height: 27px !important;
      padding: 0 9px !important;
      font-size: 10.5px !important;
    }
  }

  @media (max-width: 559px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 9px !important;
      margin-top: 13px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    .bp-info-row {
      min-height: 72px !important;
      padding: 12px 13px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      border-radius: 19px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 7px 18px rgba(54,41,25,.038) !important;
      display: grid !important;
      grid-template-columns: 35px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 10px !important;
      overflow: hidden !important;
    }

    .bp-info-icon {
      width: 35px !important;
      height: 35px !important;
      min-width: 35px !important;
      border-radius: 999px !important;
    }

    .bp-info-label {
      display: none !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number {
      font-size: 13px !important;
      line-height: 1.25 !important;
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .bp-contact-number {
      white-space: nowrap !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 6px !important;
      gap: 6px !important;
    }

    .bp-contact-action,
    .bp-maps-link,
    .bp-maps-link-premium,
    .bp-inline-link,
    .bp-info-main-action,
    .bp-hours-toggle {
      min-height: 27px !important;
      padding: 0 9px !important;
      font-size: 10.8px !important;
    }
  }



  /* =========================================================
     HOTFIX 43 — BUSINESS PAGE MOBILE HEADER SPACER COLLAPSE
     Console confirmed:
     - fixed header: 0 → 58px
     - .mainmenu-header-only-root spacer: 58 → 116px
     - .business-page-shell starts at 116px
     We collapse only the spacer; fixed header remains visible.
     ========================================================= */
  @media (max-width: 920px) {
    .mainmenu-header-only-root {
      height: 0 !important;
      min-height: 0 !important;
      max-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      overflow: visible !important;
      background: transparent !important;
    }

    .mainmenu-header-only-root > header,
    .mainmenu-header-only-root .loop-mobile-header-v2,
    header.loop-mobile-header-v2.loop-header-emerald-hardlock {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      height: 58px !important;
      min-height: 58px !important;
      max-height: 58px !important;
      z-index: 29000 !important;
    }

    .business-page-shell {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
  }


  /* =========================================================
     HOTFIX 44 — BUSINESS PAGE INFO PANEL STABLE RESPONSIVE
     - Keeps the fixed-header spacer collapse from v43.
     - Forces contact/info panel to span the full hero width on
       tablet/intermediate sizes.
     - Desktop remains a unified premium strip.
     - Tablet becomes two clean cards per row.
     - Mobile becomes one clean card per row.
     ========================================================= */

  @media (min-width: 1181px) {
    .bp-hero {
      grid-template-areas:
        "title logo"
        "info info" !important;
    }

    .bp-info-panel {
      grid-area: info !important;
      grid-column: 1 / -1 !important;
      width: 100% !important;
      max-width: 100% !important;
      justify-self: stretch !important;
      display: grid !important;
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 0 !important;
      margin-top: 26px !important;
      padding: 0 22px !important;
      border-radius: 26px !important;
      overflow: hidden !important;
      background: rgba(255,255,255,.84) !important;
      border: 1px solid rgba(108,88,61,.11) !important;
      box-shadow: 0 16px 42px rgba(54,41,25,.065) !important;
      backdrop-filter: blur(14px) !important;
      -webkit-backdrop-filter: blur(14px) !important;
    }

    .bp-info-row {
      min-width: 0 !important;
      min-height: 82px !important;
      padding: 0 20px !important;
      border: 0 !important;
      border-right: 1px solid rgba(108,88,61,.10) !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      display: grid !important;
      grid-template-columns: 40px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 13px !important;
      overflow: hidden !important;
    }

    .bp-info-row:first-child { padding-left: 0 !important; }
    .bp-info-row:last-child,
    .bp-info-row:nth-child(4n) {
      border-right: 0 !important;
      padding-right: 0 !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .bp-hero {
      display: grid !important;
      grid-template-areas:
        "title logo"
        "info info" !important;
      grid-template-columns: minmax(280px, .92fr) minmax(300px, 1fr) !important;
      column-gap: clamp(20px, 3.5vw, 44px) !important;
      row-gap: 18px !important;
      align-items: center !important;
      overflow: visible !important;
    }

    .bp-hero-top {
      grid-area: title !important;
      grid-column: auto !important;
      grid-row: auto !important;
      min-width: 0 !important;
      margin: 0 !important;
    }

    .bp-logo-card-raw,
    .bp-logo-card {
      grid-area: logo !important;
      grid-column: auto !important;
      grid-row: auto !important;
      justify-self: end !important;
      width: 100% !important;
      max-width: min(48vw, 540px) !important;
      margin: 0 !important;
    }

    .bp-hero-grid {
      display: contents !important;
    }

    .bp-info-panel {
      grid-area: info !important;
      grid-column: 1 / -1 !important;
      grid-row: auto !important;
      width: 100% !important;
      max-width: 100% !important;
      justify-self: stretch !important;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 12px !important;
      margin-top: 6px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      overflow: visible !important;
    }

    .bp-info-row {
      min-width: 0 !important;
      width: 100% !important;
      min-height: 88px !important;
      padding: 14px 16px !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      border-radius: 22px !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 10px 24px rgba(54,41,25,.045) !important;
      display: grid !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 11px !important;
      overflow: hidden !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 860px) {
    .bp-hero {
      grid-template-columns: minmax(250px, .86fr) minmax(280px, 1fr) !important;
      column-gap: 18px !important;
    }

    .bp-title {
      font-size: clamp(34px, 5.2vw, 46px) !important;
    }

    .bp-logo-card-raw,
    .bp-logo-card {
      max-width: min(49vw, 430px) !important;
    }
  }

  @media (max-width: 767px) {
    .bp-info-panel {
      grid-column: auto !important;
      width: 100% !important;
      max-width: 100% !important;
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 10px !important;
      margin-top: 14px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      overflow: visible !important;
    }

    .bp-info-row {
      min-height: 72px !important;
      padding: 12px 13px !important;
      border-radius: 19px !important;
      border: 1px solid rgba(108,88,61,.09) !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 7px 18px rgba(54,41,25,.038) !important;
      display: grid !important;
      grid-template-columns: 35px minmax(0,1fr) !important;
      gap: 10px !important;
      overflow: hidden !important;
    }
  }

  @media (max-width: 640px) {
    .bp-info-panel {
      grid-template-columns: 1fr !important;
    }

    .bp-contact-number,
    .bp-info-value,
    .bp-info-value-primary {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: clip !important;
    }
  }


  /* =========================================================
     HOTFIX V54A — BOOKING MODAL MOBILE HEADER PREMIUM
     Keeps desktop stable while replacing the plain mobile modal
     topbar with a branded Loop-style reservation header.
     ========================================================= */
  .business-booking-topbar {
    min-height: 68px;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(226,232,240,.92);
    background:
      radial-gradient(circle at 0% 0%, rgba(15,118,110,.075), transparent 38%),
      rgba(255,255,255,.96);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex: 0 0 auto;
  }

  .business-booking-brand {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .business-booking-brand-logo {
    width: 44px;
    height: 44px;
    border-radius: 15px;
    overflow: hidden;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    box-shadow: 0 10px 22px rgba(15,23,42,.06);
  }

  .business-booking-brand-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .business-booking-brand-logo span {
    color: #0f766e;
    font-size: 14px;
    font-weight: 950;
  }

  .business-booking-brand-copy {
    min-width: 0;
    display: grid;
    gap: 1px;
  }

  .business-booking-eyebrow {
    color: #007467;
    font-size: 10px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .12em;
  }

  .business-booking-title {
    color: #0f172a;
    font-size: 18px;
    line-height: 1.04;
    font-weight: 950;
    letter-spacing: -.04em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .business-booking-subtitle {
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.15;
  }

  .business-booking-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .business-booking-icon-btn,
  .business-booking-close-btn {
    height: 44px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #0f172a;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(15,23,42,.055);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .business-booking-icon-btn {
    width: 44px;
    border-radius: 16px;
  }

  .business-booking-close-btn {
    border-radius: 16px;
    gap: 7px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 950;
  }

  @media (max-width: 640px) {
    .business-booking-topbar {
      min-height: 82px;
      padding: calc(10px + env(safe-area-inset-top, 0px)) 10px 10px;
      align-items: center;
    }

    .business-booking-brand-logo {
      width: 42px;
      height: 42px;
      border-radius: 14px;
    }

    .business-booking-title {
      font-size: clamp(20px, 6vw, 25px);
      max-width: calc(100vw - 178px);
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .business-booking-subtitle {
      font-size: 11px;
    }

    .business-booking-eyebrow {
      font-size: 9px;
    }

    .business-booking-icon-btn,
    .business-booking-close-btn {
      width: 42px;
      height: 42px;
      border-radius: 15px;
      padding: 0;
    }

    .business-booking-close-btn span {
      display: none;
    }
  }

  /* LOOP LIVE RATINGS FINAL OVERRIDES */
  .bp-reviews-list > .bp-rating-card {
    margin: 0 !important;
    border-radius: 20px !important;
    background: rgba(255,255,255,.92) !important;
    border: 1px solid var(--bp-line-soft, rgba(226,232,240,.92)) !important;
    box-shadow: 0 14px 34px rgba(54,41,25,.07) !important;
  }

  /* =========================================================
     LOOP BUSINESS PAGE — PUBLIC CONTACT MOBILE V2
     Compact, customer-friendly contact cards for the public profile.
     Keeps desktop/tablet premium strip intact, but makes mobile less tall.
     ========================================================= */

  .bp-info-panel-title {
    display: none !important;
  }

  @media (max-width: 767px) {
    .bp-info-panel {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
      margin-top: 12px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    .bp-info-panel-title {
      display: grid !important;
      gap: 3px !important;
      padding: 0 3px 2px !important;
    }

    .bp-info-panel-title strong {
      color: #111827 !important;
      font-size: 17px !important;
      line-height: 1 !important;
      letter-spacing: -.045em !important;
      font-weight: 950 !important;
    }

    .bp-info-panel-title span {
      color: #64748b !important;
      font-size: 11.5px !important;
      line-height: 1.2 !important;
      font-weight: 760 !important;
    }

    .bp-info-row {
      min-height: 68px !important;
      padding: 10px 12px !important;
      border-radius: 20px !important;
      border: 1px solid rgba(108,88,61,.08) !important;
      background: rgba(255,255,255,.88) !important;
      box-shadow: 0 8px 20px rgba(54,41,25,.042) !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 10px !important;
      align-items: center !important;
    }

    .bp-info-row::before {
      background: radial-gradient(circle at 0% 0%, rgba(0,103,95,.045), transparent 48%) !important;
      opacity: .82 !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
      border-radius: 999px !important;
      background: #eaf8f4 !important;
      color: #00796f !important;
      box-shadow: inset 0 0 0 1px rgba(0,103,95,.05) !important;
    }

    .bp-info-icon svg {
      width: 17px !important;
      height: 17px !important;
    }

    .bp-info-content {
      min-width: 0 !important;
      display: grid !important;
      gap: 4px !important;
      align-content: center !important;
    }

    .bp-info-label {
      display: block !important;
      margin: 0 !important;
      color: #667085 !important;
      font-size: 10px !important;
      line-height: 1 !important;
      letter-spacing: .04em !important;
      text-transform: uppercase !important;
      font-weight: 950 !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number,
    .bp-social-row .bp-info-value a {
      color: #111827 !important;
      font-size: 13px !important;
      line-height: 1.18 !important;
      letter-spacing: -.018em !important;
      font-weight: 900 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .bp-location-row .bp-info-value-primary {
      max-width: 100% !important;
    }

    .bp-info-main-action,
    .bp-inline-link,
    .bp-hours-toggle,
    .bp-contact-action {
      width: fit-content !important;
      min-height: 27px !important;
      height: 27px !important;
      padding: 0 9px !important;
      border-radius: 999px !important;
      font-size: 10.5px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 2px !important;
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
      flex-wrap: wrap !important;
    }

    .bp-contact-action.whatsapp {
      background: #f0fdf4 !important;
      border-color: #bbf7d0 !important;
      color: #047857 !important;
    }

    .bp-social-row .bp-info-content {
      grid-template-columns: minmax(0, 1fr) auto !important;
      column-gap: 8px !important;
      row-gap: 3px !important;
      align-items: center !important;
    }

    .bp-social-row .bp-info-label,
    .bp-social-row .bp-info-value {
      grid-column: 1 !important;
    }

    .bp-social-actions {
      grid-column: 2 !important;
      grid-row: 1 / span 2 !important;
      margin-top: 0 !important;
      justify-self: end !important;
    }

    .bp-social-actions a {
      width: 30px !important;
      height: 30px !important;
      min-width: 30px !important;
      border-radius: 999px !important;
      background: #f0fdfa !important;
      border: 1px solid #ccfbf1 !important;
      color: #00675f !important;
      box-shadow: none !important;
    }

    .bp-social-actions a svg {
      width: 16px !important;
      height: 16px !important;
    }

    .bp-hours-row {
      min-height: 68px !important;
    }

    .bp-hours-summary {
      width: 100% !important;
      display: flex !important;
      align-items: center !important;
      gap: 7px !important;
    }

    .bp-hours-summary-label {
      color: #64748b !important;
      font-size: 12px !important;
      line-height: 1 !important;
      font-weight: 900 !important;
    }

    .bp-hours-summary strong {
      min-width: 0 !important;
      color: #111827 !important;
      font-size: 13px !important;
      line-height: 1.18 !important;
      font-weight: 950 !important;
      text-align: left !important;
      align-items: flex-start !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .bp-hours-toggle {
      margin-top: 4px !important;
      min-height: 24px !important;
      height: 24px !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      color: #00796f !important;
      box-shadow: none !important;
    }

    .bp-hours-week-premium {
      margin-top: 8px !important;
      border-radius: 14px !important;
    }

    .bp-hours-week-premium .bp-hours-day {
      padding: 7px 9px !important;
      font-size: 12px !important;
    }

    .bp-hours-week-premium .bp-hours-day strong {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: clip !important;
    }
  }


  /* =========================================================
     LOOP BUSINESS PAGE — PUBLIC CONTACT MOBILE V3 TIGHTEN
     - No duplicate Instagram action when Instagram is already the row target.
     - Align Redes row with the same icon/content grid as the other contact rows.
     - Reduce vertical whitespace between Ubicación, Contacto and Horario.
     ========================================================= */
  @media (max-width: 767px) {
    .bp-info-panel {
      gap: 8px !important;
    }

    .bp-info-row {
      min-height: 64px !important;
      padding: 10px 12px !important;
      border-radius: 20px !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 10px !important;
      align-items: center !important;
      box-shadow: 0 7px 18px rgba(54,41,25,.036) !important;
    }

    .bp-location-row,
    .bp-contact-row,
    .bp-hours-row,
    .bp-social-row {
      min-height: 64px !important;
    }

    .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      border-radius: 999px !important;
      align-self: center !important;
      justify-self: start !important;
    }

    .bp-info-icon svg {
      width: 16px !important;
      height: 16px !important;
    }

    .bp-info-content {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 3px !important;
      align-content: center !important;
      min-width: 0 !important;
    }

    .bp-info-label {
      font-size: 9.5px !important;
      line-height: 1 !important;
      margin: 0 !important;
      letter-spacing: .055em !important;
    }

    .bp-info-value,
    .bp-info-value-primary,
    .bp-contact-number,
    .bp-social-row .bp-info-value a {
      font-size: 12.5px !important;
      line-height: 1.14 !important;
    }

    .bp-info-main-action,
    .bp-inline-link,
    .bp-contact-action,
    .bp-hours-toggle {
      min-height: 24px !important;
      height: 24px !important;
      padding: 0 9px !important;
      font-size: 10.5px !important;
    }

    .bp-contact-actions,
    .bp-social-actions {
      margin-top: 1px !important;
      gap: 6px !important;
    }

    .bp-social-row .bp-info-content {
      grid-template-columns: minmax(0, 1fr) !important;
      column-gap: 0 !important;
      row-gap: 3px !important;
      align-items: center !important;
    }

    .bp-social-row .bp-info-label,
    .bp-social-row .bp-info-value,
    .bp-social-row .bp-social-actions {
      grid-column: 1 !important;
      grid-row: auto !important;
    }

    .bp-social-actions {
      justify-self: start !important;
      margin-top: 4px !important;
    }

    .bp-social-actions:empty {
      display: none !important;
    }

    .bp-social-actions a {
      width: 26px !important;
      height: 26px !important;
      min-width: 26px !important;
    }

    .bp-social-actions a svg {
      width: 14px !important;
      height: 14px !important;
    }

    .bp-hours-summary {
      gap: 6px !important;
      align-items: baseline !important;
    }

    .bp-hours-summary-label,
    .bp-hours-summary strong {
      font-size: 12.5px !important;
      line-height: 1.14 !important;
    }

    .bp-hours-toggle {
      margin-top: 2px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
    }

    .bp-info-panel-title {
      margin-bottom: 6px !important;
    }
  }


  /* =========================================================
     LOOP BUSINESS PAGE — PUBLIC CONTACT MOBILE V4 ICON PARITY
     - Forces Redes/Instagram to use the exact same icon bubble size,
       alignment and grid as Ubicación, Contacto and Horario.
     - Keeps the social row compact without adding a second Instagram icon.
     ========================================================= */
  @media (max-width: 767px) {
    .bp-info-panel .bp-info-row,
    .bp-info-panel .bp-location-row,
    .bp-info-panel .bp-contact-row,
    .bp-info-panel .bp-hours-row,
    .bp-info-panel .bp-social-row {
      display: grid !important;
      grid-template-columns: 38px minmax(0, 1fr) !important;
      gap: 10px !important;
      align-items: center !important;
      min-height: 64px !important;
      padding: 10px 12px !important;
      border-radius: 20px !important;
    }

    .bp-info-panel .bp-info-row .bp-info-icon,
    .bp-info-panel .bp-location-row .bp-info-icon,
    .bp-info-panel .bp-contact-row .bp-info-icon,
    .bp-info-panel .bp-hours-row .bp-info-icon,
    .bp-info-panel .bp-social-row .bp-info-icon {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
      min-height: 38px !important;
      max-width: 38px !important;
      max-height: 38px !important;
      flex: 0 0 38px !important;
      margin: 0 !important;
      padding: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      justify-self: start !important;
      align-self: center !important;
      box-sizing: border-box !important;
      border-radius: 999px !important;
      background: #eaf8f4 !important;
      color: #00796f !important;
      border: 1px solid rgba(0,103,95,.06) !important;
      box-shadow: inset 0 0 0 1px rgba(0,103,95,.035) !important;
    }

    .bp-info-panel .bp-info-row .bp-info-icon svg,
    .bp-info-panel .bp-location-row .bp-info-icon svg,
    .bp-info-panel .bp-contact-row .bp-info-icon svg,
    .bp-info-panel .bp-hours-row .bp-info-icon svg,
    .bp-info-panel .bp-social-row .bp-info-icon svg {
      width: 16px !important;
      height: 16px !important;
      min-width: 16px !important;
      min-height: 16px !important;
      margin: 0 !important;
      transform: none !important;
      stroke-width: 2.2 !important;
    }

    .bp-info-panel .bp-social-row .bp-info-content {
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 3px !important;
      align-content: center !important;
      justify-items: start !important;
    }

    .bp-info-panel .bp-social-row .bp-info-label,
    .bp-info-panel .bp-social-row .bp-info-value {
      grid-column: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
      text-align: left !important;
    }

    .bp-info-panel .bp-social-row .bp-info-value a {
      display: inline-block !important;
      max-width: 100% !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      text-align: left !important;
    }
  }



  /* =========================================================
     LOOP DEVICE FOOTER FIT V45
     Scope: BusinessPage sticky Reservar/Favorito/Compartir dock.
     Goal:
     - Android: keep dock above system navigation buttons.
     - iOS: use only the real safe-area inset, no fake footer.
     - Web/tablet: keep previous visual contract.
     ========================================================= */
  @media (max-width: 767px) {
    .business-page-shell {
      --loop-device-bottom-safe: env(safe-area-inset-bottom, 0px);
      --loop-mobile-system-nav-safe: var(--loop-device-bottom-safe);
      --loop-mobile-cta-gap: 10px;
      --loop-mobile-cta-height: 58px;

      padding-bottom: calc(
        var(--loop-mobile-cta-height) +
        var(--loop-device-bottom-safe) +
        28px
      ) !important;
      scroll-padding-bottom: calc(
        var(--loop-mobile-cta-height) +
        var(--loop-device-bottom-safe) +
        18px
      ) !important;
    }

    body.is-mobile-app.is-android-app .business-page-shell,
    html.is-android-app body.is-mobile-app .business-page-shell {
      --loop-device-bottom-safe: max(
        env(safe-area-inset-bottom, 0px),
        var(--safe-area-inset-bottom, 0px),
        var(--loop-safe-bottom, 0px)
      );
      --loop-mobile-system-nav-safe: var(--loop-device-bottom-safe);
    }

    body.is-mobile-app.is-ios-app .business-page-shell,
    html.is-ios-app body.is-mobile-app .business-page-shell {
      --loop-device-bottom-safe: env(safe-area-inset-bottom, 0px);
      --loop-mobile-system-nav-safe: var(--loop-device-bottom-safe);
    }

    .business-page-shell .bp-container {
      padding-bottom: calc(
        var(--loop-mobile-cta-height) +
        var(--loop-device-bottom-safe) +
        16px
      ) !important;
    }

    .business-page-shell .bp-actions {
      bottom: calc(var(--loop-device-bottom-safe) + var(--loop-mobile-cta-gap)) !important;
      transform: translateZ(0) !important;
    }

    .business-page-shell .bp-final-cta {
      margin-bottom: calc(
        var(--loop-mobile-cta-height) +
        var(--loop-device-bottom-safe) +
        14px
      ) !important;
    }

    .business-page-shell .business-booking-overlay {
      padding-bottom: calc(18px + var(--loop-device-bottom-safe)) !important;
    }

    .business-page-shell .business-booking-modal {
      max-height: calc(100dvh - 36px - var(--loop-device-bottom-safe)) !important;
    }
  }


  /* =========================================================
     LOOP BUSINESS PAGE — DEVICE SCROLL END STOP V46
     Fixes the large blank area between the final CTA/footer and
     the sticky bottom action dock.
     Contract:
     - The bottom dock floats above Android navigation buttons.
     - iOS only uses the real safe-area inset.
     - Content keeps only the minimum padding needed so the footer
       is not hidden behind the dock.
     ========================================================= */
  @media (max-width: 767px) {
    .business-page-shell {
      --loop-bp-device-bottom-safe: env(safe-area-inset-bottom, 0px) !important;
      --loop-bp-dock-gap: 8px !important;
      --loop-bp-dock-height: 58px !important;
      --loop-bp-content-end-pad: calc(
        var(--loop-bp-dock-height) +
        var(--loop-bp-device-bottom-safe) +
        var(--loop-bp-dock-gap) +
        8px
      ) !important;

      min-height: 0 !important;
      height: auto !important;
      max-height: none !important;
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
      scroll-padding-bottom: var(--loop-bp-content-end-pad) !important;
      overflow: visible !important;
    }

    body.is-mobile-app.is-android-app .business-page-shell,
    html.is-android-app body.is-mobile-app .business-page-shell {
      --loop-bp-device-bottom-safe: max(
        env(safe-area-inset-bottom, 0px),
        var(--safe-area-inset-bottom, 0px),
        var(--loop-safe-bottom, 0px)
      ) !important;
    }

    body.is-mobile-app.is-ios-app .business-page-shell,
    html.is-ios-app body.is-mobile-app .business-page-shell {
      --loop-bp-device-bottom-safe: env(safe-area-inset-bottom, 0px) !important;
    }

    .business-page-shell .bp-container {
      padding-bottom: var(--loop-bp-content-end-pad) !important;
      margin-bottom: 0 !important;
    }

    .business-page-shell .bp-page,
    .business-page-shell .bp-hero,
    .business-page-shell .bp-packages,
    .business-page-shell .bp-split-section,
    .business-page-shell .bp-section,
    .business-page-shell .bp-content-max {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
      min-height: 0 !important;
    }

    .business-page-shell .bp-final-cta {
      margin-top: 28px !important;
      margin-bottom: 10px !important;
    }

    .business-page-shell .bp-final-cta + div {
      padding-top: 8px !important;
      padding-bottom: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }

    .business-page-shell .bp-actions {
      position: fixed !important;
      left: 12px !important;
      right: 12px !important;
      bottom: calc(var(--loop-bp-device-bottom-safe) + var(--loop-bp-dock-gap)) !important;
      transform: translateZ(0) !important;
      z-index: 28000 !important;
    }

    .business-page-shell::after,
    .business-page-shell .bp-container::after,
    .business-page-shell .bp-page::after,
    .business-page-shell .bp-final-cta::after {
      content: none !important;
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .business-page-shell .business-booking-overlay {
      padding-bottom: calc(18px + var(--loop-bp-device-bottom-safe)) !important;
    }

    .business-page-shell .business-booking-modal {
      max-height: calc(100dvh - 36px - var(--loop-bp-device-bottom-safe)) !important;
    }
  }

  @media (max-width: 380px) {
    .business-page-shell {
      --loop-bp-dock-height: 54px !important;
      --loop-bp-dock-gap: 8px !important;
    }

    .business-page-shell .bp-actions {
      left: 10px !important;
      right: 10px !important;
    }
  }



  /* =========================================================
     LOOP BUSINESS PAGE — MOBILE REVIEWS POLISH V47
     Premium compact comments/ratings section for native/mobile.
     Keeps review data logic unchanged; only improves markup styling.
     ========================================================= */
  @media (max-width: 920px) {
    .bp-about-section:has(.bp-reviews-head) {
      margin-top: 28px !important;
      padding-top: 26px !important;
      border-top: 0 !important;
    }

    .bp-reviews-head {
      max-width: none !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      align-items: start !important;
      gap: 10px !important;
      margin: 0 0 14px !important;
    }

    .bp-reviews-head .bp-section-head {
      min-width: 0 !important;
      display: grid !important;
      gap: 10px !important;
    }

    .bp-reviews-head .bp-section-title {
      position: relative !important;
      max-width: 250px !important;
      margin: 0 !important;
      padding-left: 14px !important;
      color: #101828 !important;
      font-size: 29px !important;
      line-height: .94 !important;
      letter-spacing: -.072em !important;
      font-weight: 950 !important;
    }

    .bp-reviews-head .bp-section-title::before {
      content: "" !important;
      position: absolute !important;
      left: 0 !important;
      top: 5px !important;
      width: 5px !important;
      height: 34px !important;
      border-radius: 999px !important;
      background: linear-gradient(180deg, #00a693 0%, #00675f 100%) !important;
      box-shadow: 0 8px 18px rgba(0,103,95,.16) !important;
    }

    .bp-review-average {
      width: max-content !important;
      min-height: 34px !important;
      display: inline-flex !important;
      align-items: center !important;
      gap: 7px !important;
      border-radius: 999px !important;
      padding: 0 12px !important;
      background: #fff9e9 !important;
      border: 1px solid rgba(245,158,11,.22) !important;
      color: #00675f !important;
      box-shadow: 0 10px 22px rgba(54,41,25,.055) !important;
      font-size: 12px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      white-space: nowrap !important;
    }

    .bp-review-average svg {
      width: 15px !important;
      height: 15px !important;
      color: #f59e0b !important;
      flex: 0 0 auto !important;
    }

    .bp-head-link {
      align-self: start !important;
      min-height: 34px !important;
      border-radius: 999px !important;
      border: 1px solid rgba(0,103,95,.14) !important;
      background: rgba(255,255,255,.82) !important;
      color: #00675f !important;
      box-shadow: 0 10px 22px rgba(54,41,25,.055) !important;
      padding: 0 12px !important;
      font-size: 12px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      white-space: nowrap !important;
      text-decoration: none !important;
    }

    .bp-reviews-list,
    .bp-reviews-list.space-y-4 {
      max-width: none !important;
      display: grid !important;
      gap: 12px !important;
      margin: 0 !important;
    }

    .bp-reviews-list.space-y-4 > :not([hidden]) ~ :not([hidden]) {
      --tw-space-y-reverse: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }

    .bp-rating-card {
      min-height: 0 !important;
      border-radius: 22px !important;
      padding: 14px !important;
      background: rgba(255,255,255,.92) !important;
      border: 1px solid rgba(108,88,61,.10) !important;
      box-shadow: 0 16px 38px rgba(54,41,25,.075) !important;
      overflow: hidden !important;
    }

    .bp-rating-card-head {
      display: grid !important;
      grid-template-columns: 42px minmax(0, 1fr) auto !important;
      gap: 10px !important;
      align-items: center !important;
    }

    .bp-rating-avatar {
      width: 42px !important;
      height: 42px !important;
      border-radius: 999px !important;
      background: #ecfdf5 !important;
      color: #00675f !important;
      box-shadow: inset 0 0 0 1px rgba(0,103,95,.08) !important;
      font-size: 14px !important;
      font-weight: 950 !important;
    }

    .bp-rating-user {
      min-width: 0 !important;
      display: grid !important;
      gap: 4px !important;
    }

    .bp-rating-user-main {
      min-width: 0 !important;
      display: flex !important;
      align-items: center !important;
      gap: 7px !important;
    }

    .bp-rating-user strong {
      min-width: 0 !important;
      max-width: 100% !important;
      color: #101828 !important;
      font-size: 14px !important;
      line-height: 1.05 !important;
      letter-spacing: -.025em !important;
      font-weight: 950 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .bp-rating-user em {
      flex: 0 1 auto !important;
      max-width: 118px !important;
      min-height: 20px !important;
      display: inline-flex !important;
      align-items: center !important;
      border-radius: 999px !important;
      padding: 0 8px !important;
      background: #eaf8f4 !important;
      color: #00675f !important;
      font-size: 10.5px !important;
      line-height: 1 !important;
      font-style: normal !important;
      font-weight: 950 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .bp-rating-user span {
      color: #7b8492 !important;
      font-size: 11px !important;
      line-height: 1.15 !important;
      font-weight: 760 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .bp-rating-score {
      min-height: 32px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 5px !important;
      border-radius: 999px !important;
      border: 1px solid rgba(245,158,11,.22) !important;
      background: #fff8e7 !important;
      color: #d97706 !important;
      padding: 0 9px !important;
      box-shadow: none !important;
      font-size: 12px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      white-space: nowrap !important;
    }

    .bp-rating-score > div {
      display: none !important;
    }

    .bp-rating-score svg {
      width: 14px !important;
      height: 14px !important;
      flex: 0 0 auto !important;
      color: #f59e0b !important;
    }

    .bp-rating-comment {
      margin: 11px 0 0 !important;
      padding: 12px 13px !important;
      border-radius: 16px !important;
      background: #fffaf2 !important;
      border: 1px solid rgba(108,88,61,.07) !important;
      color: #334155 !important;
      font-size: 13px !important;
      line-height: 1.45 !important;
      font-weight: 760 !important;
      letter-spacing: -.01em !important;
      display: -webkit-box !important;
      -webkit-line-clamp: 3 !important;
      -webkit-box-orient: vertical !important;
      overflow: hidden !important;
    }

    .bp-rating-comment span[aria-hidden="true"] {
      color: #00675f !important;
      font-weight: 950 !important;
    }

    .bp-rating-comment.is-empty {
      background: #f8fafc !important;
      color: #94a3b8 !important;
      font-style: italic !important;
      font-weight: 760 !important;
    }

    .business-show-more-reviews {
      width: 100% !important;
      min-height: 46px !important;
      margin: 14px 0 0 !important;
      border-radius: 18px !important;
      border: 1px solid rgba(0,103,95,.14) !important;
      background: rgba(255,255,255,.88) !important;
      color: #00675f !important;
      box-shadow: 0 14px 30px rgba(54,41,25,.07) !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      font-size: 13px !important;
      font-weight: 950 !important;
    }
  }

  @media (max-width: 420px) {
    .bp-reviews-head .bp-section-title {
      max-width: 222px !important;
      font-size: 27px !important;
    }

    .bp-head-link {
      padding: 0 10px !important;
      font-size: 11.5px !important;
    }

    .bp-rating-card {
      padding: 13px !important;
      border-radius: 21px !important;
    }

    .bp-rating-card-head {
      grid-template-columns: 40px minmax(0, 1fr) auto !important;
      gap: 9px !important;
    }

    .bp-rating-avatar {
      width: 40px !important;
      height: 40px !important;
    }

    .bp-rating-user em {
      max-width: 92px !important;
    }
  }




  /* =========================================================
     LOOP SERVICE DESCRIPTION V1
     Descriptions remain visible without making service cards taller.
     ========================================================= */
  .business-page-shell .event-package-card.has-service-description .event-package-body > div:first-child,
  .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body > div:first-child {
    display:block !important;
    overflow:hidden !important;
    text-align:left !important;
  }

  .business-page-shell .event-package-card.has-service-description .event-package-body h4,
  .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body h4 {
    display:-webkit-box !important;
    -webkit-line-clamp:1 !important;
    -webkit-box-orient:vertical !important;
    overflow:hidden !important;
    text-overflow:ellipsis !important;
    white-space:normal !important;
  }

  .business-page-shell .event-package-card.has-service-description .event-package-body p,
  .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body p {
    display:-webkit-box !important;
    -webkit-line-clamp:2 !important;
    -webkit-box-orient:vertical !important;
    overflow:hidden !important;
    margin:3px 0 0 !important;
    color:#667085 !important;
    font-size:10.2px !important;
    line-height:1.15 !important;
    font-weight:600 !important;
    letter-spacing:-.01em !important;
    text-align:left !important;
  }

  .business-page-shell .event-package-card.has-service-icon.has-service-description .event-package-body.no-photo-body > div:first-child {
    display:block !important;
    align-items:initial !important;
  }

  /* Mixed photo/icon rails keep their approved equal card geometry. */
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-description > .event-package-body > div:first-child,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon.has-service-description > .event-package-body.no-photo-body > div:first-child {
    display:block !important;
    height:48px !important;
    min-height:48px !important;
    max-height:48px !important;
  }

  @media (max-width:920px){
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-description > .event-package-body > div:first-child,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon.has-service-description > .event-package-body.no-photo-body > div:first-child {
      height:44px !important;
      min-height:44px !important;
      max-height:44px !important;
    }

    .business-page-shell .event-package-card.has-service-description .event-package-body p,
    .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body p {
      font-size:9.8px !important;
      line-height:1.12 !important;
    }
  }


  /* =========================================================
     LOOP BUSINESSPAGE SERVICE CARDS POLISH V1
     Final service-card pass: real premium icons, clearer descriptions,
     no public $0, no redundant chevron, balanced spacing + subtle hover.
     ========================================================= */

  .business-page-shell .bp-packages-rail > article.event-package-card,
  .business-page-shell .event-packages-grid > article.event-package-card {
    transition:
      transform .16s ease,
      box-shadow .16s ease,
      border-color .16s ease !important;
  }

  @media (hover:hover) and (pointer:fine) {
    .business-page-shell .bp-packages-rail > article.event-package-card:hover,
    .business-page-shell .event-packages-grid > article.event-package-card:hover {
      transform:translateY(-2px) !important;
      border-color:rgba(0,103,95,.20) !important;
      box-shadow:0 16px 34px rgba(15,23,42,.075) !important;
    }

    .business-page-shell .bp-packages-rail > article.event-package-card.has-service-icon:hover .event-package-icon-bubble-pro,
    .business-page-shell .event-packages-grid > article.event-package-card.has-service-icon:hover .event-package-icon-bubble-pro {
      background:#f0fdfa !important;
      border-color:rgba(0,103,95,.24) !important;
      transform:translateY(-1px) !important;
    }
  }

  .business-page-shell .event-package-icon-bubble-pro {
    color:#00675f !important;
    transition:transform .16s ease, background .16s ease, border-color .16s ease !important;
  }

  .business-page-shell .event-package-icon-bubble-pro .event-package-icon-svg {
    display:block !important;
    width:22px !important;
    height:22px !important;
    color:currentColor !important;
    stroke:currentColor !important;
  }

  /* Description gets enough contrast to sell the service, while remaining secondary. */
  .business-page-shell .event-package-card.has-service-description .event-package-body p,
  .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body p {
    display:-webkit-box !important;
    -webkit-line-clamp:2 !important;
    -webkit-box-orient:vertical !important;
    overflow:hidden !important;
    margin:4px 0 0 !important;
    color:#536071 !important;
    font-size:11px !important;
    line-height:1.20 !important;
    font-weight:600 !important;
    letter-spacing:-.008em !important;
  }

  .business-page-shell .event-package-card.has-service-description .event-package-body h4,
  .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body h4 {
    margin-bottom:0 !important;
  }

  /* The CTA already provides the action; use the metadata row only for duration + price. */
  .business-page-shell .bp-packages-rail > article.event-package-card .event-package-chevron,
  .business-page-shell .event-packages-grid > article.event-package-card .event-package-chevron {
    display:none !important;
  }

  .business-page-shell .bp-packages-rail > article.event-package-card .event-package-meta,
  .business-page-shell .event-packages-grid > article.event-package-card .event-package-meta,
  .business-page-shell .mixed-service-rail > article.event-package-card .event-package-meta {
    display:flex !important;
    grid-template-columns:none !important;
    align-items:center !important;
    justify-content:flex-start !important;
    gap:6px !important;
    min-width:0 !important;
  }

  .business-page-shell .bp-packages-rail > article.event-package-card .event-package-meta span,
  .business-page-shell .event-packages-grid > article.event-package-card .event-package-meta span {
    flex:0 0 auto !important;
    max-width:100% !important;
  }

  .business-page-shell .bp-packages-rail > article.event-package-card .event-package-meta span:nth-child(2),
  .business-page-shell .event-packages-grid > article.event-package-card .event-package-meta span:nth-child(2) {
    white-space:nowrap !important;
  }

  /* Keep compact cards compact, but reserve a predictable text zone for 1 title + 2 description lines. */
  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    min-height:42px !important;
    max-height:48px !important;
    overflow:hidden !important;
  }

  .business-page-shell .event-package-card.has-service-icon.has-service-description .event-package-body.no-photo-body > div:first-child {
    min-height:46px !important;
    max-height:50px !important;
  }

  .business-page-shell .event-package-card.has-service-icon .event-package-reserve,
  .business-page-shell .event-package-card.has-service-photo .event-package-reserve {
    letter-spacing:-.01em !important;
  }

  @media (max-width:920px) {
    .business-page-shell .event-package-card.has-service-description .event-package-body p,
    .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body p {
      font-size:10.4px !important;
      line-height:1.18 !important;
    }

    .business-page-shell .bp-packages-rail > article.event-package-card .event-package-meta,
    .business-page-shell .event-packages-grid > article.event-package-card .event-package-meta {
      gap:5px !important;
    }
  }

  @media (max-width:430px) {
    .business-page-shell .event-package-card.has-service-description .event-package-body p,
    .business-page-shell .event-package-card.has-service-description .event-package-body.no-photo-body p {
      font-size:10px !important;
      line-height:1.16 !important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE SERVICE CARDS ALIGNMENT V1
     Keep icon-service cards vertically aligned whether or not
     a service has a description. The title always starts at the
     same top position and the description slot is reserved.
     ========================================================= */

  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    display:block !important;
    align-items:initial !important;
    justify-content:initial !important;
    height:50px !important;
    min-height:50px !important;
    max-height:50px !important;
    margin:0 !important;
    padding:0 !important;
    overflow:hidden !important;
    text-align:left !important;
  }

  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body h4 {
    display:-webkit-box !important;
    -webkit-line-clamp:1 !important;
    -webkit-box-orient:vertical !important;
    overflow:hidden !important;
    margin:0 !important;
    min-height:15px !important;
    max-height:16px !important;
    line-height:1.12 !important;
    text-align:left !important;
  }

  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body p {
    display:-webkit-box !important;
    -webkit-line-clamp:2 !important;
    -webkit-box-orient:vertical !important;
    overflow:hidden !important;
    margin:4px 0 0 !important;
    min-height:0 !important;
    max-height:27px !important;
    text-align:left !important;
  }

  /* No description? Keep the reserved text zone empty instead of
     vertically centering the title inside it. */
  .business-page-shell .event-package-card.has-service-icon:not(.has-service-description) .event-package-body.no-photo-body > div:first-child {
    padding-top:0 !important;
  }

  /* Metadata always starts from the same vertical point. */
  .business-page-shell .event-package-card.has-service-icon .event-package-meta,
  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-meta {
    margin-top:7px !important;
  }

  @media (max-width:920px) {
    .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
      height:46px !important;
      min-height:46px !important;
      max-height:46px !important;
    }

    .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body p {
      max-height:24px !important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE SERVICE CARDS ALIGNMENT V1.1
     Regression fix: V1 aligned the text zone but allowed the
     absolutely-positioned service icon to cover the title.
     Reserve horizontal space for the icon only in the title/
     description block; metadata and CTA keep the full width.
     ========================================================= */

  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    box-sizing:border-box !important;
    padding-left:46px !important;
    padding-right:0 !important;
  }

  .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
    padding-left:50px !important;
  }

  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body h4,
  .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body p {
    max-width:100% !important;
  }

  @media (max-width:920px) {
    .business-page-shell .event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
      padding-left:44px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail > article.event-package-card.has-service-icon .event-package-body.no-photo-body > div:first-child {
      padding-left:46px !important;
    }
  }


  /* =========================================================
     LOOP BUSINESSPAGE CONTACT ABOUT FOUNDATION V1
     Public-facing contact semantics + robust About mobile layout.
     One DOM contract across desktop/mobile/native.
     ========================================================= */
  .business-page-shell .bp-info-panel{
    display:grid!important;
    grid-template-columns:repeat(auto-fit,minmax(190px,1fr))!important;
    gap:10px!important;
    padding:0!important;
    margin-top:0!important;
    overflow:visible!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    backdrop-filter:none!important;
    -webkit-backdrop-filter:none!important;
  }

  .business-page-shell .bp-info-panel-title-v1{
    grid-column:1/-1!important;
    display:grid!important;
    gap:3px!important;
    margin:0 0 2px!important;
    padding:0 2px!important;
  }

  .business-page-shell .bp-info-panel-title-v1 strong{
    color:#101828!important;
    font-size:15px!important;
    line-height:1.05!important;
    letter-spacing:-.035em!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-info-panel-title-v1 span{
    color:#667085!important;
    font-size:11px!important;
    line-height:1.3!important;
    font-weight:720!important;
  }

  .business-page-shell .bp-info-panel .bp-info-row{
    min-width:0!important;
    min-height:88px!important;
    height:auto!important;
    display:grid!important;
    grid-template-columns:40px minmax(0,1fr)!important;
    gap:10px!important;
    align-items:center!important;
    padding:12px!important;
    border:1px solid rgba(108,88,61,.09)!important;
    border-radius:19px!important;
    background:rgba(255,255,255,.86)!important;
    box-shadow:0 9px 24px rgba(54,41,25,.045)!important;
    overflow:hidden!important;
  }

  .business-page-shell .bp-info-panel .bp-info-row::before{
    opacity:.72!important;
  }

  .business-page-shell .bp-info-panel .bp-info-icon{
    width:40px!important;
    height:40px!important;
    min-width:40px!important;
    min-height:40px!important;
    max-width:40px!important;
    max-height:40px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    align-self:center!important;
    justify-self:start!important;
    margin:0!important;
    padding:0!important;
    border-radius:999px!important;
    background:#eaf8f4!important;
    border:1px solid rgba(0,103,95,.06)!important;
    color:#00796f!important;
    box-shadow:inset 0 0 0 1px rgba(0,103,95,.03)!important;
  }

  .business-page-shell .bp-info-panel .bp-whatsapp-icon-v1{
    background:#ecfdf5!important;
    color:#059669!important;
  }

  .business-page-shell .bp-info-panel .bp-info-content{
    min-width:0!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr)!important;
    gap:4px!important;
    align-content:center!important;
    justify-items:start!important;
    margin:0!important;
    padding:0!important;
  }

  .business-page-shell .bp-info-panel .bp-info-label{
    margin:0!important;
    color:#667085!important;
    font-size:9.5px!important;
    line-height:1!important;
    letter-spacing:.065em!important;
    text-transform:uppercase!important;
    font-weight:900!important;
  }

  .business-page-shell .bp-info-panel .bp-info-value,
  .business-page-shell .bp-info-panel .bp-info-value-primary,
  .business-page-shell .bp-info-panel .bp-contact-number{
    min-width:0!important;
    max-width:100%!important;
    color:#101828!important;
    font-size:12.5px!important;
    line-height:1.15!important;
    font-weight:900!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .business-page-shell .bp-contact-channel-action-v1,
  .business-page-shell .bp-info-panel .bp-inline-link,
  .business-page-shell .bp-info-panel .bp-hours-toggle{
    width:max-content!important;
    max-width:100%!important;
    min-height:25px!important;
    height:auto!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:flex-start!important;
    gap:5px!important;
    margin:1px 0 0!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
    color:#00796f!important;
    box-shadow:none!important;
    font-size:10.5px!important;
    line-height:1.15!important;
    font-weight:900!important;
    text-decoration:none!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-contact-channel-action-v1.whatsapp{
    color:#059669!important;
  }

  .business-page-shell .bp-about,
  .business-page-shell .bp-about > *,
  .business-page-shell .bp-about div,
  .business-page-shell .bp-about p,
  .business-page-shell .bp-about span,
  .business-page-shell .bp-about li{
    min-width:0!important;
    max-width:100%!important;
    box-sizing:border-box!important;
    overflow-wrap:anywhere!important;
    word-break:normal!important;
  }

  .business-page-shell .bp-about.is-expanded{
    display:block!important;
    height:auto!important;
    min-height:0!important;
    max-height:none!important;
    overflow:visible!important;
    -webkit-line-clamp:unset!important;
  }

  .business-page-shell .bp-about.is-expanded::after{
    content:none!important;
    display:none!important;
  }

  .business-page-shell .bp-about img,
  .business-page-shell .bp-about video,
  .business-page-shell .bp-about svg,
  .business-page-shell .bp-about canvas{
    max-width:100%!important;
    height:auto!important;
  }

  .business-page-shell .bp-about iframe{
    width:100%!important;
    max-width:100%!important;
    border:0!important;
  }

  .business-page-shell .bp-about table{
    width:100%!important;
    max-width:100%!important;
    display:block!important;
    overflow-x:auto!important;
    -webkit-overflow-scrolling:touch!important;
  }

  .business-page-shell .bp-about pre,
  .business-page-shell .bp-about code{
    max-width:100%!important;
    white-space:pre-wrap!important;
    overflow-wrap:anywhere!important;
  }

  @media(max-width:920px){
    .business-page-shell .bp-info-panel{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:9px!important;
      margin-top:10px!important;
    }

    .business-page-shell .bp-about{
      width:100%!important;
      max-width:100%!important;
    }

    .business-page-shell .bp-about.is-expanded{
      max-height:none!important;
    }
  }

  @media(max-width:767px){
    .business-page-shell .bp-info-panel{
      grid-template-columns:1fr!important;
      gap:8px!important;
      margin-top:10px!important;
    }

    .business-page-shell .bp-info-panel-title-v1{
      padding:0 3px 3px!important;
      gap:3px!important;
    }

    .business-page-shell .bp-info-panel-title-v1 strong{
      font-size:17px!important;
    }

    .business-page-shell .bp-info-panel-title-v1 span{
      font-size:11.5px!important;
    }

    .business-page-shell .bp-info-panel .bp-info-row{
      min-height:66px!important;
      grid-template-columns:38px minmax(0,1fr)!important;
      gap:10px!important;
      padding:10px 12px!important;
      border-radius:19px!important;
    }

    .business-page-shell .bp-info-panel .bp-info-icon{
      width:38px!important;
      height:38px!important;
      min-width:38px!important;
      min-height:38px!important;
      max-width:38px!important;
      max-height:38px!important;
    }

    .business-page-shell .bp-about{
      max-width:100%!important;
      padding-right:0!important;
      margin-right:0!important;
      overflow-wrap:anywhere!important;
    }

    .business-page-shell .bp-about.is-expanded{
      max-height:none!important;
      overflow:visible!important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE REVIEWS PARITY V3
     One review experience across desktop/mobile:
     - rating summary + star distribution
     - full 5-star row in every review card
     - richer desktop cards without changing review data
     - premium loading/empty states
     - mobile keeps the compact V47 visual language
     ========================================================= */

  .business-page-shell .bp-reviews-section-v3{
    max-width:1320px!important;
    margin-left:auto!important;
    margin-right:auto!important;
  }

  .business-page-shell .bp-reviews-head-v3{
    max-width:none!important;
    display:flex!important;
    align-items:flex-end!important;
    justify-content:space-between!important;
    gap:18px!important;
    margin:0 0 18px!important;
  }

  .business-page-shell .bp-reviews-head-v3 .bp-section-head{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    flex-wrap:wrap!important;
    gap:12px!important;
  }

  .business-page-shell .bp-review-average-v3{
    min-height:34px!important;
    display:inline-flex!important;
    align-items:center!important;
    gap:7px!important;
    padding:0 12px!important;
    border:1px solid rgba(245,158,11,.22)!important;
    border-radius:999px!important;
    background:#fff9e9!important;
    color:#00675f!important;
    box-shadow:0 8px 20px rgba(54,41,25,.045)!important;
    font-size:11.5px!important;
    line-height:1!important;
    font-weight:950!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-review-average-v3 svg{
    color:#f59e0b!important;
  }

  .business-page-shell .bp-reviews-summary-v3{
    width:100%!important;
    min-height:150px!important;
    display:grid!important;
    grid-template-columns:220px minmax(0,1fr)!important;
    gap:28px!important;
    align-items:center!important;
    margin:0 0 18px!important;
    padding:20px 22px!important;
    border:1px solid rgba(108,88,61,.09)!important;
    border-radius:22px!important;
    background:linear-gradient(135deg,rgba(255,255,255,.96) 0%,rgba(255,250,242,.88) 100%)!important;
    box-shadow:0 14px 32px rgba(54,41,25,.055)!important;
  }

  .business-page-shell .bp-reviews-score-v3{
    min-width:0!important;
    display:grid!important;
    justify-items:start!important;
    align-content:center!important;
    gap:6px!important;
  }

  .business-page-shell .bp-reviews-score-v3 > strong{
    color:#101828!important;
    font-size:52px!important;
    line-height:.86!important;
    letter-spacing:-.065em!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-reviews-score-v3 > span{
    color:#667085!important;
    font-size:11.5px!important;
    line-height:1.2!important;
    font-weight:800!important;
  }

  .business-page-shell .bp-reviews-score-stars-v3,
  .business-page-shell .bp-rating-stars-v3{
    display:flex!important;
    align-items:center!important;
    gap:3px!important;
    color:#d8dee7!important;
  }

  .business-page-shell .bp-reviews-score-stars-v3 svg.is-filled,
  .business-page-shell .bp-rating-stars-v3 svg.is-filled{
    color:#f59e0b!important;
  }

  .business-page-shell .bp-reviews-distribution-v3{
    min-width:0!important;
    display:grid!important;
    gap:7px!important;
  }

  .business-page-shell .bp-review-distribution-row-v3{
    min-width:0!important;
    display:grid!important;
    grid-template-columns:14px 14px minmax(0,1fr) 24px!important;
    gap:7px!important;
    align-items:center!important;
    color:#667085!important;
    font-size:10.5px!important;
    line-height:1!important;
    font-weight:850!important;
  }

  .business-page-shell .bp-review-distribution-row-v3 svg{
    color:#f59e0b!important;
  }

  .business-page-shell .bp-review-distribution-row-v3 small{
    text-align:right!important;
    color:#7b8492!important;
    font-size:10.5px!important;
    font-weight:800!important;
  }

  .business-page-shell .bp-review-distribution-track-v3{
    min-width:0!important;
    height:7px!important;
    border-radius:999px!important;
    overflow:hidden!important;
    background:#edf1f4!important;
  }

  .business-page-shell .bp-review-distribution-track-v3 > span{
    display:block!important;
    height:100%!important;
    min-width:0!important;
    border-radius:inherit!important;
    background:linear-gradient(90deg,#fbbf24 0%,#f59e0b 100%)!important;
  }

  .business-page-shell .bp-reviews-list-v3,
  .business-page-shell .bp-reviews-list-v3.space-y-4{
    max-width:none!important;
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:14px!important;
    margin:0!important;
  }

  .business-page-shell .bp-reviews-list-v3.space-y-4 > :not([hidden]) ~ :not([hidden]){
    margin-top:0!important;
    margin-bottom:0!important;
  }

  .business-page-shell .bp-rating-card-v3{
    min-width:0!important;
    min-height:172px!important;
    height:100%!important;
    padding:16px!important;
    border:1px solid rgba(108,88,61,.09)!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.94)!important;
    box-shadow:0 14px 32px rgba(54,41,25,.055)!important;
    overflow:hidden!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-card-head{
    display:grid!important;
    grid-template-columns:44px minmax(0,1fr) auto!important;
    gap:11px!important;
    align-items:center!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-avatar{
    width:44px!important;
    height:44px!important;
    min-width:44px!important;
    border-radius:999px!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-user{
    min-width:0!important;
    display:grid!important;
    gap:4px!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-user-main{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    flex-wrap:wrap!important;
    gap:6px!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-user strong{
    min-width:0!important;
    color:#101828!important;
    font-size:13.5px!important;
    line-height:1.08!important;
    letter-spacing:-.02em!important;
    font-weight:950!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-user em{
    max-width:150px!important;
    min-height:20px!important;
    display:inline-flex!important;
    align-items:center!important;
    padding:0 8px!important;
    border-radius:999px!important;
    background:#eaf8f4!important;
    color:#00675f!important;
    font-size:10px!important;
    line-height:1!important;
    font-style:normal!important;
    font-weight:900!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-rating-card-v3 .bp-rating-user > span{
    color:#7b8492!important;
    font-size:10.5px!important;
    line-height:1.15!important;
    font-weight:760!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-rating-score-v3{
    min-height:31px!important;
    display:inline-flex!important;
    align-items:baseline!important;
    justify-content:center!important;
    gap:3px!important;
    padding:0 9px!important;
    border:1px solid rgba(245,158,11,.18)!important;
    border-radius:999px!important;
    background:#fff9e9!important;
    color:#d97706!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:950!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-rating-score-v3 small{
    color:#a16a12!important;
    font-size:9px!important;
    font-weight:850!important;
  }

  .business-page-shell .bp-rating-stars-v3{
    margin-top:12px!important;
  }

  .business-page-shell .bp-rating-comment-v3{
    margin:10px 0 0!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
    color:#344054!important;
    font-size:12.5px!important;
    line-height:1.5!important;
    font-weight:690!important;
    letter-spacing:-.005em!important;
    display:-webkit-box!important;
    -webkit-line-clamp:4!important;
    -webkit-box-orient:vertical!important;
    overflow:hidden!important;
  }

  .business-page-shell .bp-rating-comment-v3.is-empty{
    color:#98a2b3!important;
    font-style:italic!important;
    font-weight:700!important;
  }

  .business-page-shell .business-show-more-reviews-v3{
    width:max-content!important;
    min-width:180px!important;
    min-height:42px!important;
    margin:16px 0 0!important;
    padding:0 16px!important;
    border:1px solid rgba(0,103,95,.14)!important;
    border-radius:14px!important;
    background:rgba(255,255,255,.90)!important;
    color:#00675f!important;
    box-shadow:0 10px 24px rgba(54,41,25,.055)!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:7px!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-reviews-empty-v3{
    min-height:124px!important;
    display:flex!important;
    align-items:center!important;
    gap:14px!important;
    padding:20px!important;
    border:1px dashed rgba(0,103,95,.20)!important;
    border-radius:20px!important;
    background:linear-gradient(135deg,#f7fffd 0%,#fffaf2 100%)!important;
  }

  .business-page-shell .bp-reviews-empty-icon-v3{
    width:46px!important;
    height:46px!important;
    min-width:46px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:999px!important;
    background:#fff9e9!important;
    color:#f59e0b!important;
    border:1px solid rgba(245,158,11,.18)!important;
  }

  .business-page-shell .bp-reviews-empty-v3 strong{
    color:#10323d!important;
    font-size:15px!important;
    line-height:1.15!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-reviews-empty-v3 p{
    margin:4px 0 0!important;
    max-width:580px!important;
    color:#667085!important;
    font-size:12px!important;
    line-height:1.4!important;
    font-weight:700!important;
  }

  .business-page-shell .bp-reviews-loading-v3{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:14px!important;
  }

  .business-page-shell .bp-reviews-summary-skeleton-v3{
    grid-column:1/-1!important;
    height:150px!important;
  }

  .business-page-shell .bp-reviews-summary-skeleton-v3,
  .business-page-shell .bp-review-card-skeleton-v3{
    border:1px solid rgba(108,88,61,.07)!important;
    border-radius:22px!important;
    background:linear-gradient(
      90deg,
      rgba(241,245,249,.74) 0%,
      rgba(255,255,255,.98) 46%,
      rgba(241,245,249,.74) 100%
    )!important;
    background-size:220% 100%!important;
    animation:bp-reviews-v3-shimmer 1.25s ease-in-out infinite!important;
  }

  .business-page-shell .bp-review-card-skeleton-v3{
    height:172px!important;
  }

  @keyframes bp-reviews-v3-shimmer{
    0%{background-position:100% 0}
    100%{background-position:-100% 0}
  }

  @media(max-width:920px){
    .business-page-shell .bp-reviews-summary-v3{
      grid-template-columns:150px minmax(0,1fr)!important;
      gap:18px!important;
      min-height:140px!important;
      padding:16px!important;
      border-radius:20px!important;
    }

    .business-page-shell .bp-reviews-score-v3 > strong{
      font-size:44px!important;
    }

    .business-page-shell .bp-reviews-list-v3,
    .business-page-shell .bp-reviews-list-v3.space-y-4{
      grid-template-columns:1fr!important;
      gap:12px!important;
    }

    .business-page-shell .bp-reviews-loading-v3{
      grid-template-columns:1fr!important;
    }

    .business-page-shell .bp-reviews-summary-skeleton-v3{
      grid-column:auto!important;
      height:138px!important;
    }

    .business-page-shell .bp-reviews-head-v3{
      align-items:flex-start!important;
    }
  }

  @media(max-width:640px){
    .business-page-shell .bp-reviews-head-v3{
      display:grid!important;
      grid-template-columns:minmax(0,1fr) auto!important;
      gap:10px!important;
      margin-bottom:14px!important;
    }

    .business-page-shell .bp-reviews-head-v3 .bp-section-head{
      display:grid!important;
      gap:10px!important;
    }

    .business-page-shell .bp-reviews-summary-v3{
      grid-template-columns:1fr!important;
      gap:14px!important;
      min-height:0!important;
      margin-bottom:12px!important;
      padding:14px!important;
      border-radius:20px!important;
    }

    .business-page-shell .bp-reviews-score-v3{
      grid-template-columns:auto minmax(0,1fr)!important;
      grid-template-areas:
        "score stars"
        "score total"!important;
      align-items:center!important;
      column-gap:12px!important;
      row-gap:4px!important;
    }

    .business-page-shell .bp-reviews-score-v3 > strong{
      grid-area:score!important;
      font-size:42px!important;
    }

    .business-page-shell .bp-reviews-score-stars-v3{
      grid-area:stars!important;
    }

    .business-page-shell .bp-reviews-score-v3 > span{
      grid-area:total!important;
    }

    .business-page-shell .bp-review-distribution-row-v3{
      grid-template-columns:12px 13px minmax(0,1fr) 20px!important;
      gap:6px!important;
    }

    .business-page-shell .bp-rating-card-v3{
      min-height:0!important;
      padding:14px!important;
      border-radius:22px!important;
    }

    .business-page-shell .bp-rating-card-v3 .bp-rating-card-head{
      grid-template-columns:42px minmax(0,1fr) auto!important;
      gap:10px!important;
    }

    .business-page-shell .bp-rating-card-v3 .bp-rating-avatar{
      width:42px!important;
      height:42px!important;
      min-width:42px!important;
    }

    .business-page-shell .bp-rating-stars-v3{
      margin-top:11px!important;
    }

    .business-page-shell .bp-rating-comment-v3{
      margin-top:9px!important;
      font-size:13px!important;
      line-height:1.45!important;
      -webkit-line-clamp:3!important;
    }

    .business-page-shell .business-show-more-reviews-v3{
      width:100%!important;
      min-height:46px!important;
      margin-top:14px!important;
      border-radius:18px!important;
    }

    .business-page-shell .bp-reviews-empty-v3{
      min-height:118px!important;
      align-items:flex-start!important;
      padding:16px!important;
      border-radius:20px!important;
    }

    .business-page-shell .bp-reviews-loading-v3{
      gap:12px!important;
    }

    .business-page-shell .bp-review-card-skeleton-v3{
      height:150px!important;
    }
  }

  @media(prefers-reduced-motion:reduce){
    .business-page-shell .bp-reviews-summary-skeleton-v3,
    .business-page-shell .bp-review-card-skeleton-v3{
      animation:none!important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE GALLERY PUBLISHING V4
     Public gallery fed by business.featuredProjects.
     No public projects-subcollection read is required.
     ========================================================= */

  .business-page-shell .bp-public-gallery-v4{
    max-width:1320px!important;
    margin-left:auto!important;
    margin-right:auto!important;
  }

  .business-page-shell .bp-public-gallery-head-v4{
    display:flex!important;
    align-items:flex-end!important;
    justify-content:space-between!important;
    gap:18px!important;
    margin-bottom:16px!important;
  }

  .business-page-shell .bp-public-gallery-head-v4 .bp-section-title{
    margin:3px 0 0!important;
  }

  .business-page-shell .bp-public-gallery-head-v4 p{
    max-width:650px!important;
    margin:7px 0 0!important;
    color:#64748b!important;
    font-size:12.5px!important;
    line-height:1.4!important;
    font-weight:700!important;
  }

  .business-page-shell .bp-public-gallery-count-v4{
    min-height:34px!important;
    display:inline-flex!important;
    align-items:center!important;
    padding:0 12px!important;
    border:1px solid rgba(0,121,111,.14)!important;
    border-radius:999px!important;
    background:#f0fdfa!important;
    color:#00796f!important;
    font-size:11px!important;
    line-height:1!important;
    font-weight:950!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4{
    min-width:0!important;
    display:grid!important;
    gap:10px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-one{
    grid-template-columns:minmax(0,720px)!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-two{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-many{
    grid-template-columns:repeat(4,minmax(0,1fr))!important;
    grid-template-rows:repeat(2,160px)!important;
  }

  .business-page-shell .bp-public-gallery-item-v4{
    min-width:0!important;
    position:relative!important;
    overflow:hidden!important;
    padding:0!important;
    border:1px solid rgba(108,88,61,.09)!important;
    border-radius:20px!important;
    background:#f1f5f9!important;
    cursor:pointer!important;
    box-shadow:0 12px 30px rgba(54,41,25,.055)!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-one .bp-public-gallery-item-v4{
    aspect-ratio:16/9!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-two .bp-public-gallery-item-v4{
    aspect-ratio:4/3!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-many .bp-public-gallery-item-v4.is-featured{
    grid-column:span 2!important;
    grid-row:span 2!important;
  }

  .business-page-shell .bp-public-gallery-item-v4 img{
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    display:block!important;
    transition:transform .22s ease!important;
  }

  .business-page-shell .bp-public-gallery-item-v4::after{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    background:linear-gradient(180deg,transparent 60%,rgba(15,23,42,.18) 100%)!important;
    pointer-events:none!important;
  }

  .business-page-shell .bp-public-gallery-more-v4{
    position:absolute!important;
    z-index:2!important;
    inset:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:rgba(15,23,42,.50)!important;
    color:#fff!important;
    font-size:26px!important;
    font-weight:950!important;
    backdrop-filter:blur(2px)!important;
  }

  @media(hover:hover) and (pointer:fine){
    .business-page-shell .bp-public-gallery-item-v4:hover img{
      transform:scale(1.025)!important;
    }
  }

  .bp-public-gallery-modal-v4{
    position:fixed!important;
    z-index:12000!important;
    inset:0!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    padding:20px!important;
    background:rgba(9,19,24,.78)!important;
    backdrop-filter:blur(16px)!important;
    -webkit-backdrop-filter:blur(16px)!important;
  }

  .bp-public-gallery-modal-card-v4{
    width:min(100%,1100px)!important;
    height:min(86dvh,820px)!important;
    position:relative!important;
    overflow:hidden!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    border:1px solid rgba(255,255,255,.16)!important;
    border-radius:28px!important;
    background:#0b171b!important;
    box-shadow:0 30px 90px rgba(0,0,0,.34)!important;
  }

  .bp-public-gallery-modal-card-v4 > img{
    width:100%!important;
    height:100%!important;
    object-fit:contain!important;
    display:block!important;
  }

  .bp-public-gallery-modal-close-v4,
  .bp-public-gallery-modal-arrow-v4{
    position:absolute!important;
    z-index:4!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    border:1px solid rgba(255,255,255,.25)!important;
    background:rgba(255,255,255,.93)!important;
    color:#10323d!important;
    box-shadow:0 10px 28px rgba(0,0,0,.14)!important;
    cursor:pointer!important;
  }

  .bp-public-gallery-modal-close-v4{
    top:16px!important;
    right:16px!important;
    width:42px!important;
    height:42px!important;
    border-radius:999px!important;
  }

  .bp-public-gallery-modal-arrow-v4{
    top:50%!important;
    width:44px!important;
    height:44px!important;
    border-radius:999px!important;
    transform:translateY(-50%)!important;
    font-size:20px!important;
    font-weight:950!important;
  }

  .bp-public-gallery-modal-arrow-v4.is-prev{left:16px!important}
  .bp-public-gallery-modal-arrow-v4.is-next{right:16px!important}

  .bp-public-gallery-modal-meta-v4{
    position:absolute!important;
    z-index:4!important;
    left:16px!important;
    bottom:16px!important;
    min-height:42px!important;
    display:flex!important;
    align-items:center!important;
    gap:10px!important;
    padding:0 13px!important;
    border:1px solid rgba(255,255,255,.20)!important;
    border-radius:14px!important;
    background:rgba(9,19,24,.64)!important;
    color:#fff!important;
    backdrop-filter:blur(10px)!important;
  }

  .bp-public-gallery-modal-meta-v4 strong{
    max-width:380px!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
    font-size:12px!important;
    font-weight:900!important;
  }

  .bp-public-gallery-modal-meta-v4 span{
    color:rgba(255,255,255,.72)!important;
    font-size:10.5px!important;
    font-weight:800!important;
  }

  @media(max-width:920px){
    .business-page-shell .bp-public-gallery-grid-v4.is-many{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      grid-template-rows:auto!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-many .bp-public-gallery-item-v4{
      aspect-ratio:4/3!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-many .bp-public-gallery-item-v4.is-featured{
      grid-column:1/-1!important;
      grid-row:auto!important;
      aspect-ratio:16/8.5!important;
    }
  }

  @media(max-width:640px){
    .business-page-shell .bp-public-gallery-head-v4{
      align-items:flex-start!important;
      margin-bottom:13px!important;
    }

    .business-page-shell .bp-public-gallery-head-v4 p{
      font-size:12px!important;
    }

    .business-page-shell .bp-public-gallery-count-v4{
      min-height:31px!important;
      padding:0 10px!important;
      font-size:10px!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4,
    .business-page-shell .bp-public-gallery-grid-v4.is-two,
    .business-page-shell .bp-public-gallery-grid-v4.is-many{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:8px!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-one{
      grid-template-columns:1fr!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-many .bp-public-gallery-item-v4,
    .business-page-shell .bp-public-gallery-grid-v4.is-two .bp-public-gallery-item-v4{
      aspect-ratio:1/1!important;
      border-radius:16px!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-many .bp-public-gallery-item-v4.is-featured{
      grid-column:1/-1!important;
      aspect-ratio:16/9!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-one .bp-public-gallery-item-v4{
      aspect-ratio:16/10!important;
      border-radius:18px!important;
    }

    .bp-public-gallery-modal-v4{
      padding:10px!important;
    }

    .bp-public-gallery-modal-card-v4{
      width:100%!important;
      height:min(82dvh,680px)!important;
      border-radius:22px!important;
    }

    .bp-public-gallery-modal-close-v4{
      top:10px!important;
      right:10px!important;
    }

    .bp-public-gallery-modal-arrow-v4{
      width:40px!important;
      height:40px!important;
    }

    .bp-public-gallery-modal-arrow-v4.is-prev{left:10px!important}
    .bp-public-gallery-modal-arrow-v4.is-next{right:10px!important}

    .bp-public-gallery-modal-meta-v4{
      left:10px!important;
      right:10px!important;
      bottom:10px!important;
      justify-content:space-between!important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE GALLERY COMPACT V4.1
     Desktop gallery density pass:
     - keep the BusinessPage section axis
     - make gallery imagery a supporting section, not a second hero
     - 1 photo max 680px
     - 2 photos max 900px total
     - 3+ mosaic max 980px with shallower rows
     - mobile keeps the existing touch-friendly layout
     ========================================================= */

  .business-page-shell .bp-public-gallery-head-v4{
    width:100%!important;
    max-width:980px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4{
    width:100%!important;
    max-width:980px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-one{
    max-width:680px!important;
    grid-template-columns:minmax(0,680px)!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-one .bp-public-gallery-item-v4{
    aspect-ratio:16/9!important;
    max-height:382px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-two{
    max-width:900px!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:12px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-two .bp-public-gallery-item-v4{
    aspect-ratio:16/10!important;
    max-height:280px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-many{
    max-width:980px!important;
    grid-template-columns:repeat(4,minmax(0,1fr))!important;
    grid-template-rows:repeat(2,132px)!important;
    gap:10px!important;
  }

  .business-page-shell .bp-public-gallery-grid-v4.is-many .bp-public-gallery-item-v4.is-featured{
    grid-column:span 2!important;
    grid-row:span 2!important;
  }

  .business-page-shell .bp-public-gallery-item-v4{
    border-radius:18px!important;
    box-shadow:0 9px 24px rgba(54,41,25,.05)!important;
  }

  .business-page-shell .bp-public-gallery-count-v4{
    min-height:31px!important;
    padding:0 10px!important;
    font-size:10.5px!important;
  }

  @media(min-width:921px){
    .business-page-shell .bp-public-gallery-v4{
      padding-top:30px!important;
      padding-bottom:30px!important;
    }

    .business-page-shell .bp-public-gallery-head-v4{
      margin-bottom:13px!important;
    }
  }

  @media(min-width:641px) and (max-width:920px){
    .business-page-shell .bp-public-gallery-head-v4,
    .business-page-shell .bp-public-gallery-grid-v4{
      max-width:100%!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-one{
      max-width:660px!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-two{
      max-width:100%!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-two .bp-public-gallery-item-v4{
      aspect-ratio:4/3!important;
      max-height:none!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-many{
      max-width:100%!important;
      grid-template-rows:auto!important;
    }
  }

  @media(max-width:640px){
    .business-page-shell .bp-public-gallery-head-v4,
    .business-page-shell .bp-public-gallery-grid-v4,
    .business-page-shell .bp-public-gallery-grid-v4.is-one,
    .business-page-shell .bp-public-gallery-grid-v4.is-two,
    .business-page-shell .bp-public-gallery-grid-v4.is-many{
      max-width:100%!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-two .bp-public-gallery-item-v4{
      max-height:none!important;
    }

    .business-page-shell .bp-public-gallery-grid-v4.is-one .bp-public-gallery-item-v4{
      max-height:none!important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE HERO CONSOLIDATION V7
     Audited clean-room hero media contract.
     - unique selectors: no bp-logo-stage / V6 cascade
     - main media remains the business logo/hero
     - up to 3 real project photos live in a right rail
     - 4+ photos use +N / Ver todas on the last visible tile
     - no-gallery businesses render the logo at its natural ratio
     ========================================================= */

  .business-page-shell .bp-hero-media-v7{
    grid-area:logo!important;
    grid-column:auto!important;
    grid-row:auto!important;
    justify-self:end!important;
    align-self:center!important;
    width:100%!important;
    max-width:min(52vw,620px)!important;
    min-width:0!important;
    margin:0!important;
    padding:0!important;
    background:transparent!important;
    border:0!important;
    box-shadow:none!important;
  }

  .business-page-shell .bp-hero-media-grid-v7{
    width:100%!important;
    height:258px!important;
    min-width:0!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) 132px!important;
    gap:8px!important;
    align-items:stretch!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-1 .bp-hero-media-grid-v7{
    grid-template-columns:minmax(0,1fr) 190px!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-2 .bp-hero-media-grid-v7{
    grid-template-columns:minmax(0,1fr) 176px!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-3 .bp-hero-media-grid-v7{
    grid-template-columns:minmax(0,1fr) 132px!important;
  }

  .business-page-shell .bp-hero-main-v7{
    position:relative!important;
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    min-height:0!important;
    overflow:hidden!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    border-radius:24px!important;
    background:transparent!important;
    box-shadow:0 12px 30px rgba(35,45,41,.065)!important;
    line-height:0!important;
  }

  .business-page-shell .bp-hero-main-image-v7{
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    min-height:0!important;
    display:block!important;
    object-fit:cover!important;
    object-position:center!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    border-radius:inherit!important;
    background:transparent!important;
    box-shadow:none!important;
  }

  .business-page-shell .bp-hero-media-v7.bp-logo-shape-square .bp-hero-main-image-v7,
  .business-page-shell .bp-hero-media-v7.bp-logo-shape-tall .bp-hero-main-image-v7{
    object-fit:contain!important;
  }

  .business-page-shell .bp-hero-rail-v7{
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    min-height:0!important;
    display:grid!important;
    gap:8px!important;
    align-content:stretch!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-1 .bp-hero-rail-v7{
    display:flex!important;
    align-items:center!important;
    justify-content:stretch!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-2 .bp-hero-rail-v7{
    grid-template-rows:repeat(2,minmax(0,1fr))!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-3 .bp-hero-rail-v7{
    grid-template-rows:repeat(3,minmax(0,1fr))!important;
  }

  .business-page-shell .bp-hero-thumb-v7{
    position:relative!important;
    width:100%!important;
    height:100%!important;
    min-width:0!important;
    min-height:0!important;
    display:block!important;
    overflow:hidden!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    outline:0!important;
    border-radius:15px!important;
    background:transparent!important;
    box-shadow:0 6px 18px rgba(35,45,41,.055)!important;
    cursor:pointer!important;
    line-height:0!important;
  }

  .business-page-shell .bp-hero-media-v7.gallery-count-1 .bp-hero-thumb-v7{
    height:auto!important;
    aspect-ratio:4/3!important;
  }

  .business-page-shell .bp-hero-thumb-image-v7{
    position:absolute!important;
    inset:0!important;
    width:100%!important;
    height:100%!important;
    display:block!important;
    object-fit:cover!important;
    object-position:center!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    border-radius:inherit!important;
    background:transparent!important;
    transition:transform .22s ease!important;
  }

  .business-page-shell .bp-hero-thumb-extra-v7{
    position:absolute!important;
    z-index:3!important;
    inset:0!important;
    display:flex!important;
    flex-direction:column!important;
    align-items:center!important;
    justify-content:center!important;
    gap:4px!important;
    border-radius:inherit!important;
    background:rgba(8,25,24,.56)!important;
    color:#fff!important;
    backdrop-filter:blur(4px)!important;
    -webkit-backdrop-filter:blur(4px)!important;
    pointer-events:none!important;
  }

  .business-page-shell .bp-hero-thumb-extra-v7 strong{
    font-size:22px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:-.04em!important;
  }

  .business-page-shell .bp-hero-thumb-extra-v7 small{
    font-size:9px!important;
    line-height:1!important;
    font-weight:900!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-hero-media-v7.logo-only{
    display:flex!important;
    align-items:center!important;
    justify-content:flex-end!important;
    max-width:min(48vw,560px)!important;
    min-height:0!important;
  }

  .business-page-shell .bp-hero-logo-only-image-v7{
    width:auto!important;
    height:auto!important;
    max-width:100%!important;
    max-height:260px!important;
    display:block!important;
    object-fit:contain!important;
    object-position:center!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    border-radius:24px!important;
    background:transparent!important;
    box-shadow:none!important;
  }

  .business-page-shell .bp-hero-empty-v7{
    width:100%!important;
    min-height:190px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:24px!important;
    background:rgba(255,255,255,.52)!important;
    color:#718096!important;
    font-size:13px!important;
    font-weight:800!important;
  }

  @media(hover:hover) and (pointer:fine){
    .business-page-shell .bp-hero-thumb-v7:hover .bp-hero-thumb-image-v7{
      transform:scale(1.025)!important;
    }
  }

  @media(min-width:768px) and (max-width:1180px){
    .business-page-shell .bp-hero-media-v7{
      max-width:min(49vw,540px)!important;
    }

    .business-page-shell .bp-hero-media-grid-v7{
      height:246px!important;
      gap:7px!important;
    }

    .business-page-shell .bp-hero-media-v7.gallery-count-1 .bp-hero-media-grid-v7{
      grid-template-columns:minmax(0,1fr) 170px!important;
    }

    .business-page-shell .bp-hero-media-v7.gallery-count-2 .bp-hero-media-grid-v7{
      grid-template-columns:minmax(0,1fr) 156px!important;
    }

    .business-page-shell .bp-hero-media-v7.gallery-count-3 .bp-hero-media-grid-v7{
      grid-template-columns:minmax(0,1fr) 120px!important;
    }

    .business-page-shell .bp-hero-main-v7{
      border-radius:22px!important;
    }

    .business-page-shell .bp-hero-rail-v7{
      gap:7px!important;
    }

    .business-page-shell .bp-hero-thumb-v7{
      border-radius:13px!important;
    }

    .business-page-shell .bp-hero-media-v7.logo-only{
      max-width:min(48vw,520px)!important;
    }

    .business-page-shell .bp-hero-logo-only-image-v7{
      max-height:245px!important;
      border-radius:22px!important;
    }
  }

  @media(max-width:767px){
    .business-page-shell .bp-hero-media-v7,
    .business-page-shell .bp-hero-media-v7.logo-only{
      order:1!important;
      width:100%!important;
      max-width:none!important;
      margin:0!important;
      justify-self:stretch!important;
    }

    .business-page-shell .bp-hero-media-grid-v7{
      height:auto!important;
      display:grid!important;
      grid-template-columns:1fr!important;
      gap:9px!important;
    }

    .business-page-shell .bp-hero-main-v7{
      width:100%!important;
      height:auto!important;
      aspect-ratio:16/9!important;
      border-radius:22px!important;
    }

    .business-page-shell .bp-hero-rail-v7{
      width:100%!important;
      height:auto!important;
      display:grid!important;
      grid-template-rows:1fr!important;
      gap:8px!important;
      align-content:start!important;
    }

    .business-page-shell .bp-hero-media-v7.gallery-count-1 .bp-hero-rail-v7{
      display:grid!important;
      grid-template-columns:minmax(0,1fr)!important;
      max-width:36%!important;
    }

    .business-page-shell .bp-hero-media-v7.gallery-count-2 .bp-hero-rail-v7{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      max-width:72%!important;
    }

    .business-page-shell .bp-hero-media-v7.gallery-count-3 .bp-hero-rail-v7{
      grid-template-columns:repeat(3,minmax(0,1fr))!important;
      max-width:100%!important;
    }

    .business-page-shell .bp-hero-thumb-v7,
    .business-page-shell .bp-hero-media-v7.gallery-count-1 .bp-hero-thumb-v7{
      width:100%!important;
      height:auto!important;
      aspect-ratio:4/3!important;
      border-radius:13px!important;
    }

    .business-page-shell .bp-hero-logo-only-image-v7{
      width:100%!important;
      height:auto!important;
      max-width:100%!important;
      max-height:224px!important;
      object-fit:contain!important;
      border-radius:22px!important;
    }
  }

  @media(max-width:420px){
    .business-page-shell .bp-hero-main-v7{
      aspect-ratio:16/9.4!important;
      border-radius:20px!important;
    }

    .business-page-shell .bp-hero-thumb-v7{
      border-radius:12px!important;
    }
  }

  @media(prefers-reduced-motion:reduce){
    .business-page-shell .bp-hero-thumb-image-v7{
      transition:none!important;
    }
  }


  /* =========================================================
     LOOP BUSINESSPAGE ABOUT TEAM CONSOLIDATION V1
     Clean isolated About + Team contract.
     - legacy .bp-about / .bp-team-grid cascade no longer owns the DOM
     - responsive rich About content with safe expand/collapse
     - intentional desktop composition + mobile team carousel
     ========================================================= */

  .business-page-shell .bp-story-team-v1{
    width:100%!important;
    max-width:1320px!important;
    display:grid!important;
    grid-template-columns:minmax(0,1.12fr) minmax(340px,.88fr)!important;
    gap:48px!important;
    margin:42px auto 0!important;
    padding:38px 0 4px!important;
    border-top:1px solid rgba(148,163,184,.24)!important;
    box-sizing:border-box!important;
    text-align:left!important;
  }

  .business-page-shell .bp-story-team-v1.about-only,
  .business-page-shell .bp-story-team-v1.team-only{
    grid-template-columns:minmax(0,1fr)!important;
  }

  .business-page-shell .bp-about-panel-v1,
  .business-page-shell .bp-team-panel-v1{
    min-width:0!important;
    width:100%!important;
    box-sizing:border-box!important;
    text-align:left!important;
  }

  .business-page-shell .bp-story-team-v1.has-both .bp-about-panel-v1{
    padding-right:46px!important;
    border-right:1px solid rgba(148,163,184,.20)!important;
  }

  .business-page-shell .bp-story-team-v1.about-only .bp-about-panel-v1{
    max-width:820px!important;
  }

  .business-page-shell .bp-story-heading-v1,
  .business-page-shell .bp-team-head-v1{
    width:100%!important;
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:18px!important;
    margin:0 0 22px!important;
    text-align:left!important;
  }

  .business-page-shell .bp-story-title-v1{
    position:relative!important;
    display:flex!important;
    align-items:center!important;
    gap:12px!important;
    margin:0!important;
    color:#0d3541!important;
    font-size:30px!important;
    line-height:1.04!important;
    font-weight:950!important;
    letter-spacing:-.045em!important;
    text-align:left!important;
  }

  .business-page-shell .bp-story-title-v1::before{
    content:""!important;
    width:4px!important;
    height:30px!important;
    flex:0 0 4px!important;
    border-radius:999px!important;
    background:#008b7a!important;
  }

  .business-page-shell .bp-story-subtitle-v1{
    margin:7px 0 0 16px!important;
    max-width:520px!important;
    color:#64748b!important;
    font-size:12.5px!important;
    line-height:1.45!important;
    font-weight:700!important;
    text-align:left!important;
  }

  .business-page-shell .bp-about-copy-v1,
  .business-page-shell .bp-about-copy-v1 > *,
  .business-page-shell .bp-about-copy-v1 div,
  .business-page-shell .bp-about-copy-v1 p,
  .business-page-shell .bp-about-copy-v1 span,
  .business-page-shell .bp-about-copy-v1 li{
    min-width:0!important;
    max-width:100%!important;
    box-sizing:border-box!important;
    overflow-wrap:anywhere!important;
  }

  .business-page-shell .bp-about-copy-v1{
    position:relative!important;
    width:100%!important;
    margin:0!important;
    color:#334155!important;
    font-size:15.5px!important;
    line-height:1.78!important;
    font-weight:500!important;
    text-align:left!important;
  }

  .business-page-shell .bp-about-copy-v1.is-collapsed{
    max-height:168px!important;
    overflow:hidden!important;
    -webkit-mask-image:linear-gradient(to bottom,#000 0%,#000 78%,transparent 100%)!important;
    mask-image:linear-gradient(to bottom,#000 0%,#000 78%,transparent 100%)!important;
  }

  .business-page-shell .bp-about-copy-v1.is-expanded{
    max-height:none!important;
    overflow:visible!important;
    -webkit-mask-image:none!important;
    mask-image:none!important;
  }

  .business-page-shell .bp-about-copy-v1 p{
    margin:0 0 14px!important;
  }

  .business-page-shell .bp-about-copy-v1 p:last-child,
  .business-page-shell .bp-about-copy-v1 > *:last-child{
    margin-bottom:0!important;
  }

  .business-page-shell .bp-about-copy-v1 img,
  .business-page-shell .bp-about-copy-v1 video,
  .business-page-shell .bp-about-copy-v1 svg,
  .business-page-shell .bp-about-copy-v1 canvas{
    max-width:100%!important;
    height:auto!important;
    border-radius:16px!important;
  }

  .business-page-shell .bp-about-copy-v1 iframe{
    width:100%!important;
    max-width:100%!important;
    border:0!important;
    border-radius:16px!important;
  }

  .business-page-shell .bp-about-copy-v1 table{
    width:100%!important;
    max-width:100%!important;
    display:block!important;
    overflow-x:auto!important;
    -webkit-overflow-scrolling:touch!important;
  }

  .business-page-shell .bp-about-copy-v1 pre,
  .business-page-shell .bp-about-copy-v1 code{
    max-width:100%!important;
    white-space:pre-wrap!important;
    overflow-wrap:anywhere!important;
  }

  .business-page-shell .bp-about-toggle-v1{
    min-height:40px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:flex-start!important;
    gap:7px!important;
    margin:13px 0 0!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
    color:#00796f!important;
    box-shadow:none!important;
    font-size:13px!important;
    line-height:1!important;
    font-weight:950!important;
    cursor:pointer!important;
  }

  .business-page-shell .bp-about-toggle-v1 svg{
    transition:transform .18s ease!important;
  }

  .business-page-shell .bp-about-toggle-v1 svg.is-open{
    transform:rotate(180deg)!important;
  }

  .business-page-shell .bp-team-head-v1{
    margin-bottom:16px!important;
  }

  .business-page-shell .bp-team-head-v1 > div:first-child{
    min-width:0!important;
  }

  .business-page-shell .bp-team-actions-v1{
    flex:0 0 auto!important;
    display:inline-flex!important;
    align-items:center!important;
    gap:8px!important;
  }

  .business-page-shell .bp-team-actions-v1 button{
    width:38px!important;
    height:38px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    margin:0!important;
    padding:0!important;
    border:1px solid rgba(148,163,184,.28)!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.82)!important;
    color:#0d3541!important;
    box-shadow:0 7px 18px rgba(15,23,42,.04)!important;
    cursor:pointer!important;
  }

  .business-page-shell .bp-team-track-v1{
    width:100%!important;
    min-width:0!important;
    display:grid!important;
    grid-auto-flow:column!important;
    grid-auto-columns:118px!important;
    gap:18px!important;
    overflow-x:auto!important;
    overflow-y:hidden!important;
    scroll-snap-type:x proximity!important;
    scroll-padding-left:4px!important;
    -webkit-overflow-scrolling:touch!important;
    scrollbar-width:none!important;
    margin:0!important;
    padding:6px 4px 20px!important;
  }

  .business-page-shell .bp-team-track-v1::-webkit-scrollbar{
    display:none!important;
  }

  .business-page-shell .bp-team-card-v1{
    width:118px!important;
    min-width:0!important;
    display:flex!important;
    flex-direction:column!important;
    align-items:center!important;
    justify-content:flex-start!important;
    scroll-snap-align:start!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    outline:0!important;
    background:transparent!important;
    box-shadow:none!important;
    color:#0f172a!important;
    text-align:center!important;
    cursor:pointer!important;
    transition:transform .16s ease!important;
  }

  .business-page-shell .bp-team-avatar-v1{
    width:88px!important;
    height:88px!important;
    flex:0 0 88px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    overflow:hidden!important;
    border:3px solid #fff!important;
    border-radius:999px!important;
    background:#f4f1ed!important;
    box-shadow:0 9px 22px rgba(15,23,42,.09)!important;
  }

  .business-page-shell .bp-team-avatar-v1 img{
    width:100%!important;
    height:100%!important;
    display:block!important;
    object-fit:cover!important;
    object-position:center!important;
  }

  .business-page-shell .bp-team-initials-v1{
    color:#94a3b8!important;
    font-size:24px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:-.04em!important;
  }

  .business-page-shell .bp-team-card-copy-v1{
    width:100%!important;
    min-width:0!important;
    display:flex!important;
    flex-direction:column!important;
    align-items:center!important;
    gap:3px!important;
    margin-top:10px!important;
  }

  .business-page-shell .bp-team-card-copy-v1 strong{
    width:100%!important;
    min-height:30px!important;
    display:-webkit-box!important;
    overflow:hidden!important;
    -webkit-box-orient:vertical!important;
    -webkit-line-clamp:2!important;
    color:#111827!important;
    font-size:13px!important;
    line-height:1.15!important;
    font-weight:900!important;
    text-align:center!important;
  }

  .business-page-shell .bp-team-card-copy-v1 > span{
    width:100%!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
    color:#64748b!important;
    font-size:11.5px!important;
    line-height:1.2!important;
    font-style:italic!important;
    text-align:center!important;
  }

  @media(hover:hover) and (pointer:fine){
    .business-page-shell .bp-team-card-v1:hover{
      transform:translateY(-2px)!important;
    }

    .business-page-shell .bp-team-actions-v1 button:hover{
      border-color:rgba(0,139,122,.28)!important;
      background:#f4fbf9!important;
    }
  }

  @media(min-width:768px) and (max-width:1180px){
    .business-page-shell .bp-story-team-v1{
      grid-template-columns:minmax(0,1fr)!important;
      gap:32px!important;
      margin-top:36px!important;
      padding-top:34px!important;
    }

    .business-page-shell .bp-story-team-v1.has-both .bp-about-panel-v1{
      padding-right:0!important;
      padding-bottom:30px!important;
      border-right:0!important;
      border-bottom:1px solid rgba(148,163,184,.18)!important;
    }

    .business-page-shell .bp-story-team-v1.about-only .bp-about-panel-v1{
      max-width:900px!important;
    }

    .business-page-shell .bp-team-track-v1{
      grid-auto-columns:112px!important;
      gap:18px!important;
    }

    .business-page-shell .bp-team-card-v1{
      width:112px!important;
    }
  }

  @media(max-width:767px){
    .business-page-shell .bp-story-team-v1{
      width:100%!important;
      display:block!important;
      margin:28px auto 0!important;
      padding:28px 0 8px!important;
      border-top:1px solid rgba(148,163,184,.20)!important;
    }

    .business-page-shell .bp-story-team-v1.has-both .bp-about-panel-v1{
      padding:0 0 30px!important;
      border-right:0!important;
      border-bottom:1px solid rgba(148,163,184,.16)!important;
    }

    .business-page-shell .bp-team-panel-v1{
      padding-top:30px!important;
    }

    .business-page-shell .bp-story-team-v1.team-only .bp-team-panel-v1{
      padding-top:0!important;
    }

    .business-page-shell .bp-story-heading-v1,
    .business-page-shell .bp-team-head-v1{
      display:block!important;
      margin-bottom:16px!important;
    }

    .business-page-shell .bp-story-title-v1{
      gap:10px!important;
      font-size:28px!important;
      line-height:1.03!important;
    }

    .business-page-shell .bp-story-title-v1::before{
      width:4px!important;
      height:27px!important;
      flex-basis:4px!important;
    }

    .business-page-shell .bp-story-subtitle-v1{
      margin:7px 0 0 14px!important;
      max-width:92%!important;
      font-size:12px!important;
    }

    .business-page-shell .bp-about-copy-v1{
      font-size:15px!important;
      line-height:1.82!important;
    }

    .business-page-shell .bp-about-copy-v1.is-collapsed{
      max-height:178px!important;
      -webkit-mask-image:linear-gradient(to bottom,#000 0%,#000 84%,transparent 100%)!important;
      mask-image:linear-gradient(to bottom,#000 0%,#000 84%,transparent 100%)!important;
    }

    .business-page-shell .bp-about-toggle-v1{
      min-height:44px!important;
      margin-top:9px!important;
      font-size:13.5px!important;
    }

    .business-page-shell .bp-team-actions-v1{
      display:none!important;
    }

    .business-page-shell .bp-team-track-v1{
      width:auto!important;
      grid-auto-columns:108px!important;
      gap:16px!important;
      margin:0 -16px!important;
      padding:6px 16px 26px!important;
      scroll-snap-type:x mandatory!important;
      scroll-padding-left:16px!important;
    }

    .business-page-shell .bp-team-card-v1{
      width:108px!important;
    }

    .business-page-shell .bp-team-avatar-v1{
      width:78px!important;
      height:78px!important;
      flex-basis:78px!important;
      box-shadow:0 8px 18px rgba(15,23,42,.075)!important;
    }

    .business-page-shell .bp-team-card-copy-v1{
      margin-top:9px!important;
    }

    .business-page-shell .bp-team-card-copy-v1 strong{
      min-height:29px!important;
      font-size:12.5px!important;
    }

    .business-page-shell .bp-team-card-copy-v1 > span{
      font-size:11px!important;
    }
  }

  @media(max-width:420px){
    .business-page-shell .bp-team-track-v1{
      grid-auto-columns:104px!important;
      gap:14px!important;
    }

    .business-page-shell .bp-team-card-v1{
      width:104px!important;
    }
  }

  @media(prefers-reduced-motion:reduce){
    .business-page-shell .bp-team-card-v1,
    .business-page-shell .bp-about-toggle-v1 svg{
      transition:none!important;
    }
  }



  /* =========================================================
     LOOP BUSINESSPAGE REVIEWS CONSOLIDATION V4
     One isolated reviews contract after Hero V7 + About/Team V1.
     Data / Firestore reads remain unchanged.
     ========================================================= */

  .business-page-shell .bp-reviews-v4{
    width:100%!important;
    min-width:0!important;
    padding:34px 0 0!important;
    margin:34px 0 0!important;
    border-top:1px solid rgba(15,118,110,.12)!important;
  }

  .business-page-shell .bp-reviews-header-v4{
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:24px!important;
    margin:0 0 22px!important;
  }

  .business-page-shell .bp-reviews-heading-v4{
    position:relative!important;
    min-width:0!important;
    padding-left:18px!important;
  }

  .business-page-shell .bp-reviews-heading-v4::before{
    content:""!important;
    position:absolute!important;
    left:0!important;
    top:2px!important;
    width:4px!important;
    height:30px!important;
    border-radius:999px!important;
    background:#0f8f80!important;
  }

  .business-page-shell .bp-reviews-heading-v4 h3{
    margin:0!important;
    color:#0d3440!important;
    font-size:clamp(25px,2.1vw,34px)!important;
    line-height:1.02!important;
    letter-spacing:-.045em!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-reviews-heading-v4 p{
    margin:7px 0 0!important;
    color:#68778d!important;
    font-size:12.5px!important;
    line-height:1.45!important;
    font-weight:700!important;
  }

  .business-page-shell .bp-reviews-header-actions-v4{
    flex:0 0 auto!important;
    display:flex!important;
    align-items:center!important;
    justify-content:flex-end!important;
    gap:10px!important;
    padding-top:1px!important;
  }

  .business-page-shell .bp-reviews-status-v4,
  .business-page-shell .bp-reviews-view-all-v4,
  .business-page-shell .bp-reviews-collapse-v4{
    min-height:36px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:7px!important;
    border-radius:999px!important;
    font-size:11.5px!important;
    line-height:1!important;
    font-weight:900!important;
    white-space:nowrap!important;
  }

  .business-page-shell .bp-reviews-status-v4{
    padding:0 13px!important;
    border:1px solid #f1d39a!important;
    background:#fff9ed!important;
    color:#9a6500!important;
  }

  .business-page-shell .bp-reviews-status-v4.has-rating{
    border-color:#bde9e1!important;
    background:#effcf8!important;
    color:#08796c!important;
  }

  .business-page-shell .bp-reviews-view-all-v4,
  .business-page-shell .bp-reviews-collapse-v4{
    padding:0 13px!important;
    border:1px solid #dbe7e5!important;
    background:#fff!important;
    color:#08796c!important;
    cursor:pointer!important;
  }

  .business-page-shell .bp-reviews-view-all-v4:hover,
  .business-page-shell .bp-reviews-collapse-v4:hover{
    background:#f2fbf8!important;
    border-color:#b8ddd6!important;
  }

  .business-page-shell .bp-reviews-empty-v4{
    width:100%!important;
    min-height:92px!important;
    display:flex!important;
    align-items:center!important;
    gap:15px!important;
    padding:18px 20px!important;
    border:1px solid #d7ebe7!important;
    border-radius:20px!important;
    background:linear-gradient(135deg,#f7fffc 0%,#fffaf3 100%)!important;
  }

  .business-page-shell .bp-reviews-empty-icon-v4{
    flex:0 0 42px!important;
    width:42px!important;
    height:42px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:50%!important;
    background:#fff7e8!important;
    color:#f0a000!important;
    border:1px solid #f2d89f!important;
  }

  .business-page-shell .bp-reviews-empty-v4 strong{
    display:block!important;
    margin:0!important;
    color:#173b44!important;
    font-size:15px!important;
    line-height:1.25!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-reviews-empty-v4 p{
    margin:5px 0 0!important;
    color:#718096!important;
    font-size:12px!important;
    line-height:1.45!important;
    font-weight:650!important;
  }

  .business-page-shell .bp-reviews-overview-v4{
    width:100%!important;
    min-width:0!important;
    display:grid!important;
    grid-template-columns:minmax(170px,.72fr) minmax(300px,1.65fr)!important;
    gap:28px!important;
    align-items:center!important;
    padding:22px 24px!important;
    margin:0 0 18px!important;
    border-radius:22px!important;
    background:#fffdf8!important;
    border:1px solid rgba(15,118,110,.10)!important;
    box-shadow:0 10px 30px rgba(24,48,44,.035)!important;
  }

  .business-page-shell .bp-reviews-score-v4{
    min-width:0!important;
    display:grid!important;
    grid-template-columns:auto 1fr!important;
    grid-template-areas:
      "score stars"
      "score count"!important;
    column-gap:14px!important;
    align-items:center!important;
  }

  .business-page-shell .bp-reviews-score-v4 > strong{
    grid-area:score!important;
    color:#0c3b45!important;
    font-size:52px!important;
    line-height:.9!important;
    letter-spacing:-.06em!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-reviews-stars-v4{
    grid-area:stars!important;
    display:flex!important;
    align-items:center!important;
    gap:3px!important;
    color:#c7d3d1!important;
  }

  .business-page-shell .bp-reviews-stars-v4 svg.is-filled,
  .business-page-shell .bp-review-stars-v4 svg.is-filled{
    color:#f2a000!important;
  }

  .business-page-shell .bp-reviews-score-v4 > span{
    grid-area:count!important;
    color:#718096!important;
    font-size:11.5px!important;
    line-height:1.25!important;
    font-weight:750!important;
  }

  .business-page-shell .bp-reviews-distribution-v4{
    min-width:0!important;
    display:grid!important;
    gap:6px!important;
  }

  .business-page-shell .bp-review-dist-row-v4{
    min-width:0!important;
    display:grid!important;
    grid-template-columns:13px 13px minmax(90px,1fr) 24px!important;
    gap:6px!important;
    align-items:center!important;
    color:#8b98a8!important;
    font-size:10.5px!important;
    font-weight:850!important;
  }

  .business-page-shell .bp-review-dist-row-v4 > svg{
    color:#f2a000!important;
  }

  .business-page-shell .bp-review-dist-row-v4 small{
    text-align:right!important;
    color:#8794a5!important;
    font-size:10px!important;
    font-weight:800!important;
  }

  .business-page-shell .bp-review-dist-track-v4{
    position:relative!important;
    height:6px!important;
    overflow:hidden!important;
    border-radius:999px!important;
    background:#e9efed!important;
  }

  .business-page-shell .bp-review-dist-track-v4 > span{
    position:absolute!important;
    inset:0 auto 0 0!important;
    min-width:0!important;
    border-radius:inherit!important;
    background:#0f8f80!important;
  }

  .business-page-shell .bp-reviews-list-v4{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:14px!important;
  }

  .business-page-shell .bp-review-card-v4{
    min-width:0!important;
    display:flex!important;
    flex-direction:column!important;
    gap:12px!important;
    padding:17px 18px!important;
    border-radius:20px!important;
    border:1px solid rgba(15,118,110,.105)!important;
    background:#fff!important;
    box-shadow:0 8px 24px rgba(24,48,44,.035)!important;
  }

  .business-page-shell .bp-review-card-top-v4{
    min-width:0!important;
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:12px!important;
  }

  .business-page-shell .bp-review-person-v4{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    gap:10px!important;
  }

  .business-page-shell .bp-review-avatar-v4{
    flex:0 0 38px!important;
    width:38px!important;
    height:38px!important;
    overflow:hidden!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:50%!important;
    background:#eef8f5!important;
    border:1px solid #d7e9e5!important;
    color:#0f7f72!important;
    font-size:13px!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-review-avatar-v4 img{
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    display:block!important;
  }

  .business-page-shell .bp-review-identity-v4{
    min-width:0!important;
  }

  .business-page-shell .bp-review-identity-v4 > strong{
    display:block!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
    color:#173b44!important;
    font-size:13px!important;
    line-height:1.25!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-review-meta-v4{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    flex-wrap:wrap!important;
    gap:5px 8px!important;
    margin-top:4px!important;
  }

  .business-page-shell .bp-review-service-v4{
    max-width:190px!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    white-space:nowrap!important;
    color:#0b8174!important;
    font-size:9.5px!important;
    line-height:1!important;
    font-weight:850!important;
  }

  .business-page-shell .bp-review-date-v4{
    color:#98a4b2!important;
    font-size:9.5px!important;
    line-height:1!important;
    font-weight:750!important;
  }

  .business-page-shell .bp-review-score-pill-v4{
    flex:0 0 auto!important;
    min-height:28px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:5px!important;
    padding:0 9px!important;
    border-radius:999px!important;
    background:#fff8e9!important;
    border:1px solid #f0d59d!important;
    color:#d28b00!important;
    font-size:10.5px!important;
  }

  .business-page-shell .bp-review-score-pill-v4 strong{
    color:#7b5a13!important;
    font-size:10.5px!important;
    font-weight:950!important;
  }

  .business-page-shell .bp-review-stars-v4{
    display:flex!important;
    align-items:center!important;
    gap:3px!important;
    color:#cbd6d4!important;
  }

  .business-page-shell .bp-review-comment-v4{
    margin:0!important;
    color:#47586e!important;
    font-size:12px!important;
    line-height:1.55!important;
    font-weight:650!important;
    overflow-wrap:anywhere!important;
  }

  .business-page-shell .bp-review-comment-v4.is-empty{
    color:#9aa5b3!important;
    font-style:italic!important;
  }

  .business-page-shell .bp-reviews-collapse-v4{
    margin:16px auto 0!important;
  }

  .business-page-shell .bp-reviews-loading-v4{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:14px!important;
  }

  .business-page-shell .bp-reviews-overview-skeleton-v4{
    grid-column:1/-1!important;
    height:120px!important;
    border-radius:22px!important;
    background:linear-gradient(90deg,#f1f5f3 25%,#fafcfb 50%,#f1f5f3 75%)!important;
    background-size:200% 100%!important;
    animation:bp-reviews-v4-shimmer 1.25s ease-in-out infinite!important;
  }

  .business-page-shell .bp-review-card-skeleton-v4{
    height:140px!important;
    border-radius:20px!important;
    background:linear-gradient(90deg,#f1f5f3 25%,#fafcfb 50%,#f1f5f3 75%)!important;
    background-size:200% 100%!important;
    animation:bp-reviews-v4-shimmer 1.25s ease-in-out infinite!important;
  }

  @keyframes bp-reviews-v4-shimmer{
    0%{background-position:200% 0}
    100%{background-position:-200% 0}
  }

  @media(max-width:900px){
    .business-page-shell .bp-reviews-overview-v4{
      grid-template-columns:1fr!important;
      gap:18px!important;
    }
  }

  @media(max-width:720px){
    .business-page-shell .bp-reviews-v4{
      padding-top:26px!important;
      margin-top:26px!important;
      padding-bottom:88px!important;
    }

    .business-page-shell .bp-reviews-header-v4{
      display:grid!important;
      grid-template-columns:1fr!important;
      gap:14px!important;
      margin-bottom:17px!important;
    }

    .business-page-shell .bp-reviews-heading-v4{
      padding-left:14px!important;
    }

    .business-page-shell .bp-reviews-heading-v4::before{
      width:4px!important;
      height:28px!important;
    }

    .business-page-shell .bp-reviews-heading-v4 h3{
      max-width:280px!important;
      font-size:28px!important;
      line-height:.98!important;
    }

    .business-page-shell .bp-reviews-heading-v4 p{
      max-width:340px!important;
      margin-top:8px!important;
      font-size:11.5px!important;
    }

    .business-page-shell .bp-reviews-header-actions-v4{
      justify-content:flex-start!important;
      flex-wrap:wrap!important;
      gap:8px!important;
    }

    .business-page-shell .bp-reviews-status-v4,
    .business-page-shell .bp-reviews-view-all-v4{
      min-height:34px!important;
    }

    .business-page-shell .bp-reviews-empty-v4{
      min-height:0!important;
      padding:16px!important;
      border-radius:18px!important;
      align-items:flex-start!important;
    }

    .business-page-shell .bp-reviews-empty-icon-v4{
      flex-basis:38px!important;
      width:38px!important;
      height:38px!important;
    }

    .business-page-shell .bp-reviews-overview-v4{
      padding:18px!important;
      margin-bottom:14px!important;
      border-radius:18px!important;
      gap:16px!important;
    }

    .business-page-shell .bp-reviews-score-v4 > strong{
      font-size:46px!important;
    }

    .business-page-shell .bp-reviews-list-v4,
    .business-page-shell .bp-reviews-loading-v4{
      grid-template-columns:1fr!important;
      gap:11px!important;
    }

    .business-page-shell .bp-review-card-v4{
      padding:15px!important;
      border-radius:18px!important;
      gap:10px!important;
    }

    .business-page-shell .bp-review-service-v4{
      max-width:145px!important;
    }

    .business-page-shell .bp-review-comment-v4{
      font-size:11.8px!important;
      line-height:1.55!important;
    }

    .business-page-shell .bp-reviews-overview-skeleton-v4{
      grid-column:auto!important;
      height:110px!important;
    }
  }

  @media(max-width:390px){
    .business-page-shell .bp-reviews-heading-v4 h3{
      max-width:250px!important;
      font-size:26px!important;
    }

    .business-page-shell .bp-review-card-top-v4{
      gap:8px!important;
    }

    .business-page-shell .bp-review-avatar-v4{
      flex-basis:36px!important;
      width:36px!important;
      height:36px!important;
    }

    .business-page-shell .bp-review-score-pill-v4{
      padding:0 8px!important;
    }
  }

  @media(prefers-reduced-motion:reduce){
    .business-page-shell .bp-reviews-overview-skeleton-v4,
    .business-page-shell .bp-review-card-skeleton-v4{
      animation:none!important;
    }
  }


  /* =========================================================
     LOOP BUSINESSPAGE POLISH V4
     Scope: final mobile UX/rhythm pass only.
     - Sticky dock yields when final CTA is on screen.
     - Service cards get more readable mobile typography.
     - About preview is shorter/more intentional.
     - Reviews header/summary are slightly more compact.
     Desktop structure/data remain unchanged.
     ========================================================= */

  @media (max-width: 767px) {
    /* Final CTA becomes the only booking CTA when it enters the viewport. */
    .business-page-shell .bp-actions {
      transition:
        opacity .18s ease,
        transform .18s ease,
        visibility .18s ease !important;
      will-change: opacity, transform !important;
    }

    .business-page-shell .bp-actions.is-suppressed-near-final {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      transform: translate3d(0, 18px, 0) !important;
    }

    /* The content still clears the dock while it is visible, but without a huge dead zone. */
    .business-page-shell {
      --loop-bp-content-end-pad: calc(
        var(--loop-bp-dock-height, 58px) +
        var(--loop-bp-device-bottom-safe, env(safe-area-inset-bottom, 0px)) +
        18px
      ) !important;
    }

    .business-page-shell .bp-final-cta {
      scroll-margin-bottom: 22px !important;
    }

    /* Service rail: slightly wider cards + two-line titles instead of abrupt ellipsis. */
    .business-page-shell .bp-packages-rail,
    .business-page-shell .event-packages-grid {
      grid-auto-columns: minmax(164px, 48vw) !important;
      gap: 12px !important;
      scroll-padding-inline: 2px !important;
    }

    .business-page-shell .event-package-card h4,
    .business-page-shell .event-package-card .event-package-body h4,
    .business-page-shell .event-package-card .event-package-body.no-photo-body h4 {
      white-space: normal !important;
      display: -webkit-box !important;
      -webkit-box-orient: vertical !important;
      -webkit-line-clamp: 2 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      line-height: 1.12 !important;
      min-height: 30px !important;
      max-height: 32px !important;
      overflow-wrap: normal !important;
      word-break: normal !important;
    }

    .business-page-shell .event-package-meta {
      gap: 5px !important;
    }

    .business-page-shell .event-package-meta span:not(.event-package-chevron) {
      max-width: 100% !important;
    }

    /* About preview: enough context to sell the business without becoming a wall of text. */
    .business-page-shell .bp-about-copy-v1:not(.is-expanded) {
      max-height: 142px !important;
      -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 76%, transparent 100%) !important;
      mask-image: linear-gradient(to bottom, #000 0%, #000 76%, transparent 100%) !important;
    }

    .business-page-shell .bp-about-toggle-v1 {
      margin-top: 10px !important;
      min-height: 36px !important;
    }

    /* Reviews: preserve hierarchy while reducing vertical bulk on narrow screens. */
    .business-page-shell .bp-reviews-v4 {
      padding-top: 24px !important;
      margin-top: 24px !important;
      padding-bottom: 72px !important;
    }

    .business-page-shell .bp-reviews-header-v4 {
      gap: 10px !important;
      margin-bottom: 14px !important;
    }

    .business-page-shell .bp-reviews-heading-v4 p {
      margin-top: 6px !important;
    }

    .business-page-shell .bp-reviews-header-actions-v4 {
      gap: 7px !important;
    }

    .business-page-shell .bp-reviews-status-v4,
    .business-page-shell .bp-reviews-view-all-v4 {
      min-height: 32px !important;
      padding-left: 11px !important;
      padding-right: 11px !important;
    }

    .business-page-shell .bp-reviews-overview-v4 {
      padding: 15px !important;
      gap: 12px !important;
      margin-bottom: 12px !important;
    }

    .business-page-shell .bp-reviews-list-v4 {
      gap: 12px !important;
    }
  }

  @media (max-width: 390px) {
    .business-page-shell .bp-packages-rail,
    .business-page-shell .event-packages-grid {
      grid-auto-columns: minmax(158px, 49vw) !important;
    }

    .business-page-shell .event-package-card h4,
    .business-page-shell .event-package-card .event-package-body h4,
    .business-page-shell .event-package-card .event-package-body.no-photo-body h4 {
      font-size: 12.8px !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .business-page-shell .bp-actions {
      transition: none !important;
    }
  }


  /* =========================================================
     LOOP BUSINESSPAGE POLISH V5
     Mobile QA follow-up:
     - Final CTA observer attaches after loading.
     - High-specificity service rules beat legacy !important CSS.
     - Meta row stops reserving phantom chevron space.
     ========================================================= */

  @media (max-width: 767px) {
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail {
      grid-auto-columns: clamp(170px, 46vw, 184px) !important;
      gap: 12px !important;
      scroll-padding-inline: 2px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card
      > .event-package-body
      > div:first-child,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card.has-service-icon
      > .event-package-body.no-photo-body
      > div:first-child {
      height: 50px !important;
      min-height: 50px !important;
      max-height: 50px !important;
      overflow: hidden !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card h4 {
      white-space: normal !important;
      display: -webkit-box !important;
      -webkit-box-orient: vertical !important;
      -webkit-line-clamp: 2 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      overflow-wrap: normal !important;
      word-break: normal !important;
      line-height: 1.12 !important;
      min-height: 30px !important;
      max-height: 32px !important;
      font-size: 13px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card .event-package-meta,
    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card.has-service-icon .event-package-meta {
      left: 12px !important;
      right: 12px !important;
      width: auto !important;
      max-width: none !important;
      padding-right: 0 !important;
      gap: 5px !important;
      overflow: hidden !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card .event-package-meta span:nth-child(1) {
      flex: 0 0 auto !important;
      min-width: 0 !important;
      max-width: 58px !important;
      padding-left: 9px !important;
      padding-right: 9px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card .event-package-meta span:nth-child(2) {
      flex: 1 1 auto !important;
      min-width: 0 !important;
      max-width: none !important;
      padding-left: 9px !important;
      padding-right: 9px !important;
      font-size: 10.5px !important;
    }

    .business-page-shell .event-packages-grid.bp-packages-rail.mixed-service-rail
      > article.event-package-card .event-package-chevron {
      display: none !important;
    }

    .business-page-shell .bp-actions.is-suppressed-near-final {
      display: none !important;
    }
  }


  /* =========================================================
     LOOP BUSINESSPAGE POLISH V6
     - Mobile hero: clean logo-first layout; project thumbnails move into gallery trigger.
     - Reviews: expansion control lives after the review cards ("Ver más").
     ========================================================= */

  .business-page-shell .bp-hero-gallery-mobile-trigger-v8{
    display:none!important;
  }

  .business-page-shell .bp-reviews-more-v6{
    margin:14px 0 0!important;
    min-height:40px!important;
    padding:0 15px!important;
    border:1px solid rgba(0,103,95,.16)!important;
    border-radius:999px!important;
    background:#ffffff!important;
    color:#00796f!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:7px!important;
    font-family:inherit!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:950!important;
    cursor:pointer!important;
    box-shadow:0 8px 20px rgba(54,41,25,.045)!important;
  }

  .business-page-shell .bp-reviews-more-v6 svg{
    transition:transform .18s ease!important;
  }

  .business-page-shell .bp-reviews-more-v6 svg.is-open{
    transform:rotate(180deg)!important;
  }

  @media(max-width:767px){
    /* On phones, the small horizontal thumbs looked like desktop squeezed into mobile.
       Keep the hero clean and let a single premium pill open the existing gallery. */
    .business-page-shell .bp-hero-media-v7.has-gallery{
      position:relative!important;
    }

    .business-page-shell .bp-hero-media-v7.has-gallery .bp-hero-media-grid-v7{
      display:block!important;
    }

    .business-page-shell .bp-hero-media-v7.has-gallery .bp-hero-rail-v7{
      display:none!important;
    }

    .business-page-shell .bp-hero-gallery-mobile-trigger-v8{
      position:absolute!important;
      right:12px!important;
      bottom:12px!important;
      z-index:4!important;
      min-height:38px!important;
      display:inline-flex!important;
      align-items:center!important;
      justify-content:center!important;
      gap:7px!important;
      padding:0 12px!important;
      border:1px solid rgba(255,255,255,.76)!important;
      border-radius:999px!important;
      background:rgba(255,255,255,.94)!important;
      color:#0f5f57!important;
      font-family:inherit!important;
      font-size:11.5px!important;
      line-height:1!important;
      font-weight:950!important;
      box-shadow:0 10px 26px rgba(15,23,42,.16)!important;
      backdrop-filter:blur(12px)!important;
      -webkit-backdrop-filter:blur(12px)!important;
      cursor:pointer!important;
    }

    .business-page-shell .bp-hero-gallery-mobile-trigger-v8 strong{
      min-width:22px!important;
      height:22px!important;
      padding:0 6px!important;
      border-radius:999px!important;
      background:#eaf8f4!important;
      color:#00796f!important;
      display:inline-flex!important;
      align-items:center!important;
      justify-content:center!important;
      font-size:10px!important;
      font-weight:950!important;
    }

    .business-page-shell .bp-reviews-more-v6{
      width:100%!important;
      min-height:44px!important;
      margin-top:12px!important;
      border-radius:16px!important;
    }
  }

  @media(prefers-reduced-motion:reduce){
    .business-page-shell .bp-reviews-more-v6 svg{
      transition:none!important;
    }
  }


`;



const TeamCard = ({ person, onClick }) => {
  const initials = (person?.name || "U")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const displayName = person?.name || "Nombre";
  const displayRole = person?.role || person?.title || "";

  return (
    <button
      type="button"
      onClick={() => onClick && onClick(person)}
      className="bp-team-card-v1"
      aria-label={`Ver perfil de ${displayName}`}
    >
      <span className="bp-team-avatar-v1" aria-hidden="true">
        {person?.photoUrl ? (
          <img
            src={person.photoUrl}
            alt=""
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="bp-team-initials-v1">{initials}</span>
        )}
      </span>

      <span className="bp-team-card-copy-v1">
        <strong>{displayName}</strong>
        {displayRole ? <span>{displayRole}</span> : null}
      </span>
    </button>
  );
};

const EmployeeProjectsModal = ({ employee, onClose }) => {
  const [viewerUrl, setViewerUrl] = useState(null);
  if (!employee) return null;
  const projects = Array.isArray(employee.projects) ? employee.projects : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 120, padding: 12 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, margin: '6vh auto', background: '#fff', padding: 18, borderRadius: 16 }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">{t("Trabajos de") + " "}{employee.name}</h3>
            <div className="text-xs text-slate-500">{employee.role || ''}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded bg-gray-100 hover:bg-gray-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="text-sm text-slate-500">{t("No hay trabajos subidos para este trabajador.")}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {projects.map((url, idx) => (
                <div key={idx} className="rounded overflow-hidden bg-slate-100" style={{ height: 140, cursor: 'pointer' }}>
                  <img
                    src={url}
                    alt={`Trabajo ${idx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onClick={() => setViewerUrl(url)}
                    onError={(e) => { e.currentTarget.style.opacity = 0.6; e.currentTarget.title = 'No se pudo cargar imagen'; }}
                  />
                </div>
              ))}
            </div>

            {viewerUrl && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 130 }} onClick={() => setViewerUrl(null)}>
                <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', maxHeight: '90%', margin: '5vh auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={viewerUrl} alt="viewer" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 10 }} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const RatingRow = ({ r }) => {
  const rawRating = Number(r.rating || 0);
  const ratingValue = Number.isFinite(rawRating) ? Math.max(0, Math.min(5, rawRating)) : 0;
  const ratingLabel = ratingValue > 0 ? ratingValue.toFixed(ratingValue % 1 === 0 ? 0 : 1) : "-";
  const filledStars = Math.max(0, Math.min(5, Math.round(ratingValue)));
  const comment = String(r.comment || "").trim();
  const dateLabel = String(r.dateStr || "").trim();
  const serviceLabel = String(r.serviceName || "").trim();
  const userName = String(r.userName || "Usuario").trim() || "Usuario";

  return (
    <article className="bp-review-card-v4">
      <div className="bp-review-card-top-v4">
        <div className="bp-review-person-v4">
          <div className="bp-review-avatar-v4" aria-hidden="true">
            {r.userPhoto ? (
              <img src={r.userPhoto} alt="" loading="lazy" decoding="async" />
            ) : (
              <span>{userName.slice(0, 1).toUpperCase()}</span>
            )}
          </div>

          <div className="bp-review-identity-v4">
            <strong>{userName}</strong>
            <div className="bp-review-meta-v4">
              {serviceLabel ? <span className="bp-review-service-v4">{serviceLabel}</span> : null}
              {dateLabel ? <span className="bp-review-date-v4">{dateLabel}</span> : null}
            </div>
          </div>
        </div>

        <span className="bp-review-score-pill-v4" aria-label={`Valoración ${ratingLabel} de 5`}>
          <Star size={13} fill="currentColor" />
          <strong>{ratingLabel}</strong>
        </span>
      </div>

      <div className="bp-review-stars-v4" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            size={16}
            className={index < filledStars ? "is-filled" : ""}
            fill={index < filledStars ? "currentColor" : "none"}
          />
        ))}
      </div>

      {comment ? (
        <p className="bp-review-comment-v4">{comment}</p>
      ) : (
        <p className="bp-review-comment-v4 is-empty">{t("Valoración sin comentario.")}</p>
      )}
    </article>
  );
};

function getEffectiveUser() {
  try {
    const u = auth && auth.currentUser ? auth.currentUser : null;
    if (u && (u.uid || u.email)) return u;
  } catch (_) {}

  try {
    if (isIosCapacitor()) {
      const ma = getManualAuth();
      if (ma && isManualAuthLikelyValid(ma) && (ma.uid || ma.email)) {
        return {
          uid: ma.uid || null,
          email: ma.email || null,
          displayName: ma.displayName || '',
        };
      }
    }
  } catch (_) {}

  return null;
}

function firstNonEmpty(...values) {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v != null && typeof v !== "string") return v;
  }
  return "";
}

function pickImageUrl(...values) {
  for (const v of values) {
    if (!v) continue;
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "object") {
      const candidate = v.url || v.src || v.path || v.downloadURL || v.image || v.logoUrl || v.photoUrl || null;
      if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    }
  }
  return null;
}

function extractLatLngFromGoogleMapsUrl(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;

  let m = text.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)(?:,|\/|\?)/);
  if (m) return { lat: m[1], lng: m[2] };

  m = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2] };

  m = text.match(/[?&]ll=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2] };

  m = text.match(/[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2] };

  return null;
}

function buildMapEmbedSrc(biz) {
  const link = String(biz?.__viewGoogleMapsLink || biz?.googleMapsLink || biz?.mapsLink || "").trim();
  const address = String(biz?.__viewAddress || biz?.address || "").trim();
  const name = String(biz?.__viewName || biz?.name || "").trim();

  if (link.includes("<iframe")) {
    const match = link.match(/src=["']([^"']+)["']/i);
    return match && match[1] ? match[1] : "";
  }

  if (link.includes("/embed?")) return link;

  const coords = extractLatLngFromGoogleMapsUrl(link);
  if (coords) {
    return `https://www.google.com/maps?q=${coords.lat},${coords.lng}&z=17&output=embed`;
  }

  const query = address || name || link;
  if (!query) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`;
}


function onlyDigits(value) {
  return String(value || "").replace(/[^\d]/g, "");
}

function normalizePhoneForTel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return `tel:${raw.replace(/\s+/g, "")}`;
  const digits = onlyDigits(raw);
  if (!digits) return "";
  // Panama default if user saved local number only.
  return `tel:+507${digits}`;
}

function normalizeWhatsAppLink(value) {
  const raw = String(value || "").trim();
  const digits = onlyDigits(raw);
  if (!digits) return "";
  const withCountry = raw.startsWith("+") || digits.length > 8 ? digits : `507${digits}`;
  return `https://wa.me/${withCountry}`;
}

function formatContactDisplay(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const digits = onlyDigits(raw);
  if (!digits) return raw;

  // Panamá: mostrar siempre limpio como +507 6808-4475 o 6808-4475.
  // Esto evita que el número se parta raro en las tarjetas públicas.
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  if (digits.length === 11 && digits.startsWith("507")) {
    const local = digits.slice(3);
    return `+507 ${local.slice(0, 4)}-${local.slice(4)}`;
  }

  if (digits.length === 12 && digits.startsWith("507")) {
    const local = digits.slice(-8);
    return `+507 ${local.slice(0, 4)}-${local.slice(4)}`;
  }

  // Fallback: conserva formato humano, pero limpia espacios múltiples.
  return raw.replace(/\s+/g, " ");
}


function normalizeInstagram(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://instagram.com/${raw.replace(/^@/, "")}`;
}

function normalizeWebsite(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}


const WEEK_DAYS = [
  { key: "monday", short: "Lun", label: "Lunes", jsDay: 1 },
  { key: "tuesday", short: "Mar", label: "Martes", jsDay: 2 },
  { key: "wednesday", short: "Mié", label: "Miércoles", jsDay: 3 },
  { key: "thursday", short: "Jue", label: "Jueves", jsDay: 4 },
  { key: "friday", short: "Vie", label: "Viernes", jsDay: 5 },
  { key: "saturday", short: "Sáb", label: "Sábado", jsDay: 6 },
  { key: "sunday", short: "Dom", label: "Domingo", jsDay: 0 },
];

function normalizeAvailabilityRanges(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    if (value.closed === true || value.enabled === false) return [];
    if (Array.isArray(value.ranges)) return value.ranges;
    if (value.from || value.start || value.fromTime || value.startTime) return [value];
  }
  return [];
}

function normalizeBusinessAvailability(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const out = {};
  WEEK_DAYS.forEach((day) => {
    out[day.key] = normalizeAvailabilityRanges(source[day.key]).map((r) => ({
      from: r.from || r.start || r.fromTime || r.startTime || "09:00",
      to: r.to || r.end || r.toTime || r.endTime || "18:00",
    })).filter((r) => r.from && r.to);
  });
  return out;
}



function formatOpeningRanges(ranges) {
  const safe = Array.isArray(ranges) ? ranges : [];
  if (!safe.length) return ["Cerrado"];
  return safe.map((r) => `${String(r.from || "").trim()} - ${String(r.to || "").trim()}`);
}

function hasAvailabilityContent(availability) {
  if (!availability || typeof availability !== "object") return false;
  return WEEK_DAYS.some((d) => Array.isArray(availability[d.key]) && availability[d.key].length > 0);
}

function BusinessOpeningHours({ availability, fallbackHours, business }) {
  const [expanded, setExpanded] = useState(false);
  const hasStructuredHours = hasAvailabilityContent(availability);
  const todayKey = businessWeekday(new Date(),business);
  const todayRanges = hasStructuredHours ? formatOpeningRanges(availability[todayKey]) : [];

  if (!hasStructuredHours && !fallbackHours) return null;

  const todayText = hasStructuredHours
    ? (todayRanges && todayRanges.length ? todayRanges.join(" / ") : t("Cerrado"))
    : String(fallbackHours || "").trim();

  return (
    <div className="bp-info-row bp-hours-row">
      <span className="bp-info-icon"><Clock className="w-5 h-5" /></span>
      <div className="bp-info-content">
        <div className="bp-info-label">{t("Horario")}</div>

        <div className="bp-hours-summary">
          <span className="bp-hours-summary-label">{t("Hoy")}</span>
          <strong>{todayText}</strong>
        </div>

        {hasStructuredHours ? (
          <>
            {expanded ? (
              <div className="bp-hours-week bp-hours-week-premium">
                {WEEK_DAYS.map((day) => {
                  const rows = formatOpeningRanges(availability[day.key]);
                  const value = rows && rows.length ? rows.join(" / ") : t("Cerrado");
                  return (
                    <div className="bp-hours-day" key={day.key}>
                      <span>{t(day.label)}</span>
                      <strong>{value}</strong>
                    </div>
                  );
                })}
              </div>
            ) : null}

            <button type="button" className="bp-hours-toggle" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
              {expanded ? t("Ocultar semana") : t("Ver semana")}
              <ChevronDown className={expanded ? "is-open" : ""} size={14} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function normalizeBusinessServiceAreas(raw) {
  const source = Array.isArray(raw?.serviceAreasAdvanced)
    ? raw.serviceAreasAdvanced
    : Array.isArray(raw?.deliveryAreas)
      ? raw.deliveryAreas
      : Array.isArray(raw?.serviceAreas)
        ? raw.serviceAreas
        : String(raw?.serviceArea || raw?.coverageArea || "").split(",");

  return source
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") {
        const label = item.trim();
        return label ? { label, extraFee: 0, level: "custom" } : null;
      }
      const label = item.label || [item.zone, item.corregimiento, item.district, item.province].filter(Boolean).join(", ");
      if (!label) return null;
      return {
        ...item,
        label,
        extraFee: Number(item.extraFee || item.fee || item.deliveryFee || 0),
      };
    })
    .filter(Boolean);
}

function normalizeBusinessView(raw) {
  const biz = raw || {};
  const nested = biz.businessPage || biz.profile || biz.page || {};

  const logoUrl = pickImageUrl(
    biz.logoUrl,
    biz.logo,
    biz.photoUrl,
    biz.mainPhotoUrl,
    biz.coverImage,
    biz.image,
    biz.heroImage,
    nested.logoUrl,
    nested.logo,
    nested.photoUrl,
    nested.mainPhotoUrl,
    nested.coverImage,
    nested.image,
    nested.heroImage
  );

  const name = firstNonEmpty(
    biz.name,
    biz.title,
    biz.businessName,
    nested.name,
    nested.title,
    nested.businessName
  ) || "Nombre del negocio";

  const tagline = firstNonEmpty(
    biz.tagline,
    biz.subtitle,
    biz.slogan,
    nested.tagline,
    nested.subtitle,
    nested.slogan
  );

  const aboutHtml = firstNonEmpty(
    biz.about,
    biz.quienesSomos,
    biz.description,
    nested.about,
    nested.quienesSomos,
    nested.description,
    nested.longDescription,
    nested.bio
  );

  const address = firstNonEmpty(
    biz.address,
    biz.location,
    nested.address,
    nested.location
  );

  const phone = firstNonEmpty(
    biz.phone,
    biz.phoneNumber,
    biz.telefono,
    biz.telefonoNegocio,
    biz.mobile,
    biz.contactPhone,
    biz.contact?.phone,
    nested.phone,
    nested.phoneNumber,
    nested.telefono,
    nested.telefonoNegocio,
    nested.mobile,
    nested.contactPhone
  );

  const whatsapp = firstNonEmpty(
    biz.whatsapp,
    biz.whatsApp,
    biz.whatsAppNumber,
    biz.whatsappNumber,
    biz.telefonoWhatsApp,
    biz.contact?.whatsapp,
    nested.whatsapp,
    nested.whatsApp,
    nested.whatsAppNumber,
    nested.whatsappNumber,
    nested.telefonoWhatsApp
  );

  const hours = firstNonEmpty(
    biz.hours,
    biz.horarios,
    biz.businessHours,
    nested.hours,
    nested.horarios,
    nested.businessHours
  );

  const availability = normalizeBusinessAvailability(
    biz.availability ||
    biz.openingHours ||
    biz.schedule ||
    nested.availability ||
    nested.openingHours ||
    nested.schedule ||
    {}
  );

  const email = firstNonEmpty(
    biz.email,
    nested.email
  );

  const website = firstNonEmpty(
    biz.website,
    nested.website
  );

  const facebook = firstNonEmpty(
    biz.facebook,
    nested.facebook
  );

  const instagram = firstNonEmpty(
    biz.instagram,
    nested.instagram
  );

  const googleMapsLink = firstNonEmpty(
    biz.googleMapsLink,
    biz.mapsLink,
    biz.googleMapsUrl,
    biz.mapUrl,
    biz.locationUrl,
    nested.googleMapsLink,
    nested.mapsLink,
    nested.googleMapsUrl,
    nested.mapUrl,
    nested.locationUrl
  );

  const featuredProjects = Array.isArray(biz.featuredProjects)
    ? biz.featuredProjects
    : (Array.isArray(nested.featuredProjects) ? nested.featuredProjects : []);

  return {
    ...biz,
    __viewName: name,
    __viewTagline: tagline,
    __viewAbout: aboutHtml,
    __viewLogoUrl: logoUrl,
    __viewAddress: address,
    __viewPhone: phone,
    __viewWhatsapp: whatsapp,
    __viewHours: hours,
    __viewAvailability: availability,
    __viewEmail: email,
    __viewWebsite: website,
    __viewFacebook: facebook,
    __viewInstagram: instagram,
    __viewGoogleMapsLink: googleMapsLink,
    __viewFeaturedProjects: featuredProjects,
    __viewServiceAreas: normalizeBusinessServiceAreas(biz),
    __viewShowTeamOnBusinessPage: biz.showTeamOnBusinessPage !== false && biz.hideTeamOnBusinessPage !== true,
    __viewShowProjectsOnBusinessPage: biz.showProjectsOnBusinessPage !== false && biz.hideProjectsOnBusinessPage !== true,
  };
}


function normalizePlainTextForEvents(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isEventsBusinessProfile(biz) {
  const fields = [
    biz?.businessType,
    biz?.type,
    biz?.category,
    biz?.serviceCategory,
    biz?.vertical,
    biz?.eventCategory,
  ].map(normalizePlainTextForEvents).filter(Boolean);

  return fields.some((x) => x.includes("evento") || x.includes("events"));
}

function getServiceImageOnly(service) {
  return firstNonEmpty(
    service?.imageUrl,
    service?.photoUrl,
    service?.coverImage,
    service?.mainPhotoUrl,
    service?.image
  ) || "";
}

function getServicePresentationMode(service) {
  const raw = String(
    service?.servicePresentationMode ||
    service?.presentationMode ||
    service?.displayMode ||
    ""
  ).toLowerCase().trim();

  if (["image", "photo", "foto", "visual"].includes(raw)) return "image";
  if (["icon", "simple", "icono", "ícono"].includes(raw)) return "icon";

  return getServiceImageOnly(service) ? "image" : "icon";
}

function getServicePublicImage(service) {
  const image = getServiceImageOnly(service);
  return getServicePresentationMode(service) === "image" ? image : "";
}


function getServiceImageFocusClass(service) {
  const raw = String(
    service?.serviceImageFocus ||
    service?.imageFocus ||
    service?.photoFocus ||
    service?.focalPoint ||
    service?.imagePosition ||
    service?.photoPosition ||
    service?.objectPosition ||
    ""
  )
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

  if (!raw) return "bp-service-focus-center";
  if (raw.includes("left") || raw.includes("izquierda") || raw === "l") return "bp-service-focus-left";
  if (raw.includes("right") || raw.includes("derecha") || raw === "r") return "bp-service-focus-right";
  if (raw.includes("top") || raw.includes("arriba") || raw.includes("superior")) return "bp-service-focus-top";
  if (raw.includes("bottom") || raw.includes("abajo") || raw.includes("inferior")) return "bp-service-focus-bottom";
  if (raw.includes("center") || raw.includes("centro") || raw.includes("middle")) return "bp-service-focus-center";

  return "bp-service-focus-center";
}

function getServiceImageFitClass(service) {
  const raw = String(
    service?.serviceImageFit ||
    service?.imageFit ||
    service?.photoFit ||
    service?.objectFit ||
    ""
  )
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

  if (raw.includes("cover") || raw.includes("llenar") || raw.includes("fill") || raw.includes("recortar")) {
    return "bp-service-fit-cover";
  }

  return "bp-service-fit-contain";
}

function getServiceTitle(service) {
  return firstNonEmpty(service?.name, service?.title, service?.serviceName) || "Experiencia";
}

function getServiceDescription(service) {
  return firstNonEmpty(
    service?.description,
    service?.subtitle,
    service?.details,
    service?.tagline
  ) || "";
}

function normalizePublicServicePriceType(service) {
  const rawType = String(service?.priceType || service?.pricingType || "").toLowerCase().trim();
  if (["quote", "quotation", "cotizacion", "cotización"].includes(rawType)) return "quote";
  if (["from", "starting", "desde"].includes(rawType)) return "from";
  if (
    !rawType &&
    (service?.fromPrice !== null && service?.fromPrice !== undefined && service?.fromPrice !== "")
  ) return "from";

  // Event packages are variable by nature (duration/options/extras). Preserve
  // their existing public "Desde" behavior unless the package is a quote.
  const serviceKind = String(service?.serviceKind || service?.kind || "").toLowerCase().trim();
  if (serviceKind === "event_package") return "from";

  return "fixed";
}

function getServicePriceText(service, business = {}) {
  const priceType = normalizePublicServicePriceType(service);
  if (priceType === "quote") {
    return firstNonEmpty(service?.priceLabel, service?.publicPriceLabel) || "Solicitar cotización";
  }

  const raw = priceType === "from"
    ? (service?.price ?? service?.fromPrice ?? service?.startingPrice)
    : (service?.price ?? service?.amount);

  if (raw === null || typeof raw === "undefined" || raw === "") return "Precio a consultar";

  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (n <= 0) return "Precio a consultar";

  const market=getMarket(business.countryCode); const amount = formatMarketMoney(n,business.currency || market.currency,business.locale || market.locale);
  return priceType === "from" ? `Desde ${amount}` : amount;
}

function getServiceDurationText(service) {
  const n = Number(service?.duration || service?.durationMinutes || 0);
  return n > 0 ? `${n} min` : "Duración a coordinar";
}



const SERVICE_ICON_COMPONENTS = {
  scissors: Scissors,
  blower: Wind,
  manicure: Sparkles,
  pedicure: Sparkles,
  shampoo: Droplets,
  keratin: Wand2,
  makeup: Palette,
  spa: Sparkles,
  fitness: Dumbbell,
  default: Package,
};

const SERVICE_ICON_OPTION_MAP = new Map(
  (Array.isArray(SERVICE_ICON_OPTIONS) ? SERVICE_ICON_OPTIONS : []).map((opt) => [String(opt.key || ""), opt])
);

function normalizeServiceIconKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
}

function serviceIconText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function resolveServiceIconKeyFromText(value) {
  const text = serviceIconText(value);
  if (!text) return "package";

  const directKey = normalizeServiceIconKey(text);
  if (SERVICE_ICON_OPTION_MAP.has(directKey) && directKey !== "auto") return directKey;

  const matches = [
    ["barber_combo", ["corte y barba", "corte + barba", "combo barba", "barba y cabello"]],
    ["beard_design", ["diseno de barba", "perfilado de barba", "barba perfilada", "barba premium"]],
    ["beard_trim", ["barba", "beard"]],
    ["fade_cut", ["fade", "degradado", "taper"]],
    ["lineup", ["line up", "lineup", "cerquillo", "definicion"]],
    ["shave", ["afeitado", "navaja", "shave"]],
    ["men_haircut", ["corte hombre", "caballero", "masculino", "hombre"]],
    ["women_haircut", ["corte mujer", "dama", "femenino", "mujer"]],
    ["kids_haircut", ["nino", "nina", "infantil", "kids"]],
    ["hair_wash", ["lavado", "shampoo", "wash"]],
    ["blow_dry", ["blower", "secado", "brushing", "dry"]],
    ["hair_color", ["tinte", "color", "coloracion", "coloración"]],
    ["highlights", ["mechas", "rayitos", "highlights", "iluminacion"]],
    ["balayage", ["balayage"]],
    ["keratin", ["keratina", "keratin", "alisado"]],
    ["hair_extensions", ["extension", "extensiones"]],
    ["hair_treatment", ["tratamiento capilar", "hidratacion", "hidratación", "capilar", "reparacion"]],
    ["haircut", ["corte", "cabello", "haircut", "pelo"]],
    ["gel_nails", ["gel", "semipermanente", "gelish"]],
    ["nail_art", ["nail art", "diseno de unas", "diseño de uñas", "arte unas", "arte uñas"]],
    ["pedicure", ["pedicure", "pedi", "pies", "pie"]],
    ["manicure", ["manicure", "mani", "unas", "uñas", "nails", "acrilicas", "acrílicas"]],
    ["facial_cleaning", ["limpieza facial", "deep facial", "limpieza de cutis"]],
    ["facial", ["facial", "rostro"]],
    ["hot_stones", ["piedras calientes", "hot stones"]],
    ["massage", ["masaje", "massage", "relajante", "descontracturante"]],
    ["waxing", ["depilacion", "depilación", "waxing", "cera"]],
    ["eyebrows", ["cejas", "brow", "brows"]],
    ["lashes", ["pestanas", "pestañas", "lashes", "lash"]],
    ["makeup", ["maquillaje", "makeup", "make up"]],
    ["spa", ["spa", "wellness", "bienestar"]],
    ["pilates", ["pilates"]],
    ["personal_training", ["personal training", "entrenador", "trainer"]],
    ["nutrition", ["nutricion", "nutrición", "nutrition"]],
    ["yoga", ["yoga"]],
    ["fitness", ["fitness", "gym", "gimnasio", "entrenamiento"]],
    ["birthday_party", ["cumple", "birthday", "fiesta"]],
    ["character_show", ["personaje", "mascota", "show", "animacion", "animación"]],
    ["magic_show", ["magia", "mago", "magic"]],
    ["face_paint", ["pintacaritas", "pinta caritas", "face paint"]],
    ["balloons", ["globo", "globoflexia", "balloon"]],
    ["photo_session", ["foto", "fotografia", "fotografía", "photo", "sesion"]],
    ["decor", ["decoracion", "decoración", "decor"]],
    ["dj_music", ["dj", "musica", "música"]],
    ["catering", ["catering", "comida"]],
    ["home_service", ["domicilio", "home service", "a domicilio"]],
    ["cleaning", ["limpieza", "cleaning"]],
    ["pet_grooming", ["mascota", "pet", "grooming"]],
    ["car_wash", ["car wash", "lavado de auto", "auto", "detailing"]],
    ["consultation", ["consulta", "consultoria", "consultoría", "asesoria", "asesoría"]],
    ["package", ["paquete", "package", "servicio"]],
  ];

  const found = matches.find(([, terms]) => terms.some((term) => text.includes(term)));
  return found ? found[0] : "package";
}

function getServiceIconKey(service) {
  const explicit = normalizeServiceIconKey(service?.serviceIconKey || service?.iconKey || service?.icon || service?.serviceIcon);
  if (explicit && explicit !== "auto" && SERVICE_ICON_OPTION_MAP.has(explicit)) return explicit;
  return resolveServiceIconKeyFromText([
    getServiceTitle(service),
    service?.category,
    service?.type,
    service?.serviceType,
  ].filter(Boolean).join(" "));
}

const PUBLIC_SERVICE_ICON_PACKS = {
  lucide: Lucide,
  fa: FaIcons,
  gi: GiIcons,
  md: MdIcons,
};

function resolvePublicServiceIconComponent(iconKey) {
  const key = normalizeServiceIconKey(iconKey);
  const option = SERVICE_ICON_OPTION_MAP.get(key) || SERVICE_ICON_OPTION_MAP.get("package") || {};
  const candidates = Array.isArray(option.iconCandidates) && option.iconCandidates.length
    ? option.iconCandidates
    : [{ pack: option.iconPack || "lucide", name: option.iconName }];

  for (const candidate of candidates) {
    if (!candidate || !candidate.name) continue;
    const pack = PUBLIC_SERVICE_ICON_PACKS[candidate.pack || "lucide"] || Lucide;
    const Icon = pack?.[candidate.name];
    if (Icon) return Icon;
  }

  if (option.iconName && Lucide?.[option.iconName]) return Lucide[option.iconName];
  return SERVICE_ICON_COMPONENTS[key] || Lucide?.Sparkles || SERVICE_ICON_COMPONENTS.default;
}

function ServiceIconBubble({ iconKey }) {
  const PremiumIcon = resolvePublicServiceIconComponent(iconKey);

  return (
    <div className="event-package-icon-bubble event-package-icon-bubble-pro" aria-hidden="true">
      <PremiumIcon className="event-package-icon-svg" size={25} strokeWidth={2.1} />
    </div>
  );
}


function handleBusinessShare(biz) {
  const title = biz?.__viewName || biz?.name || "Loop";
  const text = `Mira ${title} en Loop`;
  const url = typeof window !== "undefined" ? window.location.href : "";

  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator.share({ title, text, url });
      return;
    }
  } catch (_) {}

  try {
    if (navigator?.clipboard && url) {
      navigator.clipboard.writeText(url);
      return;
    }
  } catch (_) {}
}



function scrollEventPackages(direction = 1) {
  try {
    const rail = document.querySelector('.event-packages-grid');
    if (!rail) return;

    const amount = Math.min(rail.clientWidth * 0.82, 420);

    rail.scrollBy({
      left: direction > 0 ? amount : -amount,
      behavior: 'smooth',
    });
  } catch (_) {}
}




const LOOP_BP_CACHE_VERSION = "v4-ratings-stable-live";
const LOOP_BP_CACHE_TTL_MS = 1000 * 60 * 60 * 6; // 6 horas: suficiente para abrir rápido sin mostrar data vieja por días.

const LOOP_BP_LEGACY_CACHE_PREFIXES = [
  "loop:businessPage:v1-fast-public-page:",
];

function purgeLegacyBusinessPageEmployeeCaches() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;

    const keysToRemove = [];

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);

      if (
        key &&
        LOOP_BP_LEGACY_CACHE_PREFIXES.some((prefix) =>
          key.startsWith(prefix)
        )
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch (_) {}
}

function businessPageCacheKey(businessIdOrParam) {
  const clean = String(businessIdOrParam || "").trim();
  return clean ? `loop:businessPage:${LOOP_BP_CACHE_VERSION}:${clean}` : "";
}

function readBusinessPageCache(businessIdOrParam) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const key = businessPageCacheKey(businessIdOrParam);
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt || 0);
    if (!savedAt || Date.now() - savedAt > LOOP_BP_CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeBusinessPageCache(businessIdOrParam, payload = {}) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const key = businessPageCacheKey(businessIdOrParam);
    if (!key) return;
    window.localStorage.setItem(key, JSON.stringify({
      savedAt: Date.now(),
      business: payload.business || null,
      services: Array.isArray(payload.services) ? payload.services : [],
      employees: Array.isArray(payload.employees) ? payload.employees : [],
      projects: Array.isArray(payload.projects) ? payload.projects : [],
      ratings: Array.isArray(payload.ratings) ? payload.ratings : [],
      avgRating: payload.avgRating ?? null,
    }));
  } catch (_) {}
}

function applyCachedBusinessPageData(cached, setters = {}) {
  if (!cached) return false;
  try {
    const biz = cached.business ? normalizeBusinessView(cached.business) : null;
    if (!biz) return false;
    setters.setBusiness?.(biz);
    setters.setProjects?.(Array.isArray(cached.projects) ? cached.projects : (Array.isArray(biz.__viewFeaturedProjects) ? biz.__viewFeaturedProjects : []));
    setters.setServices?.(sortBusinessServicesForPublic(Array.isArray(cached.services) ? cached.services : []));
    setters.setEmployees?.(Array.isArray(cached.employees) ? cached.employees : []);
    setters.setRatings?.(Array.isArray(cached.ratings) ? cached.ratings : []);
    setters.setAvgRating?.(cached.avgRating ?? null);
    setters.setLoading?.(false);
    setters.setLoadingRatings?.(false);
    return true;
  } catch (_) {
    return false;
  }
}

async function resolveBusinessIdentifierFast(db, paramId) {
  const raw = String(paramId || "").trim();
  if (!raw) return { businessId: null, slug: "" };
  const slug = slugifyBusinessName(raw);

  // Fast path 1: if the URL already has the Firestore ID, one document read is enough.
  try {
    if (isIosCapacitor()) {
      const direct = await fsClient.getDocByPath(`businesses/${raw}`, { db });
      if (direct) return {
        businessId: direct.id || raw,
        slug: direct.slug || direct.publicSlug || direct.businessSlug || direct.slugLower || slug,
      };
    } else {
      const directSnap = await getDoc(doc(db, "businesses", raw));
      if (directSnap.exists()) {
        const data = directSnap.data() || {};
        return {
          businessId: directSnap.id,
          slug: data.slug || data.publicSlug || data.businessSlug || data.slugLower || slug,
        };
      }
    }
  } catch (_) {}

  // Fast path 2: canonical slug registry. This avoids listing every business on mobile.
  const slugCandidates = Array.from(new Set([raw, slug].filter(Boolean)));
  for (const candidate of slugCandidates) {
    try {
      if (isIosCapacitor()) {
        const slugDoc = await fsClient.getDocByPath(`businessSlugs/${candidate}`, { db });
        const foundId = slugDoc?.businessId || slugDoc?.id || slugDoc?.targetId || slugDoc?.businessDocId || null;
        if (foundId) return { businessId: String(foundId), slug: candidate };
      } else {
        const slugSnap = await getDoc(doc(db, "businessSlugs", candidate));
        if (slugSnap.exists()) {
          const data = slugSnap.data() || {};
          const foundId = data.businessId || data.id || data.targetId || data.businessDocId || null;
          if (foundId) return { businessId: String(foundId), slug: candidate };
        }
      }
    } catch (_) {}
  }

  // iOS fallback: some older businesses do not have a businessSlugs registry doc yet.
  // In WKWebView this meant direct read failed, then we treated the slug as the doc id,
  // so the page briefly showed cached/real data and then got overwritten by the empty fallback.
  // Scan the public businesses list once and match by id/name/slug before giving up.
  if (isIosCapacitor()) {
    try {
      const rows = await fsClient.listCollectionByPath("businesses", { limit: 350 }, { db });
      const list = Array.isArray(rows) ? rows : [];
      const wanted = new Set(slugCandidates.map((x) => String(x || "").toLowerCase()));
      const found = list.find((item) => {
        if (!item || typeof item !== "object") return false;
        const candidates = [
          item.id,
          item.businessId,
          item.uid,
          item.slug,
          item.publicSlug,
          item.businessSlug,
          item.slugLower,
          item.name,
          item.title,
          item.businessName,
        ];
        return candidates.some((value) => {
          const clean = String(value || "").trim();
          if (!clean) return false;
          return wanted.has(clean.toLowerCase()) || wanted.has(slugifyBusinessName(clean));
        });
      });
      if (found) {
        const foundId = String(found.id || found.businessId || found.uid || "").trim();
        if (foundId) {
          return {
            businessId: foundId,
            slug: found.slug || found.publicSlug || found.businessSlug || found.slugLower || slugifyBusinessName(found.name || found.businessName || found.title || raw),
          };
        }
      }
    } catch (_) {}
  } else {
    try {
      const result = await resolveBusinessIdentifier(db, raw);
      if (result?.businessId) return { businessId: result.businessId, slug: result.slug || slug };
    } catch (_) {}
  }

  return { businessId: raw, slug };
}

function toDateMaybe(v) {
  try {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    if (v.seconds) return new Date(v.seconds * 1000);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function normalizeRating(r) {
  const createdAt = toDateMaybe(r.createdAt || r.date || r.updatedAt || r.ratingSubmittedAt);
  return {
    id: r.id,
    rating: r.rating ?? r.stars ?? r.score ?? null,
    comment: r.comment ?? r.message ?? r.review ?? r.text ?? '',
    createdAt,
    createdAtMs: createdAt ? createdAt.getTime() : 0,
    userId: r.userId || r.user || r.userUid || r.uid || null,
    userName: r.userName || r.displayName || r.name || r.customerName || null,
    userPhoto: r.userPhoto || r.photoUrl || r.avatarUrl || null,
    serviceName: r.serviceName || r.service || r.serviceTitle || '',
    source: r.source || '',
    status: r.status || '',
    published: r.published,
    dateStr: createdAt ? new Date(createdAt).toLocaleString(interfaceLocale()) : '',
  };
}

function sortRatingsNewestFirst(list = []) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const ams = Number(a.createdAtMs || (a.createdAt ? new Date(a.createdAt).getTime() : 0)) || 0;
    const bms = Number(b.createdAtMs || (b.createdAt ? new Date(b.createdAt).getTime() : 0)) || 0;
    return bms - ams;
  });
}

// LOOP BUSINESSPAGE REVIEWS DATA COMPAT V4.1
function getEmbeddedBusinessRatings(raw = {}) {
  const business = raw && typeof raw === "object" ? raw : {};
  const nested = business.businessPage || business.profile || business.page || {};

  const pools = [
    business.ratings,
    business.reviews,
    business.comments,
    nested.ratings,
    nested.reviews,
    nested.comments,
  ];

  const result = [];
  pools.forEach((pool) => {
    if (!Array.isArray(pool)) return;
    pool.forEach((item, index) => {
      if (!item || typeof item !== "object") return;
      result.push({
        ...item,
        id: item.id || item.ratingId || item.reviewId || item.bookingId || `embedded-rating-${index}`,
      });
    });
  });

  return result;
}

function rawRatingIdentity(raw = {}, index = 0) {
  const id = String(raw.id || raw.ratingId || raw.reviewId || "").trim();
  if (id) return `id:${id}`;

  const bookingId = String(raw.bookingId || raw.booking || "").trim();
  const userId = String(raw.userId || raw.userUid || raw.uid || "").trim();
  if (bookingId || userId) {
    return `booking-user:${bookingId}:${userId}`;
  }

  const rating = String(raw.rating ?? raw.stars ?? raw.score ?? "").trim();
  const comment = String(raw.comment ?? raw.message ?? raw.review ?? raw.text ?? "").trim();
  const dateValue = raw.createdAt || raw.date || raw.updatedAt || raw.ratingSubmittedAt || "";
  let dateKey = "";
  try {
    if (dateValue && typeof dateValue.toMillis === "function") dateKey = String(dateValue.toMillis());
    else if (dateValue && typeof dateValue.toDate === "function") dateKey = String(dateValue.toDate().getTime());
    else if (dateValue && dateValue.seconds) dateKey = String(Number(dateValue.seconds) * 1000);
    else dateKey = String(dateValue || "");
  } catch (_) {}

  return `sig:${rating}:${comment}:${dateKey}:${index}`;
}

function mergeRawRatings(...sources) {
  const out = [];
  const seen = new Set();

  sources.forEach((source) => {
    if (!Array.isArray(source)) return;
    source.forEach((raw, index) => {
      if (!raw || typeof raw !== "object") return;
      const key = rawRatingIdentity(raw, index);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(raw);
    });
  });

  return out;
}

export default function BusinessPage({ db: dbProp }) {
  useLanguage();
  const { businessId: paramId } = useParams();

  useEffect(() => {
    purgeLegacyBusinessPageEmployeeCaches();
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const db = dbProp || defaultDb;
  const [businessLookup, setBusinessLookup] = useState({ input: paramId || "", businessId: null, slug: "", loading: true });
  const businessId = businessLookup.businessId;

  const minimalFromState = location && location.state && location.state.minimal;
  const urlParams = new URLSearchParams(location.search || '');
  const minimalFromQuery = urlParams.get('minimal') === '1' || urlParams.get('minimal') === 'true';
  const minimal = !!(minimalFromState || minimalFromQuery);

  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogPage, setCatalogPage] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [avgRating, setAvgRating] = useState(null);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [hideMobileDockNearFinal, setHideMobileDockNearFinal] = useState(false);
  const teamRailRef = useRef(null);
  const finalCtaRef = useRef(null);
  const pageViewTrackedRef = useRef(false);

  // REVIEWS STABLE LIVE V3
  // Keep latest page data available to the ratings listener without making
  // business/services/employees/projects dependencies of the subscription.
  const businessLatestRef = useRef(null);
  const servicesLatestRef = useRef([]);
  const employeesLatestRef = useRef([]);
  const projectsLatestRef = useRef([]);

  useEffect(() => { businessLatestRef.current = business; }, [business]);
  useEffect(() => { servicesLatestRef.current = services; }, [services]);
  useEffect(() => { employeesLatestRef.current = employees; }, [employees]);
  useEffect(() => { projectsLatestRef.current = projects; }, [projects]);

  const [loading, setLoading] = useState(true);
  const [loadingRatings, setLoadingRatings] = useState(true);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedBookingService, setSelectedBookingService] = useState(null);
  const [heroLogoRatio, setHeroLogoRatio] = useState(null);

  const trackBusinessEvent = (type, payload = {}) => {
    trackBusinessAnalyticsEventSafe({ db, businessId, type, payload });
  };

  const openBookingForService = (service = null, source = "general_cta") => {
    const serviceId = service?.id || service?.serviceId || service?.slug || null;
    const serviceName = service?.__serviceTitle || service?.name || service?.title || service?.serviceName || null;

    if (service) {
      trackBusinessEvent("service_clicked", {
        sourceArea: source,
        serviceId,
        serviceName,
        servicePrice: service.price ?? service.basePrice ?? service.amount ?? null,
        serviceDuration: service.duration ?? service.durationMins ?? service.minutes ?? null,
        funnelStep: "service_clicked",
      });
    }

    trackBusinessEvent("reserve_click", {
      sourceArea: source,
      serviceId,
      serviceName,
      bookingSource: "client_online",
      funnelStep: "reserve_click",
    });

    trackBusinessEvent("booking_opened", {
      sourceArea: source,
      serviceId,
      serviceName,
      bookingSource: "client_online",
      funnelStep: "booking_opened",
    });

    setSelectedBookingService(service || null);
    setShowBooking(true);
  };

  const membershipAutoOpenRef = useRef(false);

  useEffect(() => {
    if (membershipAutoOpenRef.current) return;
    if (!businessId || !business || !urlParams || urlParams.get('useMembership') !== '1') return;
    membershipAutoOpenRef.current = true;
    openBookingForService(null, "membership_cta");
  }, [businessId, business, location.search]);

  const handleBookingCompletedAnalytics = (summary = {}) => {
    const service = selectedBookingService || {};
    trackBusinessEvent("booking_completed", {
      sourceArea: selectedBookingService ? "service_card" : "booking_modal",
      bookingId: summary.bookingId || summary.id || null,
      serviceId: summary.serviceId || service.id || service.serviceId || service.slug || null,
      serviceName: summary.serviceName || service.__serviceTitle || service.name || service.title || service.serviceName || null,
      totalPrice: summary.totalPrice ?? summary.total ?? summary.price ?? null,
      status: summary.status || null,
      approvalModeAtCreation: summary.approvalModeAtCreation || null,
      confirmedAutomatically: summary.confirmedAutomatically === true,
    });
  };

  useEffect(() => {
    try {
      if (typeof document === "undefined") return;
      const name = business?.__viewName || business?.name || business?.businessName || "";
      document.title = name ? `${name} | Loop` : "Loop Reservas";
    } catch (_) {}
  }, [business]);

  // BUSINESSPAGE POLISH V5 — mobile sticky dock yields to the final CTA.
  // Wait until the loading screen is gone so finalCtaRef actually exists.
  useEffect(() => {
    if (loading) return undefined;

    const node = finalCtaRef.current;
    if (!node || typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const mobile = window.matchMedia
          ? window.matchMedia("(max-width: 767px)").matches
          : window.innerWidth <= 767;

        setHideMobileDockNearFinal(Boolean(mobile && entry?.isIntersecting));
      },
      {
        root: null,
        threshold: 0.06,
        rootMargin: "0px 0px -4% 0px",
      }
    );

    observer.observe(node);
    return () => {
      try { observer.disconnect(); } catch (_) {}
      setHideMobileDockNearFinal(false);
    };
  }, [businessId, loading]);

  useEffect(() => {
    if (!business || !businessId || !paramId || businessLookup.loading) return;

    const canonicalParam = getBusinessRouteParam({ id: businessId, businessId, ...business });
    if (!canonicalParam || String(canonicalParam) === String(paramId)) return;

    try {
      const suffix = `${location.search || ""}${location.hash || ""}`;
      navigate(`/business/${encodeURIComponent(canonicalParam)}${suffix}`, {
        replace: true,
        state: location.state || undefined,
      });
    } catch (_) {}
  }, [business, businessId, businessLookup.loading, paramId, location.search, location.hash, location.state, navigate]);

  useEffect(() => {
    let mounted = true;

    async function resolveBusiness() {
      if (!db || !paramId) {
        if (mounted) setBusinessLookup({ input: paramId || "", businessId: null, slug: "", loading: false });
        return;
      }

      // Instant optimistic path: if we already cached this URL param, show the page while resolving.
      const cachedByParam = readBusinessPageCache(paramId);
      if (cachedByParam && mounted) {
        applyCachedBusinessPageData(cachedByParam, {
          setBusiness,
          setProjects,
          setServices,
          setEmployees,
          setRatings,
          setAvgRating,
          setLoading,
          setLoadingRatings,
        });
      }

      setBusinessLookup((prev) => ({ ...prev, input: paramId, loading: true }));

      try {
        const result = await resolveBusinessIdentifierFast(db, paramId);
        if (!mounted) return;
        setBusinessLookup({
          input: paramId,
          businessId: result.businessId || paramId,
          slug: result.slug || "",
          loading: false,
        });
      } catch (_) {
        if (mounted) setBusinessLookup({ input: paramId, businessId: paramId, slug: "", loading: false });
      }
    }

    resolveBusiness();
    return () => { mounted = false; };
  }, [db, paramId]);

  const [favoritesSet, setFavoritesSet] = useState(new Set());

  const scrollTeamRail = (direction = 1) => {
    const rail = teamRailRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * Math.max(260, Math.round(rail.clientWidth * 0.75)),
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!db || !businessId || !business || pageViewTrackedRef.current) return;

    const sessionKey = `loop_business_page_viewed:${businessId}`;
    try {
      if (typeof window !== "undefined" && window.sessionStorage && window.sessionStorage.getItem(sessionKey)) {
        pageViewTrackedRef.current = true;
        return;
      }
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem(sessionKey, "1");
      }
    } catch (_) {}

    pageViewTrackedRef.current = true;
    trackBusinessEvent("business_page_view", {
      businessName: business.__viewName || business.name || null,
      businessType: business.type || business.category || business.businessType || null,
    });
  }, [db, businessId, business]);

  useEffect(() => {
    if (!db || businessLookup.loading || !businessId) {
      if (!businessLookup.loading) setLoading(false);
      return undefined;
    }

    let mounted = true;
    const bRef = doc(db, 'businesses', businessId);

    function applyRatings(rawRatings) {
      const raw = Array.isArray(rawRatings) ? rawRatings.map((x) => ({ id: x.id, ...(x || {}) })) : [];
      raw.sort((a, b) => {
        const da = toDateMaybe(a.createdAt || a.date || a.updatedAt)?.getTime() || 0;
        const dbt = toDateMaybe(b.createdAt || b.date || b.updatedAt)?.getTime() || 0;
        return dbt - da;
      });

      // Fast first paint: do not block the whole business page on one extra user read per review.
      return Promise.all(raw.slice(0, 12).map(async (r) => {
        const item = normalizeRating(r);

        if (!item.userName && item.userId) {
          try {
            if (isIosCapacitor()) {
              const uDoc = await fsClient.getDocByPath(`users/${item.userId}`, { db });
              if (uDoc) {
                item.userName = uDoc.nombre || uDoc.displayName || uDoc.name || uDoc.email || null;
                item.userPhoto = uDoc.photoUrl || uDoc.fotoPerfil || null;
              }
            } else {
              const uRef = doc(db, 'users', item.userId);
              const uSnap = await getDoc(uRef);
              if (uSnap.exists()) {
                const ud = uSnap.data();
                item.userName = ud.nombre || ud.displayName || ud.name || ud.email || null;
                item.userPhoto = ud.photoUrl || ud.fotoPerfil || null;
              }
            }
          } catch (_) {}
        }

        return item;
      }));
    }

    const cached = readBusinessPageCache(businessId) || readBusinessPageCache(paramId);
    const hadCache = applyCachedBusinessPageData(cached, {
      setBusiness,
      setProjects,
      setServices,
      setEmployees,
      setRatings,
      setAvgRating,
      setLoading,
      setLoadingRatings,
    });

    async function loadFreshBusinessPage() {
      if (!hadCache) setLoading(true);
      setLoadingRatings(!hadCache);

      try {
        const businessPromise = isIosCapacitor()
          ? fsClient.getDocByPath(`businesses/${businessId}`, { db })
          : getDoc(bRef).then((snap) => snap.exists() ? ({ id: snap.id, ...snap.data() }) : null);

        const servicesPromise = isIosCapacitor()
          ? fsClient.listCollectionByPath(`businesses/${businessId}/services`, {}, { db }).catch(() => [])
          : getDocs(collection(db, `businesses/${businessId}/services`)).then((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))).catch(() => []);

        const employeesPromise = isIosCapacitor()
          ? fsClient.listCollectionByPath(`businesses/${businessId}/publicEmployees`, {}, { db }).catch(() => [])
          : getDocs(collection(db, `businesses/${businessId}/publicEmployees`)).then((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))).catch(() => []);

        // REVIEWS SINGLE SOURCE V1
        // Web: the live onSnapshot below is the only writer of ratings state.
        // iOS: keep the one-shot REST read because live sync is intentionally skipped there.
        const ratingsPromise = isIosCapacitor()
          ? fsClient.listCollectionByPath(`businesses/${businessId}/ratings`, {}, { db }).catch((err) => {
              console.warn("[BusinessPage] iOS ratings read failed", err);
              return [];
            })
          : Promise.resolve(null);

        const [bizDoc, servicesList, employeesList] = await Promise.all([businessPromise, servicesPromise, employeesPromise]);
        if (!mounted) return;

        const bizData = bizDoc ? normalizeBusinessView({ id: bizDoc.id || businessId, ...bizDoc }) : null;
        const sortedServices = sortBusinessServicesForPublic(Array.isArray(servicesList) ? servicesList.map((x) => ({ id: x.id, ...(x || {}) })) : []);
        const cleanEmployees = Array.isArray(employeesList) ? employeesList.map((x) => ({ id: x.id, ...(x || {}) })) : [];

        // iOS hard guard: never replace good cached/visible data with an empty REST result.
        // This is what caused “Nombre del negocio / Sin logo / no services” after 1–2 seconds.
        if (bizData) {
          setBusiness(bizData);
          setProjects((prev) => {
            const freshProjects = Array.isArray(bizData.__viewFeaturedProjects) ? bizData.__viewFeaturedProjects : [];
            if (freshProjects.length > 0) return freshProjects;
            if (isIosCapacitor() && Array.isArray(prev) && prev.length > 0) return prev;
            return freshProjects;
          });
        } else if (!isIosCapacitor()) {
          setBusiness(null);
          setProjects([]);
        }

        setServices((prev) => {
          if (sortedServices.length > 0) return sortedServices;
          if (isIosCapacitor() && Array.isArray(prev) && prev.length > 0) return prev;
          return sortedServices;
        });
        setEmployees((prev) => {
          if (cleanEmployees.length > 0) return cleanEmployees;
          if (isIosCapacitor() && Array.isArray(prev) && prev.length > 0) return prev;
          return cleanEmployees;
        });
        setLoading(false);

        // Finish ratings after the page is already visible.
        // iOS keeps the one-shot path. On web, onSnapshot is the single source of truth
        // so an initial getDocs failure/empty result can never overwrite a good live snapshot.
        if (isIosCapacitor()) {
          const ratingsList = await ratingsPromise;
          if (!mounted) return;
          const embeddedRatings = getEmbeddedBusinessRatings(bizDoc || {});
          const mergedRatings = mergeRawRatings(ratingsList, embeddedRatings);
          const enriched = await applyRatings(mergedRatings);
          if (!mounted) return;
          setRatings(enriched);
          const numeric = enriched.map((x) => Number(x.rating || 0)).filter((n) => !isNaN(n) && n > 0);
          const avg = numeric.length > 0 ? (numeric.reduce((a, b) => a + b, 0) / numeric.length) : null;
          const roundedAvg = avg ? Math.round(avg * 10) / 10 : null;
          setAvgRating(roundedAvg);
          setLoadingRatings(false);

          if (bizData) {
            const cachePayload = {
              business: bizData,
              services: sortedServices,
              employees: cleanEmployees,
              projects: Array.isArray(bizData?.__viewFeaturedProjects) ? bizData.__viewFeaturedProjects : [],
              ratings: enriched,
              avgRating: roundedAvg,
            };
            writeBusinessPageCache(businessId, cachePayload);
            writeBusinessPageCache(paramId, cachePayload);
          }
        }
      } catch (err) {
        console.error('BusinessPage fast load failed', err);
        if (!mounted) return;
        if (!hadCache && !isIosCapacitor()) {
          setBusiness(null);
          setServices([]);
          setEmployees([]);
          setRatings([]);
          setProjects([]);
          setAvgRating(null);
        }
        setLoading(false);
        setLoadingRatings(false);
      }
    }

    loadFreshBusinessPage();
    return () => { mounted = false; };
  }, [db, businessId, businessLookup.loading, paramId]);

  // LOOP RATINGS LIVE SYNC — STABLE V3
  // One subscription per businessId. Page-content state changes must NOT
  // tear down/recreate this listener while the BusinessPage is loading.
  useEffect(() => {
    if (!db || !businessId || businessLookup.loading || isIosCapacitor()) return undefined;

    setLoadingRatings(true);

    const ratingsPath = `businesses/${businessId}/ratings`;
    const ratingsRef = collection(db, ratingsPath);

    const unsub = onSnapshot(ratingsRef, (snap) => {
      const rawSnapshotRatings = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      const embeddedRatings = getEmbeddedBusinessRatings(businessLatestRef.current || {});
      const next = mergeRawRatings(rawSnapshotRatings, embeddedRatings)
        .map((raw) => normalizeRating(raw))
        .filter((r) => r.rating !== null && r.rating !== undefined);

      const sorted = sortRatingsNewestFirst(next);
      setRatings(sorted);

      const numeric = sorted
        .map((x) => Number(x.rating || 0))
        .filter((n) => Number.isFinite(n) && n > 0);

      const avg = numeric.length > 0
        ? Math.round((numeric.reduce((a, b) => a + b, 0) / numeric.length) * 10) / 10
        : null;

      setAvgRating(avg);
      setLoadingRatings(false);

      // Cache using refs so this subscription remains stable.
      try {
        const latestBusiness = businessLatestRef.current;
        if (latestBusiness) {
          const cachePayload = {
            business: latestBusiness,
            services: servicesLatestRef.current,
            employees: employeesLatestRef.current,
            projects: projectsLatestRef.current,
            ratings: sorted,
            avgRating: avg,
          };
          writeBusinessPageCache(businessId, cachePayload);
          writeBusinessPageCache(paramId, cachePayload);
        }
      } catch (_) {}
    }, (err) => {
      console.warn("[BusinessPage] live ratings sync failed", {
        businessId,
        ratingsPath,
        code: err?.code || null,
        message: err?.message || String(err || ""),
      });
      setLoadingRatings(false);
    });

    return () => {
      try { unsub(); } catch (_) {}
    };
  }, [db, businessId, businessLookup.loading, paramId]);

  useEffect(() => {
    let mounted = true;

    const loadFavs = async () => {
      try {
        const u = getEffectiveUser();
        if (!u || !u.uid) {
          if (mounted) setFavoritesSet(new Set());
          return;
        }

        if (isIosCapacitor()) {
          const uDoc = await fsClient.getDocByPath(`users/${u.uid}`, { db });
          const favs = Array.isArray(uDoc?.favorites) ? uDoc.favorites : (Array.isArray(uDoc?.favoriteBusinessIds) ? uDoc.favoriteBusinessIds : []);
          if (mounted) setFavoritesSet(new Set(favs || []));
          return;
        }

        const uRef = doc(db, 'users', u.uid);
        const snap = await getDoc(uRef);
        if (mounted && snap.exists()) {
          const data = snap.data();
          const favs = Array.isArray(data.favorites) ? data.favorites : (Array.isArray(data.favoriteBusinessIds) ? data.favoriteBusinessIds : []);
          setFavoritesSet(new Set(favs || []));
        }
      } catch (err) {
        console.error('loadFavs', err);
      }
    };

    const unsubAuth = !isIosCapacitor() && auth && typeof auth.onAuthStateChanged === 'function'
      ? auth.onAuthStateChanged(() => { loadFavs(); })
      : null;

    loadFavs();

    const onManualChanged = () => {
      if (isIosCapacitor()) loadFavs();
    };

    try {
      window.addEventListener('manual-auth-changed', onManualChanged);
      window.addEventListener('manual-auth-detected', onManualChanged);
      window.addEventListener('manual-auth-cleared', onManualChanged);
    } catch (_) {}

    return () => {
      mounted = false;
      if (typeof unsubAuth === 'function') unsubAuth();
      try {
        window.removeEventListener('manual-auth-changed', onManualChanged);
        window.removeEventListener('manual-auth-detected', onManualChanged);
        window.removeEventListener('manual-auth-cleared', onManualChanged);
      } catch (_) {}
    };
  }, [db, businessId]);

  const toggleFavorite = async (bizId) => {
    const u = getEffectiveUser();
    if (!u || !u.uid) { alert('Debes iniciar sesión para guardar favoritos.'); return; }

    try {
      const isFav = favoritesSet.has(bizId);

      if (isIosCapacitor()) {
        const uDoc = await fsClient.getDocByPath(`users/${u.uid}`, { db });
        const favs = Array.isArray(uDoc?.favorites) ? uDoc.favorites.map(String) : [];
        const set = new Set(favs);

        if (isFav) set.delete(String(bizId));
        else set.add(String(bizId));

        const nextFavs = Array.from(set);
        await fsClient.updateDocByPath(`users/${u.uid}`, { favorites: nextFavs }, { db });
        setFavoritesSet(new Set(nextFavs));
        return;
      }

      const uRef = doc(db, 'users', u.uid);
      if (isFav) {
        await updateDoc(uRef, { favorites: arrayRemove(bizId) });
        setFavoritesSet((prev) => {
          const next = new Set(prev);
          next.delete(bizId);
          return next;
        });
      } else {
        await updateDoc(uRef, { favorites: arrayUnion(bizId) });
        setFavoritesSet((prev) => {
          const next = new Set(prev);
          next.add(bizId);
          return next;
        });
      }
    } catch (err) {
      console.error('toggleFavorite', err);
      alert('No se pudo actualizar favorito.');
    }
  };

  if (loading) {
    return <><style>{pageStyles}</style><style>{businessRefreshStyles}</style><div className="business-page-shell" id="bp-refresh-root"><BusinessProfileLoading /></div></>;
  }

  const biz = normalizeBusinessView(business || {});
  const tagline = biz.__viewTagline || '';
  const aboutHtml = biz.__viewAbout || '';

  // iOS fix: show reviews even when they do not have written comments.
  const allReviewsToShow = ratings.filter(r => Number(r.rating || 0) > 0 || (r.comment && String(r.comment).trim() !== ''));
  const visibleReviewsLimit = (() => {
    try {
      const w = window.innerWidth || 0;
      if (w <= 640) return 3;
      if (w <= 1024) return 4;
      return 6;
    } catch (_) {
      return 4;
    }
  })();
  const reviewsToShow = reviewsExpanded ? allReviewsToShow : allReviewsToShow.slice(0, visibleReviewsLimit);
  const canExpandReviews = allReviewsToShow.length > visibleReviewsLimit;

  const reviewStats = (() => {
    const numericRatings = allReviewsToShow
      .map((review) => Number(review?.rating || 0))
      .filter((value) => Number.isFinite(value) && value > 0);

    const counts = [1, 2, 3, 4, 5].reduce((acc, score) => {
      acc[score] = 0;
      return acc;
    }, {});

    numericRatings.forEach((value) => {
      const bucket = Math.max(1, Math.min(5, Math.round(value)));
      counts[bucket] += 1;
    });

    const total = numericRatings.length;
    const computedAverage = total
      ? numericRatings.reduce((sum, value) => sum + value, 0) / total
      : 0;

    const average = avgRating !== null && Number.isFinite(Number(avgRating))
      ? Number(avgRating)
      : computedAverage;

    return {
      total,
      average,
      counts,
    };
  })();

  const logoUrl = biz.__viewLogoUrl || null;
  const businessLogoSrc = getBusinessPageLogo(biz);

  const normalizedHeroLogoRatio = Number.isFinite(Number(heroLogoRatio)) && Number(heroLogoRatio) > 0
    ? Math.max(0.78, Math.min(3.35, Number(heroLogoRatio)))
    : null;
  const heroLogoShapeClass = !normalizedHeroLogoRatio
    ? "bp-logo-shape-unknown"
    : normalizedHeroLogoRatio >= 2.35
      ? "bp-logo-shape-ultrawide"
      : normalizedHeroLogoRatio >= 1.45
        ? "bp-logo-shape-wide"
        : normalizedHeroLogoRatio >= 1.05
          ? "bp-logo-shape-square"
          : "bp-logo-shape-tall";
  const mapEmbedSrc = buildMapEmbedSrc(biz);

  const canEdit = (() => {
    try {
      const u = getEffectiveUser();
      if (!u || !biz) return false;
      if (u.email && (SUPER_USERS || []).includes(u.email)) return true;
      if (biz.ownerUid && u.uid && biz.ownerUid === u.uid) return true;
      if (biz.ownerEmail && u.email && biz.ownerEmail === u.email) return true;
      return false;
    } catch {
      return false;
    }
  })();

  const isFav = favoritesSet.has(businessId);
  const effectiveUser = getEffectiveUser();

  if (biz && !isPublicBusiness(biz) && !canEdit) {
    return (
      <>
        <style>{pageStyles}</style>
        <div className="business-page-shell">
          <div className="business-page-container">
            <div className="business-card" style={{ padding: 28 }}>
              <h1 className="business-title" style={{ color: "#0f172a" }}>{t("Negocio no disponible")}</h1>
              <p className="business-tagline">{t("Este negocio está oculto o pausado temporalmente.")}</p>
              <button className="business-secondary-btn" onClick={() => navigate("/")}>
                Volver al inicio
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }


  const isEventsBusiness = isEventsBusinessProfile(biz);
  const showTeamOnBusinessPage = biz.__viewShowTeamOnBusinessPage !== false;
  const showProjectsOnBusinessPage = biz.__viewShowProjectsOnBusinessPage !== false;
  const publicEmployees = showTeamOnBusinessPage ? employees : [];

  // LOOP BUSINESSPAGE ABOUT TEAM CONSOLIDATION V1
  const aboutRawV1 = String(aboutHtml || "").trim();
  const aboutPlainTextV1 = aboutRawV1
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
  const aboutHasRichBlocksV1 = /<(?:img|video|iframe|table|ul|ol|blockquote|h[1-6])\b/i.test(aboutRawV1);
  const hasAboutContentV1 = Boolean(aboutPlainTextV1 || aboutHasRichBlocksV1);
  const aboutCanExpandV1 = aboutPlainTextV1.length > 340 || aboutHasRichBlocksV1;
  const hasPublicTeamV1 = publicEmployees.length > 0;
  const showAboutTeamV1 = hasAboutContentV1 || hasPublicTeamV1;
  const aboutTeamLayoutV1 = hasAboutContentV1 && hasPublicTeamV1
    ? "has-both"
    : (hasAboutContentV1 ? "about-only" : "team-only");
  const showTeamArrowsV1 = publicEmployees.length > 3;
  // LOOP BUSINESSPAGE GALLERY PUBLISHING V4
  const publicProjects = (showProjectsOnBusinessPage ? projects : [])
    .map((project, index) => {
      const url = typeof project === "string"
        ? project
        : (project?.url || project?.imageUrl || project?.src || project?.downloadURL || "");
      const cleanUrl = String(url || "").trim();
      return cleanUrl
        ? {
            id: project?.id || `public-gallery-${index}`,
            url: cleanUrl,
            name: String(project?.name || project?.title || `Foto ${index + 1}`).trim(),
          }
        : null;
    })
    .filter(Boolean);

  // LOOP BUSINESSPAGE HERO CONSOLIDATION V7
  const heroGalleryProjects = publicProjects.slice(0, 3);
  const heroGalleryExtraCount = Math.max(0, publicProjects.length - 3);
  const heroHasGalleryProjects = heroGalleryProjects.length > 0;

  const eventServices = sortBusinessServicesForPublic(Array.isArray(services) ? services : [])
    .filter((service) => !service?.disabled)
    .map((service) => ({
      ...service,
      __serviceTitle: getServiceTitle(service),
      __serviceDescription: getServiceDescription(service),
      __serviceImage: getServicePublicImage(service),
      __serviceImageFocusClass: getServiceImageFocusClass(service),
      __serviceImageFitClass: getServiceImageFitClass(service),
      __serviceIconKey: getServiceIconKey(service),
      __servicePrice: getServicePriceText(service,business || {}),
      __serviceDuration: getServiceDurationText(service),
    }));


  const hasInfo = !!(biz.__viewAddress || biz.__viewPhone || biz.__viewEmail || biz.__viewInstagram || biz.__viewHours || biz.__viewWhatsapp);

  const infoCard = (
    <div className="business-info-card">
      <div className="business-info-head">
        <div>
          <div className="business-info-title">{t("Nuestra Información")}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t("Datos principales para contactarnos")}</div>
        </div>
        {biz.__viewGoogleMapsLink ? (
          <a href={biz.__viewGoogleMapsLink} target="_blank" rel="noreferrer" className="business-link-pill" style={{ padding: '7px 10px', fontSize: 12 }}>
            <Navigation className="w-3.5 h-3.5" />
            Mapa
          </a>
        ) : null}
      </div>

      {hasInfo ? (
        <div className="business-info-grid">
          {biz.__viewAddress && (
            <div className="business-info-item">
              <span className="business-info-item-icon"><MapPin className="w-4 h-4" /></span>
              <div className="min-w-0">
                <div className="business-info-label">{t("Dirección")}</div>
                <div className="business-info-value">{biz.__viewAddress}</div>
              </div>
            </div>
          )}
          {biz.__viewPhone && (
            <div className="business-info-item">
              <span className="business-info-item-icon"><Phone className="w-4 h-4" /></span>
              <div className="min-w-0">
                <div className="business-info-label">{t("Teléfono")}</div>
                <div className="business-info-value">
                  <a href={normalizePhoneForTel(biz.__viewPhone)}>{biz.__viewPhone}</a>
                </div>
              </div>
            </div>
          )}
          {biz.__viewWhatsapp && (
            <div className="business-info-item">
              <span className="business-info-item-icon"><MessageCircle className="w-4 h-4" /></span>
              <div className="min-w-0">
                <div className="business-info-label">WhatsApp</div>
                <div className="business-info-value">
                  <a href={normalizeWhatsAppLink(biz.__viewWhatsapp)} target="_blank" rel="noreferrer">{biz.__viewWhatsapp}</a>
                </div>
              </div>
            </div>
          )}
          {biz.__viewEmail && (
            <div className="business-info-item">
              <span className="business-info-item-icon"><Mail className="w-4 h-4" /></span>
              <div className="min-w-0">
                <div className="business-info-label">{t("Email")}</div>
                <div className="business-info-value">{biz.__viewEmail}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-slate-500 text-sm">{t("No hay información de contacto pública.")}</div>
      )}
    </div>
  );

  const filteredServices = eventServices.filter(service =>
    (service.__serviceTitle + " " + service.__serviceDescription).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(catalogSearch.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()));
  const browseServices = () => document.getElementById("bp-services")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pageInner = (
    <div className="bp-container">
      <article className="bp-page">
        <header className="bp-refresh-hero">
          <div className="bp-refresh-identity">
            {businessLogoSrc && <div className="bp-brand-mark"><img src={businessLogoSrc} alt={(biz.__viewName || biz.name || "Negocio") + " · logo"} /></div>}
            <div className="bp-refresh-name"><h1>{biz.__viewName || biz.name || t("Negocio")}</h1>
              {tagline && <p>{tagline}</p>}
              <div className="bp-refresh-meta">
                <a href="#bp-reviews-title-v4"><Star size={16} /> {avgRating !== null ? `${avgRating} · ${ratings.length} opiniones` : "Sin opiniones todavía"}</a>
                <span>{getBusinessVisitMode(biz) === "delivery" ? t("A domicilio") : getBusinessVisitMode(biz) === "hybrid" ? "En local y a domicilio" : "En local"}</span>
                {shouldShowBusinessLocation(biz) && <span>{getStructuredBusinessAddress(biz)}</span>}
              </div>
            </div>
            <div className="bp-refresh-actions">
              <button type="button" onClick={() => toggleFavorite(businessId)} aria-label={isFav ? "Quitar de favoritos" : "Guardar en favoritos"} aria-pressed={isFav}><Heart size={20} fill={isFav ? "currentColor" : "none"} /></button>
              <button type="button" onClick={() => handleBusinessShare(biz)} aria-label={t("Compartir negocio")}><Share2 size={20} /></button>
              {canEdit && <button type="button" onClick={() => navigate(`/business/${encodeURIComponent(getBusinessRouteParam(business) || businessId)}/edit`)}>{t("Editar")}</button>}
            </div>
          </div>
          <div className="bp-refresh-contact">
            {eventServices.length > 0 && <button type="button" className="bp-hero-reserve" onClick={browseServices}>{isEventsBusiness ? "Elegir mi experiencia" : "Reservar una cita"}<Lucide.ArrowUpRight size={18}/></button>}
            {publicProjects.length > 0 && <button className="bp-brand-photos" type="button" onClick={() => setGalleryIndex(0)}><Lucide.GalleryHorizontal size={17} />{" " + t("Ver fotos ·") + " "}{publicProjects.length}</button>}
            {biz.__viewWhatsapp && <a href={normalizeWhatsAppLink(biz.__viewWhatsapp)} target="_blank" rel="noreferrer"><MessageCircle size={16} /> WhatsApp</a>}
            {(shouldShowBusinessLocation(biz) || hasBusinessMapsLink(biz, mapEmbedSrc)) && getBusinessVisitMode(biz) !== "delivery" && <a href={getBusinessMapsHref(biz, mapEmbedSrc)} target="_blank" rel="noreferrer"><MapPin size={16} />{" " + t("Cómo llegar")}</a>}
            <a href="#bp-information">{t("Horarios y contacto") + " "}<ChevronDown size={16} /></a>
          </div>
        </header>
        <nav className="bp-refresh-nav" aria-label={t("Secciones del negocio")}>
          <a href="#bp-services">{isEventsBusiness ? "Paquetes" : t("Servicios")}</a>
          {publicProjects.length > 0 && <button type="button" onClick={() => setGalleryIndex(0)}>Fotos</button>}
          <a href="#bp-reviews-title-v4">Opiniones</a><a href="#bp-information">{t("Información")}</a>
        </nav>
        <div className="bp-refresh-dock"><button type="button" onClick={browseServices}>{t("Ver") + " "}{isEventsBusiness ? "paquetes" : t("servicios")} <CalendarDays size={18} /></button></div>

        { (
          <section className="bp-packages" id="bp-services">
            <div className="bp-packages-head">
              <div>
                <div className="bp-kicker">{t("Servicios")}</div>
                <h2 className="bp-packages-title">{isEventsBusiness ? "Encuentra tu experiencia" : t("Elige tu servicio")}</h2>
                <p className="bp-packages-copy">
                  {isEventsBusiness ? "Elige una temática y personaliza tu evento al reservar." : "Consulta precios y elige la opción que necesitas."}
                </p>
              </div>

              {eventServices.length > 5 && <label className="bp-refresh-search"><Lucide.Search size={18} /><input type="search" aria-label="Buscar servicios" placeholder={isEventsBusiness ? "Buscar temática o paquete" : t("Buscar servicio")} value={catalogSearch} onChange={event => {setCatalogSearch(event.target.value); setCatalogPage(0);}} /></label>}
            </div>

            {eventServices.length === 0 ? (
              <div className="event-packages-empty">{t("Este negocio todavía no tiene servicios publicados.")}</div>
            ) : (
              <div
                className={`event-packages-grid bp-packages-rail ${
                  eventServices.some((s) => !!s.__serviceImage) ? "has-photo-services" : ""
                } ${
                  eventServices.some((s) => !s.__serviceImage) ? "has-icon-services" : ""
                } ${
                  eventServices.some((s) => !!s.__serviceImage) && eventServices.some((s) => !s.__serviceImage) ? "mixed-service-rail" : ""
                }`}
              >
                {filteredServices.length === 0 && <p role="status">{t("No encontramos opciones. Prueba con otro nombre.")}</p>}
                {filteredServices.slice(catalogPage * 6, catalogPage * 6 + 6).map((service) => (
                  <article
                    key={service.id || service.__serviceTitle}
                    className={`event-package-card bp-service-payment-layout ${service.__serviceImage ? "has-service-photo" : "has-service-icon"} ${service.__serviceDescription ? "has-service-description" : ""}`}

                  >
                    {service.__serviceImage ? (
                      <div className={`event-package-photo has-photo bp-service-photo-frame ${service.__serviceImageFocusClass || "bp-service-focus-center"} ${service.__serviceImageFitClass || "bp-service-fit-contain"}`}>
                        <img
                          className="bp-service-photo-backdrop"
                          src={service.__serviceImage}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async"
                        />
                        <img
                          className="bp-service-photo-main"
                          src={service.__serviceImage}
                          alt={service.__serviceTitle}
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="event-package-badges">
                          {service.featured ? <span>{t("Destacado")}</span> : null}
                        </div>
                      </div>
                    ) : (
                      <div className="event-package-photo no-photo bp-service-icon-frame" aria-hidden="true">
                        <ServiceIconBubble iconKey={service.__serviceIconKey} />
                      </div>
                    )}

                    <div className={service.__serviceImage ? "event-package-body" : "event-package-body no-photo-body"}>
                      <div>
                        <h4>{service.__serviceTitle}</h4>
                        {service.__serviceDescription ? <p className="bp-readable-description">{service.__serviceDescription}</p> : null}
                      </div>
                      <div className="bp-service-price-row">
                        <span>{service.__serviceDuration}</span>
                        <span aria-hidden="true">·</span>
                        <strong>{service.__servicePrice}</strong>
                      </div>
                      <span className={`bp-service-payment-hint ${publicServicePaymentLabel(service) === "Sin anticipo" ? "is-no-deposit" : ""}`}>
                        {publicServicePaymentLabel(service)}
                      </span>
                      <button
                        className="event-package-reserve"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openBookingForService(service, "service_card_button");
                        }}
                      >
                        {isEventsBusiness ? "Ver opciones" : "Elegir servicio"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {filteredServices.length > 6 && <nav className="bp-catalog-pages" aria-label={t("Páginas de servicios")}>
              <button type="button" disabled={catalogPage === 0} onClick={() => {setCatalogPage(p => p - 1); browseServices();}}>{t("Anterior")}</button>
              <span aria-live="polite">{catalogPage + 1} / {Math.ceil(filteredServices.length / 6)} · {filteredServices.length}{" " + t("opciones")}</span>
              <button type="button" disabled={(catalogPage + 1) * 6 >= filteredServices.length} onClick={() => {setCatalogPage(p => p + 1); browseServices();}}>{t("Siguiente")}</button>
            </nav>}
          </section>
        )}

        <PublicMembershipPlansSection
          businessId={businessId}
          businessName={biz.__viewName || biz.name || ""}
          user={effectiveUser}
        />

        {showAboutTeamV1 ? (
          <section className={`bp-story-team-v1 ${aboutTeamLayoutV1}`} aria-label={t("Información del negocio y equipo")}>
            {hasAboutContentV1 ? (
              <div className="bp-about-panel-v1">
                <div className="bp-story-heading-v1">
                  <h3 className="bp-story-title-v1">{t("Sobre nosotros")}</h3>
                  <p className="bp-story-subtitle-v1">{t("Conoce la historia, enfoque y esencia de este negocio.")}</p>
                </div>

                <div
                  id="bp-about-copy-v1"
                  className={`bp-about-copy-v1 ${aboutCanExpandV1 && !aboutExpanded ? "is-collapsed" : "is-expanded"}`}
                >
                  {(String(aboutHtml).includes("<") && String(aboutHtml).includes(">")) ? (
                    <div dangerouslySetInnerHTML={{ __html: aboutHtml }} />
                  ) : (
                    <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{aboutHtml}</p>
                  )}
                </div>

                {aboutCanExpandV1 ? (
                  <button
                    type="button"
                    className="bp-about-toggle-v1"
                    onClick={() => setAboutExpanded((value) => !value)}
                    aria-expanded={aboutExpanded}
                    aria-controls="bp-about-copy-v1"
                  >
                    <span>{aboutExpanded ? "Leer menos" : "Leer más"}</span>
                    <ChevronDown size={16} className={aboutExpanded ? "is-open" : ""} />
                  </button>
                ) : null}
              </div>
            ) : null}

            {hasPublicTeamV1 ? (
              <div className="bp-team-panel-v1">
                <div className="bp-team-head-v1">
                  <div>
                    <h3 className="bp-story-title-v1">{t("Nuestro equipo")}</h3>
                    <p className="bp-story-subtitle-v1">{t("Conoce a las personas que estarán detrás de tu experiencia.")}</p>
                  </div>

                  {showTeamArrowsV1 ? (
                    <div className="bp-team-actions-v1" aria-label={t("Navegar equipo")}>
                      <button type="button" aria-label={t("Equipo anterior")} onClick={() => scrollTeamRail(-1)}>
                        <Lucide.ChevronLeft size={18} strokeWidth={2.2} />
                      </button>
                      <button type="button" aria-label={t("Equipo siguiente")} onClick={() => scrollTeamRail(1)}>
                        <Lucide.ChevronRight size={18} strokeWidth={2.2} />
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="bp-team-track-v1" ref={teamRailRef}>
                  {publicEmployees.map((emp) => (
                    <TeamCard key={emp.id} person={emp} onClick={(person) => setSelectedEmployee(person)} />
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="bp-reviews-v4" aria-labelledby="bp-reviews-title-v4">
          <div className="bp-reviews-header-v4">
            <div className="bp-reviews-heading-v4">
              <h3 id="bp-reviews-title-v4">{t("Comentarios y valoraciones")}</h3>
              <p>{t("Opiniones reales de clientes que reservaron con este negocio.")}</p>
            </div>

            <div className="bp-reviews-header-actions-v4">
              <span className={`bp-reviews-status-v4 ${allReviewsToShow.length ? "has-rating" : "is-empty"}`}>
                <Star size={14} fill={allReviewsToShow.length ? "currentColor" : "none"} />
                {avgRating !== null
                  ? `${avgRating} · ${allReviewsToShow.length} ${allReviewsToShow.length === 1 ? "valoración" : "valoraciones"}`
                  : "Sin valoración"}
              </span>

            </div>
          </div>

          {loadingRatings ? (
            <div className="bp-reviews-loading-v4" aria-label={t("Cargando comentarios y valoraciones")} aria-busy="true">
              <div className="bp-reviews-overview-skeleton-v4" />
              <div className="bp-review-card-skeleton-v4" />
              <div className="bp-review-card-skeleton-v4" />
            </div>
          ) : allReviewsToShow.length === 0 ? (
            <div className="bp-reviews-empty-v4">
              <span className="bp-reviews-empty-icon-v4" aria-hidden="true">
                <Star size={21} />
              </span>
              <div>
                <strong>{t("Aún no hay valoraciones")}</strong>
                <p>{t("Cuando los clientes califiquen sus reservas, sus comentarios aparecerán aquí.")}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="bp-reviews-overview-v4">
                <div className="bp-reviews-score-v4">
                  <strong>{reviewStats.average ? reviewStats.average.toFixed(1) : "-"}</strong>
                  <div className="bp-reviews-stars-v4" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={index}
                        size={18}
                        className={index < Math.round(reviewStats.average || 0) ? "is-filled" : ""}
                        fill={index < Math.round(reviewStats.average || 0) ? "currentColor" : "none"}
                      />
                    ))}
                  </div>
                  <span>{reviewStats.total} {reviewStats.total === 1 ? "valoración" : "valoraciones"}</span>
                </div>

                <div className="bp-reviews-distribution-v4" aria-label={t("Distribución de valoraciones")}>
                  {[5, 4, 3, 2, 1].map((score) => {
                    const count = reviewStats.counts[score] || 0;
                    const percent = reviewStats.total ? Math.round((count / reviewStats.total) * 100) : 0;
                    return (
                      <div className="bp-review-dist-row-v4" key={score}>
                        <span>{score}</span>
                        <Star size={11} fill="currentColor" />
                        <div className="bp-review-dist-track-v4">
                          <span style={{ width: `${percent}%` }} />
                        </div>
                        <small>{count}</small>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bp-reviews-list-v4">
                {reviewsToShow.map((review) => <RatingRow key={review.id} r={review} />)}
              </div>

              {canExpandReviews ? (
                <button
                  type="button"
                  className={`bp-reviews-more-v6 ${reviewsExpanded ? "is-open" : ""}`}
                  onClick={() => setReviewsExpanded((value) => !value)}
                >
                  {reviewsExpanded ? "Mostrar menos" : "Ver más"}
                  <ChevronDown size={16} className={reviewsExpanded ? "is-open" : ""} />
                </button>
              ) : null}
            </>
          )}
        </section>

        <section id="bp-information" className="bp-refresh-information"><h2>{t("Información del negocio")}</h2>
            <aside className="bp-info-panel" aria-label={t("Información del negocio")}>
              <div className="bp-info-panel-title bp-info-panel-title-v1">
                <strong>{t("Información y contacto")}</strong>
                <span>{t("Todo lo que necesitas para ubicar y contactar al negocio.")}</span>
              </div>

              {biz.__viewPhone ? (
                <div className="bp-info-row bp-contact-channel-v1 bp-phone-row-v1">
                  <span className="bp-info-icon"><Phone className="w-5 h-5" /></span>
                  <div className="bp-info-content">
                    <div className="bp-info-label">{t("Teléfono")}</div>
                    <div className="bp-info-value bp-contact-number">{formatContactDisplay(biz.__viewPhone)}</div>
                    <a className="bp-info-main-action bp-contact-channel-action-v1" href={normalizePhoneForTel(biz.__viewPhone)}>{t("Llamar")}</a>
                  </div>
                </div>
              ) : null}

              <BusinessOpeningHours business={biz} availability={biz.__viewAvailability} fallbackHours={biz.__viewHours} />

              {biz.__viewInstagram ? (
                <div className="bp-info-row bp-contact-channel-v1 bp-instagram-row-v1">
                  <span className="bp-info-icon"><Instagram className="w-5 h-5" /></span>
                  <div className="bp-info-content">
                    <div className="bp-info-label">Instagram</div>
                    <div className="bp-info-value bp-info-value-primary">
                      {String(biz.__viewInstagram).replace(/^https?:\/\/(www\.)?instagram\.com\//i, "@").replace(/\/$/, "")}
                    </div>
                    <a className="bp-info-main-action bp-contact-channel-action-v1" href={normalizeInstagram(biz.__viewInstagram)} target="_blank" rel="noreferrer">{t("Ver Instagram")}<ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ) : null}

              {biz.__viewWebsite ? (
                <div className="bp-info-row bp-contact-channel-v1 bp-website-row-v1">
                  <span className="bp-info-icon"><Globe className="w-5 h-5" /></span>
                  <div className="bp-info-content">
                    <div className="bp-info-label">{t("Sitio web")}</div>
                    <div className="bp-info-value bp-info-value-primary">{t("Web oficial")}</div>
                    <a className="bp-info-main-action bp-contact-channel-action-v1" href={normalizeWebsite(biz.__viewWebsite)} target="_blank" rel="noreferrer">{t("Visitar sitio")}<ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ) : null}

              {biz.__viewFacebook ? (
                <div className="bp-info-row bp-contact-channel-v1 bp-facebook-row-v1">
                  <span className="bp-info-icon"><Facebook className="w-5 h-5" /></span>
                  <div className="bp-info-content">
                    <div className="bp-info-label">Facebook</div>
                    <div className="bp-info-value bp-info-value-primary">Facebook</div>
                    <a className="bp-info-main-action bp-contact-channel-action-v1" href={biz.__viewFacebook} target="_blank" rel="noreferrer">{t("Ver Facebook")}<ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ) : null}
            </aside>
        </section>

        <section className="bp-final-cta" ref={finalCtaRef}>
          <div className="bp-final-icon"><CalendarDays className="w-7 h-7" /></div>
          <div>
            <strong>{t("Tu próxima reserva empieza aquí")}</strong>
            <p style={{ margin: '7px 0 0', color: '#475569', fontWeight: 650 }}>{t("Elige el servicio, confirma tu horario y disfruta la experiencia de") + " "}{biz.__viewName || biz.name || 'Loop'}.</p>
          </div>
          <button className="bp-primary-btn bp-final-btn" onClick={() => openBookingForService(null, "final_cta")}>
            <span>Reservar ahora</span>
            <span className="bp-btn-arrow">›</span>
          </button>
        </section>

        <div style={{ padding: '28px 0 0', color: '#94a3b8', fontSize: 12, textAlign: 'center' }}>
          © 2026 {biz.__viewName || biz.name || 'Loop'}{t(". Todos los derechos reservados.")}</div>
      </article>
    </div>
  );


  const bookingModal = showBooking && (
    <div className="business-booking-overlay" onClick={() => setShowBooking(false)}>
      <div className="business-booking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="business-booking-topbar">
          <div className="business-booking-brand">
            <div className="business-booking-brand-logo">
              {getBusinessPageLogo(biz) ? (
                <img src={getBusinessPageLogo(biz)} alt={biz.__viewName || biz.name || t("Negocio")} />
              ) : (
                <span>{String(biz.__viewName || biz.name || 'L').trim().slice(0, 2).toUpperCase()}</span>
              )}
            </div>
            <div className="business-booking-brand-copy">
              <div className="business-booking-eyebrow">Reservar</div>
              <div className="business-booking-title">{biz.__viewName || biz.name}</div>
              <div className="business-booking-subtitle">{isEventsBusiness ? t("Prepara tu evento") : "Reserva tu cita"}</div>
            </div>
          </div>

          <div className="business-booking-actions">
            <button
              onClick={() => toggleFavorite(businessId)}
              title={isFav ? 'Quitar de Favoritos' : 'Agregar a Favoritos'}
              className="business-booking-icon-btn business-booking-fav-btn"
              aria-label={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
            >
              <Heart
                size={20}
                strokeWidth={2.4}
                color={isFav ? '#ef4444' : '#0f766e'}
                fill={isFav ? '#ef4444' : 'none'}
              />
            </button>

            <button
              onClick={() => setShowBooking(false)}
              className="business-booking-close-btn"
              aria-label={t("Cerrar reserva")}
            >
              <X size={20} strokeWidth={2.4} />
              <span>{t("Cerrar")}</span>
            </button>
          </div>
        </div>

        <div className="business-booking-body">
          <BookingInterface
            db={db}
            userId={effectiveUser && effectiveUser.uid ? effectiveUser.uid : null}
            businessId={businessId}
            businessName={biz.__viewName || biz.name}
            appId="web-app-d9799"
            services={services}
            servicesLoading={false}
            initialServiceId={selectedBookingService?.id || selectedBookingService?.serviceId || selectedBookingService?.slug || null}
            initialService={selectedBookingService || null}
            onBookingCompleted={handleBookingCompletedAnalytics}
            businessSchedule={null}
            user={effectiveUser || null}
            isEditing={false}
            onBackToBusinesses={() => { setShowBooking(false); navigate('/'); }}
            onEditToggle={() => {}}
            isAdmin={false}
          />
        </div>
      </div>
    </div>
  );

  const galleryModal = galleryIndex !== null && publicProjects[galleryIndex] ? (
    <div
      className="bp-public-gallery-modal-v4"
      role="dialog"
      aria-modal="true"
      aria-label={t("Galería del negocio")}
      onClick={() => setGalleryIndex(null)}
    >
      <div className="bp-public-gallery-modal-card-v4" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="bp-public-gallery-modal-close-v4"
          onClick={() => setGalleryIndex(null)}
          aria-label={t("Cerrar galería")}
        >
          <X size={20} />
        </button>

        <img
          src={publicProjects[galleryIndex].url}
          alt={publicProjects[galleryIndex].name || `Galería ${galleryIndex + 1}`}
        />

        {publicProjects.length > 1 ? (
          <>
            <button
              type="button"
              className="bp-public-gallery-modal-arrow-v4 is-prev"
              onClick={() => setGalleryIndex((current) => (
                current <= 0 ? publicProjects.length - 1 : current - 1
              ))}
              aria-label={t("Foto anterior")}
            >
              ←
            </button>
            <button
              type="button"
              className="bp-public-gallery-modal-arrow-v4 is-next"
              onClick={() => setGalleryIndex((current) => (
                current >= publicProjects.length - 1 ? 0 : current + 1
              ))}
              aria-label={t("Foto siguiente")}
            >
              →
            </button>
          </>
        ) : null}

        <div className="bp-public-gallery-modal-meta-v4">
          <strong>{publicProjects[galleryIndex].name || `Foto ${galleryIndex + 1}`}</strong>
          <span>{galleryIndex + 1}{" " + t("de") + " "}{publicProjects.length}</span>
        </div>
      </div>
    </div>
  ) : null;

  const projectsModal = selectedEmployee && (
    <EmployeeProjectsModal employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} />
  );
  return (
    <>
      <style>{pageStyles}</style>
      <style>{businessRefreshStyles}</style>
      <div id="bp-refresh-root" className={`business-page-shell bp-refresh ${isEventsBusiness ? "event-business-page" : ""}`}>
        {pageInner}
        {bookingModal}
        {galleryModal}
        {projectsModal}
      </div>
    </>
  );
}

/* rating CSS appended manually */
